import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  BODY_TYPE_COPY,
  CALIDAD_COPY,
  ILUMINACION_COPY,
  SCENE_SETTING_COPY,
  SKIN_LEVEL_COPY,
  type ProfileOption,
} from '../creative-profile';
import {
  api,
  ApiRequestError,
  useApi,
  type AssetView,
  type BeautyMode,
  type CreatorDetailData,
  type ContentView,
  type CreatorSummary,
  type TaskView,
} from '../api';
import {
  Button,
  Card,
  CopyButton,
  EmptyState,
  ErrorBanner,
  Icon,
  Loading,
  StatusBadge,
  Toma,
  formatDateTime,
  miniatura,
  haceCuanto,
  useTaskTypeLabel,
} from '../components/ui';
import { useAviso } from '../avisos';
import { idiomaActual, texto, useT, type Clave } from '../i18n';
import { ListaDeCorreos, type CorreoView } from '../components/Correos';
import { EditorDeFicha, type Ficha } from '../components/EditorDeFicha';

/**
 * Pestañas del detalle, en el orden que pide §16.
 *
 * ── Por qué la captación no está acá ───────────────────────────────────────────
 * Hubo una pestaña «Captación» —antes «Navegador»— con los referentes de esta influencer. Se fue
 * a **Referencias**, en el menú principal, porque la relación es de muchos a muchos: un referente
 * bueno lo trabajan varias influencers, y preguntándolo desde la ficha había que abrir todas para
 * saber quién trabaja una cuenta. Del otro lado se ve la grilla entera.
 *
 * Lo que se hacía acá y no era asignar —abrir el perfil, anotar la vuelta— es trabajo que ocurre
 * dentro de Instagram, no en este panel.
 */
const TABS = [
  'Resumen',
  'Identidad',
  'Galería',
  'Contenido',
  'LoRA',
  'Plataformas',
  'Correo',
  'Tareas',
  'Workflow',
  'Consumo',
  'Auditoría',
] as const;

type Tab = (typeof TABS)[number];

/**
 * Cómo se llama cada pestaña en pantalla.
 *
 * El identificador de `TABS` se queda en español porque es lo que se compara —`tabActiva ===
 * 'Galería'`— en una veintena de sitios de este archivo. Cambiarlo por una clave obligaría a
 * tocar los veinte para no ganar nada: lo que ve una persona es esta tabla, y es lo único que
 * tiene que hablar su idioma.
 */
const ETIQUETA_DE_TAB: Record<Tab, Clave> = {
  Resumen: 'tab.resumen',
  Identidad: 'tab.identidad',
  Galería: 'tab.galeria',
  Contenido: 'tab.contenido',
  LoRA: 'tab.lora',
  Plataformas: 'tab.plataformas',
  Correo: 'tab.correo',
  Tareas: 'tab.tareas',
  Workflow: 'tab.workflow',
  Consumo: 'tab.consumo',
  Auditoría: 'tab.auditoria',
};

/**
 * Deduce la relación de aspecto a partir de las dimensiones reales de la imagen.
 *
 * Se calcula y no se guarda porque el modelo no siempre honra lo que se le pide: schnell
 * ignora `width`/`height` y devuelve 1024×1024 aunque la base registre 4:5. Preguntarle a
 * los píxeles es lo único que no miente.
 */
function aspectRatioOf(width: number, height: number): string {
  const ratio = width / height;
  if (ratio > 1.4) return '16:9';
  if (ratio < 0.65) return '9:16';
  if (ratio < 0.9) return '4:5';
  return '1:1';
}

export function CreatorDetail() {
  const { t, idioma } = useT();
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState<Tab>('Resumen');

  /**
   * El sondeo se acelera mientras hay una generación en curso.
   *
   * Con veinte segundos fijos, pulsar «generar» dejaba la pantalla idéntica durante casi
   * medio minuto: la hoja de identidad se generaba bien y aun así parecía haber fallado.
   * Tres segundos mientras trabaja, veinte cuando no — sondear rápido en reposo sería
   * gastar peticiones para mirar algo que no cambia.
   */
  const [fastPoll, setFastPoll] = useState(false);

  /**
   * El borrado, con su confirmación.
   *
   * Los hooks van todos acá arriba y no junto al bloque que los usa: declarados después de
   * los `return` de carga, React los ejecuta en unos renders y en otros no —«rendered more
   * hooks than during the previous render»— y la pantalla queda en blanco.
   */
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const navigate = useNavigate();

  const act = async (key: string, run: () => Promise<unknown>): Promise<void> => {
    setBusy(key);
    try {
      await run();
    } finally {
      setBusy(null);
    }
  };
  /*
    El idioma viaja con la petición del detalle, y no es cosmético.

    Las instrucciones de las tareas se copian a la base al crearse: el servidor las compara
    contra la plantilla en cada carga y las reescribe si difieren. Sin mandar el idioma, esa
    comparación usaría el del sistema y las tareas quedarían en inglés con el panel en español.
  */
  const detail = useApi<CreatorDetailData>(
    id ? `/api/creators/${id}?idioma=${idioma}` : null,
    fastPoll ? 3_000 : 20_000,
  );

  const status = detail.data?.creator.status;
  useEffect(() => {
    setFastPoll(status === 'GENERATING_MEDIA');
  }, [status]);

  if (detail.loading && !detail.data) return <Loading label={t('ficha.cargando')} />;
  if (detail.error) return <ErrorBanner error={detail.error} onRetry={detail.reload} />;
  if (!detail.data) return null;

  const data = detail.data;

  /**
   * Una externa no tiene ficha: se vuelve a la tabla.
   *
   * ── Por qué no alcanzaba con recortarle las pestañas ──────────────────────────
   * Primero le dejé Plataformas y Auditoría, cuando conectarla exigía entrar acá. Desde que el
   * enlace de OAuth está en la propia fila, esta página no tiene nada que no esté allá: las
   * métricas se ven en la tabla, la conexión se hace en la tabla. Lo que quedaba era una ficha
   * con dos pestañas para no decir nada nuevo, y una URL que existe pero no sirve es otra
   * versión del control que se ve y no hace nada.
   *
   * `replace` para que el botón de atrás no traiga de vuelta a una página que se acaba de dejar.
   */
  if (data.creator.kind === 'external') return <Navigate to="/plataformas" replace />;

  const tabActiva: Tab = tab;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-semibold tracking-tight">{data.creator.publicName}</h1>
            <StatusBadge status={data.creator.status} />
          </div>
          <p className="mt-1 text-sm text-[var(--color-ink-muted)]">
            {data.creator.niche.join(' · ')}
            {data.creator.countryNarrative ? ` · ${data.creator.countryNarrative}` : ''}
            {data.creator.fictionalAge
              ? t('ficha.edadFicticia', { n: data.creator.fictionalAge })
              : ''}
          </p>
          {/* El disclosure se muestra siempre, no se esconde en una pestaña (§3). */}
          {data.creator.disclosureText && (
            <p className="mt-2 inline-block rounded-md bg-[var(--color-accent-soft)] px-2.5 py-1 text-xs">
              {data.creator.disclosureText}
            </p>
          )}
        </div>

        <div className="flex gap-2">
          {data.creator.status === 'PAUSED' ? (
            <Button
              variant="secondary"
              onClick={() => void api.post(`/api/creators/${id}/resume`).then(detail.reload)}
            >
              {t('ficha.reanudar')}
            </Button>
          ) : (
            <Button
              variant="secondary"
              onClick={() => void api.post(`/api/creators/${id}/pause`).then(detail.reload)}
            >
              {t('ficha.pausar')}
            </Button>
          )}

          {/*
            Borrar pide confirmación escribiendo el nombre, no un «¿estás seguro?».
            
            Un diálogo de confirmación se acepta por reflejo cuando aparece donde uno esperaba
            otro botón. Escribir el nombre obliga a mirar cuál es la influencer que se está
            borrando, que es el error que de verdad ocurre: borrar la de al lado.
          */}
          <Button variant="secondary" onClick={() => setDeleting(true)}>
            {t('ficha.borrar')}
          </Button>
        </div>
      </header>

      {deleting && (
        <div className="rounded-2xl border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/10 p-5">
          <p className="text-sm font-medium">
            {t('ficha.borrarA', { quien: data.creator.publicName })}
          </p>
          <p className="mt-1 max-w-2xl text-sm text-[var(--color-ink-muted)]">
            {t('ficha.borrarQueAntes')}
            <strong>{t('ficha.borrarSePuedeRecuperar')}</strong>
            {t('ficha.borrarQueDespues')}
          </p>

          <label className="mt-4 block text-xs font-medium" htmlFor="confirmar-borrado">
            {t('ficha.escribiParaConfirmarAntes')}
            <strong>{data.creator.publicName}</strong>
            {t('ficha.escribiParaConfirmarDespues')}
          </label>
          <input
            id="confirmar-borrado"
            value={deleteConfirm}
            onChange={(event) => setDeleteConfirm(event.target.value)}
            className="mt-1 w-full max-w-sm rounded-xl border border-[var(--color-border-subtle)] px-3 py-2 text-sm"
            placeholder={data.creator.publicName}
          />

          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              disabled={deleteConfirm.trim() !== data.creator.publicName || busy !== null}
              onClick={() =>
                void act('borrar', async () => {
                  await api.del(`/api/creators/${id}`);
                  navigate('/');
                })
              }
            >
              {t('ficha.borrar')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setDeleting(false);
                setDeleteConfirm('');
              }}
            >
              {t('comun.cancelar')}
            </Button>
          </div>

          {/*
            Liberar espacio es el caso raro y por eso es una casilla aparte, no el
            comportamiento por omisión: borrar una influencer casi siempre es sacarla de la
            vista, y eso no tiene por qué costar las imágenes que ya se pagaron.
          */}
          <p className="mt-4 text-xs text-[var(--color-ink-muted)]">
            {t('ficha.recuperarEspacio')}{' '}
            <button
              type="button"
              disabled={deleteConfirm.trim() !== data.creator.publicName || busy !== null}
              onClick={() =>
                void act('borrar', async () => {
                  await api.del(`/api/creators/${id}?purgeMedia=true`);
                  navigate('/');
                })
              }
              className="underline underline-offset-2 hover:text-[var(--color-danger)] disabled:opacity-50"
            >
              {t('ficha.borrarTambienImagenes')}
            </button>
            . <strong>{t('ficha.noSePuedeDeshacer')}</strong>
            {t('ficha.purgaQueDespues')}
          </p>
        </div>
      )}

      {data.creator.error && (
        <ErrorDeCreacion
          error={data.creator.error}
          enError={data.creator.status === 'ERROR'}
          creatorId={data.creator.id}
          onRetomar={detail.reload}
        />
      )}

      {/*
        Control segmentado en vez de pestañas subrayadas: nueve secciones subrayadas se leen
        como una lista de enlaces, y la activa se pierde. Encapsuladas en una pastilla, el
        conjunto se lee como un selector y la activa salta.
      */}
      <nav
        /*
          Envuelven en vez de deslizarse.
          
          Eran nueve en una tira `overflow-x-auto`: en un teléfono entran cuatro y las otras
          cinco quedan detrás de un gesto que nada anuncia — medido, la novena empezaba 298 px
          fuera de la pantalla. Es el mismo defecto que se acaba de sacar del menú principal, y
          acá pesaba más: son las secciones de la influencer, no un extra.
          
          Envolviendo ocupan dos o tres filas en el teléfono y una sola en un escritorio, donde
          siempre entraron. Nada se esconde.
        */
        className="flex flex-wrap gap-1 rounded-2xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-1"
        aria-label={t('tab.secciones')}
      >
        {/*
          La de Correo sólo si hay dominio configurado.

          Sin dominio propio no hay casilla que mirar ni dirección que cambiar: la pestaña
          existiría para mostrar un hueco y un formulario que falla. Las demás no dependen de
          nada porque siempre tienen algo que decir, aunque sea que está vacío.
        */}
        {TABS.filter((candidate) => candidate !== 'Correo' || data.dominioDeCorreo).map(
          (candidate) => (
            <button
              key={candidate}
              type="button"
              onClick={() => setTab(candidate)}
              aria-current={tabActiva === candidate ? 'page' : undefined}
              className={`shrink-0 rounded-xl px-3.5 py-1.5 text-sm font-medium transition ${
                tabActiva === candidate
                  ? 'bg-[var(--color-surface-raised)] text-[var(--color-ink)] shadow-[var(--shadow-panel)]'
                  : 'text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]'
              }`}
            >
              {t(ETIQUETA_DE_TAB[candidate])}
              {candidate === 'Tareas' && pendientesDeLaFicha(data.tasks) > 0 && (
                <span className="ml-2 rounded-full bg-[var(--color-warn)]/20 px-1.5 text-xs text-[var(--color-warn)]">
                  {pendientesDeLaFicha(data.tasks)}
                </span>
              )}
            </button>
          ),
        )}
      </nav>

      {tabActiva === 'Resumen' && <SummaryTab data={data} />}
      {tabActiva === 'Identidad' && <IdentityTab data={data} onReload={detail.reload} />}
      {tabActiva === 'Galería' && <GalleryTab data={data} onChange={detail.reload} />}
      {tabActiva === 'Contenido' && <Contenido data={data} onChange={detail.reload} />}
      {tabActiva === 'LoRA' && <LoraTab data={data} />}
      {tabActiva === 'Plataformas' && <PlatformsTab data={data} onChange={detail.reload} />}
      {tabActiva === 'Correo' && <CorreoTab data={data} onChange={detail.reload} />}
      {tabActiva === 'Tareas' && (
        <>
          {/*
            La bandeja se fue a su pestaña, y acá queda el aviso.

            Vivía acá con un argumento bueno: el único motivo para mirar este correo es
            completar un alta, y la confirmación llega mientras se está en esta pantalla. En
            una pestaña aparte hay que acordarse de ir a buscarla.

            El aviso conserva eso sin duplicar la bandeja: dice que llegó algo y adónde ir. Lo
            que el argumento no cubría era cambiar la dirección, que no es una tarea y no tenía
            dónde vivir.
          */}
          <AvisoDeCorreo creatorId={data.creator.id} />
          <TasksTab tasks={data.tasks} onChange={detail.reload} data={data} />
        </>
      )}
      {tabActiva === 'Workflow' && <WorkflowTab data={data} />}
      {tabActiva === 'Consumo' && <UsageTab data={data} />}
      {tabActiva === 'Auditoría' && <AuditTab data={data} />}
    </div>
  );
}

function SummaryTab({ data }: { data: CreatorDetailData }) {
  const { t } = useT();
  // Se busca por el id que declara el creator, no por estado: `APPROVED` también lo llevan
  // las tomas del set, y antes cualquiera de ellas podía aparecer como «la referencia».
  const canonical = data.assets.find(
    (asset) => asset.id === data.creator.canonicalReferenceAssetId,
  );
  // Sin los pasos de la persona: viven en Credenciales y contarlos acá haría que una ficha
  // impecable mostrara pendientes que no son suyos.
  const pendingTasks = data.tasks.filter(
    (task) => task.status !== 'COMPLETED' && !esPasoDeLaPersona(task),
  );

  return (
    /*
      `items-start` por lo mismo que en el tablero: sin él, «Referencia canónica» se estiraba
      220 px por debajo de su imagen para igualar el alto de las dos tarjetas de al lado. Un
      recuadro vacío que termina lejos de su contenido se lee como un error de maquetado, que
      es exactamente lo que era.
    */
    <div className="grid items-start gap-6 lg:grid-cols-3">
      <Card title={t('ficha.referenciaCanonica')} className="lg:col-span-1">
        {canonical ? (
          <figure>
            <Toma
              url={canonical.url}
              alt={t('ficha.referenciaVisualDe', { quien: data.creator.publicName })}
              ancho={420}
              className="w-full rounded-lg"
            />
            <figcaption className="mt-2 text-xs text-[var(--color-ink-muted)]">
              {t('ficha.seAdjunta')}
            </figcaption>
          </figure>
        ) : (
          <EmptyState title={t('ficha.sinReferencia')} description={t('ficha.sinReferenciaQue')} />
        )}
      </Card>

      <div className="space-y-6 lg:col-span-2">
        <Card title={t('ficha.estado')}>
          <dl className="grid gap-3 sm:grid-cols-2">
            <div>
              <dt className="text-xs text-[var(--color-ink-muted)]">
                {t('ficha.imagenesGeneradas')}
              </dt>
              <dd className="text-lg font-semibold tabular-nums">{data.assets.length}</dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--color-ink-muted)]">{t('ficha.enCalendario')}</dt>
              {/*
                Sin las de Fanvue, porque no están en el calendario.

                Ésas se publican a mano desde su pestaña y no se programan. Contarlas acá inflaba
                un número que dice «cuánto hay planificado para las redes».
              */}
              <dd className="text-lg font-semibold tabular-nums">
                {data.content.filter((item) => item.clasificacion !== 'nsfw').length}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--color-ink-muted)]">
                {t('ficha.tareasPendientes')}
              </dt>
              <dd className="text-lg font-semibold tabular-nums">{pendingTasks.length}</dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--color-ink-muted)]">
                {t('ficha.versionDeLaFicha')}
              </dt>
              <dd className="text-lg font-semibold tabular-nums">v{data.bibleVersion}</dd>
            </div>
          </dl>
        </Card>

        {pendingTasks.length > 0 && (
          <Card title={t('ficha.proximaAccion')}>
            <p className="text-sm font-medium">{pendingTasks[0]?.type}</p>
            <p className="mt-1 text-sm text-[var(--color-ink-muted)]">
              {pendingTasks[0]?.rationale}
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}

function IdentityTab({ data, onReload }: { data: CreatorDetailData; onReload: () => void }) {
  const { t } = useT();
  const [perfil, setPerfil] = useState(data.creator.creativeProfile);
  const [guardando, setGuardando] = useState(false);
  const [resultado, setResultado] = useState<string | null>(null);

  if (!data.bible) {
    return <EmptyState title={t('ficha.sinFicha')} description={t('ficha.sinFichaQue')} />;
  }

  const esPersonaje = data.creator.kind === 'character';
  const sinGuardar = JSON.stringify(perfil) !== JSON.stringify(data.creator.creativeProfile);
  const cambiaCuerpo = perfil.bodyType !== data.creator.creativeProfile.bodyType;

  const guardar = async (): Promise<void> => {
    setGuardando(true);
    setResultado(null);
    try {
      const respuesta = await api.patch<{ needsRegeneration: boolean; bibleRewritten: boolean }>(
        `/api/creators/${data.creator.id}/creative-profile`,
        perfil,
      );
      setResultado(
        respuesta.needsRegeneration
          ? respuesta.bibleRewritten
            ? t('ident.guardadoYReescrito')
            : t('ident.guardadoSinReescribir')
          : t('ident.guardadoSinRehacer'),
      );
      onReload();
    } finally {
      setGuardando(false);
    }
  };

  const bible = data.bible as Record<string, unknown>;

  return (
    <div className="space-y-6">
      {/*
        Las perillas se editan acá y no en un formulario aparte porque son parte de la
        identidad: quién es y cómo se la fotografía. Y no se escriben a mano — son las mismas
        opciones que al crearla, porque un prompt escrito libre se sale de las políticas y de
        lo que el planificador sabe manejar.
      */}
      <Card title={t('ident.estiloVisual')}>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {/*
            Un personaje no humano no tiene nivel de piel, complexión ni interiores.

            El código ya los ignora —el sufijo le prohíbe la ropa y el planificador lo manda a
            su propio mundo— pero la pantalla los mostraba igual, diciendo «ropa cotidiana,
            siempre cubierta» sobre una criatura que el generador tiene prohibido vestir. Un
            control que se ve, se puede cambiar y no hace nada es peor que uno ausente: hace
            dudar de si el resto de la pantalla dice la verdad.
          */}
          {/*
            El registro y el tono del feed ya no se ofrecen.

            Eran cuatro registros por cuatro tonos —dieciséis combinaciones que nadie midió— y
            sólo una se parece a lo que la gente publica. La vara la pusieron dos selfies reales:
            con el encuadre igualado, este sistema dio cara de 290 px contra 315 y nitidez 236,3
            contra 244,7. No faltaba calidad, faltaba pedir un look concreto, y ahora es el mismo
            para todas — `LOOK_FIJO`.

            Los campos siguen en el esquema y en la base con sus valores, así que volver a
            ofrecerlos no cuesta una migración. Lo que se saca es la elección, no el dato: un
            control que se puede cambiar y no cambia nada es peor que uno ausente.
          */}
          {(
            [
              ...(esPersonaje
                ? []
                : ([
                    [t('ident.nivelDePiel'), 'skinLevel', SKIN_LEVEL_COPY],
                    [t('ident.complexion'), 'bodyType', BODY_TYPE_COPY],
                  ] as [string, keyof typeof perfil, readonly ProfileOption[]][])),
              ...(esPersonaje
                ? []
                : ([[t('ident.escenas'), 'sceneSetting', SCENE_SETTING_COPY]] as [
                    string,
                    keyof typeof perfil,
                    readonly ProfileOption[],
                  ][])),
              /*
                La luz SÍ se ofrece para un personaje no humano.

                A diferencia del nivel de piel o la complexión —que no tienen sentido sobre una
                criatura— la iluminación es de la FOTO, no del sujeto: un flash de celular de
                noche sobre una criatura es tan válido como sobre una persona, y es de lo poco
                que distingue una toma suya de otra.
              */
              [t('ident.iluminacion'), 'iluminacion', ILUMINACION_COPY] as [
                string,
                keyof typeof perfil,
                readonly ProfileOption[],
              ],
              /*
                La calidad va aparte de la luz porque son independientes.

                Estuvieron juntas un día —había una luz llamada «cruda» que era una foto mala de
                noche— y el defecto se vio enseguida: «siempre salen de noche, también deberían
                salir de día». Una cámara mala es la misma de día que de noche; mezclarlas dejaba
                la mitad de los casos sin poder pedirse.
              */
              [t('ident.calidad'), 'calidad', CALIDAD_COPY] as [
                string,
                keyof typeof perfil,
                readonly ProfileOption[],
              ],
            ] as [string, keyof typeof perfil, readonly ProfileOption[]][]
          ).map(([label, campo, opciones]) => (
            <div key={campo}>
              <label className="block text-xs font-medium">{label}</label>
              <select
                value={perfil[campo]}
                onChange={(event) => setPerfil({ ...perfil, [campo]: event.target.value })}
                className="mt-1 w-full rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] px-3 py-2 text-sm"
              >
                {opciones.map((opcion) => (
                  <option key={opcion.value} value={opcion.value}>
                    {t(opcion.label)}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-[var(--color-ink-muted)]">
                {(() => {
                  // Las opciones traen CLAVES, no texto: sin `t` la pantalla mostraría
                  // «perfil.piel.calle». Ver `creative-profile.ts`.
                  const elegida = opciones.find((opcion) => opcion.value === perfil[campo]);
                  return elegida ? t(elegida.detail) : null;
                })()}
              </p>
            </div>
          ))}
        </div>

        {/*
          La consecuencia se dice antes de guardar, no después.

          La complexión no vive en estas perillas sino en la descripción física de la ficha,
          que es el texto que se repite en cada generación. Cambiarla obliga a reescribirla y
          deja las imágenes canónicas con el cuerpo anterior — y eso no se deduce del selector.
        */}
        {esPersonaje && (
          <p className="mt-4 rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-3 text-xs text-[var(--color-ink-muted)]">
            {t('ident.esPersonaje')}
          </p>
        )}

        {cambiaCuerpo && !esPersonaje && (
          <p className="mt-4 rounded-xl border border-[var(--color-warn)]/40 bg-[var(--color-warn)]/10 p-3 text-xs">
            {t('ident.cambiaCuerpoAntes')}
            <strong>{t('ident.cambiaCuerpoFuerte')}</strong>
            {t('ident.cambiaCuerpoDespues')}
          </p>
        )}

        {resultado && (
          <p className="mt-4 rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-3 text-xs">
            {resultado}
          </p>
        )}

        <div className="mt-4">
          <Button disabled={!sinGuardar || guardando} onClick={() => void guardar()}>
            {t(guardando ? 'comun.guardando' : 'ident.guardarCambios')}
          </Button>
        </div>
      </Card>

      {/*
        La ficha entera, editable, con el mismo componente que se usa al crearla.

        Antes esto eran tres tarjetas de sólo lectura: «Personaje», «Identidad visual» —que
        mostraba las claves crudas del JSON, `skinTone`, `faceShape`— y «Afirmaciones
        prohibidas», que era justo lo que uno querría ajustar y era lo único que no se podía
        tocar. Para cambiar una muletilla había que regenerar la influencer entera.
      */}
      <FichaEditable
        creatorId={data.creator.id}
        bible={bible as unknown as Ficha}
        version={data.bibleVersion}
        onSaved={onReload}
      />
    </div>
  );
}

/**
 * La ficha de una influencer ya creada.
 *
 * Guarda sólo cuando hay algo distinto y lo dice: una ficha larga con un botón siempre activo no
 * deja saber si lo que se ve en pantalla es lo que está guardado.
 */
function FichaEditable({
  creatorId,
  bible,
  version,
  onSaved,
}: {
  creatorId: string;
  bible: Ficha;
  version: number;
  onSaved: () => void;
}) {
  const aviso = useAviso();
  const { t } = useT();
  const [borrador, setBorrador] = useState<Ficha>(bible);
  const [guardando, setGuardando] = useState(false);

  const sinGuardar = JSON.stringify(borrador) !== JSON.stringify(bible);

  const guardar = async (): Promise<void> => {
    setGuardando(true);
    try {
      await api.patch(`/api/creators/${creatorId}/bible`, borrador);
      aviso.ok(t('ident.fichaGuardada'));
      onSaved();
    } catch (caught) {
      if (!(caught instanceof ApiRequestError)) aviso.error(t('ident.noSePudoGuardarFicha'));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Card
      title={t('ident.ficha')}
      actions={
        <div className="flex items-center gap-3">
          <span className="text-xs text-[var(--color-ink-muted)]">v{version}</span>
          <Button disabled={!sinGuardar || guardando} onClick={() => void guardar()}>
            {t(guardando ? 'comun.guardando' : 'ident.guardarFicha')}
          </Button>
        </div>
      }
    >
      {/*
        Cada guardado deja una versión nueva, y las anteriores son la procedencia de las
        imágenes ya generadas. Cambiar la descripción física no rehace nada de lo ya hecho.
      */}
      <p className="mb-4 text-xs text-[var(--color-ink-muted)]">
        {t('ident.versionNuevaAntes')}
        <strong>{t('ident.descripcionFisica')}</strong>
        {t('ident.versionNuevaDespues')}
      </p>
      <EditorDeFicha ficha={borrador} onChange={setBorrador} />
    </Card>
  );
}

/**
 * Un dato que se puede corregir sin salir de donde se lee.
 *
 * Muestra el valor como texto y se convierte en campo al pulsarlo. El error del servidor se
 * muestra en su lugar en vez de descartarse: las reglas de usuario de cada plataforma se
 * validan del otro lado, y sin verlas quien corrige prueba a ciegas.
 */
function EditableField({
  label,
  value,
  placeholder,
  onSave,
}: {
  label: string;
  value: string | null;
  placeholder: string;
  onSave: (value: string) => Promise<unknown>;
}) {
  const aviso = useAviso();
  const { t } = useT();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [busy, setBusy] = useState(false);

  const guardar = async (): Promise<void> => {
    setBusy(true);
    try {
      await onSave(draft.trim());
      setEditing(false);
    } catch (caught) {
      if (!(caught instanceof ApiRequestError)) aviso.error(t('ident.noSePudoGuardar'));
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-baseline justify-between gap-3">
        <dt className="text-[var(--color-ink-muted)]">{label}</dt>
        <dd className="flex items-baseline gap-2">
          <span>{value ?? placeholder}</span>
          <button
            type="button"
            onClick={() => {
              setDraft(value ?? '');
              setEditing(true);
            }}
            className="text-xs text-[var(--color-ink-muted)] underline underline-offset-2 hover:text-[var(--color-accent)]"
          >
            {t('ident.editar')}
          </button>
        </dd>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <dt className="text-[var(--color-ink-muted)]">{label}</dt>
      <dd className="space-y-2">
        <input
          autoFocus
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void guardar();
            if (event.key === 'Escape') setEditing(false);
          }}
          className="w-full rounded-lg border border-[var(--color-border-subtle)] px-2 py-1 text-sm"
        />
        <div className="flex gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => void guardar()}
            className="rounded-lg bg-[var(--color-accent)] px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
          >
            {t(busy ? 'comun.guardando' : 'comun.guardar')}
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="rounded-lg border border-[var(--color-border-subtle)] px-3 py-1 text-xs"
          >
            Cancelar
          </button>
        </div>
      </dd>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-medium text-[var(--color-ink-muted)]">{label}</dt>
      <dd className="mt-0.5">{value || '—'}</dd>
    </div>
  );
}

/** Los tres ángulos de la hoja de identidad. No son contenido publicable. */
const IDENTITY_KINDS = ['identity_three_quarter', 'identity_profile', 'identity_medium'];

/**
 * Galería con revisión humana (§16), organizada por etapa.
 *
 * ── Por qué pestañas y no una lista larga ───────────────────────────────────────
 * Las tres etapas producen imágenes que **no son lo mismo**: las frontales y la hoja son
 * anclas técnicas de 448×448 que nunca se publican, y el contenido son las fotos que sí van
 * a las redes. Mezcladas en una sola columna se leían como una galería desordenada de la
 * misma cosa, y no había forma de mirar sólo lo que importa en cada momento.
 *
 * Se puede navegar libremente entre las tres. La pestaña arranca en la etapa activa, pero
 * volver a mirar las frontales después de aprobar la hoja es una necesidad real —comparar
 * la cara— y bloquearlo sería paternalista.
 *
 * ── Por qué la elección de la canónica es humana ────────────────────────────────
 * Comparar caras de verdad exige un modelo de visión que cuesta más neuronas que generar
 * las propias imágenes, y elegir una cara es, al final, una decisión de criterio.
 *
 * Por eso tampoco se muestra un «puntaje de consistencia» junto a las candidatas: el que se
 * calcula compara parámetros de generación, no píxeles, y exhibirlo al lado de las fotos
 * invitaba a leerlo como parecido facial.
 */
/**
 * Estados en los que una imagen ya no participa de nada. Espeja al servidor.
 *
 * No se exige `APPROVED` a secas: las tomas del flujo viejo quedaron en `PENDING_MODERATION`
 * y son perfectamente publicables. Pedir aprobación explícita las escondería sin que nadie
 * las hubiera rechazado.
 */
const RETIRED_STATUSES = ['DISCARDED', 'REJECTED', 'NEEDS_REGENERATION'];

const isRetired = (asset: AssetView): boolean => RETIRED_STATUSES.includes(asset.status);

/**
 * Cuántas publicadas se dibujan, de las dos pestañas que las muestran.
 *
 * ── El defecto ────────────────────────────────────────────────────────────────
 * Las dos listas de material ya publicado se dibujaban enteras. Cada tarjeta descarga su
 * imagen firmada, así que una influencer con dos meses de trabajo abría la pestaña pidiendo
 * cien imágenes al servidor de archivos — todas de material que ya cumplió su función.
 *
 * ── Por qué diez ──────────────────────────────────────────────────────────────
 * Porque lo publicado se mira para una sola cosa: ver qué se acaba de sacar antes de repetir
 * un encuadre o un pie de foto. Eso se contesta con lo último, y una tanda es de diez.
 *
 * ── Por qué se recorta en la pantalla y no en la consulta ─────────────────────
 * Porque el resto de las dos pestañas necesita la lista completa —los contadores, el reparto
 * entre publicado y pendiente, el cruce con las publicaciones— y pedir dos listas distintas al
 * servidor serían dos fuentes que pueden divergir. Lo caro no era traerlas: era dibujarlas.
 *
 * Lo que falta por publicar nunca se recorta: ésa es la pregunta que las dos pantallas
 * contestan, y una respuesta cortada por la mitad es peor que una lista larga.
 */
const ULTIMAS_PUBLICADAS = 10;

/**
 * Lo que se fija a mano antes de pedir una tanda.
 *
 * ── Qué hace cada select vacío ────────────────────────────────────────────────
 * Nada, y eso es el punto: los cinco ejes ya se reparten hoy, uno por toma. Un select vacío
 * deja ese sorteo como estaba, y por eso la tanda de siempre sigue saliendo igual sin que nadie
 * toque un control. Lo que se elige, en cambio, vale para las cuatro tomas.
 *
 * ── Por qué las opciones vienen del servidor ──────────────────────────────────
 * Porque son los catálogos de verdad —setenta y cuatro lugares, cuarenta y dos actividades— y
 * una copia acá se desincroniza el día que alguien agrega un lugar: el select ofreceria una
 * clave que el servidor descarta en silencio. Se piden una vez y sirven para todas.
 *
 * ── Por qué el de ropa se apaga cuando hay lugar ──────────────────────────────
 * Porque el lugar ya dice de qué cubo es. Un lugar de gimnasio con «ropa de agua» pide una
 * bikini en una sala de pesas, y el servidor lo resuelve a favor del lugar igual. Un control
 * que se puede mover y no hace nada es peor que uno apagado que dice por qué.
 */
type Encargo = {
  tipo?: string;
  participa?: string;
  techo?: string;
  lugar?: string;
  encuadre?: string;
  cubo?: string;
  actividad?: string;
  /** El papel que juega esta tanda. Vacío es ella misma, sin rol. */
  rol?: string;
  /**
   * Sesión de fotos: todas las tomas en el mismo lugar y con la misma ropa.
   *
   * El único del encargo que no es una cadena, y por eso no pasa por `fijar`. Ausente y `false`
   * son lo mismo: se borra en vez de guardarse apagado. Ver `fijarSesion`.
   */
  sesion?: boolean;
};

type NombreLegible = { es: string; en: string };

type OpcionesDeTanda = {
  lugares: {
    clave: string;
    setting: string;
    nombre: NombreLegible;
    participa: boolean;
    techo: string;
  }[];
  actividades: { setting: string; hace: string; nombre: NombreLegible }[];
  tipos: string[];
  encuadres: string[];
  cubos: string[];
  roles: { clave: string; nombre: NombreLegible; cubo: string }[];
};

/*
  Los rótulos de los tres ejes cortos salen de la tabla de textos, como todo el panel.

  Estaban escritos acá, en castellano, dentro de un panel que también se ve en inglés. Es la
  mitad del defecto que Rodrigo vio —«ahí están en español»— y la otra mitad eran las claves de
  los lugares mostradas crudas. Son trece valores de listas cerradas, así que acá sí
  corresponde la tabla de textos: no crecen cuando alguien agrega un lugar.
*/
/** Se rellenan dentro del componente, que es donde existe el traductor. */

function ConfiguracionDeLaTanda({
  creatorId,
  encargo,
  onCambio,
  disabled,
}: {
  /** Para la ruta que traduce el texto libre. Es lo único que necesita del servidor. */
  creatorId: string;
  encargo: Encargo;
  onCambio: (encargo: Encargo) => void;
  disabled: boolean;
}) {
  const { t, idioma } = useT();

  /*
    El texto libre y lo que el LLM entendió de él.

    Viven acá y no suben al padre a propósito: el padre guarda el ENCARGO, que es lo que la
    tanda necesita. El texto es cómo se llegó a él, y conservarlo arriba haría creer que
    también viaja al servidor, cuando lo que viaja es el encargo ya traducido.
  */
  const [textoLibre, setTextoLibre] = useState('');
  const [entendi, setEntendi] = useState('');
  const [traduciendo, setTraduciendo] = useState(false);

  /*
    Traducir REEMPLAZA el encargo entero en vez de mezclarlo con lo que hubiera.

    Mezclar dejaría restos de un pedido anterior: se escribe «una pasarela» sobre un encargo
    que tenía «pileta de barrio» y el lugar viejo sobrevive porque el LLM no nombró ese eje. El
    resultado sería una tanda que nadie pidió y que no se parece ni a lo escrito ni a lo que
    mostraba la pantalla antes.

    Los desplegables quedan debajo para ajustar a mano lo que haga falta.
  */
  const traducir = async () => {
    setTraduciendo(true);
    try {
      const r = await api.post<{ encargo: Encargo; entendi: string }>(
        `/api/creators/${creatorId}/encargo-escrito`,
        { texto: textoLibre },
      );
      onCambio(r.encargo ?? {});
      setEntendi(r.entendi ?? '');
    } catch (e) {
      setEntendi(e instanceof Error ? e.message : String(e));
    } finally {
      setTraduciendo(false);
    }
  };
  const opciones = useApi<OpcionesDeTanda>('/api/opciones-de-tanda');
  const datos = opciones.data;

  /*
    Los rótulos de los tres ejes cortos, traducidos como todo el resto del panel.

    Estaban escritos en castellano fuera del componente, dentro de un panel que también se ve en
    inglés. Acá la tabla de textos es lo que corresponde —son trece valores de listas cerradas y
    no crecen cuando alguien agrega un lugar— y por eso se rellenan acá adentro, que es donde
    existe el traductor.
  */
  const NOMBRE_DEL_TIPO: Record<string, string> = {
    actividad: t('encargo.tipo.actividad'),
    selfie: t('encargo.tipo.selfie'),
    espejo: t('encargo.tipo.espejo'),
    atractivo: t('encargo.tipo.atractivo'),
    imperfecta: t('encargo.tipo.imperfecta'),
  };
  const NOMBRE_DEL_ENCUADRE: Record<string, string> = {
    close_up: t('encargo.encuadre.close_up'),
    medium: t('encargo.encuadre.medium'),
    full_body: t('encargo.encuadre.full_body'),
    wide: t('encargo.encuadre.wide'),
  };
  const NOMBRE_DEL_CUBO: Record<string, string> = {
    agua: t('encargo.cubo.agua'),
    gimnasio: t('encargo.cubo.gimnasio'),
    interior: t('encargo.cubo.interior'),
    calle: t('encargo.cubo.calle'),
  };

  /*
    Y el nombre de cada lugar y cada actividad, en el idioma del panel.

    Los dos viajan desde el servidor y elige la pantalla. El select de lugares mostraba la CLAVE
    —«toro mecanico», sin acentos y en castellano— dentro de un panel en inglés, y el de
    actividades mostraba el inglés crudo: dos controles pegados y dos idiomas.
  */
  const enSuIdioma = (nombre: NombreLegible) => nombre[idioma] ?? nombre.en;

  /*
    Un cambio a vacío borra la clave en vez de guardarla vacía.

    El servidor distingue «no vino» de «vino vacío» sólo porque el esquema lo declara opcional;
    mandar una cadena vacía lo haría rechazar el encargo entero por un select que alguien
    volvió a poner en «al azar».
  */
  /*
    `keyof Encargo` menos `sesion`, porque `sesion` es la única que no es una cadena.

    No es cosmético: sin el descarte, TypeScript acepta `fijar('sesion', 'lo que sea')` y lo
    que se guarda es la cadena, no un booleano. El encargo llegaría al servidor con
    `sesion: 'true'`, que la limpieza descarta por no ser `=== true`, y la casilla parecería no
    hacer nada sin que nada fallara.
  */
  const fijar = (campo: Exclude<keyof Encargo, 'sesion'>, valor: string) => {
    const siguiente = { ...encargo };
    if (valor) siguiente[campo] = valor;
    else delete siguiente[campo];
    onCambio(siguiente);
  };

  /*
    La sesión va aparte de `fijar` porque es la única del encargo que no es una cadena.

    Y se BORRA en vez de guardarse en `false`: los demás ejes vacíos significan «no elegí», y
    un `sesion: false` guardado diría «elegí que no», que es lo mismo con más ruido. La
    limpieza del encargo descarta ese false igual, así que guardarlo sólo desincronizaría la
    pantalla con lo que el servidor conserva.
  */
  const fijarSesion = (activa: boolean) => {
    const siguiente = { ...encargo };
    if (activa) siguiente.sesion = true;
    else delete siguiente.sesion;
    onCambio(siguiente);
  };

  const lugarElegido = datos?.lugares.find((lugar) => lugar.clave === encargo.lugar);

  /*
    El primer select recorta el segundo.

    Setenta y cinco lugares en un desplegable es una lista que se recorre, no una que se elige.
    Partida en bajo techo y al aire libre son treinta y cuarenta y cinco, y la mitad de las
    veces quien busca ya sabe de cuál de las dos.

    El filtro no borra lo elegido: si se cambia el techo con un lugar puesto que ya no entra, se
    ve igual en la lista. Borrarlo sería perder una elección por tocar un filtro.
  */
  const lugaresVisibles = (datos?.lugares ?? []).filter(
    (lugar) => !encargo.techo || lugar.techo === encargo.techo || lugar.clave === encargo.lugar,
  );

  const clase =
    'mt-1 w-full rounded-lg border border-[var(--color-border-subtle)] bg-transparent px-2 py-1.5 text-sm disabled:opacity-40';

  return (
    <details className="rounded-2xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)]">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-4 text-sm">
        <span>
          <span className="font-medium">{t('encargo.titulo')}</span>
          <span className="ml-2 text-xs text-[var(--color-ink-muted)]">
            {Object.keys(encargo).length > 0
              ? t('encargo.fijados', { n: Object.keys(encargo).length })
              : t('encargo.todoAlAzar')}
          </span>
        </span>
        <span aria-hidden="true" className="shrink-0 text-[var(--color-ink-muted)]">
          ›
        </span>
      </summary>

      {/*
        El texto libre va ARRIBA de los desplegables, y los dos se quedan.

        ── Por qué no reemplaza a la grilla ────────────────────────────────────────
        Se pidió cambiar los ocho selects «por un texto libre asistido por LLM». Lo que se hizo
        es que el texto ESCRIBA los selects, y eso es mejor que reemplazarlos por dos motivos
        que sólo se ven usándolo:

          se puede corregir   El LLM entiende mal a veces. Si el texto fuera lo único, la única
                              manera de arreglar un detalle sería reescribir la frase entera y
                              volver a jugarse los otros siete ejes.
          se ve qué entendió  Los desplegables quedan como el resultado legible de la
                              traducción. Un texto libre que produce una foto sin mostrar qué
                              entendió deja adivinando por qué salió lo que salió.

        O sea: escribís, mira lo que quedó puesto, ajustás lo que haga falta, generás.

        ── Y por qué no cuesta placa ──────────────────────────────────────────────
        Traducir es una llamada de texto. Se puede reescribir el pedido las veces que haga
        falta sin gastar una imagen, y por eso son dos botones y no uno.
      */}
      <div className="px-4 pb-3">
        <label className="block">
          <span className="text-xs font-medium">{t('encargo.escrito')}</span>
          <textarea
            value={textoLibre}
            onChange={(e) => setTextoLibre(e.target.value)}
            disabled={disabled || traduciendo}
            rows={2}
            placeholder={t('encargo.escritoEjemplo')}
            className={`${clase} resize-y`}
          />
        </label>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={disabled || traduciendo || textoLibre.trim().length < 3}
            onClick={() => void traducir()}
            className="rounded-xl border border-[var(--color-accent)]/40 px-3 py-1.5 text-sm font-medium transition hover:border-[var(--color-accent)] disabled:opacity-50"
          >
            {traduciendo ? t('encargo.traduciendo') : t('encargo.traducir')}
          </button>
          {entendi && (
            <span className="text-xs text-[var(--color-ink-muted)]">
              {t('encargo.entendi', { texto: entendi })}
            </span>
          )}
        </div>
      </div>

      <div className="grid gap-3 p-4 pt-0 sm:grid-cols-2 lg:grid-cols-3">
        {/*
          El rol va PRIMERO porque es el que más decide.

          Elegirlo llena solo el cubo, el lugar y la actividad — los tres ejes que le siguen en
          esta grilla. Ponerlo después obligaría a elegir un lugar para descubrir un renglón más
          abajo que el rol lo iba a reemplazar igual.

          Y la opción vacía se llama «Ella misma» y no «Al azar» como las demás, porque no es lo
          mismo: las otras vacías sortean un valor, y ésta no sortea ningún rol. Un desplegable
          que dice «al azar» donde en realidad dice «ninguno» promete una variedad que no hay.
        */}
        <label>
          <span className="text-xs font-medium">{t('encargo.rol')}</span>
          <select
            value={encargo.rol ?? ''}
            onChange={(e) => fijar('rol', e.target.value)}
            disabled={disabled || !datos}
            className={clase}
          >
            <option value="">{t('encargo.ellaMisma')}</option>
            {(datos?.roles ?? []).map((rol) => (
              <option key={rol.clave} value={rol.clave}>
                {rol.nombre[idioma] ?? rol.nombre.es}
              </option>
            ))}
          </select>
        </label>

        {/*
          La sesión va JUNTO AL ROL y no al final, porque los dos son de la tanda entera y no de
          cada toma: el rol decide qué hace en todas, la sesión decide que todas pasan en el
          mismo sitio. Los que siguen —techo, lugar, encuadre— son ejes que se reparten.

          Y es una casilla y no un desplegable con «al azar»: las demás vacías sortean un valor,
          y ésta no sortea nada. Un desplegable prometería una variedad que no hay, que es el
          mismo motivo por el que el rol vacío se llama «Ella misma» y no «Al azar».
        */}
        <label className="flex flex-col justify-end gap-1">
          <span className="text-xs font-medium">{t('encargo.sesion')}</span>
          <span className="flex items-center gap-2 py-2">
            <input
              type="checkbox"
              checked={encargo.sesion === true}
              onChange={(e) => fijarSesion(e.target.checked)}
              disabled={disabled || !datos}
              className="h-4 w-4 accent-neutral-900 dark:accent-neutral-100"
            />
            <span className="text-xs text-neutral-600 dark:text-neutral-400">
              {t('encargo.sesionAyuda')}
            </span>
          </span>
        </label>

        <label>
          <span className="text-xs font-medium">{t('encargo.techo')}</span>
          <select
            value={encargo.techo ?? ''}
            onChange={(e) => fijar('techo', e.target.value)}
            disabled={disabled || !datos}
            className={clase}
          >
            <option value="">{t('encargo.alAzar')}</option>
            <option value="adentro">{t('encargo.adentro')}</option>
            <option value="afuera">{t('encargo.afuera')}</option>
          </select>
        </label>

        <label>
          <span className="text-xs font-medium">{t('encargo.lugar')}</span>
          <select
            value={encargo.lugar ?? ''}
            onChange={(e) => fijar('lugar', e.target.value)}
            disabled={disabled || !datos}
            className={clase}
          >
            <option value="">{t('encargo.alAzar')}</option>
            {(datos?.cubos ?? []).map((cubo) => (
              <optgroup key={cubo} label={NOMBRE_DEL_CUBO[cubo] ?? cubo}>
                {lugaresVisibles
                  .filter((lugar) => lugar.setting === cubo)
                  .map((lugar) => (
                    <option key={lugar.clave} value={lugar.clave}>
                      {enSuIdioma(lugar.nombre)}
                    </option>
                  ))}
              </optgroup>
            ))}
          </select>
        </label>

        <label>
          <span className="text-xs font-medium">{t('encargo.tipo')}</span>
          <select
            value={encargo.tipo ?? ''}
            onChange={(e) => fijar('tipo', e.target.value)}
            disabled={disabled || !datos}
            className={clase}
          >
            <option value="">{t('encargo.alAzar')}</option>
            {(datos?.tipos ?? []).map((tipo) => (
              <option key={tipo} value={tipo}>
                {NOMBRE_DEL_TIPO[tipo] ?? tipo}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span className="text-xs font-medium">{t('encargo.encuadre')}</span>
          <select
            value={encargo.encuadre ?? ''}
            onChange={(e) => fijar('encuadre', e.target.value)}
            disabled={disabled || !datos}
            className={clase}
          >
            <option value="">{t('encargo.alAzar')}</option>
            {(datos?.encuadres ?? []).map((encuadre) => (
              <option key={encuadre} value={encuadre}>
                {NOMBRE_DEL_ENCUADRE[encuadre] ?? encuadre}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span className="text-xs font-medium">{t('encargo.ropa')}</span>
          <select
            value={lugarElegido ? lugarElegido.setting : (encargo.cubo ?? '')}
            onChange={(e) => fijar('cubo', e.target.value)}
            disabled={disabled || !datos || Boolean(lugarElegido)}
            title={lugarElegido ? t('encargo.ropaDelLugar') : undefined}
            className={clase}
          >
            <option value="">{t('encargo.alAzar')}</option>
            {(datos?.cubos ?? []).map((cubo) => (
              <option key={cubo} value={cubo}>
                {NOMBRE_DEL_CUBO[cubo] ?? cubo}
              </option>
            ))}
          </select>
        </label>

        {/*
          Si está USANDO el lugar o parada al lado.

          El catálogo describe el sitio y la pone al lado —el toro mecánico dice literalmente
          «the worn vinyl bull behind her»— y eso es una pose con decorado. Arriba del toro es
          un momento. Los dos sirven, y por eso es un mando y no una decisión tomada.

          El select dice cuándo NO aplica: la mayoría de los lugares no tiene una manera obvia
          de estar adentro de la cosa que les da nombre, y ahí esto no hace nada. Decirlo evita
          que alguien crea que el control está roto.
        */}
        <label>
          <span className="text-xs font-medium">{t('encargo.participa')}</span>
          <select
            value={encargo.participa ?? ''}
            onChange={(e) => fijar('participa', e.target.value)}
            disabled={disabled || !datos}
            className={clase}
          >
            <option value="">{t('encargo.alAzar')}</option>
            <option value="si">{t('encargo.participaSi')}</option>
            <option value="no">{t('encargo.participaNo')}</option>
          </select>
          {lugarElegido && !lugarElegido.participa && (
            <span className="mt-1 block text-xs text-[var(--color-ink-muted)]">
              {t('encargo.participaNoAplica')}
            </span>
          )}
        </label>

        {/*
          «Qué está haciendo» SE SACÓ de la pantalla, y el eje sigue existiendo.

          Se pidió quitarlo, y tiene sentido: el rol ya lo escribe. Elegir «Modelo fitness» y
          además una actividad del catálogo general era pedir dos cosas para el mismo casillero
          —«posá» y «colgá la ropa»— y de esas dos el planificador tiene que quedarse con una.

          Lo que se saca es el MANDO, no el campo: `encargo.actividad` sigue en el esquema, la
          limpieza lo sigue aceptando y el planificador lo sigue leyendo. Un encargo guardado
          con actividad sigue funcionando, y la vía del MCP queda abierta. Borrar el eje habría
          sido otra decisión, más grande, y no es la que se pidió.
        */}
      </div>
      <p className="px-4 pb-4 text-xs text-[var(--color-ink-muted)]">{t('encargo.pie')}</p>
    </details>
  );
}

function GalleryTab({ data, onChange }: { data: CreatorDetailData; onChange: () => void }) {
  const { t } = useT();
  const aviso = useAviso();
  const [busy, setBusy] = useState<string | null>(null);

  const canonicalId = data.creator.canonicalReferenceAssetId;

  const candidates = data.assets.filter((asset) => asset.kind === 'reference_candidate');
  const sheet = data.assets.filter((asset) => IDENTITY_KINDS.includes(asset.kind));
  /**
   * La etapa activa es la que el sistema espera que atiendas.
   *
   * Es la misma máquina de estados que aplica el servidor, escrita una segunda vez para la
   * interfaz. Duplicarla es aceptable acá: el servidor sigue siendo la autoridad —rechaza
   * cualquier salto— y la interfaz sólo decide qué mostrar primero.
   */
  const usableSheet = sheet.filter(
    (asset) =>
      asset.status !== 'DISCARDED' &&
      asset.status !== 'REJECTED' &&
      asset.status !== 'NEEDS_REGENERATION',
  );
  const completedKinds = new Set(usableSheet.map((asset) => asset.kind));
  const missingKinds = IDENTITY_KINDS.filter((kind) => !completedKinds.has(kind));
  const sheetReady = missingKinds.length === 0;
  const sheetApproved = IDENTITY_KINDS.every((kind) =>
    sheet.some((asset) => asset.kind === kind && asset.status === 'APPROVED'),
  );
  const cuerpoId = data.creator.referenciaDeCuerpoAssetId;
  const cuerpos = data.assets.filter((asset) => asset.kind === 'body_reference');

  /*
    La etapa activa avanza a la 3 cuando la hoja está aprobada y todavía no hay cuerpo elegido.

    No bloquea nada: sin referencia de cuerpo el contenido se genera igual, sólo que los planos
    enteros salen encuadrados de cerca. Por eso es una etapa que se puede saltar, y la 2 no.
  */
  const activeStage: Stage = canonicalId === null ? 1 : sheetApproved ? 3 : 2;

  // `null` significa «seguí a la etapa activa». En cuanto se toca una pestaña, la elección
  // manda: si no, terminar una etapa te sacaría de la pantalla que estabas mirando.
  const [picked, setPicked] = useState<Stage | null>(null);
  const stage = picked ?? activeStage;

  /**
   * Hay una generación en curso.
   *
   * El estado lo pone el servidor antes de devolver 202. Sin mostrarlo, el botón parecía no
   * hacer nada durante el minuto que tarda: la hoja se generaba bien y aun así daba la
   * sensación de haber fallado.
   */
  const working = data.creator.status === 'GENERATING_MEDIA';
  /**
   * El mismo umbral que aplica el servidor (`STALE_GENERATION_MS` en routes.ts).
   *
   * Tienen que coincidir. Con el cliente en dos minutos y el servidor en seis, el cartel de
   * «se interrumpió» aparecía y el botón devolvía «ya hay una generación en curso»: peor que
   * no ofrecerlo. Cada paso puede tardar hasta cinco minutos y reintenta, así que seis
   * cubren un paso completo más su reintento.
   */
  const generationStale =
    working && Date.now() - new Date(data.creator.updatedAt).getTime() > 6 * 60 * 1000;

  const act = async (key: string, run: () => Promise<unknown>): Promise<void> => {
    setBusy(key);
    try {
      await run();
      onChange();
    } catch (caught) {
      // Lo de la API ya salió como aviso flotante. Acá sólo lo que ni llegó a la API.
      if (!(caught instanceof ApiRequestError)) aviso.error(t('gal.accionFallo'));
    } finally {
      setBusy(null);
    }
  };

  /**
   * Fija la frontal canónica, y avisa cuando cambiarla deja algo desalineado.
   *
   * ── Por qué se puede cambiar ──────────────────────────────────────────────────
   * La primera elección era definitiva desde la pantalla: elegida una, el botón desaparecía de
   * todas. Y es una decisión que uno querría revisar — la cara de la referencia se juzga bien
   * recién cuando aparece repetida en la hoja, o cuando se descubre un defecto que en miniatura
   * no se veía. El servidor siempre aceptó el cambio; era la interfaz la que lo escondía.
   *
   * ── Por qué pregunta cuando ya hay hoja ───────────────────────────────────────
   * La hoja y todo lo que vino después se generaron **con la frontal anterior adjunta como
   * referencia**. Cambiarla no los rompe ni los borra: los deja siendo de otra persona. Eso no
   * se deduce de un botón que dice «usar esta en su lugar», y descubrirlo después de generar el
   * set final sale caro.
   *
   * Cambiar de idea es gratis: se vuelve a elegir la anterior y todo encaja de nuevo. Por eso
   * pregunta en vez de impedirlo.
   */
  const elegirCanonica = async (assetId: string): Promise<void> => {
    const reemplaza = data.creator.canonicalReferenceAssetId !== null;
    if (reemplaza && sheet.length > 0) {
      const seguir = window.confirm(t('gal.confirmarCambioFrontal'));
      if (!seguir) return;
    }

    await act(`canon-${assetId}`, () =>
      api.post(`/api/creators/${data.creator.id}/canonical-reference`, { assetId }),
    );
  };

  const cardActions = (asset: AssetView) => accionesDeAsset(asset, { busy, act });

  const disabled = busy !== null || (working && !generationStale);

  return (
    <div className="space-y-6">
      <StageTabs
        current={stage}
        active={activeStage}
        counts={{ 1: candidates.length, 2: sheet.length, 3: cuerpos.length }}
        onPick={setPicked}
      />

      {working && !generationStale && (
        <div
          aria-live="polite"
          className="flex items-center gap-3 rounded-2xl border border-[var(--color-accent)]/40 bg-[var(--color-accent-soft)] p-4 text-sm"
        >
          <span
            aria-hidden="true"
            className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-[var(--color-border-subtle)] border-t-[var(--color-accent)]"
          />
          {t('gal.generando')}
        </div>
      )}

      {generationStale && (
        <div
          role="alert"
          className="rounded-2xl border border-[var(--color-warn)]/40 bg-[var(--color-warn)]/10 p-4 text-sm"
        >
          <p className="font-semibold text-[var(--color-warn)]">{t('gal.interrumpida')}</p>
          <p className="mt-1">
            {sheetApproved ? t('gal.interrumpidaSet') : t('gal.interrumpidaHoja')}
          </p>
        </div>
      )}

      {stage === 1 && (
        <>
          {canonicalId === null ? (
            <Panel
              accent
              title={t('gal.etapa1')}
              body={t('gal.etapa1Que')}
              action={
                <CanonicaDesdeFoto
                  creatorId={data.creator.id}
                  disabled={disabled}
                  onChange={onChange}
                />
              }
            />
          ) : (
            <Panel
              title={t('gal.frontalElegida')}
              body={
                t('gal.frontalElegidaQue') +
                (sheet.length > 0 ? t('gal.frontalElegidaConHoja') : '.')
              }
              action={
                <div className="flex flex-wrap gap-2">
                  <CanonicaDesdeFoto
                    creatorId={data.creator.id}
                    disabled={disabled}
                    onChange={onChange}
                  />
                  {/*
                    «Derivar de una foto» deja cuatro candidatas cuando la cara viene girada y de
                    las cuatro queda una. Sin esto, probar dos fotos deja siete imágenes de dos
                    megas que no se publican ni se miden.
                  */}
                  {candidates.some((a) => a.id !== canonicalId) && (
                    <Button
                      variant="secondary"
                      disabled={disabled}
                      onClick={() => {
                        const cuantas = candidates.filter((a) => a.id !== canonicalId).length;
                        if (!window.confirm(t('gal.limpiarCandidatasConfirmar', { n: cuantas })))
                          return;
                        void act('candidatas-limpiar', () =>
                          api.post(`/api/creators/${data.creator.id}/candidatas/limpiar`),
                        );
                      }}
                    >
                      {busy === 'candidatas-limpiar'
                        ? t('gal.limpiando')
                        : t('gal.limpiarCandidatas', {
                            n: candidates.filter((a) => a.id !== canonicalId).length,
                          })}
                    </Button>
                  )}
                </div>
              }
            />
          )}

          <AssetGrid
            assets={candidates}
            empty={t('gal.sinCandidatas')}
            render={(asset) => (
              <AssetCard
                key={asset.id}
                asset={asset}
                isCanonical={asset.id === canonicalId}
                showCanonicalAction={asset.id !== canonicalId}
                hayOtraCanonica={canonicalId !== null}
                onCanonical={() => void elegirCanonica(asset.id)}
                {...cardActions(asset)}
              />
            )}
          />
        </>
      )}

      {stage === 2 && (
        <>
          {canonicalId === null ? (
            <Panel title={t('gal.faltaFrontal')} body={t('gal.faltaFrontalQue')} />
          ) : (
            <Panel
              accent={!sheetApproved}
              title={
                !sheetReady && sheet.length === 0
                  ? t('gal.etapa2Generar')
                  : !sheetReady
                    ? t(
                        missingKinds.length === 1 ? 'gal.etapa2FaltaUno' : 'gal.etapa2FaltanVarios',
                        { n: missingKinds.length },
                      )
                    : sheetApproved
                      ? t('gal.hojaAprobada')
                      : t('gal.etapa2Revisa')
              }
              body={
                !sheetReady && sheet.length === 0
                  ? t('gal.etapa2GenerarQue')
                  : !sheetReady
                    ? t('gal.etapa2FaltanQue', { n: completedKinds.size })
                    : sheetApproved
                      ? t('gal.hojaAprobadaQue')
                      : t('gal.etapa2RevisaQue')
              }
              action={
                !sheetReady ? (
                  <Button
                    disabled={disabled}
                    onClick={() =>
                      void act('sheet', () =>
                        api.post(`/api/creators/${data.creator.id}/identity-sheet`, {}),
                      )
                    }
                  >
                    {busy === 'sheet' || (working && !generationStale)
                      ? t('gal.generandoBoton')
                      : sheet.length === 0
                        ? t('gal.generarHoja')
                        : t('gal.completarHoja')}
                  </Button>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {!sheetApproved && (
                      <Button
                        disabled={disabled}
                        onClick={() =>
                          void act('approve-sheet', () =>
                            api.post(`/api/creators/${data.creator.id}/identity-sheet/approve`, {}),
                          )
                        }
                      >
                        {t(busy === 'approve-sheet' ? 'gal.aprobando' : 'gal.aprobarHoja')}
                      </Button>
                    )}
                    {/*
                      Cada ángulo se puede regenerar hasta que salga bien, y lo que no salió queda
                      en la galería compitiendo visualmente con la buena. Sólo aparece cuando hay
                      algo que borrar Y ese ángulo ya tiene una aprobada: borrar el único que hay
                      no sería limpiar, sería romper la hoja.
                    */}
                    {sheet.some(
                      (a) =>
                        a.status === 'DISCARDED' ||
                        (a.status !== 'APPROVED' &&
                          sheet.some((o) => o.kind === a.kind && o.status === 'APPROVED')),
                    ) && (
                      <Button
                        variant="secondary"
                        disabled={disabled}
                        onClick={() => {
                          const cuantas = sheet.filter(
                            (a) =>
                              a.status === 'DISCARDED' ||
                              (a.status !== 'APPROVED' &&
                                sheet.some((o) => o.kind === a.kind && o.status === 'APPROVED')),
                          ).length;
                          if (!window.confirm(t('gal.limpiarHojaConfirmar', { n: cuantas })))
                            return;
                          void act('hoja-limpiar', () =>
                            api.post(`/api/creators/${data.creator.id}/hoja/limpiar`),
                          );
                        }}
                      >
                        {busy === 'hoja-limpiar'
                          ? t('gal.limpiando')
                          : t('gal.limpiarHoja', {
                              n: sheet.filter(
                                (a) =>
                                  a.status !== 'APPROVED' &&
                                  sheet.some((o) => o.kind === a.kind && o.status === 'APPROVED'),
                              ).length,
                            })}
                      </Button>
                    )}
                    {/*
                      Rehacer la hoja entera.

                      «Completar hoja» no sirve cuando la hoja está completa y es mala: no falta
                      nada, así que no hace nada. Este botón descarta los tres ángulos y los
                      vuelve a generar.

                      Se pregunta antes porque no se puede deshacer y porque lo que cambia no es
                      sólo la hoja: es la cara de referencia que se adjunta a todo el contenido
                      posterior. Un «¿estás seguro?» a secas no le da a nadie con qué decidir.
                    */}
                    <Button
                      variant="secondary"
                      disabled={disabled}
                      onClick={() => {
                        const ok = window.confirm(t('gal.confirmarRehacerHoja'));
                        if (!ok) return;
                        void act('sheet-scratch', () =>
                          api.post(`/api/creators/${data.creator.id}/identity-sheet`, {
                            desdeCero: true,
                          }),
                        );
                      }}
                    >
                      {t(busy === 'sheet-scratch' ? 'gal.rehaciendo' : 'gal.rehacerHoja')}
                    </Button>
                  </div>
                )
              }
            />
          )}

          <AssetGrid
            assets={sheet}
            empty={t('gal.sinHoja')}
            render={(asset) => (
              <AssetCard
                key={asset.id}
                asset={asset}
                isCanonical={false}
                showCanonicalAction={false}
                {...cardActions(asset)}
              />
            )}
          />
        </>
      )}

      {stage === 3 && (
        <>
          {canonicalId === null ? (
            <Panel title={t('gal.faltaFrontal')} body={t('gal.faltaFrontalQue')} />
          ) : (
            <Panel
              accent={cuerpoId === null}
              title={cuerpoId === null ? t('gal.etapa3') : t('gal.cuerpoElegido')}
              body={cuerpoId === null ? t('gal.etapa3Que') : t('gal.cuerpoElegidoQue')}
              action={
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant={cuerpoId === null ? 'primary' : 'secondary'}
                    disabled={disabled}
                    /*
                      Una por pulsación, no una tanda de tres.

                      La ruta genera a 1920×2880 —5,5 megapíxeles— porque es la resolución a la
                      que la cara sale definida, y a ese tamaño tres seguidas es una espera larga
                      sin nada en pantalla. De a una se ve cada candidata al salir y se decide si
                      hace falta otra, que casi siempre es que no.
                    */
                    onClick={() =>
                      void act('cuerpo', async () => {
                        /*
                          Se somete y se consulta, porque esta toma no cabe en una petición.

                          Medido: 140 s la generación a 1920×2880 y 179 con el repaso de cabeza,
                          contra los 100 que aguanta el borde de Cloudflare. Esperándola en una
                          sola llamada, el navegador recibía un 524, la placa terminaba igual y
                          la imagen se perdía — lo que se vio en producción como «corrió algo y
                          nunca apareció nada».

                          Un proveedor de pago devuelve la imagen en la misma llamada y no manda
                          `jobId`: ahí no hay nada que consultar y se termina acá.
                        */
                        const sometido = await api.post<{ jobId?: string }>(
                          `/api/creators/${data.creator.id}/referencia-de-cuerpo/generar`,
                        );
                        if (!sometido.jobId) return;

                        // Cada cinco segundos, con techo: si la placa se cuelga, el botón tiene
                        // que soltarse igual en vez de quedar girando para siempre.
                        const limite = Date.now() + 15 * 60 * 1000;
                        while (Date.now() < limite) {
                          await new Promise((seguir) => setTimeout(seguir, 5000));
                          const paso = await api.get<{ estado?: string }>(
                            `/api/creators/${data.creator.id}/referencia-de-cuerpo/generar/${sometido.jobId}`,
                          );
                          if (paso.estado !== 'corriendo') return;
                        }
                      })
                    }
                  >
                    {busy === 'cuerpo'
                      ? t('gal.generandoBoton')
                      : cuerpos.length === 0
                        ? t('gal.generarCuerpo')
                        : t('gal.generarMasCuerpo')}
                  </Button>

                  {/*
                    Subir la de verdad, cuando existe.

                    La ruta existía desde el principio y no tenía botón, así que la única forma
                    de usarla era con `curl`. Y es el camino MEJOR de los dos: una foto real de
                    cuerpo entero tiene la figura de ella, no la que el modelo dedujo de un
                    recorte de cara pegado arriba de un lienzo alto.
                  */}
                  <SubirReferenciaDeCuerpo
                    creatorId={data.creator.id}
                    disabled={disabled}
                    onChange={onChange}
                  />

                  {/*
                    Se generan de a tres y queda una. Sin esto, cada tanda deja dos imágenes de
                    megabyte y medio que no se publican, no se miden y no sirven para nada.
                  */}
                  {cuerpos.length > (cuerpoId === null ? 0 : 1) && (
                    <Button
                      variant="secondary"
                      disabled={disabled}
                      onClick={() => {
                        const cuantas = cuerpos.filter((a) => a.id !== cuerpoId).length;
                        if (!window.confirm(t('gal.limpiarCuerpoConfirmar', { n: cuantas })))
                          return;
                        void act('cuerpo-limpiar', () =>
                          api.post(`/api/creators/${data.creator.id}/referencia-de-cuerpo/limpiar`),
                        );
                      }}
                    >
                      {busy === 'cuerpo-limpiar'
                        ? t('gal.limpiando')
                        : t('gal.limpiarCuerpo', {
                            n: cuerpos.filter((a) => a.id !== cuerpoId).length,
                          })}
                    </Button>
                  )}
                </div>
              }
            />
          )}

          {data.loraDeCuerpo && (
            <FormaDelCuerpoDeElla
              forma={data.loraDeCuerpo}
              disabled={disabled}
              onChange={onChange}
            />
          )}

          <AssetGrid
            assets={cuerpos}
            empty={t('gal.sinCuerpos')}
            render={(asset) => (
              <AssetCard
                key={asset.id}
                asset={asset}
                /*
                  El anillo de «ésta es la elegida» es el mismo que marca la canónica.

                  Son dos elecciones de la misma naturaleza —cuál de estas fotos condiciona las
                  generaciones— y darles señales distintas obligaría a aprender dos.
                */
                isCanonical={asset.id === cuerpoId}
                showCanonicalAction={false}
                {...cardActions(asset)}
                footer={
                  <ReferenciaDeCuerpo
                    asset={asset}
                    creatorId={data.creator.id}
                    esLaActual={asset.id === cuerpoId}
                    onChange={onChange}
                  />
                }
              />
            )}
          />
        </>
      )}
    </div>
  );
}

/**
 * Dos etapas, no tres.
 *
 * La galería construye la identidad: elegís una frontal y aprobás la hoja. Eso se termina y
 * no se vuelve.
 *
 * La generación de contenido vivía acá como etapa 3 y se mudó a la pestaña Contenido. La
 * razón es que dejó de ser lo mismo: desde que cada publicación nace con su imagen, la foto y
 * su texto son una sola cosa, y tenerlos en pestañas distintas obligaba a ir y volver para
 * trabajar sobre una misma partida. Además «Contenido» aparecía dos veces en la navegación,
 * nombrando cosas que se solapaban.
 */
type Stage = 1 | 2 | 3;

const STAGES: { n: Stage; label: Clave; hint: Clave }[] = [
  { n: 1, label: 'gal.frontales', hint: 'gal.frontalesQue' },
  { n: 2, label: 'gal.hojaDeIdentidad', hint: 'gal.hojaQue' },
  { n: 3, label: 'gal.cuerpo', hint: 'gal.cuerpoQue' },
];

/**
 * Navegación entre etapas.
 *
 * Muestra el camino completo desde el principio —no sólo el paso actual— porque las dos
 * pausas humanas no se explican solas: sin ver que hay una etapa 2 entre elegir la cara y
 * generar el contenido, aprobar la hoja parece un trámite en vez de una decisión.
 *
 * Las tres son navegables siempre, incluso las que todavía no llegaron: entrar y leer qué
 * va a pasar ahí es la forma más barata de entender la cadena.
 */
function StageTabs({
  current,
  active,
  counts,
  onPick,
}: {
  current: Stage;
  /** La etapa que el sistema espera que atiendas, distinta de la que estás mirando. */
  active: Stage;
  counts: Record<Stage, number>;
  onPick: (stage: Stage) => void;
}) {
  const { t } = useT();
  return (
    <nav className="grid gap-2 sm:grid-cols-3" aria-label={t('gal.etapas')}>
      {STAGES.map((item) => {
        const selected = item.n === current;
        const done = item.n < active;

        return (
          <button
            key={item.n}
            type="button"
            onClick={() => onPick(item.n)}
            aria-current={selected ? 'page' : undefined}
            className={
              'rounded-xl border px-3 py-2.5 text-left text-sm transition ' +
              (selected
                ? 'border-[var(--color-accent)]/55 bg-[var(--color-accent-soft)]'
                : 'border-[var(--color-border-subtle)] hover:border-[var(--color-border-strong)] hover:bg-[var(--color-surface-sunken)]')
            }
          >
            <span className="flex items-center justify-between gap-2">
              <span className="font-medium">
                {done ? '✓ ' : `${item.n}. `}
                {t(item.label)}
              </span>
              <span className="flex items-center gap-1.5">
                {/* El punto marca dónde te espera el sistema, que no siempre es lo que mirás. */}
                {item.n === active && (
                  <span
                    aria-label="etapa activa"
                    className="accent-gradient h-1.5 w-1.5 rounded-full"
                  />
                )}
                <span className="text-xs tabular-nums text-[var(--color-ink-muted)]">
                  {counts[item.n]}
                </span>
              </span>
            </span>
            <span className="mt-0.5 block text-xs text-[var(--color-ink-muted)]">
              {t(item.hint)}
            </span>
          </button>
        );
      })}
    </nav>
  );
}

/** Cabecera de una etapa: qué es, qué se espera, y la acción si la hay. */
function Panel({
  title,
  body,
  action,
  accent = false,
}: {
  title: string;
  body: string;
  action?: ReactNode;
  accent?: boolean;
}) {
  return (
    <div
      className={
        'flex flex-wrap items-center justify-between gap-3 rounded-2xl border p-4 ' +
        (accent
          ? 'border-[var(--color-accent)]/40 bg-[var(--color-accent-soft)]'
          : 'border-[var(--color-border-subtle)]')
      }
    >
      <div className="max-w-xl">
        <p className="text-sm font-semibold">{title}</p>
        <p className="mt-1 text-sm text-[var(--color-ink-muted)]">{body}</p>
      </div>
      {action}
    </div>
  );
}

/**
 * La grilla de una etapa, con lo descartado plegado.
 *
 * Una imagen descartada o rechazada no cubre su lugar, no se puede aprobar ni volver a
 * descartar, y no participa de ninguna decisión: lo único que hace en la grilla es competir
 * por la atención con las que sí importan. Después de un par de regeneraciones son mayoría.
 *
 * Se pliegan en vez de esconderse. Cada una costó neuronas y su procedencia es la única
 * prueba de qué se intentó y con qué prompt; borrarlas de la vista sin dejar cómo mirarlas
 * convertiría un historial en un agujero. El resumen dice cuántas hay, así que tampoco
 * desaparecen en silencio.
 */
function AssetGrid({
  assets,
  empty,
  emptyActive,
  render,
  cabeceraDe,
}: {
  assets: AssetView[];
  empty: string;
  /**
   * Qué decir cuando hay imágenes pero todas están descartadas.
   *
   * Tiene valor por defecto porque en la galería siempre es lo mismo, y se puede cambiar
   * porque fuera de ella no hay etapas: decir «esta etapa» en la pestaña Contenido nombraba
   * algo que ahí no existe.
   */
  emptyActive?: string;
  render: (asset: AssetView) => ReactNode;
  /**
   * Un título que corta la grilla en secciones, sin sacar las tarjetas de la grilla.
   *
   * ── Por qué así y no una grilla por sección ───────────────────────────────────
   * Porque una grilla por sección deja de ser una grilla. Se probó agrupando las tomas por
   * tanda con un `AssetGrid` cada una, y con tres tandas de una toma cada una el resultado
   * fueron tres grillas de un elemento: las fotos quedaron una debajo de la otra en vez de
   * en columnas. Cada grilla reparte SUS elementos en tres columnas, y una sola no llena ni
   * la primera.
   *
   * Devolviendo el título desde acá, la sección se dibuja DENTRO de la misma grilla ocupando
   * la fila entera con `col-span-full`. Las tarjetas siguen fluyendo en tres columnas y el
   * corte sigue viéndose.
   *
   * Devolver `null` para una tarjeta significa «ésta no abre sección», que es lo normal.
   */
  cabeceraDe?: (asset: AssetView, indice: number, lista: AssetView[]) => ReactNode;
}) {
  const { t } = useT();
  if (assets.length === 0) {
    return <EmptyState title={t('gal.sinImagenes')} description={empty} />;
  }

  const active = assets.filter((asset) => !isRetired(asset));
  const retired = assets.filter(isRetired);

  return (
    <div className="space-y-4">
      {active.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {active.map((asset, i) => {
            /*
              Se le pasa `active`, la lista que esta grilla DIBUJA, y no la que recibió.

              No dárselo fue lo que rompió la grilla la primera vez. Quien decide la cabecera
              comparaba con una lista propia —la de antes de filtrar las descartadas—, así que
              `lista[i - 1]` casi nunca era la tarjeta anterior de verdad: la cabecera se
              disparaba en casi todas, cada tarjeta quedaba precedida por un `col-span-full` y
              todas arrancaban renglón nuevo. Las fotos salieron una debajo de otra otra vez.

              La regla que evita que vuelva a pasar: el índice sólo significa algo contra la
              lista que se está recorriendo, así que esa lista viaja con él.
            */
            const cabecera = cabeceraDe?.(asset, i, active);
            if (!cabecera) return render(asset);
            return (
              <Fragment key={`sec-${asset.id}`}>
                <div className="col-span-full">{cabecera}</div>
                {render(asset)}
              </Fragment>
            );
          })}
        </div>
      ) : (
        <EmptyState
          title={t('gal.nadaActivo')}
          description={emptyActive ?? t('gal.nadaActivoQue')}
        />
      )}

      {retired.length > 0 && (
        <details className="rounded-2xl border border-[var(--color-border-subtle)] p-4">
          <summary className="cursor-pointer text-sm text-[var(--color-text-muted)]">
            {t(retired.length === 1 ? 'gal.descartadaUna' : 'gal.descartadasVarias', {
              n: retired.length,
            })}
          </summary>
          <div className="mt-4 grid gap-4 opacity-60 sm:grid-cols-2 lg:grid-cols-3">
            {retired.map(render)}
          </div>
        </details>
      )}
    </div>
  );
}

/**
 * Las acciones de una tarjeta de imagen, en un solo lugar.
 *
 * ── Por qué no vive dentro de un componente ─────────────────────────────────────
 * Vivía dentro de la galería, y el estudio de contenido —que muestra las mismas tarjetas—
 * escribía sus cuatro manejadores a mano. Al agregar «Reparar cara», la galería lo recibió y
 * el estudio no: el botón no aparecía justamente donde más falta hacía, y nada falló.
 *
 * Es el mismo patrón que ya costó cuatro controles en este proyecto. Con una sola función,
 * la próxima acción que se agregue aparece en los tres lados o en ninguno.
 */
function accionesDeAsset(
  asset: AssetView,
  ctx: {
    busy: string | null;
    act: (key: string, run: () => Promise<unknown>) => Promise<void>;
  },
) {
  const { busy, act } = ctx;
  return {
    busy,
    onApprove: () => void act(`ok-${asset.id}`, () => api.post(`/api/assets/${asset.id}/approve`)),
    onDiscard: () => void act(`no-${asset.id}`, () => api.post(`/api/assets/${asset.id}/discard`)),
    onRegenerate: () =>
      void act(`re-${asset.id}`, () => api.post(`/api/assets/${asset.id}/regenerate`)),
    /**
     * La misma toma, con realce de belleza.
     *
     * Va por `regenerate` y no por una ruta propia porque es lo mismo con un parámetro: mismo
     * prompt, mismo lienzo, mismas referencias. Lo único que cambia es que el servidor de GPU
     * carga el LoRA y que la semilla se repite, para que lo que se vea distinto sea el realce y
     * no el azar.
     */
    onBoost: (mode: BeautyMode) =>
      void act(`boost-${mode}-${asset.id}`, () =>
        api.post(`/api/assets/${asset.id}/regenerate?beauty=${mode}`),
      ),
    /**
     * Un SET a partir de esta foto: varias del mismo lugar y la misma ropa, otras poses.
     *
     * ── Por qué NO va por `regenerate` ────────────────────────────────────────
     * Se intentó y salió mal: las tres fotos del set eran prácticamente la misma. La causa
     * está documentada en `referenciasParaElEncuadre` y es de las que costaron caro:
     *
     *   «El grafo de contenido usa un modelo de EDICIÓN: no dibuja desde cero guiándose por
     *   una cara, redibuja la imagen que recibe. El encuadre no se pide, se hereda.»
     *
     * Con la misma referencia y el mismo prompt, la salida está prácticamente decidida: la
     * semilla mueve poco y el texto de la pose, nada. Esa misma nota enumera seis intentos
     * fallidos por texto y por `ref_boost`, y mi variación de pose era el séptimo.
     *
     * ── Por qué va por la TANDA ───────────────────────────────────────────────
     * Porque el camino que sí produce fotos distintas ya existe: el planificador le da a cada
     * toma su tipo, su encuadre y su pose, y escribe una escena nueva para cada una. Lo que
     * hace `sesion` es fijarle el lugar a todas.
     *
     * Así que un set es una tanda con el lugar, el cubo y el rol de ESTA foto, y `sesion`
     * puesto. Mismo sitio y misma ropa que la que gustó, fotos de verdad distintas.
     *
     * ── Lo que se pierde, y por qué está bien ─────────────────────────────────
     * La original ya no es la primera del set: el set es una tanda nueva, con su propia
     * tanda y su cabecera. La foto que gustó sigue donde estaba, sin descartarse.
     */
    onSet: (cantidad: number, descripcion?: string) =>
      void act(`set-${asset.id}`, () =>
        api.post(`/api/assets/${asset.id}/set`, {
          cantidad,
          ...(descripcion ? { descripcion } : {}),
        }),
      ),
    /**
     * Eliminar borra los bytes y no hay vuelta atrás, así que se pregunta antes.
     *
     * El aviso dice qué se pierde y qué queda: la imagen se va, la procedencia sobrevive.
     * Un «¿estás seguro?» a secas no le da a nadie con qué decidir.
     */
    onDelete: () => {
      const kb = Math.round(asset.byteSize / 1024);
      const ok = window.confirm(texto('gal.confirmarBorrar', { kb }));
      if (!ok) return;
      void act(`del-${asset.id}`, () => api.post(`/api/assets/${asset.id}/delete`));
    },
  };
}

function AssetCard({
  asset,
  isCanonical,
  showCanonicalAction,
  hayOtraCanonica = false,
  busy,
  onCanonical,
  onApprove,
  onDiscard,
  onRegenerate,
  onSet,
  onBoost,
  onDelete,
  footer,
}: {
  asset: AssetView;
  isCanonical: boolean;
  showCanonicalAction: boolean;
  /** Cambia la etiqueta: elegir la primera y reemplazar la elegida no son la misma acción. */
  hayOtraCanonica?: boolean;
  busy: string | null;
  onCanonical?: () => void;
  onApprove: () => void;
  onDiscard: () => void;
  onRegenerate: () => void;
  /** Derivar un set de esta foto. Ausente donde no aplica: ver el desplegable. */
  onSet?: (cantidad: number, descripcion?: string) => void;
  onBoost?: (mode: BeautyMode) => void;
  onDelete: () => void;
  /** Acción propia de la etapa. Sólo la etapa 3 la usa, para asignar la imagen a un post. */
  footer?: ReactNode;
}) {
  const { t } = useT();
  const disabled = busy !== null;

  return (
    <Card
      className={
        'panel-hover ' +
        (isCanonical
          ? 'ring-2 ring-[var(--color-accent)] shadow-[0_0_32px_-8px_var(--color-accent)]'
          : '')
      }
    >
      <div className="relative overflow-hidden rounded-xl">
        {/*
          Un clip no se dibuja con `<img>`.

          Los videos comparten la grilla con las tomas —son assets de la misma influencer— y
          con una etiqueta de imagen salían como un recuadro roto, indistinguible de un asset
          corrupto. Con `controls` además se puede mirar sin salir de la galería, que es donde
          uno decide si sirve.
        */}
        {asset.kind === 'content_video' ? (
          <video src={asset.url} controls preload="metadata" className="w-full" />
        ) : (
          <Toma
            url={asset.url}
            alt={t('asset.alt', { tipo: asset.kind, modelo: asset.provenance.model })}
            ancho={320}
            className="w-full"
          />
        )}
        {isCanonical && (
          <span className="accent-gradient absolute top-2 left-2 rounded-full px-2.5 py-1 text-xs font-semibold text-[var(--color-accent-ink)] shadow-lg">
            {t('asset.canonica')}
          </span>
        )}
      </div>

      <div className="mt-3 flex items-center justify-between gap-2">
        <span className="font-mono text-[0.7rem] tracking-tight text-[var(--color-ink-muted)]">
          {asset.kind}
        </span>
        <StatusBadge status={asset.status} />
      </div>

      {/* Procedencia completa: §3 exige registrarla, y mostrarla es lo que la hace útil. */}
      <dl className="mt-3 space-y-1 text-xs text-[var(--color-ink-muted)]">
        <div className="flex justify-between gap-2">
          <dt>{t('asset.modelo')}</dt>
          <dd className="truncate font-mono">{asset.provenance.model.split('/').pop()}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt>{t('asset.semilla')}</dt>
          <dd className="tabular-nums">{asset.provenance.seed ?? '—'}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt>{t('asset.creditos')}</dt>
          <dd className="tabular-nums">{asset.provenance.estimatedNeurons.toFixed(1)}</dd>
        </div>
        {/*
          CUÁNDO se generó, en la tarjeta y no sólo en la cabecera de su tanda.

          Ésta es la mitad que faltaba del desconcierto que empezó todo —«me está mostrando
          imágenes viejas»—: la galería muestra todo lo que no se publicó ni se descartó,
          acumulado desde siempre, y sin fecha por foto no hay forma de saber cuál es cuál.

          Va acá y no en una cabecera para cada una porque una cabecera ocupa la fila entera:
          con cuatro fotos de cuatro tandas distintas, cuatro cabeceras dejan cuatro renglones
          de una foto y la grilla desaparece. La fecha en la tarjeta dice lo mismo sin romper
          nada.
        */}
        <div className="flex justify-between gap-2">
          <dt>{t('asset.generada')}</dt>
          <dd className="tabular-nums">
            {new Date(asset.provenance.generatedAt).toLocaleString(
              idiomaActual() === 'en' ? 'en-GB' : 'es-AR',
              { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' },
            )}
          </dd>
        </div>
      </dl>

      <details className="mt-3">
        <summary className="cursor-pointer text-xs text-[var(--color-ink-muted)]">
          {t('asset.verPrompt')}
        </summary>
        <p className="mt-2 rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-2 text-xs whitespace-pre-wrap">
          {asset.provenance.prompt}
        </p>
      </details>

      <div className="mt-4 flex flex-wrap gap-2">
        {/*
          El botón sigue estando después de haber elegido una.

          Estaba condicionado a que no hubiera ninguna canónica todavía, así que la primera
          elección era definitiva: si la frontal elegida no terminaba de convencer, o —como pasó
          con la prótesis— salía con un defecto que recién se veía después, no había forma de
          cambiarla desde la pantalla. El servidor nunca lo impidió; sólo lo escondía la interfaz.
        */}
        {showCanonicalAction && onCanonical && (
          <Button disabled={disabled} onClick={onCanonical}>
            {t(hayOtraCanonica ? 'asset.usarEnSuLugar' : 'asset.usarComoReferencia')}
          </Button>
        )}
        {asset.status !== 'APPROVED' && (
          <Button variant="secondary" disabled={disabled} onClick={onApprove}>
            {t('asset.aprobar')}
          </Button>
        )}
        {asset.status !== 'DISCARDED' && !isCanonical && (
          <Button variant="secondary" disabled={disabled} onClick={onDiscard}>
            {t('asset.descartar')}
          </Button>
        )}
        <Button variant="secondary" disabled={disabled} onClick={onRegenerate}>
          ↻ Regenerar
        </Button>
        {/*
          El set va JUNTO a regenerar porque son vecinos de significado —las dos producen otra
          foto a partir de ésta— y separarlos obligaría a buscar en dos sitios lo mismo.

          Es un desplegable y no un botón porque la cantidad es la decisión: «hacé un set» sin
          decir cuántas no se puede ejecutar, y un botón que después pregunta es un paso más
          para algo que se decide antes de apretar.

          Sólo en escenas de contenido: derivar un set de una candidata o de un ángulo de la
          hoja de identidad no significa nada — ésas no se publican, se eligen.
        */}
        {onSet && asset.kind === 'content_scene' && asset.status !== 'DISCARDED' && (
          <select
            disabled={disabled}
            value=""
            onChange={(e) => {
              const cuantas = Number(e.target.value);
              /*
                La descripción se pide ACÁ y no en un campo permanente en la tarjeta.

                Es opcional —el set funciona sin ella, porque la foto ya dice el lugar, la ropa
                y la luz— así que un campo siempre visible en cada tarjeta sería ruido en la
                galería entera para algo que se usa de vez en cuando. Preguntarlo al elegir la
                cantidad lo pone donde se está decidiendo, y dejarlo vacío es un Enter.
              */
              if (cuantas > 0) {
                const pedido = window.prompt(t('asset.setDescripcion'), '');
                if (pedido !== null) onSet(cuantas, pedido.trim() || undefined);
              }
              e.currentTarget.value = '';
            }}
            className="rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-2 py-1.5 text-sm disabled:opacity-50"
            title={t('asset.setQue')}
          >
            <option value="">{t('asset.hacerSet')}</option>
            {[2, 3, 4, 5, 6].map((n) => (
              <option key={n} value={n}>
                {t('asset.setDe', { n })}
              </option>
            ))}
          </select>
        )}
        {/*
          El realce, sólo donde el proveedor sabe hacerlo.

          Lo aplica un LoRA que vive en el servidor de GPU propio, y sólo su endpoint `krea2` los
          expone: KleinPose recibe imagen, prompt, semilla, pasos y megapíxeles, y nada más.

          En la canónica NO se ofrece, y es la restricción importante. `evaluna` es un LoRA de
          identidad: no embellece la cara que había, la reemplaza por otra. Sobre una candidata
          eso está bien —la influencer todavía no tiene identidad, se define al elegir la
          canónica— pero sobre la canónica ya elegida sería cambiarle la cara a alguien que la
          hoja de identidad y todo el contenido ya están copiando.

          En cualquier otro caso —fal, Gemini, o el propio KleinPose— el parámetro viaja y se
          descarta: saldría otra foto, cobrada, sin realce y sin que nada avise. Un botón que a
          veces no hace lo que dice es peor que no tenerlo.
        */}
        {onBoost && !isCanonical && asset.provenance.model === 'local-gpu/krea2' && (
          <>
            <Button
              variant="secondary"
              disabled={disabled}
              onClick={() => onBoost('normal')}
              title={
                asset.provenance.seed === null ? t('asset.sinSemilla') : t('asset.realceNormalQue')
              }
            >
              {t('asset.realceNormal')}
            </Button>
            <Button
              variant="secondary"
              disabled={disabled}
              onClick={() => onBoost('high')}
              title={
                asset.provenance.seed === null ? t('asset.sinSemilla') : t('asset.realceAltoQue')
              }
            >
              {t('asset.realceAlto')}
            </Button>
          </>
        )}
        {/*
          Sólo en las retiradas, que es donde el servidor lo permite. Ofrecerlo en una
          imagen en uso sería un botón que siempre falla.
        */}
        {isRetired(asset) && !isCanonical && (
          <Button variant="secondary" disabled={disabled} onClick={onDelete}>
            {t('asset.eliminar')}
          </Button>
        )}
        <CopyButton text={asset.provenance.prompt} label={t('asset.copiarPrompt')} />
      </div>

      {footer && (
        <div className="mt-4 border-t border-[var(--color-border-subtle)] pt-4">{footer}</div>
      )}
    </Card>
  );
}

/**
 * Elige la imagen de una publicación mostrando las tomas disponibles.
 *
 * ── Por qué acá y no en la galería ──────────────────────────────────────────────
 * Emparejar foto y texto es una decisión que necesita ver las dos cosas. Desde la
 * publicación se ve el tema, el caption y la relación de aspecto pedida, y las tomas son
 * pocas —tres a seis— así que caben todas como miniaturas. Un clic y queda asignada.
 *
 * Se marca cuál coincide con la relación de aspecto que pidió el calendario: una toma
 * vertical en un post pensado para 4:5 se va a recortar, y eso conviene saberlo antes.
 */
/**
 * Animar una toma, con su estado.
 *
 * ── Por qué es un hook y no una función suelta ─────────────────────────────────
 * Lo necesitan dos pantallas: la galería de tomas y la publicación que va a usar el clip. La
 * primera versión lo tenía escrito dentro de una, así que la otra habría necesitado su propia
 * copia — y la copia es donde la condición de corte de la espera se vuelve a equivocar, que ya
 * pasó dos veces en esta misma sesión.
 */
export type VideoJob = { id: string; sourceAssetId: string; interactionId: string };

/** Lo que cuesta un clip con el proveedor que le toca a esta influencer. */
function precioDelClip(plan: CreatorDetailData['videoPlan']): string {
  return `${plan.seconds} s · ~USD ${(plan.usdPerSecond * plan.seconds).toFixed(2)}`;
}

/**
 * Escalar una toma a más resolución, cuando alguien la quiere para algo concreto.
 *
 * ── Por qué es un botón y no algo automático ──────────────────────────────────
 * Porque escalar ocupa la placa. Si corriera solo sobre cada toma aprobada, una sesión de veinte
 * imágenes serían veinte turnos de GPU para archivos que en el feed de Instagram —que reescala a
 * 1080 de ancho— no se distinguen del original. Sirve para lo que sale del feed: una impresión,
 * una portada, un recorte cerrado.
 *
 * ── Por qué el original sigue estando ─────────────────────────────────────────
 * El servidor no lo reemplaza, así que acá tampoco: la escalada aparece como un enlace aparte.
 * Escalar inventa detalle y a veces se equivoca; poder comparar es lo único que permite notarlo.
 *
 * ── Por qué el factor se elige y no es fijo ───────────────────────────────────
 * Porque el peso crece con el cuadrado: la misma toma en x2 pesa ~1,5 MB y en x4 pasa de 8, y en
 * KV el techo por archivo son 25 MB. x2 alcanza para publicar; x4 es para imprimir.
 */
function Escalar({
  asset,
  onChange,
  compacto = false,
}: {
  asset: AssetView;
  onChange: () => void;
  compacto?: boolean;
}) {
  const { t } = useT();
  const [factor, setFactor] = useState(2);
  const [escalando, setEscalando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
    Lo recién escalado se muestra sin esperar a que la ficha vuelva a cargar.

    `onChange` la recarga, pero tarda: entre la respuesta y los datos nuevos el botón quedaba
    quieto y sin resultado, que se lee como que no pasó nada. Al recargar, `asset.escalado` gana.
  */
  const [recien, setRecien] = useState<AssetView['escalado']>(null);
  const escalado = asset.escalado ?? recien;

  const escalar = async () => {
    setEscalando(true);
    setError(null);
    try {
      const respuesta = await api.post<{
        width: number;
        height: number;
        byteSize: number;
        url: string;
      }>(`/api/assets/${asset.id}/escalar`, { factor });
      setRecien(respuesta);
      onChange();
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : String(fallo));
    } finally {
      setEscalando(false);
    }
  };

  return (
    <div className={compacto ? 'text-xs' : 'text-xs'}>
      <div className="flex items-center gap-1.5">
        <select
          value={factor}
          disabled={escalando}
          onChange={(evento) => setFactor(Number(evento.target.value))}
          aria-label={t('estudio.factorDeEscalado')}
          className="rounded-lg border border-[var(--color-border-subtle)] bg-transparent px-1.5 py-1 disabled:opacity-50"
        >
          {[2, 3, 4].map((valor) => (
            <option key={valor} value={valor}>
              ×{valor}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={escalando}
          title={t('estudio.escalarQue', {
            ancho: asset.width * factor,
            alto: asset.height * factor,
          })}
          onClick={() => void escalar()}
          className="flex-1 rounded-lg border border-[var(--color-border-subtle)] px-2 py-1 transition hover:border-[var(--color-border-strong)] disabled:opacity-50"
        >
          {escalando
            ? t('estudio.escalando')
            : escalado
              ? t('estudio.escalarDeNuevo')
              : t('estudio.escalar')}
        </button>
      </div>

      {escalado && (
        <a
          href={escalado.url}
          target="_blank"
          rel="noreferrer"
          className="mt-1 block text-[var(--color-ink-muted)] underline underline-offset-2 tabular-nums hover:text-[var(--color-accent)]"
        >
          {t('estudio.hayEscalada', {
            ancho: escalado.width,
            alto: escalado.height,
            mb: (escalado.byteSize / 1024 / 1024).toFixed(1),
          })}
        </a>
      )}

      {error && <p className="mt-1 text-[var(--color-danger)]">{error}</p>}
    </div>
  );
}

/**
 * La canónica sacada de una foto que ya funciona, recortándola.
 *
 * ── Por qué recortar y no generar ─────────────────────────────────────────────
 * Se midió sobre la publicación de Kira que más rendía en redes, que había salido de una
 * canónica ya borrada. Reconstruirla generando —canónica derivada, hoja, cuerpo— la dejaba en
 * 0,49–0,56 contra la foto: la misma distancia que la canónica que ya había, o sea sin recuperar
 * nada. Recortarle la cara y escalarla da 0,955. La identidad la llevan los píxeles.
 *
 * ── Por qué avisa en vez de rehacer solo ──────────────────────────────────────
 * Cambiar la canónica deja la hoja y la referencia de cuerpo describiendo otra cara. Rehacerlas
 * cuesta placa y decisiones —cuál de las candidatas queda— que son de quien mira, así que se
 * avisa y se deja el paso a mano.
 */
function CanonicaDesdeFoto({
  creatorId,
  disabled,
  onChange,
}: {
  creatorId: string;
  disabled: boolean;
  onChange: () => void;
}) {
  const { t } = useT();
  const entrada = useRef<HTMLInputElement | null>(null);
  const [derivando, setDerivando] = useState(false);

  return (
    <>
      <input
        ref={entrada}
        type="file"
        accept="image/png,image/jpeg"
        className="hidden"
        onChange={async (evento) => {
          const archivo = evento.target.files?.[0];
          evento.target.value = '';
          if (!archivo) return;

          setDerivando(true);
          try {
            const r = await api.postArchivo<{ elegiVos?: boolean }>(
              `/api/creators/${creatorId}/canonica-desde-imagen`,
              archivo,
            );
            // Con la cara girada quedan candidatas y la elección es de quien mira, no del sistema.
            window.alert(t(r.elegiVos ? 'gal.desdeFotoElegi' : 'gal.desdeFotoHecho'));
            onChange();
          } finally {
            setDerivando(false);
          }
        }}
      />
      <Button
        variant="secondary"
        disabled={disabled || derivando}
        title={t('gal.desdeFotoQue')}
        onClick={() => entrada.current?.click()}
      >
        {derivando ? t('gal.derivando') : t('gal.desdeFoto')}
      </Button>
    </>
  );
}

/**
 * El peso del LoRA de forma, editable donde se ve el resultado.
 *
 * ── Por qué acá y no sólo en Ajustes ──────────────────────────────────────────
 * Porque el ciclo es mirar y corregir: se generan tres referencias de cuerpo, se ve que las
 * caderas quedaron cortas o pasadas, y el número hay que cambiarlo ahí mismo. Estaba sólo en la
 * pantalla de modelos, a tres clics y sin la imagen delante.
 *
 * ── Qué se edita, exactamente ─────────────────────────────────────────────────
 * UNA celda: la de su tipo de cuerpo cruzado con su nivel de piel, que es la que se va a usar en
 * su referencia. El servidor manda el valor ya resuelto para no repetir esa regla acá.
 *
 * Se avisa que el cambio es global para ese par: describe una FORMA, no a una persona, así que
 * dos influencers con el mismo perfil comparten el número. Ocultarlo haría que alguien lo tocara
 * creyendo que sólo afecta a la que está mirando.
 */
function FormaDelCuerpoDeElla({
  forma,
  disabled,
  onChange,
}: {
  forma: {
    bodyType: string;
    skinLevel: string;
    actual: { archivo: string; peso: number } | null;
    porDefecto: { archivo: string; pesos: Record<string, number> } | null;
    configurado: Record<string, { archivo: string; pesos: Record<string, number> } | null> | null;
  };
  disabled: boolean;
  onChange: () => void;
}) {
  const { t } = useT();
  const [guardando, setGuardando] = useState(false);
  const [peso, setPeso] = useState(forma.actual?.peso ?? 0);

  const archivo = forma.actual?.archivo ?? forma.porDefecto?.archivo ?? null;
  if (!archivo) return null;

  const guardar = (nuevo: number) => {
    setPeso(nuevo);
    setGuardando(true);
    void api
      /*
        Se FUSIONA con lo que ya había. Mandar sólo esta celda reemplazaría el mapa entero y
        borraría en silencio los pesos que alguien puso para los otros niveles.
      */
      .patch('/api/settings', {
        lorasDeCuerpo: {
          ...(forma.configurado ?? {}),
          [forma.bodyType]: {
            archivo,
            pesos: {
              ...(forma.configurado?.[forma.bodyType]?.pesos ?? {}),
              [forma.skinLevel]: nuevo,
            },
          },
        },
      })
      .then(() => onChange())
      .finally(() => setGuardando(false));
  };

  return (
    <div className="mt-3 rounded-xl border border-[var(--color-border-subtle)] p-3 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{t('gal.formaTitulo')}</span>
        <span className="text-[var(--color-ink-muted)]">
          {archivo.split('/').pop()?.replace('.safetensors', '')}
        </span>
        <input
          type="number"
          step="0.05"
          min="0"
          max="2"
          disabled={disabled || guardando}
          value={peso}
          onChange={(e) => setPeso(Number(e.target.value))}
          onBlur={() => peso !== forma.actual?.peso && guardar(peso)}
          className="w-20 rounded-lg border border-[var(--color-border-subtle)] bg-transparent px-2 py-1"
        />
        {guardando && (
          <span className="text-[var(--color-ink-muted)]">{t('gal.formaGuardando')}</span>
        )}
      </div>
      <p className="mt-2 text-[var(--color-ink-muted)]">
        {t('gal.formaQue', { perfil: forma.bodyType, nivel: forma.skinLevel })}
      </p>
      <p className="mt-1 text-[var(--color-ink-muted)]">{t('gal.formaMedido')}</p>
      <p className="mt-1 text-[var(--color-ink-muted)]">{t('gal.formaAfecta')}</p>
    </div>
  );
}

/**
 * Subir a mano una foto de cuerpo entero de ella.
 *
 * ── Por qué es el camino mejor ────────────────────────────────────────────────
 * Las tres que genera el botón de al lado salen del truco del lienzo alto: la canónica pegada
 * arriba de un cuadro vertical, con el gris muestreado de su propia esquina, y el modelo
 * completando el cuerpo hacia abajo. Funciona —es lo que hizo posible una referencia de cuerpo
 * a partir de un recorte de cara— pero la figura la INVENTA. Una foto real no.
 *
 * ── Por qué queda elegida al subirla ──────────────────────────────────────────
 * Porque el acto de elegir el archivo ya es la elección: nadie sube una foto de cuerpo entero
 * para después no usarla. Es lo mismo que hace la ruta, que llama a `setReferenciaDeCuerpo`.
 *
 * El error de formato lo devuelve el servidor mirando los primeros bytes, no la extensión: un
 * `.png` que en realidad es un HEIC entra por el `accept` y se rechaza igual, con su mensaje.
 */
function SubirReferenciaDeCuerpo({
  creatorId,
  disabled,
  onChange,
}: {
  creatorId: string;
  disabled: boolean;
  onChange: () => void;
}) {
  const { t } = useT();
  const entrada = useRef<HTMLInputElement | null>(null);
  const [subiendo, setSubiendo] = useState(false);

  return (
    <>
      <input
        ref={entrada}
        type="file"
        accept="image/png,image/jpeg"
        className="hidden"
        onChange={async (evento) => {
          const archivo = evento.target.files?.[0];
          // Se limpia para que elegir el MISMO archivo otra vez vuelva a disparar el cambio.
          evento.target.value = '';
          if (!archivo) return;

          setSubiendo(true);
          try {
            await api.postArchivo(`/api/creators/${creatorId}/referencia-de-cuerpo/subir`, archivo);
            onChange();
          } finally {
            setSubiendo(false);
          }
        }}
      />
      <Button
        variant="secondary"
        disabled={disabled || subiendo}
        title={t('gal.subirCuerpoQue')}
        onClick={() => entrada.current?.click()}
      >
        {subiendo ? t('gal.subiendoCuerpo') : t('gal.subirCuerpo')}
      </Button>
    </>
  );
}

/**
 * El botón que marca esta foto como la referencia de cuerpo entero de la influencer.
 *
 * ── Por qué es humano y no automático ─────────────────────────────────────────
 * Lo que hace útil a esta foto son dos cosas que hoy no mide nada junto: que se le vea la figura
 * completa y que la cara siga siendo la suya. El parecido de identidad sólo puntúa lo segundo, y
 * en un plano entero la cara ocupa cien píxeles, así que da bajo aunque la foto sea la correcta.
 *
 * ── Por qué vive en la tarjeta y no en un ajuste ──────────────────────────────
 * Porque se elige mirando la foto. Un selector en otra pantalla obligaría a recordar cuál era
 * cuál, que es exactamente el paso donde se elige la equivocada.
 */
function ReferenciaDeCuerpo({
  asset,
  creatorId,
  esLaActual,
  onChange,
}: {
  asset: AssetView;
  creatorId: string;
  esLaActual: boolean;
  onChange: () => void;
}) {
  const { t } = useT();
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const marcar = async (assetId: string | null) => {
    setGuardando(true);
    setError(null);
    try {
      await api.post(`/api/creators/${creatorId}/referencia-de-cuerpo`, { assetId });
      onChange();
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : String(fallo));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="text-xs">
      <button
        type="button"
        disabled={guardando}
        title={esLaActual ? t('estudio.refCuerpoQuitarQue') : t('estudio.refCuerpoQue')}
        onClick={() => void marcar(esLaActual ? null : asset.id)}
        className={`w-full rounded-lg border px-2 py-1 transition disabled:opacity-50 ${
          esLaActual
            ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
            : 'border-[var(--color-border-subtle)] hover:border-[var(--color-border-strong)]'
        }`}
      >
        {guardando
          ? t('estudio.refCuerpoGuardando')
          : esLaActual
            ? t('estudio.refCuerpoEsLa')
            : t('estudio.refCuerpo')}
      </button>

      {esLaActual && !guardando && (
        <button
          type="button"
          onClick={() => void marcar(null)}
          className="mt-1 block w-full text-[var(--color-ink-muted)] underline underline-offset-2 hover:text-[var(--color-accent)]"
        >
          {t('estudio.refCuerpoQuitar')}
        </button>
      )}

      {error && <p className="mt-1 text-[var(--color-danger)]">{error}</p>}
    </div>
  );
}

/**
 * Qué tiene que hacer ella en ESTE clip, escrito al lado del botón que lo pide.
 *
 * ── Por qué dejó de ser un campo para toda la galería ─────────────────────────
 * Estaba arriba de la pestaña, uno solo para todas las tomas, con el argumento de que son
 * ajustes de la animación y no de la imagen. Es cierto para los segundos y falso para el
 * movimiento: lo que puede hacer una influencer sentada en un café no es lo que puede hacer una
 * en la pileta, así que el texto cambia con la foto y no con la sesión. Con un campo compartido
 * había que subir, reescribir, bajar y buscar la tarjeta otra vez — y el que se olvidaba de
 * reescribir animaba la foto nueva con el gesto de la anterior.
 *
 * ── Por qué los segundos siguen arriba ────────────────────────────────────────
 * Porque son un mando de coste, no de contenido: cada segundo cuesta más de un minuto de placa,
 * y ése sí vale lo mismo para toda la tanda.
 *
 * ── Por qué vacío es una opción y no un olvido ────────────────────────────────
 * Sin texto, el clip se mueve apenas —respira y parpadea—, que es lo que se quiere para una foto
 * que ya está bien. El texto es para cuando se quiere que PASE algo.
 */
function CampoDeMovimiento({
  valor,
  onCambio,
  disabled = false,
}: {
  valor: string;
  onCambio: (texto: string) => void;
  disabled?: boolean;
}) {
  const { t } = useT();

  return (
    <label className="block">
      <span className="text-[0.7rem] font-medium text-[var(--color-ink-muted)]">
        {t('video.movimiento')}
      </span>
      <input
        type="text"
        value={valor}
        disabled={disabled}
        onChange={(evento) => onCambio(evento.target.value)}
        placeholder={t('video.movimientoEjemplo')}
        title={t('video.consejo')}
        className="mt-0.5 w-full rounded-lg border border-[var(--color-border-subtle)] bg-transparent px-2 py-1 text-xs disabled:opacity-50"
      />
    </label>
  );
}

/**
 * El movimiento pedido para cada toma, por separado.
 *
 * Un único texto no alcanza: la pestaña muestra varias tomas a la vez y se anima una tras otra,
 * así que compartir el estado haría que escribir en una borrara lo escrito en la anterior.
 */
function useMovimientos() {
  const [movimientos, setMovimientos] = useState<Record<string, string>>({});

  return {
    movimientoDe: (assetId: string) => movimientos[assetId] ?? '',
    setMovimiento: (assetId: string, texto: string) =>
      setMovimientos((actual) => ({ ...actual, [assetId]: texto })),
  };
}

/**
 * Subir un video que marque el movimiento del clip.
 *
 * ── Por qué un archivo y no más texto ─────────────────────────────────────────
 * Porque el texto no manda en este pipeline: el grafo de la placa corre a `cfg 1.0` y ahí la
 * guía está apagada. Dos pedidos claros —«she starts to twerk», «she sticks out her tongue»—
 * devolvieron otra cosa. El movimiento entra mejor mostrándolo.
 *
 * ── Qué se le dice al usuario, y por qué importa ──────────────────────────────
 * Que del video sólo sale el movimiento. Sin decirlo, la pregunta obvia es si la persona del
 * video va a aparecer en el clip —o si hace falta que se parezca a la influencer—, y la
 * respuesta a las dos es no: se le extrae el esqueleto y nada más.
 */
/**
 * Animar con un video, en cualquiera de los dos modos.
 *
 * ── Qué los separa ────────────────────────────────────────────────────────────
 * `animar` saca del video SÓLO el esqueleto: la influencer sale sobre su propio fondo, con la
 * ropa y el lugar de la foto que se anima. Del video no queda nada más que el movimiento.
 *
 * `reemplazo` mete el video entero —escena, luz, encuadre— y cambia únicamente quién lo
 * protagoniza. Es más espectacular y tiene un costo que conviene saber antes: todo el sistema
 * de lugares, cubos de ropa y niveles de piel deja de intervenir, porque el sitio y la ropa los
 * pone el video.
 *
 * ── Por qué son dos botones y no una casilla ──────────────────────────────────
 * Porque son dos resultados distintos, no una variante del mismo. Una casilla al lado de un
 * botón se marca sin leerla; dos botones que dicen cada uno lo que hacen, no. Es el mismo
 * criterio que ya está escrito para cobrar o regalar en Telegram.
 */
function AnimarConVideo({
  asset,
  animando,
  onVideo,
}: {
  asset: AssetView;
  animando: boolean;
  onVideo: (video: File, modo: 'animar' | 'reemplazo') => void;
}) {
  const { t } = useT();
  const entrada = useRef<HTMLInputElement | null>(null);
  const modoPedido = useRef<'animar' | 'reemplazo'>('animar');
  const listo = asset.status === 'APPROVED';

  return (
    <div className="text-xs">
      <input
        ref={entrada}
        type="file"
        accept="video/*"
        className="hidden"
        onChange={(evento) => {
          const archivo = evento.target.files?.[0];
          // Se limpia para que elegir el MISMO archivo otra vez vuelva a disparar el cambio.
          evento.target.value = '';
          if (archivo) onVideo(archivo, modoPedido.current);
        }}
      />
      {!listo ? (
        <button
          type="button"
          disabled
          title={t('estudio.aprobarPrimero')}
          className="w-full rounded-xl border border-[var(--color-border-subtle)] px-3 py-2 text-xs font-medium opacity-50"
        >
          {t('estudio.aprobarParaAnimar')}
        </button>
      ) : (
        <div className="grid grid-cols-2 gap-1.5">
          {(
            [
              ['animar', 'video.conVideo', 'video.conVideoQue'],
              ['reemplazo', 'video.reemplazo', 'video.reemplazoQue'],
            ] as const
          ).map(([modo, etiqueta, ayuda]) => (
            <button
              key={modo}
              type="button"
              disabled={animando}
              title={t(ayuda)}
              onClick={() => {
                modoPedido.current = modo;
                entrada.current?.click();
              }}
              className="rounded-xl border border-[var(--color-border-subtle)] px-2 py-2 text-xs font-medium transition hover:border-[var(--color-border-strong)] disabled:opacity-50"
            >
              {t(etiqueta)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Una pista del catálogo libre, antes de traerla a la biblioteca. */
type ResultadoDePista = {
  id: string;
  titulo: string;
  autor: string;
  segundos: number | null;
  licencia: string;
  /** Para escucharla antes de traerla. La reproduce el navegador contra el CDN del catalogo. */
  escucha: string | null;
};

/**
 * La canción de un clip, al preparar la publicación.
 *
 * ── Por qué acá y no al publicar ──────────────────────────────────────────────
 * Porque publicar tiene que ser un acto sin decisiones pendientes: el cron manda posts con el
 * panel cerrado, y una elección que quedó sin hacer se convertiría en un clip mudo publicado sin
 * que nadie se entere. Elegida acá, la publicación queda completa y revisable antes de salir.
 *
 * ── Por qué la música no reemplaza al clip ────────────────────────────────────
 * La mezcla se guarda aparte y el original queda intacto, así que cambiar de canción —o quitarla—
 * no obliga a regenerar nada. Es el mismo criterio que el escalado.
 *
 * ── Por qué no hay catálogo para buscar ───────────────────────────────────────
 * Porque las licencias de música libre se parecen entre sí y no son iguales: unas piden
 * atribución en el post, otras prohíben uso comercial, y varias cambian de términos. Equivocarse
 * ahí no cuesta un defecto: cuesta que Instagram silencie el video o le pegue un strike a la
 * cuenta. Lo que sube el dueño ya trae su decisión de derechos tomada.
 */
function MusicaDelClip({ asset, onChange }: { asset: AssetView; onChange: () => void }) {
  const { t } = useT();
  const [pistas, setPistas] = useState<{ id: string; titulo: string }[]>([]);
  const [elegida, setElegida] = useState(asset.musica?.pistaId ?? '');
  const [volumen, setVolumen] = useState(asset.musica?.volumen ?? 0.6);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [consulta, setConsulta] = useState('');
  const [resultados, setResultados] = useState<ResultadoDePista[]>([]);
  const subir = useRef<HTMLInputElement | null>(null);

  const cargar = useCallback(async () => {
    const { pistas: lista } = await api.get<{ pistas: { id: string; titulo: string }[] }>(
      '/api/pistas',
    );
    setPistas(lista);
  }, []);

  useEffect(() => {
    void cargar().catch(() => undefined);
  }, [cargar]);

  const aplicar = async (pistaId: string, vol: number) => {
    setOcupado(true);
    setError(null);
    try {
      await api.post(`/api/assets/${asset.id}/musica`, {
        pistaId: pistaId || null,
        volumen: vol,
      });
      onChange();
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : String(fallo));
    } finally {
      setOcupado(false);
    }
  };

  return (
    <div className="mt-2 text-xs">
      <p className="font-medium text-[var(--color-ink-muted)]">{t('musica.titulo')}</p>

      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        <select
          value={elegida}
          disabled={ocupado}
          onChange={(evento) => {
            setElegida(evento.target.value);
            void aplicar(evento.target.value, volumen);
          }}
          className="min-w-[9rem] flex-1 rounded-lg border border-[var(--color-border-subtle)] bg-transparent px-1.5 py-1 disabled:opacity-50"
        >
          <option value="">{t('musica.sinMusica')}</option>
          {pistas.map((pista) => (
            <option key={pista.id} value={pista.id}>
              {pista.titulo}
            </option>
          ))}
        </select>

        <input
          ref={subir}
          type="file"
          accept="audio/*"
          className="hidden"
          onChange={async (evento) => {
            const archivo = evento.target.files?.[0];
            evento.target.value = '';
            if (!archivo) return;
            setOcupado(true);
            setError(null);
            try {
              const cuerpo = new FormData();
              cuerpo.append('pista', archivo);
              // Igual que al importar: subirla desde acá es querer ponérsela a este clip.
              const { pista } = await api.postForm<{ pista: { id: string } }>(
                '/api/pistas',
                cuerpo,
              );
              await cargar();
              setElegida(pista.id);
              await aplicar(pista.id, volumen);
            } catch (fallo) {
              setError(fallo instanceof Error ? fallo.message : String(fallo));
            } finally {
              setOcupado(false);
            }
          }}
        />
        <button
          type="button"
          disabled={ocupado}
          title={t('musica.subirQue')}
          onClick={() => subir.current?.click()}
          className="rounded-lg border border-[var(--color-border-subtle)] px-2 py-1 transition hover:border-[var(--color-border-strong)] disabled:opacity-50"
        >
          {t('musica.subir')}
        </button>
        <button
          type="button"
          disabled={ocupado}
          title={t('musica.buscarQue')}
          onClick={() => setBuscando((abierto) => !abierto)}
          className="rounded-lg border border-[var(--color-border-subtle)] px-2 py-1 transition hover:border-[var(--color-border-strong)] disabled:opacity-50"
        >
          {t('musica.buscar')}
        </button>
      </div>

      {buscando && (
        <div className="mt-1.5 rounded-lg border border-[var(--color-border-subtle)] p-2">
          <form
            onSubmit={(evento) => {
              evento.preventDefault();
              void (async () => {
                setOcupado(true);
                setError(null);
                try {
                  const { resultados } = await api.get<{ resultados: ResultadoDePista[] }>(
                    `/api/pistas/buscar?q=${encodeURIComponent(consulta)}`,
                  );
                  setResultados(resultados);
                } catch (fallo) {
                  setError(fallo instanceof Error ? fallo.message : String(fallo));
                } finally {
                  setOcupado(false);
                }
              })();
            }}
            className="flex gap-1.5"
          >
            <input
              type="search"
              value={consulta}
              onChange={(evento) => setConsulta(evento.target.value)}
              placeholder={t('musica.buscarEjemplo')}
              className="min-w-0 flex-1 rounded-lg border border-[var(--color-border-subtle)] bg-transparent px-2 py-1"
            />
            <button
              type="submit"
              disabled={ocupado || consulta.trim().length < 2}
              className="rounded-lg border border-[var(--color-border-subtle)] px-2 py-1 disabled:opacity-50"
            >
              {t('musica.buscarAccion')}
            </button>
          </form>

          {/*
            Se avisa de qué catálogo es, porque marca la expectativa.

            Son en su mayoría bucles de librería y no canciones terminadas. Sin decirlo, buscar
            «pop» y recibir «Lofi Guitar Loop 119 BPM» se lee como que el buscador anda mal.
          */}
          <p className="mt-1 text-[0.7rem] text-[var(--color-ink-muted)]">{t('musica.deDonde')}</p>

          <ul className="mt-1 max-h-44 space-y-1 overflow-y-auto">
            {resultados.map((resultado) => (
              <li key={resultado.id} className="flex items-center gap-1.5">
                <button
                  type="button"
                  disabled={ocupado}
                  title={t('musica.importarQue')}
                  onClick={() => {
                    void (async () => {
                      setOcupado(true);
                      setError(null);
                      try {
                        /*
                          Traerla la PONE en el clip, no solo en la biblioteca.

                          Se busca desde dentro del control de musica de un clip concreto: que el
                          resultado fuera «quedo guardada, ahora elegila arriba» dejaba el paso
                          que importa sin hacer, y encima el desplegable queda tapado por el
                          buscador abierto.

                          Se puede deshacer eligiendo «Sin musica», y remezclar cuesta menos de
                          un segundo.
                        */
                        const { pista } = await api.post<{ pista: { id: string } }>(
                          '/api/pistas/importar',
                          { id: resultado.id },
                        );
                        await cargar();
                        setElegida(pista.id);
                        await aplicar(pista.id, volumen);
                      } catch (fallo) {
                        setError(fallo instanceof Error ? fallo.message : String(fallo));
                      } finally {
                        setOcupado(false);
                      }
                    })();
                  }}
                  className="rounded border border-[var(--color-border-subtle)] px-1.5 py-0.5 text-[0.7rem] disabled:opacity-50"
                >
                  +
                </button>
                <span className="min-w-0 flex-1 truncate">
                  {resultado.titulo}
                  {resultado.autor && (
                    <span className="text-[var(--color-ink-muted)]"> · {resultado.autor}</span>
                  )}
                </span>
                {resultado.segundos !== null && (
                  <span className="tabular-nums text-[var(--color-ink-muted)]">
                    {resultado.segundos}s
                  </span>
                )}
                {/*
                  Se escucha antes de traerla.

                  `preload="none"` a proposito: doce resultados precargando doce audios serian
                  varios megas por cada busqueda, para escuchar uno.
                */}
                {resultado.escucha && (
                  <audio
                    src={resultado.escucha}
                    controls
                    preload="none"
                    className="h-7 w-28 shrink-0"
                  />
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {elegida && (
        <label className="mt-1.5 flex items-center gap-2">
          <span className="text-[var(--color-ink-muted)]">{t('musica.volumen')}</span>
          <input
            type="range"
            min={0}
            max={1.5}
            step={0.1}
            value={volumen}
            disabled={ocupado}
            onChange={(evento) => setVolumen(Number(evento.target.value))}
            /* Se aplica al soltar y no en cada píxel: cada cambio remezcla el clip entero. */
            onMouseUp={() => void aplicar(elegida, volumen)}
            onTouchEnd={() => void aplicar(elegida, volumen)}
            className="flex-1"
          />
          <span className="tabular-nums">{Math.round(volumen * 100)}%</span>
        </label>
      )}

      {ocupado && <p className="mt-1 text-[var(--color-ink-muted)]">{t('musica.mezclando')}</p>}
      {error && <p className="mt-1 text-[var(--color-danger)]">{error}</p>}
    </div>
  );
}

/**
 * Qué va a pasar con esta foto al animarla, cuando no es lo obvio.
 *
 * ── Por qué depende del proveedor y no sólo de la foto ────────────────────────
 * Porque sólo uno de los dos recompone. Gemini entrega 9:16 o 16:9 y lo que no coincida lo
 * reencuadra —y reencuadrar es regenerar: en la primera prueba el recorte se llevó la cabeza de
 * la influencer—, así que ahí la foto se extiende antes y eso cuesta una imagen. fal acepta la
 * proporción del origen, incluida la cuadrada: no hay nada que preparar ni que cobrar.
 *
 * El aviso decía la versión de Google para todas. Con una influencer sensual o atrevida —que van
 * por fal— anunciaba un paso que no ocurre y un cargo que no existe.
 *
 * El umbral es del 2 %: por debajo la diferencia es de bordes y no hace falta extender nada.
 */
function recorteAvisado(asset: AssetView, plan: CreatorDetailData['videoPlan']): string | null {
  if (plan.followsSourceAspect) return null;
  if (asset.kind === 'content_video' || !asset.video || asset.video.cropped < 0.02) return null;

  return texto('video.recorteAvisado', { aspecto: asset.video.aspect });
}

function useAnimar(onChange: () => void, pendientes: VideoJob[] = []) {
  const [videoDe, setVideoDe] = useState<Record<string, string>>({});
  const [videoEstado, setVideoEstado] = useState<Record<string, string>>({});
  /**
   * Qué tomas tienen un clip corriendo. Varias a la vez.
   *
   * Era un único identificador, y eso alcanzaba mientras el único clip posible fuera el que
   * acababas de pedir. Con el servidor recogiéndolos, al abrir la ficha puede haber varios en
   * curso —de otra sesión, de otro día— y un solo hueco los borraría a todos menos uno.
   */
  const [animando, setAnimando] = useState<Record<string, boolean>>({});
  /**
   * El último fallo de cada toma, aparte del progreso.
   *
   * Estaban en el mismo sitio, y el progreso sólo se dibuja mientras la generación corre: al
   * terminar en error, la etiqueta volvía a «Animar» y se llevaba el motivo puesto. Se veía
   * «Enviando…» y después nada, que es indistinguible de un botón que no hace nada.
   */
  const [videoError, setVideoError] = useState<Record<string, string>>({});

  /**
   * Mira un clip hasta que esté guardado.
   *
   * Ya no es quien lo guarda: eso lo hace el servidor, y lo haría igual con la pestaña cerrada.
   * Esto es sólo la vista rápida — sin ella habría que esperar al cron, que pasa cada cinco
   * minutos. Consultar es una lectura y es idempotente, así que que los dos coincidan no duplica
   * nada.
   */
  const esperar = async (assetId: string, interactionId: string): Promise<string | null> => {
    setAnimando((actual) => ({ ...actual, [assetId]: true }));
    setVideoError((actual) => ({ ...actual, [assetId]: '' }));

    try {
      for (let intento = 0; intento < 90; intento++) {
        const estado = await api.get<{
          done: boolean;
          status?: string;
          asset?: { id: string; url: string };
        }>(`/api/assets/${assetId}/video/${interactionId}`);

        if (estado.done && estado.asset) {
          setVideoDe((actual) => ({ ...actual, [assetId]: estado.asset!.url }));
          setVideoEstado((actual) => ({ ...actual, [assetId]: '' }));
          onChange();
          return estado.asset.id;
        }

        setVideoEstado((actual) => ({
          ...actual,
          [assetId]: texto('video.estadoEspera', {
            estado: estado.status ?? texto('video.generando'),
            segundos: (intento + 1) * 5,
          }),
        }));
        await new Promise((resolve) => setTimeout(resolve, 5_000));
      }

      // No se perdió: el cron lo recoge igual. Lo que se acabó es la paciencia de esta pantalla.
      setVideoEstado((actual) => ({
        ...actual,
        [assetId]: texto('video.sigueGenerandose'),
      }));
      return null;
    } catch (error) {
      setVideoError((actual) => ({
        ...actual,
        [assetId]:
          error instanceof ApiRequestError
            ? error.payload.message
            : error instanceof Error
              ? error.message
              : texto('video.noSePudo'),
      }));
      return null;
    } finally {
      setAnimando((actual) => ({ ...actual, [assetId]: false }));
    }
  };

  /** Somete el clip y se queda mirando. */
  /**
   * Anima una toma. `movimiento` decide cuánto se mueve, y por omisión se mueve poco.
   *
   * ── Por qué el poco es el valor por omisión ───────────────────────────────────
   * Porque este gancho lo usan las tres pantallas, y dos de ellas animan material para las redes,
   * donde lo que sirve es un cinemagrama: quieto salvo un parpadeo, con la cara intacta. La que
   * pide movimiento amplio es la de Fanvue, y lo pide explícitamente.
   */
  const animar = async (
    assetId: string,
    movimiento: 'sutil' | 'amplio' = 'sutil',
    /**
     * Lo que pidió quien anima, cuando lo pidió.
     *
     * El texto reemplaza al automático entero: los dos textos que genera el sistema están
     * escritos para NO moverse mucho —el sutil pide «minimal motion only»— y sumarle uno que
     * pide movimiento daría dos instrucciones contradictorias que el modelo promedia.
     */
    extra?: { prompt?: string; seconds?: number },
  ): Promise<string | null> => {
    setAnimando((actual) => ({ ...actual, [assetId]: true }));
    setVideoError((actual) => ({ ...actual, [assetId]: '' }));
    setVideoEstado((actual) => ({ ...actual, [assetId]: 'Enviando…' }));

    let interactionId: string;
    try {
      ({ interactionId } = await api.post<{ interactionId: string }>(
        `/api/assets/${assetId}/video`,
        {
          movimiento,
          ...(extra?.prompt ? { prompt: extra.prompt } : {}),
          ...(extra?.seconds ? { seconds: extra.seconds } : {}),
        },
      ));
    } catch (error) {
      setVideoError((actual) => ({
        ...actual,
        [assetId]:
          error instanceof ApiRequestError
            ? error.payload.message
            : error instanceof Error
              ? error.message
              : texto('video.noSePudo'),
      }));
      setAnimando((actual) => ({ ...actual, [assetId]: false }));
      return null;
    }

    return esperar(assetId, interactionId);
  };

  /**
   * Retoma los clips que ya estaban corriendo al abrir la ficha.
   *
   * Es lo que hace que cerrar la pestaña deje de ser una decisión: el trabajo vive en la base,
   * así que al volver se sigue mirando el mismo, sin someter nada ni volver a pagar.
   */
  const clave = pendientes.map((job) => job.id).join(',');
  const vistos = useRef(new Set<string>());
  useEffect(() => {
    for (const job of pendientes) {
      if (vistos.current.has(job.id)) continue;
      vistos.current.add(job.id);
      void esperar(job.sourceAssetId, job.interactionId);
    }
    // `clave` resume los identificadores: depender de `pendientes` relanzaría el efecto en cada
    // recarga de la ficha, y `esperar` cambia de identidad en cada render.
  }, [clave]);

  /**
   * Animar con el MOVIMIENTO de otro video, en vez de describirlo.
   *
   * ── Por qué existe al lado de `animar` ────────────────────────────────────────
   * Porque describir el movimiento no funciona con la placa: su grafo corre a `cfg 1.0` —lo
   * exige el LoRA destilado de 4 pasos— y a cfg 1 la guía está apagada, así que el prompt entra
   * como condicionamiento y nada empuja la salida hacia él. Se pidió «she starts to twerk» y no
   * pasó nada; se pidió «she sticks out her tongue» y sonrió.
   *
   * Del video sólo se usa el esqueleto: la persona que aparezca en él no llega al resultado.
   *
   * ── Por qué comparte estado con `animar` ──────────────────────────────────────
   * Porque para todo lo demás es el mismo clip: la misma espera, el mismo trabajo en la base, el
   * mismo asset al final. Separar el estado haría que una toma pudiera verse «animando» dos
   * veces, que es justo lo que el servidor no permite.
   */
  const animarConVideo = async (
    assetId: string,
    video: File,
    modo: 'animar' | 'reemplazo' = 'animar',
  ): Promise<string | null> => {
    setAnimando((actual) => ({ ...actual, [assetId]: true }));
    setVideoError((actual) => ({ ...actual, [assetId]: '' }));
    setVideoEstado((actual) => ({ ...actual, [assetId]: 'Subiendo el video…' }));

    let interactionId: string;
    try {
      const cuerpo = new FormData();
      cuerpo.append('video', video);
      cuerpo.append('modo', modo);
      ({ interactionId } = await api.postForm<{ interactionId: string }>(
        `/api/assets/${assetId}/video/animate`,
        cuerpo,
      ));
    } catch (error) {
      setVideoError((actual) => ({
        ...actual,
        [assetId]:
          error instanceof ApiRequestError
            ? error.payload.message
            : error instanceof Error
              ? error.message
              : texto('video.noSePudo'),
      }));
      setAnimando((actual) => ({ ...actual, [assetId]: false }));
      return null;
    }

    return esperar(assetId, interactionId);
  };

  return { animar, animarConVideo, videoDe, videoEstado, videoError, animando };
}

function ImagePicker({
  item,
  shots,
  busy,
  onAssign,
  onChange,
}: {
  item: CreatorDetailData['content'][number];
  shots: AssetView[];
  busy: string | null;
  onAssign: (assetId: string) => void;
  /** Recarga la ficha después de escalar, para que la versión en alta sobreviva al refresco. */
  onChange: () => void;
}) {
  const { t } = useT();
  /**
   * Con algo elegido se muestra eso, no el catálogo entero.
   *
   * La grilla completa tenía sentido mientras no hubiera nada seleccionado: hay que elegir de
   * algún lado. Después deja de tenerlo — lo que importa de una publicación es qué va a salir, y
   * veinte miniaturas alrededor de la elegida hacen que haya que buscar el recuadro con borde
   * para saber cuál es. Cambiarla es un clic, y quien quiera cambiarla lo va a buscar.
   */
  const [cambiando, setCambiando] = useState(false);
  const elegida = shots.find((asset) => asset.id === item.assetId) ?? null;

  if (shots.length === 0) {
    return <p className="text-xs text-[var(--color-warn)]">{t('estudio.sinTomas')}</p>;
  }

  if (elegida && !cambiando) {
    const ratio = aspectRatioOf(elegida.width, elegida.height);

    return (
      <div>
        <div className="flex flex-wrap items-start gap-3">
          {elegida.kind === 'content_video' ? (
            <video
              src={elegida.url}
              controls
              preload="metadata"
              className="w-40 rounded-xl ring-1 ring-[var(--color-border-subtle)]"
            />
          ) : (
            <Toma
              url={elegida.url}
              alt=""
              ancho={160}
              className="w-40 rounded-xl ring-1 ring-[var(--color-border-subtle)]"
            />
          )}

          <div className="text-xs">
            <p className="font-medium">
              {t(
                elegida.kind === 'content_video'
                  ? 'estudio.sePublicaClip'
                  : 'estudio.sePublicaFoto',
              )}
            </p>
            <p className="mt-1 text-[var(--color-ink-muted)] tabular-nums">
              {elegida.width}×{elegida.height} · {ratio}
              {ratio !== item.aspectRatio && (
                <>{t('estudio.seRecorta', { aspecto: item.aspectRatio })}</>
              )}
            </p>
            {elegida.kind === 'content_video' && (
              // Instagram no publica video en el feed: el único contenedor que lo acepta es el
              // de reel, y decirlo acá evita la sorpresa de ver un reel donde se esperaba un post.
              <p className="mt-1 text-[var(--color-ink-muted)]">{t('estudio.comoReel')}</p>
            )}

            {elegida.kind === 'content_video' && (
              <MusicaDelClip asset={elegida} onChange={onChange} />
            )}
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => setCambiando(true)}
              className="mt-2 rounded-lg border border-[var(--color-border-subtle)] px-2.5 py-1 transition hover:border-[var(--color-border-strong)] disabled:opacity-50"
            >
              {t('estudio.cambiar')}
            </button>

            {/*
              Escalar va acá, que es donde se mira la foto que va a salir.

              Estaba sólo en la grilla de tomas sueltas, y eso lo dejaba invisible justo para lo
              que sirve: una toma deja de estar suelta en cuanto se le escribe la publicación,
              así que las buenas —las que uno querría en alta para imprimir o para una portada—
              eran precisamente las que no tenían el botón.
            */}
            {elegida.kind !== 'content_video' && (
              <div className="mt-2 max-w-[16rem]">
                <Escalar asset={elegida} onChange={onChange} />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-xs font-medium text-[var(--color-ink-muted)]">
          {t('estudio.elegiImagen')}
        </p>
        {elegida && (
          <button
            type="button"
            onClick={() => setCambiando(false)}
            className="text-xs text-[var(--color-ink-muted)] underline underline-offset-2"
          >
            {t('comun.cancelar')}
          </button>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {shots.map((asset) => {
          const selected = item.assetId === asset.id;
          const ratio = aspectRatioOf(asset.width, asset.height);
          const sameRatio = ratio === item.aspectRatio;

          return (
            <button
              key={asset.id}
              type="button"
              disabled={busy !== null}
              onClick={() => {
                onAssign(asset.id);
                setCambiando(false);
              }}
              aria-pressed={selected}
              title={
                sameRatio
                  ? t('estudio.coincide', {
                      ancho: asset.width ?? 0,
                      alto: asset.height ?? 0,
                      aspecto: item.aspectRatio,
                    })
                  : t('estudio.noCoincide', {
                      ancho: asset.width ?? 0,
                      alto: asset.height ?? 0,
                      aspecto: item.aspectRatio,
                    })
              }
              className={
                'relative overflow-hidden rounded-xl transition ' +
                (selected
                  ? 'ring-2 ring-[var(--color-accent)] ring-offset-2 ring-offset-[var(--color-surface-sunken)]'
                  : 'ring-1 ring-[var(--color-border-subtle)] hover:ring-[var(--color-border-strong)] hover:brightness-110') +
                (busy !== null ? ' opacity-60' : '')
              }
            >
              {/*
                Un clip no se dibuja con `<img>`.

                `shots` incluye los videos desde que se pueden generar, y con una etiqueta de
                imagen salían rotos: un recuadro vacío que parecía un asset corrupto. Se
                muestra el primer fotograma —`preload="metadata"`— más un distintivo, porque
                una miniatura de video y una foto son indistinguibles quietas.
              */}
              {asset.kind === 'content_video' ? (
                <>
                  <video
                    src={asset.url}
                    preload="metadata"
                    muted
                    className="h-20 w-20 object-cover"
                  />
                  <span className="absolute bottom-1 left-1 rounded bg-black/70 px-1 text-[0.6rem] font-medium text-white">
                    ▶ video
                  </span>
                </>
              ) : (
                <img
                  src={miniatura(asset.url, 80)}
                  alt=""
                  className="h-20 w-20 object-cover"
                  loading="lazy"
                />
              )}
              {/*
                La relación real de la imagen, siempre visible.
                Antes se marcaban sólo las que no coincidían, y como el plan de escenas elige
                el encuadre por su cuenta, terminaban marcadas todas: un aviso que aparece en
                todo deja de ser un aviso. Mostrar el dato y resaltar la coincidencia informa
                lo mismo sin gritar.
              */}
              <span
                className={
                  'absolute inset-x-0 bottom-0 py-0.5 text-center text-[0.6rem] font-semibold ' +
                  (sameRatio ? 'bg-[var(--color-ok)] text-black' : 'bg-black/65 text-white/85')
                }
              >
                {ratio}
              </span>
            </button>
          );
        })}
      </div>
      <p className="mt-1.5 text-xs text-[var(--color-ink-faint)]">
        {t('estudio.calendarioPidioAntes')}
        <strong>{item.aspectRatio}</strong>
        {t('estudio.calendarioPidioDespues')}
      </p>
    </div>
  );
}

/** Lo que devuelve `/publish`: o salió publicado, o salió como paquete manual. */
type PublishOutcome = {
  status: string;
  permalink?: string | null;
  externalPostId?: string;
};

/**
 * Generar contenido, y las imágenes que todavía no tienen publicación.
 *
 * Vivía en la galería como «etapa 3». Se mudó acá porque desde que cada publicación nace con
 * su imagen las dos son una sola cosa, y tenerlas en pestañas distintas obligaba a ir y volver
 * para trabajar sobre la misma partida.
 *
 * La sección de sueltas resuelve algo que antes quedaba huérfano: las imágenes generadas cuya
 * publicación se rechazó por duplicada, o las de antes de que las publicaciones nacieran con
 * su foto. Estaban en la galería sin forma de publicarse y sin explicación.
 */
/**
 * Por qué esta imagen no llegó a tener texto, y la salida.
 *
 * ── Por qué va en la tarjeta y no en un cartel del panel ──────────────────────
 * Porque el botón que lo provoca está en la tarjeta. El aviso vivía arriba de la grilla, así
 * que salvo que la foto estuviera en la primera fila quedaba fuera de la pantalla: se pulsaba
 * «escribir publicación», no cambiaba nada donde uno estaba mirando, y la conclusión razonable
 * era que el sistema hacía como que escribía. En producción se pulsó tres veces seguidas sobre
 * la misma foto, y las tres veces el motivo estaba escrito donde nadie lo veía.
 *
 * ── Por qué `escribirla igual` lleva el assetId ───────────────────────────────
 * Porque sin él forzaba el LOTE. El enlace llamaba a `escribirHuerfanas(true)` sin la imagen,
 * o sea que pedir «escribí ésta igual» escribía el texto de todas las pendientes de una vez.
 */
function AvisoDeDuplicada({
  entradas,
  disabled,
  onForzar,
}: {
  entradas: { label: string; duplicateOf: string; distance: number }[];
  disabled: boolean;
  onForzar: () => void;
}) {
  /*
    `t` se toma acá y no se recibe como prop: está tipado con la unión de todas las claves de
    traducción, y pasarlo por una prop obliga a declararla como `(clave: string) => string`,
    que es exactamente el tipo que impide detectar una clave mal escrita.
  */
  const { t } = useT();
  if (entradas.length === 0) return null;
  return (
    <>
      {entradas.map((entrada) => (
        <div
          key={entrada.label}
          className="rounded-lg border border-[var(--color-warn)]/35 bg-[var(--color-warn)]/10 p-2 text-xs"
        >
          <p>
            <strong>{entrada.label}</strong>
            {t('estudio.duplicadaAntes')}
            {entrada.duplicateOf}
            {t('estudio.duplicadaDespues')}
          </p>
          <p className="mt-1 text-[var(--color-ink-muted)]">{t('estudio.duplicadaPorQue')}</p>
          <button
            type="button"
            disabled={disabled}
            onClick={onForzar}
            className="mt-2 underline underline-offset-2 hover:text-[var(--color-accent)]"
          >
            {t('estudio.escribirlaIgual')}
          </button>
        </div>
      ))}
    </>
  );
}

function ContentStudio({ data, onChange }: { data: CreatorDetailData; onChange: () => void }) {
  /**
   * Lo que se le pide al video, compartido por toda la galería.
   *
   * Va una sola vez y no por toma: son ajustes de la ANIMACIÓN, no de la imagen. Repetirlos en
   * cada tarjeta llenaría la grilla de campos que casi siempre valen lo mismo, y obligaría a
   * reescribir el movimiento para probar la misma idea sobre otra foto.
   */
  const [segundosDeClip, setSegundosDeClip] = useState(5);
  const { movimientoDe, setMovimiento } = useMovimientos();
  const { t, idioma } = useT();
  const aviso = useAviso();
  const [busy, setBusy] = useState<string | null>(null);

  /*
    Lo fijado vale para la tanda que se pida, no para la influencer.

    Por eso vive en el estado de la pantalla y no se guarda en su ficha: guardarlo haría que la
    tanda de mañana saliera con lo que alguien eligió hoy, que es justo lo contrario de un
    control que se toca mirando el resultado de la anterior.
  */
  const [encargo, setEncargo] = useState<Encargo>({});

  const act = async (key: string, run: () => Promise<unknown>): Promise<void> => {
    setBusy(key);
    try {
      await run();
      onChange();
    } catch (caught) {
      // Lo de la API ya salió como aviso flotante. Acá sólo lo que ni llegó a la API.
      if (!(caught instanceof ApiRequestError)) aviso.error(t('gal.accionFallo'));
    } finally {
      setBusy(null);
    }
  };

  const sheet = data.assets.filter((asset) => IDENTITY_KINDS.includes(asset.kind));
  const sheetApproved = IDENTITY_KINDS.every((kind) =>
    sheet.some((asset) => asset.kind === kind && asset.status === 'APPROVED'),
  );

  const working = data.creator.status === 'GENERATING_MEDIA';
  // El mismo umbral que aplica el servidor: con uno más corto acá, el cartel de «se
  // interrumpió» aparece y el botón responde que ya hay una generación en curso.
  const generationStale =
    working && Date.now() - new Date(data.creator.updatedAt).getTime() > 6 * 60 * 1000;
  const disabled = busy !== null || (working && !generationStale);

  /**
   * Lo que esta pestaña muestra, que es el material publicable en todas partes.
   *
   * ── Por qué el material para adultos no está acá ───────────────────────────
   * Porque tiene su propia sub-pestaña y porque no puede ir a ningún lado más que al feed de
   * suscriptores de Fanvue: el servidor lo impide al publicar. Mezclado con el resto, la lista
   * ofrecía tomas explícitas para adjuntar a una publicación de Instagram — un error que se
   * descubre recién al pulsar publicar, y con el trabajo de elegirla ya hecho.
   *
   * El filtro es por `clasificacion` y no por nivel de piel: es la misma columna que usa el
   * servidor para decidir dónde se puede publicar, así que las dos respuestas no pueden
   * separarse.
   */
  const shots = data.assets.filter(
    (asset) =>
      asset.kind !== 'reference_candidate' &&
      // Las extendidas son el insumo de un clip, no una toma que alguien eligió.
      asset.kind !== 'content_extended' &&
      /*
        La referencia de cuerpo tampoco: es identidad y vive en la etapa 3 de la Galería.

        Con el tipo de contenido aparecía acá como una foto suelta a la que había que escribirle
        una publicación — y el botón de huérfanas se la habría escrito.
      */
      asset.kind !== 'body_reference' &&
      /*
        La portada tampoco: tiene su propio lugar, en «Foto de portada».

        Es apaisada 851×315 y se sube a mano a Facebook, así que no es una toma que se publique
        desde acá. Apareciendo en esta lista se confunde con contenido — pasó: se generaron tres
        portadas y se las buscó en Fanvue, donde no estaban, y aparecieron mezcladas acá.
      */
      asset.kind !== 'generic' &&
      !IDENTITY_KINDS.includes(asset.kind) &&
      asset.clasificacion !== 'nsfw',
  );

  /**
   * Cuántas van de cuántas, mientras la tanda corre.
   *
   * Sin esto, «Generando…» dura lo mismo con una imagen hecha que con tres, y la única forma
   * de saber si el sistema sigue vivo es recargar y contar a ojo. Pasó: una tanda de cuatro
   * con un cuelgue de Cloudflare en el medio se leyó como «generó una sola», y volver a
   * pulsar generar cuesta dinero.
   *
   * Sale de la procedencia de los propios assets —cada toma guarda su tanda y su tamaño— así
   * que no hace falta pedirle nada nuevo al servidor.
   */
  const batchProgress = (() => {
    if (!working) return null;

    /*
      Sólo cuenta lo generado HACE POCO, y eso es el arreglo.

      ── El defecto ──────────────────────────────────────────────────────────────
      Rodrigo, generando de a una: «sigo viendo 1/4 arriba». El cartel tomaba la tanda del
      asset más nuevo que tuviera tanda, sin mirar de cuándo era. Mientras la toma nueva
      todavía no existe —los primeros cuarenta segundos— el más nuevo es de la tanda ANTERIOR,
      así que el cartel anunciaba el avance de una tanda de cuatro que ya no corre.

      Se notaba poco cuando el panel pedía cuatro y la recuperación las retomaba: el número
      viejo y el nuevo coincidían. Con tandas de una, la mentira quedó a la vista.

      ── Por qué diez minutos ────────────────────────────────────────────────────
      Porque una generación que lleva más que eso ya pasó los seis de `GENERACION_COLGADA_MS`
      y la pantalla la da por interrumpida. Lo que quede afuera de esa ventana no es «lo que
      está corriendo» por definición.
    */
    const RECIENTE_MS = 10 * 60 * 1000;

    const enTanda = shots
      .map((asset) => ({
        batchId: asset.provenance.parameters?.contentBatchId,
        size: asset.provenance.parameters?.contentBatchSize,
        at: asset.provenance.generatedAt,
      }))
      .filter(
        (entry): entry is { batchId: string; size: number; at: string } =>
          typeof entry.batchId === 'string' &&
          typeof entry.size === 'number' &&
          typeof entry.at === 'string' &&
          Date.now() - new Date(entry.at).getTime() < RECIENTE_MS,
      )
      .sort((a, b) => b.at.localeCompare(a.at));

    const ultima = enTanda[0];
    if (!ultima) return null;

    const hechas = enTanda.filter((entry) => entry.batchId === ultima.batchId).length;
    /*
      Se devuelve TERMINADA también, en vez de `null`.

      Antes esto devolvía `null` cuando la tanda estaba completa, y el cartel —que colgaba de
      este valor— desaparecía solo. Al pasar el cartel a depender del estado de la ficha, esa
      apagada se perdió: la toma salía, la ficha tardaba en volver a `ACTIVE` y el cartel seguía
      diciendo «generando una toma» sobre algo que ya estaba hecho. Rodrigo: «se me trabó ahí
      pero ya generó la foto».

      Con el dato completo, el cartel puede preguntar las dos cosas por separado: si la ficha
      dice que está generando, y si lo último que generó ya llegó.
    */
    return { hechas, total: ultima.size, terminada: hechas >= ultima.size };
  })();

  /**
   * Sin publicación que las use, y separadas por si todavía sirven.
   *
   * Una imagen descartada no es una pendiente: ya decidiste. Contarlas juntas daba un título
   * que decía «2 sin publicación» sobre un cuerpo que decía que no había ninguna activa —el
   * mismo número describiendo dos cosas distintas.
   *
   * Las descartadas igual se muestran, plegadas y con su nombre: es la única forma de
   * recuperar una que se descartó por error, ahora que la galería ya no tiene etapa 3.
   */
  /** Lo que el servidor va a generar realmente, cuando no coincide con el selector. */
  const [setPlan, setSetPlan] = useState<string | null>(null);
  const { animar, animarConVideo, videoDe, videoEstado, videoError, animando } = useAnimar(
    onChange,
    data.videoJobs,
  );

  const [rechazadas, setRechazadas] = useState<
    { label: string; assetId: string; duplicateOf: string; distance: number }[]
  >([]);

  /**
   * Escribe la publicación de una imagen, o de todas las que no tengan.
   *
   * Con `assetId` es lo que se pide desde una tarjeta: el texto nace atado a esa foto y el
   * resultado habla de ella. Sin `assetId` es el botón en lote, que quedó como recurso para
   * ponerse al día — y es el que producía «pedí cuatro, salieron dos» sin decir cuáles.
   */
  const escribirHuerfanas = async (force: boolean, assetId?: string): Promise<void> => {
    await act(assetId ? `post-${assetId}` : 'huerfanas', async () => {
      const respuesta = await api.post<{
        created: number;
        detail?: string;
        rejected: { label: string; assetId: string; duplicateOf: string; distance: number }[];
      }>(`/api/creators/${data.creator.id}/orphan-posts`, {
        force,
        ...(assetId ? { assetId } : {}),
      });
      /*
        Pedido desde una tarjeta, sólo se reemplaza LO DE ESA IMAGEN.

        Pisar la lista entera con la respuesta borraba el motivo de las demás: escribirle el
        texto a una foto hacía desaparecer el aviso de la de al lado, que seguía sin
        publicación y ahora también sin explicación. En el lote sí se reemplaza todo, porque
        el lote vuelve a intentarlas todas.
      */
      const nuevas = respuesta.rejected ?? [];
      setRechazadas((previas) =>
        assetId ? [...previas.filter((entrada) => entrada.assetId !== assetId), ...nuevas] : nuevas,
      );
    });
  };

  /*
    Qué imágenes ya tienen publicación, para no ofrecerlas como sueltas.

    Cuenta las de Fanvue también: una toma explícita con publicación NO está suelta, y ofrecerla
    acá la haría aparecer como disponible para el contenido de las redes.
  */
  const usadas = new Set(data.content.map((item) => item.assetId).filter(Boolean));
  const sueltas = shots.filter((asset) => !usadas.has(asset.id));

  /*
    Las imágenes se agrupan POR TANDA, y esto arregla un desconcierto real.

    ── El síntoma ──────────────────────────────────────────────────────────────
    «Me está mostrando imágenes viejas en lugar de las últimas que generé». No las mostraba
    mal: esta lista es todo lo que no se publicó NI se descartó, acumulado desde siempre, y sin
    nada que diga de cuándo es cada una, cuatro tomas de días distintos se leen como una tanda.

    Se pudo probar que eran viejas por la SEMILLA: las cuatro que aparecían difieren en 6247
    exactos, que es el multiplicador de la fórmula que se usaba antes de pasar a semilla al
    azar —`seedFromId(creatorId) + (shotIndex + 1) * 6247`—. O sea que son anteriores a ese
    cambio, y la repetida es el defecto que ese cambio vino a arreglar.

    ── Por qué agrupar y no sólo ordenar ───────────────────────────────────────
    Porque el dato ya estaba guardado —`contentBatchId` viaja en la procedencia de cada toma— y
    sólo lo usaba el cartel de progreso. Ordenar por fecha habría dejado las nuevas arriba y
    seguiría sin decir dónde termina una tanda y empieza otra.

    Y porque es lo que hace falta para el carrusel: para publicar varias juntas, primero hay
    que poder ver cuáles SON juntas.

    ── Las que no tienen tanda ─────────────────────────────────────────────────
    Van a un grupo al final y no se esconden. Son de antes de que existiera `contentBatchId`
    —el mismo corte que ya contempla `lanzar-tanda.ts`— y esconderlas sería borrar de la vista
    tomas que siguen estando disponibles.
  */
  const ordenadasPorTanda = (lista: AssetView[]) => {
    const tandaDe = (a: AssetView) =>
      (a.provenance?.parameters?.contentBatchId as string) ?? '';

    /** Lo más nuevo de cada tanda: por eso se ordenan las tandas entre sí. */
    const masNueva = new Map<string, string>();
    for (const asset of lista) {
      const clave = tandaDe(asset);
      const at = asset.provenance?.generatedAt ?? '';
      if (at > (masNueva.get(clave) ?? '')) masNueva.set(clave, at);
    }

    return [...lista].sort((a, b) => {
      const ta = tandaDe(a);
      const tb = tandaDe(b);
      if (ta !== tb) {
        // Las sin tanda al final, pase lo que pase con su fecha: son el cajón de lo antiguo.
        if (!ta) return 1;
        if (!tb) return -1;
        return (masNueva.get(tb) ?? '').localeCompare(masNueva.get(ta) ?? '');
      }
      /*
        Dentro de la tanda, por el puesto de la toma y no por fecha: ése es el orden en que se
        planificaron, que es el que tiene sentido mirar — y el que va a tener el carrusel. La
        fecha las ordenaría por cuál terminó primero, que no significa nada.
      */
      const ia = Number(a.provenance?.parameters?.contentShotIndex ?? 0);
      const ib = Number(b.provenance?.parameters?.contentShotIndex ?? 0);
      return ia - ib;
    });
  };
  const disponibles = sueltas.filter((asset) => !isRetired(asset));
  const descartadas = sueltas.filter(isRetired);

  return (
    <div className="space-y-4">
      {/*
        Lo que vale para toda la tanda, que ya no es el movimiento.

        Los segundos son un mando de COSTE —cada uno cuesta más de un minuto de placa— y ése sí
        se decide una vez. El movimiento se mudó a cada tarjeta: depende de la foto, no de la
        sesión. Ver `CampoDeMovimiento`.
      */}
      <div className="rounded-2xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-4">
        <div className="flex flex-wrap items-end gap-3">
          <label>
            <span className="text-xs font-medium">{t('video.segundos')}</span>
            <input
              type="number"
              min={2}
              max={10}
              value={segundosDeClip}
              onChange={(e) => setSegundosDeClip(Number(e.target.value))}
              className="mt-1 w-24 rounded-lg border border-[var(--color-border-subtle)] bg-transparent px-3 py-2 text-sm tabular-nums"
            />
          </label>
        </div>
        <p className="mt-2 text-xs text-[var(--color-ink-muted)]">{t('video.consejo')}</p>
      </div>

      {setPlan && (
        <div className="rounded-2xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-4 text-sm">
          {setPlan}
        </div>
      )}

      {/*
        Mientras genera, el cartel — con contador sólo si hay más de una toma que contar.

        Desde que el panel pide de a una, «1 de 1» no dice nada que «generando» no diga mejor.
        El contador se conserva para las tandas de varias, que el MCP todavía puede pedir.
      */}
      {working && !generationStale && !batchProgress?.terminada && (
        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-4 text-sm">
          <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-[var(--color-accent)]" />
          <span className="font-medium tabular-nums">
            {batchProgress && batchProgress.total > 1
              ? t('estudio.generadas', {
                  hechas: batchProgress.hechas,
                  total: batchProgress.total,
                })
              : t('estudio.unaToma')}
          </span>
          <span className="text-xs text-[var(--color-ink-muted)]">
            {t('estudio.cadaTomaTarda')}
          </span>
        </div>
      )}

      {/*
        Lo que falló, donde se está mirando.

        El error de la ficha se muestra arriba de todo, fuera de las pestañas, y desde la galería
        —que es donde se pulsa generar— hay que subir a buscarlo. Acá se repite el motivo pegado
        al botón que lo produjo, que es donde sirve para decidir si conviene volver a pulsar o
        cambiar algo antes.
      */}
      {data.creator.error && !working && (
        <div className="rounded-2xl border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/5 p-4 text-sm">
          <p className="font-medium">{t('estudio.laUltimaFallo')}</p>
          <p className="mt-1 text-xs break-words text-[var(--color-ink-muted)]">
            {data.creator.error.message || data.creator.error.code}
          </p>
        </div>
      )}

      {sheetApproved && (
        <ConfiguracionDeLaTanda
          creatorId={data.creator.id}
          encargo={encargo}
          onCambio={setEncargo}
          disabled={disabled}
        />
      )}

      <Panel
        accent={sheetApproved && shots.length === 0}
        title={t(sheetApproved ? 'estudio.generarContenido' : 'estudio.faltaAprobarHoja')}
        body={t(sheetApproved ? 'estudio.generarContenidoQue' : 'estudio.faltaAprobarHojaQue')}
        action={
          sheetApproved ? (
            <div className="flex items-center gap-2">
              {/*
                Una toma por vez, sin selector.

                El selector ofrecía de una a seis y era lo que más se usaba en 1 — «después de
                cambiar el vestuario lo que se quiere es una toma para mirarla»— y además
                arrastraba un error propio: con una tanda anterior a medias, la recuperación
                conserva el tamaño de aquélla, así que pedir una arrancaba con «1 de 4» en el
                contador. Dos cosas que se arreglan juntas sacando el control.

                Y ahora que los cinco ejes se fijan a mano, generar de a una es además lo que
                tiene sentido: se elige una configuración, se mira la toma, se ajusta. Cuatro de
                la misma configuración son cuatro veces la misma foto.
              */}
              {/*
                El botón dejó de ser una pared mientras hay una generación en curso.

                ── El defecto ────────────────────────────────────────────────────────
                Rodrigo: «me está bloqueando generar más; que no me bloquee si hay error, que se
                dé cuenta, lo muestre y deje generar». Una tanda que muere sin poder anunciarlo
                —un despliegue que reinicia los Durable Objects, una caída dura— deja la ficha en
                `GENERATING_MEDIA`, y el botón quedaba trabado los seis minutos completos de
                `GENERACION_COLGADA_MS`, con un cartel diciendo «1 de 4» sobre algo que ya no
                existe y sin ninguna forma de salir.

                ── Por qué ahora se puede forzar ─────────────────────────────────────
                El guardia protege de pagar dos veces la MISMA toma: dos corridas sobre la misma
                tanda generaban los mismos slots. Desde que se genera de a una, cada pulsación
                arranca una tanda nueva con su propio slot y no hay nada compartido que duplicar.
                Lo que cuesta forzar es una imagen más — que es justamente lo que se está
                pidiendo.

                Se confirma igual: una pulsación distraída no tiene por qué costar una
                generación.
              */}
              <Button
                disabled={busy !== null}
                onClick={() => {
                  const enCurso = working && !generationStale;
                  if (enCurso && !window.confirm(t('estudio.generarIgualConfirmar'))) return;
                  void act('set', async () => {
                    const resultado = await api.post<{
                      imageCount: number;
                      existingCount: number;
                      remainingCount: number;
                    }>(`/api/creators/${data.creator.id}/generate-assets`, {
                      /*
                        Una toma suelta, salvo que se haya pedido una SESIÓN.

                        La casilla de sesión dice «todo en un mismo lugar, misma ropa», y eso es
                        una propiedad de una tanda de VARIAS: con una sola foto no hay nada que
                        colapsar. Estaba fija en 1, así que marcarla no hacía absolutamente nada
                        y el pedido fue exacto: «cliqueé Photo session pero me generó una sola
                        foto».

                        Cuatro y no seis: seis es el máximo que el planificador acepta, y una
                        sesión que sale cara de más desalienta probarla. Quien quiera más puede
                        generar dos veces.
                      */
                      imageCount: encargo.sesion ? 4 : 1,
                      encargo,
                      ...(enCurso ? { forzar: true } : {}),
                    });
                    setSetPlan(
                      resultado.existingCount > 0
                        ? t('estudio.tandaReutiliza', {
                            total: resultado.imageCount,
                            existentes: resultado.existingCount,
                            faltan: resultado.remainingCount,
                          })
                        : null,
                    );
                  });
                }}
              >
                {busy === 'set'
                  ? t('gal.generandoBoton')
                  : working && !generationStale
                    ? t('estudio.generarIgual')
                    : generationStale
                      ? t('estudio.completarSet')
                      : encargo.sesion
                        ? t('estudio.generarSesion')
                        : t('estudio.generarSet')}
              </Button>
            </div>
          ) : undefined
        }
      />

      <BarraDeEspacio
        creatorId={data.creator.id}
        clasificacion="sfw"
        descartadas={descartadas}
        onChange={onChange}
      />

      {sueltas.length > 0 && (
        <details className="group rounded-2xl border border-[var(--color-border-subtle)]">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-2xl p-4 text-sm">
            <span>
              <span className="font-semibold">
                {/* «Contenido» y no «imágenes»: desde que se generan clips, la lista mezcla
                    tomas y videos, y llamarlos imágenes deja al video sin nombre. */}
                {disponibles.length > 0
                  ? t('estudio.sinPublicacion', { n: disponibles.length })
                  : t('estudio.descartado', { n: descartadas.length })}
              </span>
              <span className="ml-2 text-xs text-[var(--color-ink-muted)]">
                {t(disponibles.length > 0 ? 'estudio.sinPublicacionQue' : 'estudio.descartadoQue')}
              </span>
            </span>
            <span
              aria-hidden="true"
              className="shrink-0 text-[var(--color-ink-muted)] transition-transform group-open:rotate-90"
            >
              ›
            </span>
          </summary>
          <div className="p-4 pt-0">
            {/*
              Escribirles el texto en vez de generar imágenes nuevas.

              Una imagen queda sin publicación porque se generó antes de que nacieran juntas,
              o porque su texto se rechazó por duplicado. En los dos casos la imagen es buena
              y está paga: la única salida era generar otro set, pagando de más para rescatar
              lo que ya estaba.
            */}
            {disponibles.length > 0 && (
              <div className="mb-4 space-y-2 rounded-xl border border-[var(--color-border-subtle)] p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-[var(--color-ink-muted)]">{t('estudio.huerfanasQue')}</p>
                  <Button
                    variant="secondary"
                    disabled={busy !== null}
                    onClick={() => void escribirHuerfanas(false)}
                  >
                    {t(
                      busy === 'huerfanas'
                        ? 'estudio.escribiendo'
                        : 'estudio.escribirPublicaciones',
                    )}
                  </Button>
                </div>

                {/*
                  El motivo ya NO se dibuja acá, sino en la tarjeta de cada imagen.

                  Vivía en este cartel, arriba del panel y encima de la grilla. El botón que lo
                  provoca está en una tarjeta, así que salvo que la foto fuera de la primera fila
                  el aviso aparecía fuera de la pantalla: se pulsaba, no cambiaba nada donde uno
                  miraba, y el sistema parecía hacer como que escribía. Pasó en producción tres
                  veces seguidas sobre la misma imagen.

                  En el lote era peor todavía: un cartel por cada rechazo, sin decir de cuál foto
                  hablaba. Ahora cada aviso está pegado a la suya. Ver `AvisoDeDuplicada`.
                */}
              </div>
            )}

            {/*
              Imágenes y clips, separados y rotulados.

              Comparten estado —generados y sin publicación— pero no acción: de una imagen sale
              un video, de un clip no. Mezclados, la fila alterna tarjetas con un botón que la
              otra no tiene, y eso se lee como que el botón falta en algunas.
            */}
            {[
              [t('estudio.imagenes'), sueltas.filter((asset) => asset.kind !== 'content_video')],
              [t('estudio.videos'), sueltas.filter((asset) => asset.kind === 'content_video')],
            ].map(([titulo, grupo]) =>
              (grupo as AssetView[]).length === 0 ? null : (
                <div key={titulo as string} className="mb-6 last:mb-0">
                  <p className="mb-2 text-xs font-medium tracking-wide uppercase text-[var(--color-ink-muted)]">
                    {titulo as string} · {(grupo as AssetView[]).length}
                  </p>
                  <AssetGrid
                    assets={ordenadasPorTanda(grupo as AssetView[])}
                    /*
                      La cabecera corta la grilla EN la grilla, no la parte en varias.

                      Antes esto dibujaba un `AssetGrid` por tanda, y con tres tandas de una
                      toma cada una salieron tres grillas de un elemento: las fotos quedaron
                      una debajo de la otra. Cada grilla reparte SUS elementos en tres
                      columnas, y una sola no llena ni la primera. Ver `cabeceraDe`.
                    */
                    cabeceraDe={(asset, i, lista) => {
                      const tandaDe = (a: AssetView) =>
                        (a.provenance?.parameters?.contentBatchId as string) ?? '';
                      if (i > 0 && tandaDe(lista[i - 1] as AssetView) === tandaDe(asset)) return null;
                      const cuantas = lista.filter((a) => tandaDe(a) === tandaDe(asset)).length;
                      /*
                        Una cabecera para UNA foto no agrupa nada y cuesta la fila entera.

                        Se vio en pantalla: cuatro tomas de cuatro tandas distintas dieron
                        cuatro cabeceras que decían «· 1», y cada una empujó su tarjeta a un
                        renglón propio. La grilla de tres columnas quedó en una sola.

                        Así que la cabecera aparece cuando REÚNE —un set, una sesión, una tanda
                        de varias—, que es para lo que existe. La foto suelta lleva su fecha en
                        la tarjeta, donde no rompe la grilla.
                      */
                      if (cuantas < 2) return null;
                      return (
                        <p className="flex items-baseline gap-2 text-[11px] text-[var(--color-ink-muted)]">
                          <span className="font-medium">
                            {tandaDe(asset)
                              ? new Date(asset.provenance.generatedAt).toLocaleString(
                                  idioma === 'en' ? 'en-GB' : 'es-AR',
                                  {
                                    day: 'numeric',
                                    month: 'short',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  },
                                )
                              : t('estudio.sinTanda')}
                          </span>
                          <span>· {cuantas}</span>
                        </p>
                      );
                    }}
                    emptyActive={t('estudio.quedanDescartadas')}
                    empty=""
                    render={(asset) => (
                      <AssetCard
                        key={asset.id}
                        asset={asset}
                        isCanonical={false}
                        showCanonicalAction={false}
                        {...accionesDeAsset(asset, { busy, act })}
                        footer={
                          isRetired(asset) ? null : (
                            <div className="space-y-2">
                              <button
                                type="button"
                                disabled={busy !== null}
                                onClick={() => void escribirHuerfanas(false, asset.id)}
                                className="w-full rounded-xl border border-[var(--color-accent)]/40 px-3 py-2 text-sm font-medium transition hover:border-[var(--color-accent)] disabled:opacity-50"
                              >
                                {busy === `post-${asset.id}`
                                  ? t('estudio.escribiendo')
                                  : t('estudio.generarPublicacion')}
                              </button>

                              <AvisoDeDuplicada
                                entradas={rechazadas.filter(
                                  (entrada) => entrada.assetId === asset.id,
                                )}
                                disabled={busy !== null}
                                onForzar={() => void escribirHuerfanas(true, asset.id)}
                              />

                              {/*
                            Animar cuesta más que toda la sesión de imágenes de la influencer, así
                            que el precio va en el botón. Un botón que no dice lo que cuesta, al
                            lado de otros que son centavos, se pulsa por curiosidad.
                          */}
                              {/*
                            El botón se muestra siempre, deshabilitado si falta aprobar.

                            Ocultarlo hasta que la toma estuviera aprobada lo volvía invisible: la
                            mayoría de las tomas viven en PENDING_MODERATION, así que la función
                            parecía no existir. Un control deshabilitado que dice por qué enseña el
                            flujo; uno ausente sólo confunde.
                          */}
                              {asset.kind === 'content_scene' && (
                                <CampoDeMovimiento
                                  valor={movimientoDe(asset.id)}
                                  disabled={animando[asset.id]}
                                  onCambio={(texto) => setMovimiento(asset.id, texto)}
                                />
                              )}

                              {asset.kind === 'content_scene' && (
                                <button
                                  type="button"
                                  disabled={
                                    busy !== null ||
                                    animando[asset.id] ||
                                    asset.status !== 'APPROVED'
                                  }
                                  title={
                                    asset.status === 'APPROVED'
                                      ? // Quién lo genera sale del nivel de piel de la
                                        // influencer, y decirlo evita que el precio distinto
                                        // parezca un error.
                                        t('estudio.animarQue', {
                                          segundos: data.videoPlan.seconds,
                                          proveedor:
                                            data.videoPlan.provider === 'fal-video'
                                              ? 'Wan 2.2 en fal'
                                              : 'Gemini Omni Flash',
                                        })
                                      : t('estudio.aprobarPrimero')
                                  }
                                  onClick={() =>
                                    void animar(asset.id, 'sutil', {
                                      prompt: movimientoDe(asset.id),
                                      seconds: segundosDeClip,
                                    })
                                  }
                                  className="w-full rounded-xl border border-[var(--color-border-subtle)] px-3 py-2 text-xs font-medium transition hover:border-[var(--color-border-strong)] disabled:opacity-50"
                                >
                                  {/*
                                El estado sale del hook, no del `busy` del componente.

                                Al extraer la espera, el botón siguió mirando `busy` — que ya no se
                                toca— así que se pulsaba, arrancaba la generación y la etiqueta no
                                cambiaba. Indistinguible de un botón muerto, y volver a pulsar
                                costaba otro dólar.
                              */}
                                  {animando[asset.id]
                                    ? videoEstado[asset.id] || t('estudio.animando')
                                    : asset.status === 'APPROVED'
                                      ? t('estudio.animar', {
                                          precio: precioDelClip(data.videoPlan),
                                        })
                                      : t('estudio.aprobarParaAnimar')}
                                </button>
                              )}

                              {/*
                                Escalar va después de animar y no entre las acciones de arriba:
                                aquéllas deciden el destino de la toma —aprobar, descartar— y
                                ésta produce un archivo más. Mezcladas, la fila de decisiones
                                pasaba a tener un botón que no decide nada.
                              */}
                              {asset.kind === 'content_scene' && (
                                <AnimarConVideo
                                  asset={asset}
                                  animando={animando[asset.id] ?? false}
                                  onVideo={(video, modo) =>
                                    void animarConVideo(asset.id, video, modo)
                                  }
                                />
                              )}

                              {asset.kind !== 'content_video' && (
                                <Escalar asset={asset} onChange={onChange} />
                              )}

                              {/*
                                La musica, en la tarjeta del clip y no solo en la publicacion.

                                Estaba unicamente bajo la foto elegida de una publicacion, y un
                                clip recien generado no tiene publicacion: habia que crearla,
                                asignarle el clip y recien ahi aparecia el control. Es el mismo
                                defecto que ya tuvo el boton de escalar.
                              */}
                              {asset.kind === 'content_video' && (
                                <MusicaDelClip asset={asset} onChange={onChange} />
                              )}

                              {recorteAvisado(asset, data.videoPlan) && (
                                <p className="mt-1 text-xs text-[var(--color-ink-muted)]">
                                  {recorteAvisado(asset, data.videoPlan)}
                                </p>
                              )}
                              {videoError[asset.id] && (
                                <p className="text-xs text-[var(--color-danger)]">
                                  {videoError[asset.id]}
                                </p>
                              )}

                              {videoDe[asset.id] && (
                                <video
                                  controls
                                  src={videoDe[asset.id]}
                                  className="w-full rounded-xl border-2 border-[var(--color-accent)]"
                                />
                              )}
                            </div>
                          )
                        }
                      />
                    )}
                  />
                </div>
              ),
            )}
          </div>
        </details>
      )}
    </div>
  );
}

/**
 * Contenido, partido en lo que se puede mostrar y lo que no.
 *
 * ── Por qué dos paneles y no un filtro ────────────────────────────────────────
 * Porque no son la misma tarea con distinto material. Lo SFW se genera con los proveedores de
 * siempre, se paga en neuronas y sale a las cuatro plataformas. Lo NSFW se genera con otro
 * proveedor, se paga en dólares por unidad y sólo puede ir a Fanvue, detrás del muro de pago.
 * Distinto costo, distinto destino y distinta consecuencia si uno se equivoca.
 *
 * Un filtro sobre una lista única invitaría al error que el servidor ya rechaza: elegir una toma
 * para adultos y mandarla a Instagram. Separarlas hace que ese error haya que buscarlo.
 */
function Contenido({ data, onChange }: { data: CreatorDetailData; onChange: () => void }) {
  const { t } = useT();
  const [panel, setPanel] = useState<'sfw' | 'nsfw'>('sfw');

  return (
    <div className="space-y-4">
      <div
        className="flex gap-1 rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-1"
        role="tablist"
        aria-label={t('estudio.tipoDeContenido')}
      >
        {(
          [
            ['sfw', t('estudio.publicableEnTodas')],
            ['nsfw', t('estudio.soloFanvue')],
          ] as const
        ).map(([clave, etiqueta]) => (
          <button
            key={clave}
            type="button"
            role="tab"
            aria-selected={panel === clave}
            onClick={() => setPanel(clave)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
              panel === clave
                ? 'bg-[var(--color-surface-raised)] text-[var(--color-ink)] shadow-sm'
                : 'text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]'
            }`}
          >
            {etiqueta}
          </button>
        ))}
      </div>

      {panel === 'sfw' ? (
        <ContentTab data={data} onChange={onChange} />
      ) : (
        <ContenidoNsfw data={data} onChange={onChange} />
      )}
    </div>
  );
}

/**
 * El panel de contenido para adultos.
 *
 * ── Por qué se ve aunque falte la credencial ──────────────────────────────────
 * Igual que la GPU local, que aparece marcada como «sin configurar» en vez de desaparecer. Una
 * función que no existe hasta que alguien adivina que hay que cargar una clave no la encuentra
 * nadie; una que se ve y explica qué le falta se resuelve en un minuto.
 */
/**
 * Pedir una toma para adultos, describiendo la escena.
 *
 * ── Por qué se escribe la escena y no se elige de una lista ───────────────────
 * Porque el sistema no tiene un calendario de contenido para esto. El set de las otras
 * plataformas se planifica —veinte tomas, con su tema y su ropa— y sale de la Character Bible;
 * acá cada toma se pide cuando se necesita, y quien la pide ya sabe qué quiere.
 *
 * ── Por qué la cara no se elige ───────────────────────────────────────────────
 * Se ancla sola a la hoja de identidad, como el resto del contenido. Una influencer que en el
 * feed de pago tiene otra cara no es la misma persona, y eso es lo único que el sistema
 * construye a lo largo de meses.
 */
/**
 * La barra que ofrece vaciar lo descartado, visible sin abrir nada.
 *
 * ── Por qué no vive dentro del desplegable ────────────────────────────────────
 * Ahí estaba, con el argumento de que un botón de borrado permanente tiene que vivir al lado de
 * lo que borra. El argumento es bueno y la ubicación era mala: nadie lo encontró. El desplegable
 * viene cerrado, y cuando hay contenido sin publicación su encabezado ni siquiera nombra a las
 * descartadas — así que no había una sola pista de que ahí adentro hubiera algo.
 *
 * Un control que no se ve es un control que no existe. Se queda pegado al desplegable, que sigue
 * siendo dónde se miran las imágenes, pero afuera: dice cuántas son y cuánto ocupan, que es
 * exactamente lo que hace falta para decidir sin abrirlo.
 */
function BarraDeEspacio({
  creatorId,
  clasificacion,
  descartadas,
  onChange,
}: {
  creatorId: string;
  clasificacion: 'sfw' | 'nsfw';
  descartadas: AssetView[];
  onChange: () => void;
}) {
  const { t } = useT();
  if (descartadas.length === 0) return null;

  const mb = (descartadas.reduce((suma, a) => suma + a.byteSize, 0) / 1024 / 1024).toFixed(1);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-4 text-sm">
      <p className="text-[var(--color-ink-muted)]">
        <strong className="text-[var(--color-ink)]">
          {t(descartadas.length === 1 ? 'espacio.descartadaUna' : 'espacio.descartadasVarias', {
            n: descartadas.length,
          })}
        </strong>
        {t('espacio.ocupan', { mb })}
      </p>
      <LiberarEspacio
        creatorId={creatorId}
        clasificacion={clasificacion}
        descartadas={descartadas}
        onChange={onChange}
      />
    </div>
  );
}

/**
 * Libera el espacio de lo que ya se descartó.
 *
 * ── Por qué es un botón aparte y no parte de descartar ────────────────────────
 * Porque son dos decisiones distintas y una es reversible. Descartar dice «esta no la uso» y se
 * puede deshacer mirando la imagen; borrar pierde los bytes. Juntarlas obligaría a estar seguro
 * en el momento de mirar, que es justo cuando todavía no se sabe.
 *
 * ── Por qué existe ────────────────────────────────────────────────────────────
 * La media vive en KV, que tiene 1 GB, y las descartadas son la mayor parte de lo que lo llena:
 * de cada set de cuatro se aprueban una o dos. Vaciarlas de a una son treinta confirmaciones, y
 * una tarea de treinta pasos no se hace — el espacio se recupera cuando ya molesta.
 */
function LiberarEspacio({
  creatorId,
  clasificacion,
  descartadas,
  onChange,
}: {
  creatorId: string;
  clasificacion: 'sfw' | 'nsfw';
  descartadas: AssetView[];
  onChange: () => void;
}) {
  const { t } = useT();
  const aviso = useAviso();
  const [borrando, setBorrando] = useState(false);

  if (descartadas.length === 0) return null;

  const bytes = descartadas.reduce((suma, asset) => suma + asset.byteSize, 0);
  const mb = (bytes / 1024 / 1024).toFixed(1);

  const liberar = () => {
    /*
      El aviso dice qué se pierde y qué queda.

      Un «¿estás seguro?» a secas no le da a nadie con qué decidir. Lo que se pierde son los
      archivos; lo que queda es la procedencia —prompt, semilla, modelo y créditos— así que el
      consumo del día se sigue pudiendo explicar después de vaciar.
    */
    const ok = window.confirm(t('espacio.confirmarLiberar', { n: descartadas.length, mb }));
    if (!ok) return;

    setBorrando(true);
    void api
      .post<{ borradas: number; freedBytes: number; fallaron: number }>(
        `/api/creators/${creatorId}/liberar-espacio`,
        { clasificacion },
      )
      .then((r) => {
        const liberados = (r.freedBytes / 1024 / 1024).toFixed(1);
        aviso.ok(
          r.fallaron > 0
            ? t('espacio.parcial', {
                borradas: r.borradas,
                fallaron: r.fallaron,
                mb: liberados,
              })
            : t('espacio.total', { borradas: r.borradas, mb: liberados }),
        );
      })
      .then(onChange)
      .catch(() => undefined)
      .finally(() => setBorrando(false));
  };

  return (
    <Button variant="secondary" disabled={borrando} onClick={liberar}>
      {borrando ? 'Borrando…' : `Liberar espacio · ${mb} MB`}
    </Button>
  );
}

function GenerarNsfw({
  creatorId,
  onChange,
  sheetApproved,
}: {
  creatorId: string;
  onChange: () => void;
  sheetApproved: boolean;
}) {
  const { t } = useT();
  const aviso = useAviso();
  /*
    El idioma viaja con la petición.

    La escena se escribe y se muestra en el idioma del panel, y el modelo de imagen entiende
    inglés. El servidor traduce, pero sólo si hace falta: con el panel en inglés la escena ya está
    en inglés y traducirla sería una llamada de texto pagada para no cambiar nada.
  */
  const { idioma } = useT();
  /*
    Con qué modelo se va a generar, que decide si el cambio de cara hace falta.

    ── Por qué el ajuste llega hasta acá ─────────────────────────────────────────
    Porque el segundo paso dejó de ser obligatorio para todos. Existía porque ningún modelo
    probado hacía las dos cosas que Fanvue necesita —el que aprende la cara no genera desnudez, el
    que la genera no tiene LoRA—, así que se generaba el cuerpo con uno y se le pegaba la cara con
    otro. La GPU propia sí hace las dos: el mecanismo de edición de Krea2 sostiene la identidad
    desde la referencia y su checkpoint no tiene filtro.

    Con ella, el cambio de cara es una segunda llamada que cuesta USD 0,04 para arreglar algo que
    ya estaba bien — y encima puede empeorarlo, porque el editor tiene filtro del fabricante y una
    entrada explícita puede volver sin tocar.
  */
  const ajustes = useApi<{ settings: { nsfwImageModel: string | null } }>('/api/settings');
  const generaEnGpuPropia =
    ajustes.data?.settings.nsfwImageModel?.startsWith('local-gpu/') === true;

  const [proporcion, setProporcion] = useState<'4:5' | '9:16' | '1:1' | '16:9'>('4:5');
  /**
   * Hasta dónde llega el material de esta tanda.
   *
   * ── Por qué faltaba ───────────────────────────────────────────────────────
   * Esta pestaña tenía un solo registro: desnuda del todo. Entre eso y lo que se publica en
   * Instagram no había nada, y lo que llena los feeds de Fanvue y de Telegram está justo en el
   * medio — el torso descubierto con la prenda de abajo puesta.
   *
   * ── Por qué UN estado para las dos llamadas ───────────────────────────────
   * Porque las escenas se escriben para un registro. Con dos controles separados se puede pedir
   * seis escenas en topless y generarlas desnuda, y la escena no avisa: dice dónde está y qué
   * pose hace, no cuánta ropa lleva. Un solo mando no lo permite.
   *
   * Vive en la tarjeta que pregunta qué se ve, que es donde se decide, y no al lado del botón
   * de generar, que es donde ya se decidió.
   */
  const [nivel, setNivel] = useState<'insinuado' | 'explicito'>('explicito');
  /**
   * Una escena propuesta, con lo que el planificador decidió sobre ella.
   *
   * `caraVisible` viaja con el texto y no se deduce de él: la pose la eligió el planificador de
   * una lista cerrada, así que sabe cuáles dejan de espaldas. Adivinarlo acá con una expresión
   * regular sobre español libre sería reconstruir con menos información lo que ya venía sabido.
   */
  type EscenaPropuesta = {
    texto: string;
    caraVisible: boolean;
    /**
     * Cuál de las seis luces le toca, como índice.
     *
     * Lo reparte el planificador por posición, porque es el único punto donde las seis escenas
     * existen a la vez: cada toma se genera con su propia petición y ahí ya no hay lista.
     * Viaja como número y no como texto para que del navegador no salga nada que entre a un
     * prompt tal cual.
     */
    luz?: number;
  };

  const [sugerencias, setSugerencias] = useState<EscenaPropuesta[]>([]);
  const [elegidas, setElegidas] = useState<EscenaPropuesta[]>([]);
  const [propia, setPropia] = useState('');
  const [pensando, setPensando] = useState(false);
  const [avance, setAvance] = useState<{ hechas: number; total: number } | null>(null);
  /**
   * Lo que falló, escrito y quieto.
   *
   * ── Por qué no alcanza con el aviso flotante ──────────────────────────────────
   * Dura cinco segundos y se va. Los errores de un proveedor de imagen son técnicos y largos
   * —qué parámetro sobró, qué modelo lo rechazó, cuánto tardó— y en cinco segundos no se leen ni
   * se copian. El resultado fue que la única forma de enterarse era abrir la consola del
   * navegador, que no es una interfaz.
   *
   * Acá se queda hasta la próxima tanda: un error que costó una generación merece seguir en
   * pantalla mientras alguien decide qué hacer con él.
   */
  const [fallos, setFallos] = useState<string[]>([]);

  /**
   * Si se le cambia la cara después de generar, y por qué el valor inicial no es fijo.
   *
   * `null` significa «lo que corresponda al modelo elegido», que es lo que se quiere mientras
   * nadie lo toque: con la GPU propia el segundo paso sobra, y con un modelo de Runware sigue
   * siendo lo que dio el mejor resultado medido. Fijarlo en `true` o en `false` obligaría a
   * acordarse de cambiarlo al cambiar de modelo, que es justo lo que nadie hace.
   *
   * En cuanto alguien marca o desmarca, manda su elección: la casilla existe para poder pedir lo
   * contrario de lo que el sistema recomienda.
   */
  const [cambioDeCara, setCambioDeCara] = useState<boolean | null>(null);
  const conCambioDeCara = cambioDeCara ?? !generaEnGpuPropia;

  /**
   * Si se puede generar, preguntado antes de ofrecer un botón.
   *
   * ── Por qué no alcanza con que falle ──────────────────────────────────────────
   * Porque proponer escenas cuesta tokens y generar cuesta por imagen. Un botón que gasta y falla
   * siempre es peor que uno deshabilitado con el motivo al lado: el primero cobra por enterarse,
   * el segundo lo explica gratis.
   *
   * ── Y por qué se refresca solo ────────────────────────────────────────────────
   * Porque el motivo más común es temporal: el LoRA recién entrenado tarda unos minutos en
   * publicarse. Sin refresco habría que recargar la pantalla para descubrir que ya se puede.
   */
  const listo = useApi<{
    puede: boolean;
    motivo?: string;
    proponer: { puede: boolean; motivo?: string };
  }>(`/api/creators/${creatorId}/nsfw/estado`, 20_000);
  const bloqueado = listo.data?.puede === false;
  /*
    Proponer y generar se bloquean por separado, porque cuestan distinto.

    Pedirle escenas al modelo de texto se cobra pase lo que pase. Generar una imagen que Runware
    rechaza no cuesta nada — y es lo único que puede confirmar que un LoRA recién entrenado ya se
    puede usar. Bloquear las dos dejaría a la influencer trabada para siempre.
  */
  const noProponer = listo.data?.proponer?.puede === false;

  /*
    Las escenas las propone el modelo de texto, y salen de SU ficha.

    Escribirlas a mano no escala a trece influencers publicando cada semana. Y una lista fija daría
    lo mismo para una jugadora de vóley que para una cocinera, que es justo lo que distingue a una
    influencer de un banco de imágenes: que su contenido tenga que ver con lo que ella hace.

    Se piden sólo cuando alguien las pide. La llamada consume del proveedor de texto, y llenar la
    pantalla de sugerencias que nadie miró es gasto sin uso.
  */
  const sugerir = () => {
    setPensando(true);
    void api
      /*
        El idioma va también acá, y no sólo al generar.

        Sin él, el panel en español recibía escenas en inglés: el servidor no tiene de dónde
        sacarlo y cae en el idioma por defecto. La escena se muestra tal cual, así que el idioma
        de la propuesta es el idioma de la pantalla.
      */
      .post<{ escenas: EscenaPropuesta[] }>(
        `/api/creators/${creatorId}/nsfw/escenas?idioma=${idioma}&nivel=${nivel}`,
        {},
      )
      .then((r) => setSugerencias(r.escenas))
      .catch(() => undefined)
      .finally(() => setPensando(false));
  };

  /* El texto es la identidad de la escena: dos propuestas distintas nunca comparten el texto. */
  const alternar = (escena: EscenaPropuesta) =>
    setElegidas((actual) =>
      actual.some((e) => e.texto === escena.texto)
        ? actual.filter((e) => e.texto !== escena.texto)
        : [...actual, escena],
    );

  /*
    Una petición por escena, en fila y no en paralelo.

    Cada toma tarda decenas de segundos y el borde de Cloudflare corta a los cien: mandarlas
    juntas no las hace terminar antes, y multiplica por cuatro la chance de que una se caiga por
    tiempo. En fila, además, se puede decir cuántas van — y si una falla, las anteriores ya están
    generadas y pagas.
  */
  /*
    `conCambioDeCara` parte la generación en DOS llamadas: primero el cuerpo, después la cara.

    ── Por qué existió como paso obligatorio ─────────────────────────────────────
    Porque ningún modelo de Runware hace las dos cosas que Fanvue necesita. El que aprende la cara
    de ella —FLUX con un LoRA— no genera desnudez; el que la genera —SDXL— no tiene LoRA entrenado.
    Con dos pasos cada modelo hace sólo lo suyo: uno pone el cuerpo y la escena, el otro pone la
    cara sobre lo ya generado. El de un paso daba un 60 % de parecido y rara vez desnudez, así que
    ofrecer los dos era ofrecer una forma de gastar peor.

    ── Por qué volvió a ser opcional ─────────────────────────────────────────────
    Porque apareció un modelo que sí hace las dos: la GPU propia. Krea2 con su mecanismo de
    edición sostiene la identidad desde la referencia —medido acá, la cara se mantiene entre
    ángulos y escenas— y su checkpoint no tiene filtro, así que genera el registro sin pedir
    permiso. Con él, el segundo paso no arregla nada: cuesta USD 0,04 por toma para reemplazar una
    cara que ya era la correcta, y el editor trae filtro del fabricante, así que puede devolver la
    imagen sin tocar y cobrarla igual.

    Sigue encendido por omisión con los modelos de Runware, que es donde la medición lo respalda.
    Lo que cambió es que ahora se puede apagar, y que con la GPU propia arranca apagado.
  */
  const generarSet = async (): Promise<void> => {
    setAvance({ hechas: 0, total: elegidas.length });
    setFallos([]);
    let hechas = 0;
    const errores: string[] = [];

    for (const escena of elegidas) {
      try {
        await api.post(`/api/creators/${creatorId}/nsfw`, {
          escena: escena.texto,
          caraVisible: escena.caraVisible,
          ...(escena.luz === undefined ? {} : { luz: escena.luz }),
          aspectRatio: proporcion,
          nivel,
          idioma,
          ...(conCambioDeCara ? { conCambioDeCara: true } : {}),
        });
        hechas += 1;
      } catch (caught) {
        /*
          Se guarda el mensaje, no sólo que falló.

          Contar los fallos alcanzaba cuando el aviso flotante decía el motivo; como se va a los
          cinco segundos, el contador se quedaba solo con «una falló» y el porqué se perdía.
        */
        errores.push(caught instanceof Error ? caught.message : String(caught));
      }
      setAvance({ hechas: hechas + errores.length, total: elegidas.length });
      onChange();
    }

    setAvance(null);
    setFallos(errores);
    if (hechas > 0) setElegidas([]);
    aviso.ok(
      errores.length === 0
        ? t('nsfw.tomasGeneradas', { n: hechas })
        : t('nsfw.generadasYFallidas', { hechas, fallidas: errores.length }),
    );
  };

  /* Lo escrito a mano entra a la misma lista que lo propuesto: se elige igual y se genera igual. */
  const agregarPropia = () => {
    const texto = propia.trim();
    if (texto.length < 3) return;
    /*
      Lo escrito a mano se asume de frente.

      Quien escribe una escena describe lo que quiere ver, y una de espaldas sin cara es la
      excepción — pedirla sería una casilla más en una pantalla que ya tiene bastantes. El
      servidor asume lo mismo cuando el campo no viene, así que las dos puntas coinciden.
    */
    const nueva: EscenaPropuesta = { texto, caraVisible: true };
    setSugerencias((actual) => [nueva, ...actual.filter((e) => e.texto !== texto)]);
    setElegidas((actual) => [...actual.filter((e) => e.texto !== texto), nueva]);
    setPropia('');
  };

  if (bloqueado) {
    return (
      <Panel
        title={t('nsfw.noSePuedeAun')}
        body={listo.data?.motivo ?? t('nsfw.faltaConfigurar')}
      />
    );
  }

  if (!sheetApproved) {
    return <Panel title={t('nsfw.faltaHoja')} body={t('nsfw.faltaHojaQue')} />;
  }

  return (
    <div className="space-y-4">
      {/*
        Lo que dijo el proveedor, tal cual y sin vencimiento.

        Estos mensajes son la única pista cuando una generación falla, y traen el dato que
        importa: qué parámetro rechazó, con qué modelo, cuánto tardó. Se muestran enteros porque
        recortarlos deja afuera justamente el final, que es donde está el detalle.
      */}
      {fallos.length > 0 && (
        <Card
          title={
            fallos.length === 1
              ? t('nsfw.noSePudoGenerar')
              : t('nsfw.fallaron', { n: fallos.length })
          }
        >
          <ul className="space-y-2">
            {fallos.map((mensaje, indice) => (
              <li
                key={indice}
                className="rounded-xl border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/5 p-3 text-xs break-words"
              >
                {mensaje}
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => setFallos([])}
            className="mt-3 text-xs underline underline-offset-2 hover:text-[var(--color-accent)]"
          >
            {t('lora.entendidoOcultar')}
          </button>
        </Card>
      )}

      {avance && (
        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-4 text-sm">
          <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-[var(--color-accent)]" />
          <span className="font-medium tabular-nums">
            {t('nsfw.generadas', { hechas: avance.hechas, total: avance.total })}
          </span>
          <span className="text-xs text-[var(--color-ink-muted)]">{t('nsfw.dosPasos')}</span>
        </div>
      )}

      <Panel
        accent={sugerencias.length === 0}
        title={t('nsfw.generarContenido')}
        /*
          El texto dice lo que va a pasar con ESTE modelo, no lo que pasaba con el anterior.

          Decía siempre «cada toma se hace en dos pasos» y «son unos USD 0,04 por imagen». Con la
          GPU propia las dos frases son falsas: es un paso solo y no cuesta dinero.
        */
        body={generaEnGpuPropia ? t('nsfw.enGpuPropia') : t('nsfw.enProveedor')}
        action={
          <div className="flex items-center gap-2">
            <select
              value={proporcion}
              onChange={(event) => setProporcion(event.target.value as typeof proporcion)}
              aria-label={t('nsfw.proporcion')}
              className="rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-3 py-2 text-sm"
            >
              <option value="4:5">Vertical 4:5</option>
              <option value="9:16">Vertical 9:16</option>
              <option value="1:1">Cuadrada</option>
              <option value="16:9">Apaisada</option>
            </select>
            {/*
              El segundo paso, como casilla y no como un segundo botón.

              Dos botones obligan a elegir un camino antes de saber que hay dos, y el que elige mal
              se entera cuatro tomas después. Una casilla al lado del botón dice que el paso existe,
              qué cuesta, y deja el valor recomendado puesto — que ahora depende del modelo.
            */}
            <label
              className="flex items-center gap-2 text-sm"
              title={generaEnGpuPropia ? t('nsfw.caraEnGpuPropia') : t('nsfw.caraEnProveedor')}
            >
              <input
                type="checkbox"
                checked={conCambioDeCara}
                disabled={avance !== null}
                onChange={(event) => setCambioDeCara(event.target.checked)}
              />
              {t('nsfw.cambiarLaCara')}
            </label>
            <Button
              disabled={avance !== null || elegidas.length === 0}
              onClick={() => void generarSet()}
            >
              {avance
                ? conCambioDeCara
                  ? t('nsfw.generandoConCara')
                  : t('gal.generandoBoton')
                : elegidas.length === 0
                  ? t('nsfw.elegiUnaEscena')
                  : t('nsfw.generarSetN', { n: elegidas.length })}
            </Button>
          </div>
        }
      />

      <Card title={t('nsfw.queSeVe')}>
        <div className="flex flex-wrap items-center gap-3">
          {/*
            Se avisa, y no se bloquea.

            La primera versión deshabilitaba este botón mientras el LoRA no estuviera confirmado,
            para no gastar tokens en escenas que no se iban a poder generar. Era un callejón sin
            salida: confirmar el LoRA exige generar, generar exige una escena, y la forma cómoda de
            conseguir una era justo este botón.

            Y el gasto tampoco se pierde: una escena propuesta sigue sirviendo mañana. Lo que
            corresponde entonces es decir el riesgo, no impedir la acción — quien paga decide.
          */}
          <button
            type="button"
            disabled={pensando}
            onClick={sugerir}
            className="rounded-lg border border-[var(--color-border-subtle)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)] disabled:opacity-40"
          >
            {t(pensando ? 'nsfw.pensando' : 'nsfw.queLasProponga')}
          </button>
          <select
            value={nivel}
            onChange={(event) => setNivel(event.target.value as typeof nivel)}
            aria-label={t('nsfw.hastaDonde')}
            className="rounded-lg border border-[var(--color-border-subtle)] bg-transparent px-3 py-1.5 text-sm"
          >
            <option value="explicito">{t('nsfw.nivelExplicito')}</option>
            <option value="insinuado">{t('nsfw.nivelInsinuado')}</option>
          </select>
          <p className="text-xs text-[var(--color-ink-muted)]">{t('nsfw.salenDelNicho')}</p>
        </div>
        {/*
          El destino se dice acá y no en un aviso al publicar.

          Las dos opciones quedan fuera de Instagram y de Facebook, y eso no es una advertencia:
          es lo que hace que esta pestaña exista. Dicho al elegir, la elección se entiende; dicho
          al publicar, ya se pagaron seis tomas.
        */}
        <p className="mt-2 text-xs text-[var(--color-ink-muted)]">
          {t(nivel === 'insinuado' ? 'nsfw.nivelInsinuadoDetalle' : 'nsfw.nivelExplicitoDetalle')}
        </p>

        {/*
          Dos avisos distintos, y el segundo es el peligroso.

          Uno dice que no conviene pedir escenas todavía. El otro dice que el LoRA existe y NO se
          va a usar porque el modelo elegido no es de su familia — ése no bloquea nada, y por eso
          hay que verlo: la generación sale bien, se cobra, y la cara no es la de ella.
        */}
        {noProponer && (
          <p className="mt-3 rounded-xl border border-[var(--color-warn)]/35 bg-[var(--color-warn)]/10 p-3 text-xs">
            {listo.data?.proponer?.motivo}
          </p>
        )}

        {/*
          Se eligen varias, y por eso son casillas y no un botón que reemplaza el texto.

          Antes la propuesta se pulsaba y pisaba el campo: elegir la segunda perdía la primera, así
          que un set de cuatro eran cuatro vueltas de pedir, pulsar y generar. Lo que se quiere
          decidir es «estas cuatro sí, esas dos no», y eso es una selección, no un reemplazo.
        */}
        {sugerencias.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {sugerencias.map((propuesta) => {
              const elegida = elegidas.some((e) => e.texto === propuesta.texto);
              return (
                <li key={propuesta.texto}>
                  <button
                    type="button"
                    onClick={() => alternar(propuesta)}
                    aria-pressed={elegida}
                    className={
                      'flex w-full items-start gap-2 rounded-lg border px-3 py-2 text-left text-xs transition ' +
                      (elegida
                        ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft)] text-[var(--color-ink)]'
                        : 'border-[var(--color-border-subtle)] text-[var(--color-ink-muted)] hover:border-[var(--color-border-strong)]')
                    }
                  >
                    <span aria-hidden="true" className="mt-px shrink-0">
                      {elegida ? '☑' : '☐'}
                    </span>
                    <span>
                      {propuesta.texto}
                      {/*
                        Se dice cuáles son de espaldas porque cambia lo que se recibe.

                        Sin la marca, una toma sin cara dentro de un set de seis se lee como un
                        fallo del mecanismo de identidad —que es justamente el bug que se estuvo
                        cazando acá— y no como lo que es: la que se pidió así.
                      */}
                      {!propuesta.caraVisible && (
                        <span className="ml-1.5 text-[var(--color-ink-muted)]">
                          {t('nsfw.deEspaldas')}
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            value={propia}
            onChange={(event) => setPropia(event.target.value)}
            placeholder={t('nsfw.escribiUna')}
            className="min-w-0 flex-1 rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-3 py-2 text-sm"
          />
          <Button variant="secondary" disabled={propia.trim().length < 3} onClick={agregarPropia}>
            {t('nsfw.agregar')}
          </Button>
        </div>
        <p className="mt-1.5 text-xs text-[var(--color-ink-muted)]">{t('nsfw.describiLaEscena')}</p>
      </Card>
    </div>
  );
}

function ContenidoNsfw({ data, onChange }: { data: CreatorDetailData; onChange: () => void }) {
  const { t } = useT();
  const aviso = useAviso();
  const [busy, setBusy] = useState<string | null>(null);
  /** Cuál publicación tiene el botón armado. Publicar es irreversible: se arma y se confirma. */
  const [armada, setArmada] = useState<string | null>(null);
  /*
    El lote tiene su propio estado, separado del de una sola.

    `armada` guarda QUÉ publicación está a un clic de salir; el lote no es una de ellas. Y el
    avance tiene que sobrevivir a que la lista se recargue entre publicación y publicación, que
    es lo que hace `onChange()` dentro del bucle.
  */
  const [armadoElLote, setArmadoElLote] = useState(false);
  const [avanceLote, setAvanceLote] = useState<{ hechas: number; total: number } | null>(null);
  /*
    Lo que no salió, escrito y quieto.

    El aviso flotante se va a los cinco segundos y se lleva el motivo. Con quince publicaciones,
    «tres no salieron» sin decir cuáles ni por qué es lo mismo que no decir nada.
  */
  const [fallosDelLote, setFallosDelLote] = useState<string[]>([]);
  /*
    Animar, con el mismo gancho que las otras dos pantallas.

    El modelo lo elige el servidor a partir de la CLASE de la toma, no del perfil de la
    influencer: una explícita necesita uno sin filtro del fabricante, y el perfil de una
    influencer llega hasta `atrevido`. Acá no hay que decidir nada, sólo pedirlo.
  */
  const { animar, videoEstado, videoError, animando } = useAnimar(onChange, data.videoJobs);
  const { movimientoDe, setMovimiento } = useMovimientos();
  /*
    Qué contestó la última publicación, para poder decirlo.

    Se ignoraba, y eso escondió un fallo entero: cuando la conexión de Fanvue no puede publicar, el
    contenedor cae a paquete manual en vez de fallar — así que la llamada salía bien, no había
    error, y no se publicaba nada. Desde la pantalla es indistinguible de que el botón no ande.
  */
  const [resultado, setResultado] = useState<{ id: string; status: string } | null>(null);
  /*
    Si Fanvue puede recibir algo, preguntado y no supuesto.

    Hacen falta las dos cosas: que el manager haya autorizado la app, y que ESTA influencer tenga
    asignado un perfil de Fanvue. Con una sola, el botón sale y falla al publicar — que es el peor
    momento para enterarse, porque la subida del archivo ya ocurrió.
  */
  const fanvue = useApi<FanvueProfilesResponse>(
    `/api/integrations/fanvue/creators?creatorId=${data.creator.id}`,
  );
  const fanvueListo = Boolean(fanvue.data?.connected && fanvue.data.current);
  const ajustes = useApi<{
    settings: {
      nsfwImageModel: string | null;
      nsfwVideoModel: string | null;
      /* El precio que el servidor va a cobrar. Escribirlo en la pantalla seria una segunda
         fuente de verdad que un dia deja de coincidir. */
      telegramStars: { imagenNsfw: number };
    };
    catalogoNsfw?: {
      imagen: { id: string; etiqueta: string }[];
      video: { id: string; etiqueta: string }[];
    };
  }>('/api/settings');
  const precioEnStars = ajustes.data?.settings.telegramStars.imagenNsfw ?? 0;
  const credenciales = useApi<{ credentials: { kind: string }[] }>('/api/credentials');

  const act = async (key: string, run: () => Promise<unknown>): Promise<void> => {
    setBusy(key);
    try {
      await run();
      onChange();
    } catch (caught) {
      // Lo de la API ya salió como aviso flotante. Acá sólo lo que ni llegó a la API.
      if (!(caught instanceof ApiRequestError)) aviso.error(t('gal.accionFallo'));
    } finally {
      setBusy(null);
    }
  };

  /*
    El nombre del modelo, no su identificador ni «el que corresponda».

    Sin elegir, el sistema usa el primero del catálogo, que está ordenado por recomendación. Decir
    «por defecto» sin nombrarlo obliga a ir a Modelos a averiguar cuál es — y ahí decía lo mismo.
  */
  const nombreDelModelo = (tipo: 'imagen' | 'video'): string => {
    const catalogo = ajustes.data?.catalogoNsfw?.[tipo] ?? [];
    const elegido =
      tipo === 'imagen'
        ? ajustes.data?.settings.nsfwImageModel
        : ajustes.data?.settings.nsfwVideoModel;

    const modelo = catalogo.find((m) => m.id === elegido) ?? catalogo[0];
    if (!modelo) return '—';

    return elegido ? modelo.etiqueta : `${modelo.etiqueta} (recomendado)`;
  };

  /*
    De la imagen a la publicación, sin salir de acá.

    Todo el circuito de publicación cuelga de `content_items`, y esos los creaba sólo el plan de
    contenido —que es material apto—. Las tomas de Fanvue quedaban como archivos sueltos: el
    publicador estaba completo y no tenía qué publicar.

    El pie de cada tarjeta muestra en qué punto de ese camino está ésta, y ofrece el paso que
    sigue. Tres estados y nada más: sin publicación, con publicación sin salir, y publicada.
  */
  const publicacionDe = (assetId: string) => data.content.find((item) => item.assetId === assetId);

  /*
    Si hay canal adonde mandarlas. Sin usuario cargado no hay nada que ofrecer, y la casilla
    seria un control que promete algo que no puede pasar.
  */
  const hayCanalDeTelegram = data.accounts.some(
    (cuenta) => cuenta.platform === 'telegram' && (cuenta.username ?? '').trim().length > 0,
  );

  /*
    Animar una toma de Fanvue, en su propio pie.

    Va con la publicación y no en las acciones de la tarjeta porque es lo mismo que hace la
    pestaña de Contenido: la toma aprobada se anima, y el clip nace como un asset más — que ahora
    hereda su clasificación, así que aparece acá y no entre el material de las redes.
  */
  const pieDeAnimar = (asset: AssetView) => {
    if (asset.kind === 'content_video') return null;

    return (
      <div>
        {/*
          Acá no había ningún campo, ni siquiera el global: se animaba siempre con el movimiento
          automático. Es la pestaña donde más falta hace poder pedir un gesto concreto.
        */}
        <div className="mb-1.5">
          <CampoDeMovimiento
            valor={movimientoDe(asset.id)}
            disabled={animando[asset.id]}
            onCambio={(texto) => setMovimiento(asset.id, texto)}
          />
        </div>
        <button
          type="button"
          disabled={animando[asset.id] || asset.status !== 'APPROVED'}
          title={asset.status === 'APPROVED' ? t('fanvue.clipHereda') : t('fanvue.aprobarPrimero')}
          /*
            Movimiento amplio, que es lo que este material necesita.

            Un clip quieto es una foto que ocupa cinco segundos. En una publicación de Instagram
            eso funciona —un cinemagrama se ve bien— y acá lo que se paga es justamente que se
            mueva.
          */
          onClick={() => void animar(asset.id, 'amplio', { prompt: movimientoDe(asset.id) })}
          className="w-full rounded-lg border border-[var(--color-border-subtle)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)] disabled:opacity-40"
        >
          {animando[asset.id]
            ? videoEstado[asset.id] || t('estudio.animando')
            : asset.status === 'APPROVED'
              ? t('estudio.animar', { precio: precioDelClip(data.videoPlan) })
              : t('fanvue.aprobalaParaAnimar')}
        </button>
        {videoError[asset.id] && (
          <p className="mt-1 text-xs text-[var(--color-danger)]">{videoError[asset.id]}</p>
        )}
        {/* El mismo control que en la galería: acá el material también se descarga para publicar
            fuera del feed, y era el único sitio donde no estaba. */}
        <div className="mt-1.5">
          <Escalar asset={asset} onChange={onChange} compacto />
        </div>
      </div>
    );
  };

  const pieDePublicacion = (asset: AssetView) => {
    const publicacion = publicacionDe(asset.id);

    if (!publicacion) {
      return (
        <div className="space-y-1.5">
          <button
            type="button"
            disabled={busy !== null}
            onClick={() =>
              void act(`pub-${asset.id}`, () => api.post(`/api/assets/${asset.id}/publicacion`))
            }
            className="w-full rounded-lg border border-[var(--color-border-subtle)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)] disabled:opacity-40"
          >
            {t(busy === `pub-${asset.id}` ? 'fanvue.creando' : 'fanvue.crearPublicacion')}
          </button>
        </div>
      );
    }

    /*
      NO se corta por `publicacion.status`, y eso es el arreglo.

      Ese campo es UNO para todas las plataformas: publicada en Fanvue decia «publicada» y
      escondia todos los botones, incluido el de Telegram, donde no habia salido nunca. Es el
      mismo defecto que la pestania de contenido apto ya habia arreglado —y esta arrastraba—:
      lo que contesta «ya salio» es si hay un `external_post` de ESE par publicacion + destino.
    */
    return (
      <div className="space-y-2">
        {/*
          El texto se muestra, no se esconde detrás de un enlace al calendario.

          Lo escribe el modelo de texto y sale con la foto: si nadie lo lee antes de publicar,
          la primera vez que se ve es en Fanvue.
        */}
        {publicacion.variants[0]?.caption && (
          <p className="rounded-lg bg-[var(--color-surface-sunken)] p-2 text-xs italic">
            {publicacion.variants[0].caption}
          </p>
        )}
        {/*
          Un boton por destino, no uno solo con Fanvue escrito a mano.

          ── El defecto ─────────────────────────────────────────────────────────
          Este boton publicaba con `?platform=fanvue` fijo, de cuando Fanvue era el unico destino
          de lo explicito. Con Telegram enchufado la variante se creaba y no habia forma de
          sacarla: el muro de pago existia y no se podia ejercer.

          ── Por que el boton dice si va de pago ────────────────────────────────
          Porque es lo unico irreversible de la pantalla. Publicada gratis, una toma que se
          queria cobrar ya la vio todo el canal, y no hay como volver atras. El estado sale de la
          variante guardada —lo que el servidor va a hacer— y no de la casilla, que solo se leyo
          al crearla.
        */}
        {/*
          Los dos destinos de una vez, sin recorrer la tarjeta pulsando de a uno.

          Acá es donde más falta hacía: una toma explícita nace con variante de Fanvue y de
          Telegram, y publicarla completa costaba cuatro pulsaciones —dos botones, cada uno con
          su confirmación—. Lo caro no era el tiempo sino lo que se olvidaba en el medio.

          Se ofrece sólo lo que de verdad se puede publicar ahora: sin perfil de Fanvue asignado,
          o sin canal cargado, ese destino no entra en la lista en vez de entrar y fallar.
        */}
        <PublicarEnVarias
          contentItemId={publicacion.id}
          destinos={publicacion.variants
            .filter(
              (variante) =>
                (variante.platform === 'fanvue' ? fanvueListo : hayCanalDeTelegram) &&
                !data.posts.some(
                  (post) =>
                    post.contentItemId === publicacion.id &&
                    post.platform === variante.platform &&
                    post.status === 'PUBLISHED',
                ),
            )
            .map((variante) => ({
              platform: variante.platform,
              dePago: variante.audience === 'subscribers',
              puedeRegalar: variante.platform === 'telegram',
            }))}
          disabled={busy !== null}
          precioEnStars={precioEnStars}
          onHecho={onChange}
        />

        {publicacion.variants
          .filter((variante) => variante.platform === 'fanvue' || hayCanalDeTelegram)
          .map((variante) => {
            const dePago = variante.audience === 'subscribers';
            const esFanvue = variante.platform === 'fanvue';
            const trabada = esFanvue && !fanvueListo;
            const clave = `${publicacion.id}:${variante.platform}`;
            const yaSalio = data.posts.some(
              (post) =>
                post.contentItemId === publicacion.id &&
                post.platform === variante.platform &&
                post.status === 'PUBLISHED',
            );

            if (yaSalio) {
              /*
                Y si es de Telegram y ya vendió, cuánto.

                Es el único sitio donde el número significa algo: pegado a la foto que lo produjo.
                Un total suelto dice si el canal funciona; esto dice cuál funcionó. Sin ventas no
                se escribe nada — un «0 ventas» debajo de cada foto recién publicada es ruido.
              */
              const vendida =
                variante.platform === 'telegram'
                  ? data.ventasPorPublicacion?.[publicacion.id]
                  : undefined;

              return (
                <div key={variante.id} className="space-y-1">
                  <p className="text-xs text-[var(--color-ink-muted)]">
                    {t('fanvue.yaSalioEn', { destino: variante.platform })}
                    {vendida && vendida.ventas > 0 && (
                      <span className="ml-1 font-medium text-[var(--color-accent)] tabular-nums">
                        {t('ventas.deEsta', { n: vendida.ventas, stars: vendida.stars })}
                      </span>
                    )}
                  </p>
                  {/*
                    Volver a sacar LO MISMO, que no es lo mismo que republicar.

                    «Republicar», en la pestaña de contenido, le pide al modelo un texto nuevo:
                    ahí el destino es Instagram, donde repetir un caption palabra por palabra es
                    lo que hace que una cuenta se lea automatizada.

                    Acá el destino es un canal de suscriptores, y lo que se pide es otra cosa:
                    que la misma foto con el mismo texto vuelva a aparecer arriba de todo,
                    porque el canal creció desde la primera vez o porque pasó de largo. Escribir
                    un texto nuevo sería resolver un problema que acá no existe, cobrando una
                    llamada al modelo por hacerlo.

                    Se confirma porque sale de verdad al canal, y eso no se deshace.
                  */}
                  <button
                    type="button"
                    disabled={busy !== null}
                    title={t('fanvue.otraVezTitulo')}
                    onClick={() => {
                      if (
                        !window.confirm(
                          t('fanvue.otraVezConfirmar', { destino: variante.platform }),
                        )
                      )
                        return;
                      setResultado(null);
                      void act(`otravez-${clave}`, async () => {
                        const respuesta = await api.post<{ result: { status: string } }>(
                          `/api/content/${publicacion.id}/publish?platform=${variante.platform}&otraVez=1`,
                        );
                        setResultado({ id: publicacion.id, status: respuesta.result.status });
                      });
                    }}
                    className="w-full rounded-lg border border-dashed border-[var(--color-border-subtle)] px-3 py-1.5 text-xs text-[var(--color-ink-muted)] transition hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] disabled:opacity-40"
                  >
                    {t(busy === `otravez-${clave}` ? 'fanvue.publicando' : 'fanvue.otraVez')}
                  </button>
                </div>
              );
            }

            return (
              <div key={variante.id} className="space-y-1">
                <p className="text-xs font-medium">
                  {variante.platform === 'telegram'
                    ? t(dePago ? 'fanvue.telegramDePago' : 'fanvue.telegramGratis', {
                        stars: precioEnStars,
                      })
                    : t('fanvue.enFanvue')}
                </p>
                {/*
                  En Telegram son DOS botones y no uno con una casilla al lado.

                  Cobrar o regalar es la decision del momento de publicar, no una preferencia que
                  se marca antes: dos botones que dicen cada uno lo que hace no se pueden pulsar
                  por error creyendo que hacian lo otro. En las demas plataformas no hay eleccion,
                  asi que sale uno solo.
                */}
                {(variante.platform === 'telegram'
                  ? [
                      {
                        gratis: false,
                        etiqueta: t('fanvue.publicarConStars', { stars: precioEnStars }),
                      },
                      { gratis: true, etiqueta: t('fanvue.publicarGratis') },
                    ]
                  : [{ gratis: false, etiqueta: undefined }]
                ).map((opcion) => (
                  <PublishButton
                    key={`${clave}:${String(opcion.gratis)}`}
                    label={opcion.etiqueta}
                    armed={armada === `${clave}:${String(opcion.gratis)}`}
                    busy={busy === `salir-${clave}:${String(opcion.gratis)}`}
                    disabled={busy !== null || trabada}
                    reason={trabada ? t('fanvue.faltaConectar') : undefined}
                    onArm={() => setArmada(`${clave}:${String(opcion.gratis)}`)}
                    onCancel={() => setArmada(null)}
                    onPublish={() => {
                      setArmada(null);
                      setResultado(null);
                      void act(`salir-${clave}:${String(opcion.gratis)}`, async () => {
                        const respuesta = await api.post<{ result: { status: string } }>(
                          `/api/content/${publicacion.id}/publish?platform=${variante.platform}` +
                            (opcion.gratis ? '&gratis=1' : ''),
                        );
                        setResultado({ id: publicacion.id, status: respuesta.result.status });
                      });
                    }}
                  />
                ))}
              </div>
            );
          })}

        {/*
          Sumarle Telegram a una publicacion que nacio sin el.

          Las tomas explicitas anteriores a Telegram tienen UNA variante: era el unico destino que
          admitia ese material. Sin esto quedaban fuera para siempre, incluidas las ya publicadas
          en Fanvue — que es justo donde mas se nota, porque son las que ya funcionaron.

          El texto NO se vuelve a escribir: se copia del que ya se leyo y se aprobo. Pedirle otro
          al modelo daria dos textos distintos para la misma foto.
        */}
        {hayCanalDeTelegram &&
          !publicacion.variants.some((variante) => variante.platform === 'telegram') && (
            <button
              type="button"
              disabled={busy !== null}
              onClick={() =>
                void act(`tg-${publicacion.id}`, () =>
                  api.post(`/api/content/${publicacion.id}/telegram-variant`),
                )
              }
              className="w-full rounded-lg border border-dashed border-[var(--color-border-subtle)] px-3 py-1.5 text-xs hover:border-[var(--color-accent)] disabled:opacity-40"
            >
              {t('fanvue.sumarTelegram', { stars: precioEnStars })}
            </button>
          )}

        {/*
          Deshacer la publicación mientras no haya salido a ningún lado.

          ── El defecto ──────────────────────────────────────────────────────────
          «Crear publicación» era un camino de ida. Una publicación creada de más —sobre la foto
          equivocada, o con un texto que no sirve— no se podía sacar desde esta pantalla, y la
          toma quedaba atrapada dentro de ella: con la publicación puesta, la tarjeta ya no
          ofrece volver a crearla. La pestaña de contenido apto tenía este botón desde hacía
          tiempo; ésta no.

          ── Por qué desaparece en cuanto algo salió ─────────────────────────────
          Porque entonces deja de ser «cancelar» y pasa a ser «borrar el registro de algo que la
          gente ya vio»: el post sigue en Fanvue o en Telegram y esto no lo toca. Borrarlo acá
          sólo haría que el panel deje de conocer un post que existe —sin enlace, sin ventas y
          sin forma de volver a encontrarlo—. Para ese caso está el botón de la pestaña de
          contenido, que lo dice en su confirmación.

          La toma vuelve entera a «crear publicación»: no se borra ni se descarta la imagen.
        */}
        {!data.posts.some(
          (post) => post.contentItemId === publicacion.id && post.status === 'PUBLISHED',
        ) && (
          <button
            type="button"
            disabled={busy !== null}
            title={t('fanvue.cancelarTitulo')}
            onClick={() => {
              if (!window.confirm(t('fanvue.cancelarConfirmar'))) return;
              void act(`cancel-${publicacion.id}`, () => api.del(`/api/content/${publicacion.id}`));
            }}
            className="w-full rounded-lg border border-[var(--color-border-subtle)] px-3 py-1.5 text-xs text-[var(--color-ink-muted)] transition hover:border-[var(--color-danger)] hover:text-[var(--color-danger)] disabled:opacity-40"
          >
            {t(busy === `cancel-${publicacion.id}` ? 'fanvue.cancelando' : 'fanvue.cancelar')}
          </button>
        )}

        {/*
          Lo que contestó, cuando no fue «publicado».

          `READY_FOR_MANUAL_PUBLISH` es el modo degradado: el sistema arma el paquete y lo publica
          una persona. Es una salida legítima y era invisible — que es lo que la volvía un fallo.
        */}
        {resultado?.id === publicacion.id && resultado.status !== 'PUBLISHED' && (
          <p className="rounded-xl border border-[var(--color-warn)]/40 bg-[var(--color-warn)]/10 p-2.5 text-xs">
            {resultado.status === 'READY_FOR_MANUAL_PUBLISH'
              ? t('fanvue.manual')
              : t('fanvue.contesto', { estado: resultado.status })}
          </p>
        )}
      </div>
    );
  };

  const nsfw = data.assets.filter((asset) => asset.clasificacion === 'nsfw');
  /**
   * Las que ya salieron van al final, y separadas.
   *
   * ── El defecto ────────────────────────────────────────────────────────────
   * Estaban mezcladas con las que faltan publicar, en el mismo orden que la galeria. Esta
   * pantalla existe para contestar «que me queda por sacar», y con las publicadas intercaladas
   * hay que abrir cada tarjeta para saber cual es cual: lo unico que las distingue es una linea
   * de texto al pie. Cuantas mas se publican, mas ruido tiene la respuesta.
   *
   * ── Por que no se ocultan ─────────────────────────────────────────────────
   * Porque siguen sirviendo: de una publicada se anima un clip, se descarga escalada o se mira
   * antes de repetir el encuadre. Sacarlas de la lista obligaria a buscarlas en otra pestana.
   *
   * El orden dentro de cada grupo no se toca —`sort` es estable— asi que las que faltan siguen
   * como venian y solo bajan las que ya salieron.
   */
  const yaSalio = (asset: AssetView): boolean => publicacionDe(asset.id)?.status === 'PUBLISHED';

  const paraPublicar = nsfw
    .filter((asset) => !isRetired(asset))
    .sort((a, b) => Number(yaSalio(a)) - Number(yaSalio(b)));
  const pendientesDeSalir = paraPublicar.filter((asset) => !yaSalio(asset));
  const yaSalieron = paraPublicar.filter(yaSalio);
  /* El rótulo dice cuántas hay en total: un recorte que no se anuncia se lee como una pérdida. */
  const ultimasQueSalieron = yaSalieron.slice(0, ULTIMAS_PUBLICADAS);
  const descartadas = nsfw.filter(isRetired);

  /*
    Las que tienen publicación creada y todavía no salieron.

    Publicar de a una es correcto cuando son tres. Con quince, cada una son dos clics —armar y
    confirmar— y treinta clics para una tanda entera es donde se cuela el error: se saltea una,
    se publica dos veces la misma, o se abandona a la mitad sin saber cuáles faltaron.
  */
  const publicables = paraPublicar
    .map((asset) => publicacionDe(asset.id))
    .filter(
      (item): item is NonNullable<typeof item> => item !== undefined && item.status !== 'PUBLISHED',
    );

  const publicarTodas = async (): Promise<void> => {
    setAvanceLote({ hechas: 0, total: publicables.length });
    const fallos: string[] = [];
    let hechas = 0;

    /*
      En fila y no en paralelo, igual que la generación del set.

      Cada publicación sube media a Fanvue y tarda; mandarlas juntas no las hace terminar antes
      y multiplica la chance de que una se caiga por tiempo. En fila, además, si una falla las
      anteriores ya están publicadas de verdad — y eso hay que poder decirlo.
    */
    for (const item of publicables) {
      try {
        const respuesta = await api.post<{ result: { status: string } }>(
          `/api/content/${item.id}/publish?platform=fanvue`,
        );
        // `READY_FOR_MANUAL_PUBLISH` no es un fallo del sistema, pero tampoco es publicado: se
        // cuenta aparte para que el resumen no diga «quince publicadas» cuando salieron tres.
        if (respuesta.result.status === 'PUBLISHED') hechas += 1;
        else fallos.push(`${item.id.slice(-6)}: ${respuesta.result.status}`);
      } catch (caught) {
        fallos.push(caught instanceof Error ? caught.message : String(caught));
      }
      setAvanceLote({ hechas: hechas + fallos.length, total: publicables.length });
      onChange();
    }

    setAvanceLote(null);
    setFallosDelLote(fallos);
    aviso.ok(
      fallos.length === 0
        ? t('fanvue.publicadasTodas', { n: hechas })
        : t('fanvue.publicadasParciales', { hechas, fallos: fallos.length }),
    );
  };

  const hayClave = credenciales.data?.credentials.some((c) => c.kind === 'runware') ?? false;

  /*
    Qué necesita Runware ACÁ, que ya no es «todo».

    ── El aviso que se contradecía solo ──────────────────────────────────────────
    Esta pestaña mostraba «Falta la clave de Runware — este contenido se genera con Runware» y
    tres líneas más abajo, en «Con qué se genera», decía «Imagen: Krea2 contenido NSFW (tu
    GPU)». Las dos cosas no pueden ser ciertas: con la GPU propia la imagen no toca Runware ni
    cuesta un peso.

    El aviso miraba sólo si existía la credencial, nunca qué modelo estaba configurado. Era
    correcto cuando Runware era el único camino; con la GPU propia en el desplegable pasó a
    mandar a cargar una clave que esa generación no usa.

    El video es otra historia: `nsfwVideoModel` sigue siendo de Runware, así que la clave
    hace falta para animar aunque la imagen sea local. Por eso el aviso ahora dice para QUÉ
    falta, en vez de bloquear la pestaña entera.
  */
  const imagenEnGpuPropia =
    ajustes.data?.settings.nsfwImageModel?.startsWith('local-gpu/') === true;
  const faltaClaveParaImagen = !hayClave && !imagenEnGpuPropia;
  const faltaClaveSoloParaVideo = !hayClave && imagenEnGpuPropia;

  const sheet = data.assets.filter((asset) => IDENTITY_KINDS.includes(asset.kind));
  const sheetApproved = IDENTITY_KINDS.every((kind) =>
    sheet.some((asset) => asset.kind === kind && asset.status === 'APPROVED'),
  );

  return (
    <div className="space-y-4">
      {faltaClaveParaImagen && credenciales.data && (
        <Card title={t('fanvue.faltaClave')}>
          <p className="text-sm text-[var(--color-ink-muted)]">
            {t('fanvue.faltaClaveP1Antes')}
            <strong>{t('fanvue.credenciales')}</strong>
            {t('fanvue.faltaClaveP1Despues')}
          </p>
          <p className="mt-2 text-sm text-[var(--color-ink-muted)]">{t('fanvue.faltaClaveP2')}</p>
          <p className="mt-2 text-sm text-[var(--color-ink-muted)]">
            {t('fanvue.faltaClaveP3Antes')}
            <strong>{t('fanvue.modelos')}</strong>
            {t('fanvue.faltaClaveP3Despues')}
          </p>
          <Link
            to="/credenciales"
            className="mt-3 inline-block rounded-lg border border-[var(--color-border-subtle)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)]"
          >
            Ir a Credenciales
          </Link>
        </Card>
      )}

      {/*
        Con la imagen en la GPU propia la clave ya no frena nada de lo que se genera acá, pero
        sigue haciendo falta para el video y para el cambio de cara opcional. Decirlo así evita
        las dos formas de equivocarse: bloquear una generación que es gratis, y dejar creer que
        animar tampoco va a costar.
      */}
      {faltaClaveSoloParaVideo && credenciales.data && (
        <Card title={t('fanvue.gpuPeroVideo')}>
          <p className="text-sm text-[var(--color-ink-muted)]">
            {t('fanvue.gpuPeroVideoP1Antes')}
            <strong>{t('fanvue.tuPropiaGpu')}</strong>
            {t('fanvue.gpuPeroVideoP1Despues')}
          </p>
          <p className="mt-2 text-sm text-[var(--color-ink-muted)]">
            {t('fanvue.gpuPeroVideoP2Antes')}
            <strong>{t('fanvue.animar')}</strong>
            {t('fanvue.gpuPeroVideoP2Despues')}
          </p>
          <Link
            to="/credenciales"
            className="mt-3 inline-block rounded-lg border border-[var(--color-border-subtle)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)]"
          >
            Ir a Credenciales
          </Link>
        </Card>
      )}

      {/*
        El estado del LoRA estaba acá y se sacó, y no fue por espacio.

        Decía «las tomas de Fanvue salen con esta identidad», y con el camino de dos pasos eso es
        falso: se genera con un SDXL y el LoRA es de FLUX, así que `aceptaLoraDe` lo descarta y no
        se aplica. La cara la pone el editor en el segundo paso.

        Un cartel que afirma que algo se está usando, al lado de las imágenes que salen sin usarlo,
        es peor que no tener cartel: manda a buscar el problema donde no está. Sigue completo en la
        pestaña LoRA, que es donde se entrena y donde la afirmación no se hace.
      */}
      <Card title={t('fanvue.conQueSeGenera')}>
        <dl className="space-y-2 text-sm">
          <div className="flex flex-wrap items-baseline gap-2">
            <dt className="text-xs text-[var(--color-ink-faint)]">{t('fanvue.imagen')}</dt>
            <dd className="font-medium">{nombreDelModelo('imagen')}</dd>
          </div>
          <div className="flex flex-wrap items-baseline gap-2">
            <dt className="text-xs text-[var(--color-ink-faint)]">{t('fanvue.video')}</dt>
            <dd className="font-medium">{nombreDelModelo('video')}</dd>
          </div>
        </dl>
        <p className="mt-3 text-xs text-[var(--color-ink-muted)]">
          {t('fanvue.seEligenAntes')}
          <strong>{t('fanvue.modelos')}</strong>
          {t('fanvue.seEligenDespues')}
        </p>
      </Card>

      {/*
        El generador aparece si HAY con qué generar, que ya no es lo mismo que tener la clave.

        Estaba condicionado a `hayClave` y nada más. Con la imagen configurada en la GPU propia
        eso dejaba la pestaña sin ningún botón: la generación es gratis y local, y la pantalla la
        escondía por falta de una credencial que esa generación no usa.
      */}
      {(hayClave || imagenEnGpuPropia) && (
        <GenerarNsfw
          creatorId={data.creator.id}
          onChange={onChange}
          sheetApproved={sheetApproved}
        />
      )}

      {/*
        «Para publicar» y no «material para adultos».

        Es el mismo estado que en Contenido —generadas, no descartadas— y llamarlo distinto en cada
        pestaña obliga a traducir entre dos vocabularios para la misma cosa. Lo que cambia no es el
        estado sino adónde va: éstas sólo pueden ir al feed de suscriptores de Fanvue.
      */}
      <Card title={t('fanvue.paraPublicar', { n: paraPublicar.length })}>
        {/*
          Publicar la tanda entera, con el mismo armado que una sola.

          Publicar es la única acción del sistema que no se puede deshacer, así que el botón de
          una sola pide dos clics. El del lote pide los mismos dos y además dice cuántas van a
          salir: «Publicar» a secas sobre quince es una cifra que hay que ir a contar a otro
          lado justo antes de una acción irreversible.
        */}
        {publicables.length > 1 && (
          <div className="mb-3 flex flex-wrap items-center gap-2 border-b border-[var(--color-border-subtle)] pb-3">
            {avanceLote ? (
              <p className="text-sm text-[var(--color-ink-muted)]">
                {t('fanvue.publicandoLote', {
                  hechas: avanceLote.hechas,
                  total: avanceLote.total,
                })}
              </p>
            ) : armadoElLote ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setArmadoElLote(false);
                    setFallosDelLote([]);
                    void publicarTodas();
                  }}
                  className="rounded-lg border border-[var(--color-danger)] px-3 py-1.5 text-sm text-[var(--color-danger)]"
                >
                  {t('fanvue.siPublicar', { n: publicables.length })}
                </button>
                <button
                  type="button"
                  onClick={() => setArmadoElLote(false)}
                  className="rounded-lg border border-[var(--color-border-subtle)] px-3 py-1.5 text-sm"
                >
                  {t('comun.cancelar')}
                </button>
                <span className="text-xs text-[var(--color-ink-muted)]">
                  {t('fanvue.salenAlFeed')}
                </span>
              </>
            ) : (
              <>
                <button
                  type="button"
                  disabled={busy !== null || !fanvueListo}
                  onClick={() => setArmadoElLote(true)}
                  className="rounded-lg border border-[var(--color-border-subtle)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)] disabled:opacity-40"
                >
                  {t('fanvue.publicarLas', { n: publicables.length })}
                </button>
                {!fanvueListo && (
                  <span className="text-xs text-[var(--color-ink-muted)]">
                    {t('fanvue.faltaConectar')}
                  </span>
                )}
              </>
            )}
          </div>
        )}

        {/*
          Las que no salieron, con su motivo y sin desaparecer solas.

          `READY_FOR_MANUAL_PUBLISH` aparece acá igual que en el botón de una: no es un fallo del
          sistema, es el modo degradado — y es una salida legítima que era invisible.
        */}
        {fallosDelLote.length > 0 && (
          <div className="mb-3 rounded-xl border border-[var(--color-warn)]/40 bg-[var(--color-warn)]/10 p-2.5">
            <p className="text-xs font-medium">
              {t('fanvue.noSalieron', { n: fallosDelLote.length })}
            </p>
            <ul className="mt-1 space-y-0.5 text-xs text-[var(--color-ink-muted)]">
              {fallosDelLote.map((fallo) => (
                <li key={fallo}>· {fallo}</li>
              ))}
            </ul>
          </div>
        )}

        {paraPublicar.length === 0 ? (
          <p className="text-sm text-[var(--color-ink-muted)]">
            {t('fanvue.ningunaAun', { n: data.assets.length - nsfw.length })}
          </p>
        ) : (
          /*
            Dos grillas rotuladas, no una lista ordenada.

            Con las publicadas al final pero sin rotulo, el corte existe y no se ve: son las
            mismas tarjetas y hay que leer el pie de cada una para saber de que lado esta. El
            rotulo es lo que convierte un orden en una separacion.

            La de arriba no lleva titulo cuando no hay ninguna publicada: rotular un unico grupo
            es nombrar algo que no se opone a nada.
          */
          [
            {
              titulo:
                pendientesDeSalir.length > 0 && yaSalieron.length > 0
                  ? t('fanvue.faltanSalir')
                  : '',
              grupo: pendientesDeSalir,
              total: pendientesDeSalir.length,
            },
            {
              titulo: t('fanvue.yaSalieron'),
              grupo: ultimasQueSalieron,
              total: yaSalieron.length,
            },
          ].map(({ titulo, grupo, total }) =>
            grupo.length === 0 ? null : (
              <div key={titulo || 'pendientes'} className="mb-6 last:mb-0">
                {titulo.length > 0 && (
                  <p className="mb-2 text-xs font-medium tracking-wide uppercase text-[var(--color-ink-muted)]">
                    {titulo} ·{' '}
                    {grupo.length < total ? t('gal.ultimasDe', { n: grupo.length, total }) : total}
                  </p>
                )}
                <AssetGrid
                  assets={grupo}
                  empty=""
                  emptyActive={t('estudio.quedanDescartadas')}
                  render={(asset) => (
                    <AssetCard
                      key={asset.id}
                      asset={asset}
                      isCanonical={false}
                      showCanonicalAction={false}
                      footer={
                        <div className="space-y-2">
                          {pieDeAnimar(asset)}
                          {pieDePublicacion(asset)}
                        </div>
                      }
                      {...accionesDeAsset(asset, { busy, act })}
                    />
                  )}
                />
              </div>
            ),
          )
        )}
        <p className="mt-3 text-xs text-[var(--color-ink-muted)]">
          {t('fanvue.nadaEnRedesAntes')}
          <strong>{t('fanvue.no')}</strong>
          {t('fanvue.nadaEnRedesDespues')}
        </p>
      </Card>

      <BarraDeEspacio
        creatorId={data.creator.id}
        clasificacion="nsfw"
        descartadas={descartadas}
        onChange={onChange}
      />

      {descartadas.length > 0 && (
        <details className="group rounded-2xl border border-[var(--color-border-subtle)]">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-2xl p-4 text-sm">
            <span>
              <span className="font-semibold">
                {t('estudio.descartado', { n: descartadas.length })}
              </span>
              <span className="ml-2 text-xs text-[var(--color-ink-muted)]">
                {t('estudio.descartadoQue')}
              </span>
            </span>
            <span
              aria-hidden="true"
              className="shrink-0 text-[var(--color-ink-muted)] transition-transform group-open:rotate-90"
            >
              ›
            </span>
          </summary>
          <div className="p-4 pt-0">
            <div className="grid gap-4 opacity-60 sm:grid-cols-2 lg:grid-cols-3">
              {descartadas.map((asset) => (
                <AssetCard
                  key={asset.id}
                  asset={asset}
                  isCanonical={false}
                  showCanonicalAction={false}
                  {...accionesDeAsset(asset, { busy, act })}
                />
              ))}
            </div>
          </div>
        </details>
      )}
    </div>
  );
}

/**
 * El set de entrenamiento del LoRA de una influencer.
 *
 * ── Por qué una pestaña y no un botón suelto ──────────────────────────────────
 * Porque antes de exportar hay que decidir dos cosas —cuántas imágenes y con qué piso de
 * parecido— y esas decisiones cambian el resultado del entrenamiento más que ninguna otra. Un
 * botón «descargar» sin ver qué se lleva obliga a abrir el ZIP para enterarse.
 *
 * ── Por qué se muestran las miniaturas con su puntaje ─────────────────────────
 * Porque el número solo no alcanza. Un 0,42 puede ser un perfil bien hecho que el medidor castiga
 * por estar de costado, o puede ser otra persona. Verla al lado del puntaje es lo que permite
 * mover el piso con criterio en vez de a ciegas.
 */
/**
 * En qué etapa está el LoRA de una influencer, dicho siempre y en todas sus etapas.
 *
 * ── Por qué se muestra aunque no haya nada ────────────────────────────────────
 * Porque «no tiene LoRA» es una etapa, no una ausencia: explica por qué la identidad depende de
 * referencias, y por qué elegir Z-Image en Modelos no va a funcionar todavía. Ocultarlo hasta que
 * exista deja al usuario adivinando de dónde sale la cara.
 *
 * ── Por qué «entrenado» no es «listo» ─────────────────────────────────────────
 * Runware publica el modelo unos minutos después de terminar el entrenamiento, y en el medio
 * cualquier generación falla con `loraModelNotReady`. Son dos estados distintos y llamarlos igual
 * hace que alguien pulse generar, falle, y no entienda por qué si «ya está entrenado».
 */
function EstadoDelLora({ creatorId, compacto = false }: { creatorId: string; compacto?: boolean }) {
  const { t } = useT();
  const estado = useApi<{
    entrenamiento: {
      taskUUID: string | null;
      pasos: number | null;
      lanzadoEn: string;
      air: string | null;
      estado: string;
      avance?: number | null;
      safetensors?: string | null;
      costoUsd?: number | null;
    } | null;
  }>(`/api/creators/${creatorId}/lora`, 20_000);

  /** Si además de entrenado ya se pudo usar. Son dos cosas distintas y se ven distinto. */
  const uso = useApi<{ proponer: { puede: boolean } }>(
    `/api/creators/${creatorId}/nsfw/estado`,
    20_000,
  );
  const confirmado = uso.data?.proponer?.puede === true;

  if (!estado.data) return null;

  const lora = estado.data.entrenamiento;

  /*
    Los estados finales se enumeran; los intermedios no.

    Un estado intermedio que no conocemos se lee como «en curso», que es inofensivo. Uno final
    desconocido leído como «en curso» dejaría la etiqueta girando para siempre.
  */
  const termino = lora !== null && ['success', 'completed'].includes(lora.estado);
  const fallo = lora !== null && ['failed', 'error', 'cancelled'].includes(lora.estado);

  const etapa = !lora
    ? { texto: t('lora.sinLora'), color: 'var(--color-ink-muted)', pulso: false }
    : fallo
      ? {
          texto: t('lora.fallo', { estado: lora.estado }),
          color: 'var(--color-danger)',
          pulso: false,
        }
      : termino
        ? confirmado
          ? { texto: t('lora.listo'), color: 'var(--color-ok)', pulso: false }
          : /*
              «Sin probar» y NO «publicándose», y sin pulso.

              El pulso dice «está pasando algo» y no está pasando nada: este estado no avanza solo,
              espera a que alguien genere. Una etiqueta que sugiere progreso donde hay una espera
              deja a la gente mirando la pantalla — pasó, y con razón.

              Tampoco se puede afirmar que Runware lo esté publicando: eso era una hipótesis del
              primer rechazo, y una hora después ya no se sostiene. Lo único cierto es que todavía
              no se generó nada con él.
            */
            { texto: t('lora.sinProbar'), color: 'var(--color-warn)', pulso: false }
        : { texto: t('lora.entrenando'), color: 'var(--color-accent)', pulso: true };

  const detalle = !lora
    ? t('lora.sinLoraQue')
    : fallo
      ? t('lora.falloQue')
      : termino
        ? confirmado
          ? /*
              Se dice que sirve, no que se esté usando. La diferencia importa.

              Antes decía «las tomas de Fanvue salen con esta identidad», y eso dependía de algo
              que este componente no mira: cuál es el modelo elegido para NSFW. Si es un SDXL —que
              es lo que hace desnudez— un LoRA de FLUX no se aplica, y el cartel afirmaba lo
              contrario mientras las imágenes salían con otra cara.
            */
            t('lora.listoQue')
          : t('lora.sinProbarQue')
        : t('lora.entrenandoQue', {
            pasos: lora.pasos ?? '—',
            cuando: haceCuanto(lora.lanzadoEn),
            avance: lora.avance != null ? ` · ${lora.avance}%` : '',
          });

  if (compacto) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs" title={detalle}>
        <span
          aria-hidden="true"
          className={`inline-block h-2 w-2 rounded-full ${etapa.pulso ? 'animate-pulse' : ''}`}
          style={{ backgroundColor: etapa.color }}
        />
        <span style={{ color: etapa.color }}>{etapa.texto}</span>
      </span>
    );
  }

  return (
    <Card title={t('lora.tarjeta')}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span
          aria-hidden="true"
          className={`inline-block h-2 w-2 rounded-full ${etapa.pulso ? 'animate-pulse' : ''}`}
          style={{ backgroundColor: etapa.color }}
        />
        <strong style={{ color: etapa.color }}>{etapa.texto}</strong>
        {lora && (
          <code className="font-mono text-xs text-[var(--color-ink-muted)]">{lora.air}</code>
        )}
        {lora?.costoUsd != null && (
          <span className="text-xs tabular-nums text-[var(--color-ink-muted)]">
            USD {lora.costoUsd.toFixed(3)}
          </span>
        )}
      </div>
      <p className="mt-2 text-xs text-[var(--color-ink-muted)]">{detalle}</p>
    </Card>
  );
}

type Entrenamiento = {
  taskUUID: string | null;
  pasos: number | null;
  entrenador?: string | null;
  familia?: string | null;
  lanzadoEn: string;
  air: string;
  estado: string;
  safetensors?: string | null;
  costoUsd?: number | null;
  activo?: boolean;
  /** Sólo mientras corre; Runware lo informa en algunas respuestas y en otras no. */
  avance?: number | null;
};

/**
 * El LoRA de la placa propia: cuál de los instalados usa esta influencer, y si está encendido.
 *
 * ── Por qué se elige de una lista y no se sube ────────────────────────────────
 * Un LoRA de personaje de Krea 2 con rango 32 pesa 469 MB, y un Worker de Cloudflare acepta 100
 * MB de cuerpo de petición: el tensor no puede pasar por el Worker aunque se quisiera. Y no hace
 * falta, porque el archivo ya está en la máquina que lo va a usar — se entrena en RunPod y se baja
 * a la misma PC que corre ComfyUI. Se copia a `models/loras/` una vez y el puente lo lista.
 *
 * ── Por qué el interruptor es aparte del archivo ──────────────────────────────
 * Porque elegir el checkpoint es un ida y vuelta: se prueban varias épocas y hay que poder generar
 * SIN el LoRA para tener contra qué comparar. Si tener archivo implicara usarlo, apagarlo
 * obligaría a borrar el nombre y volver a buscarlo después.
 *
 * ── Por qué avisa cuando la GPU propia no está elegida ────────────────────────
 * Porque el LoRA se apila en el grafo de ComfyUI, y con Runware o Workers AI ese grafo no existe:
 * quedaría encendido sin efecto. Decirlo acá evita la media hora de mirar imágenes preguntándose
 * por qué el LoRA no hace nada.
 */
function LoraDeLaPlaca({ creatorId }: { creatorId: string }) {
  const { t } = useT();
  const aviso = useAviso();
  const estado = useApi<{ local: { archivo: string | null; activo: boolean }; peso: number }>(
    `/api/creators/${creatorId}/lora`,
  );
  /*
    Sólo los LoRA de ELLA, no los treinta y ocho instalados.

    Los de estilo —piel, grano, cámara— sirven para cualquier influencer y se eligen una vez en
    Modelos. El de personaje es de una sola, y ponerle a una el de otra no da un resultado peor:
    da la cara equivocada, con la generación igual de cara y sin ningún error a la vista. En un
    desplegable mezclado, esa equivocación no se nota hasta mirar las fotos.
  */
  const instalados = useApi<{
    loras: { archivo: string; nombre: string; familia: string | null; bytes: number }[];
    carpeta: string;
    deOtros: number;
    esperaCarpeta: string;
  }>(`/api/creators/${creatorId}/loras`);

  const [guardando, setGuardando] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);

  /*
    La tarjeta NO se esconde cuando falta el dato, y eso es deliberado.

    Estaba escrita con un `if (!local) return null`, y desaparecia entera contra una influencer
    sin entrenamientos en Runware — porque el endpoint salia por un atajo que omitia el campo. El
    sintoma era el peor posible: la pantalla no decia «no pude leer esto», decia nada, y desde
    afuera se leia como «esta influencer no tiene ningun LoRA para elegir» cuando habia cuatro
    instalados en la placa.

    Sin dato se asume «ninguno elegido y apagado», que es lo cierto para una influencer nueva, y
    la lista de instalados se muestra igual. Lo que no se puede leer se dice; no se oculta.
  */
  const local = estado.data?.local ?? { archivo: null, activo: false };

  const guardar = (cambio: { archivo?: string | null; activo?: boolean; peso?: number }) => {
    setGuardando(true);
    setFallo(null);
    void api
      .post(`/api/creators/${creatorId}/lora/local`, {
        archivo: cambio.archivo !== undefined ? cambio.archivo : local.archivo,
        activo: cambio.activo !== undefined ? cambio.activo : local.activo,
        ...(cambio.peso !== undefined ? { peso: cambio.peso } : {}),
      })
      .then(() => {
        aviso.ok(t('loraPlaca.guardado'));
        void estado.reload();
      })
      .catch((caught: unknown) =>
        setFallo(caught instanceof Error ? caught.message : String(caught)),
      )
      .finally(() => setGuardando(false));
  };

  return (
    <Card title={t('loraPlaca.tarjeta')}>
      <p className="text-sm text-[var(--color-ink-muted)]">{t('loraPlaca.que')}</p>

      {/*
        El error de listar se muestra y no se esconde: si la GPU está apagada, el desplegable
        queda vacío y sin este texto parecería que no hay ningún LoRA instalado.
      */}
      {instalados.error && (
        <p className="mt-3 rounded-xl border border-[var(--color-warn)] p-3 text-sm">
          {t('loraPlaca.noSePudoListar', { motivo: instalados.error.message })}
        </p>
      )}

      {/*
        Vacío tiene dos causas muy distintas —no hay ninguno instalado, o los que hay están en
        una carpeta con otro nombre— y sin distinguirlas la pantalla manda a buscar al lugar
        equivocado. `deOtros` es lo que las separa.
      */}
      {instalados.data && instalados.data.loras.length === 0 && (
        <p className="mt-3 rounded-xl border border-[var(--color-warn)]/40 p-3 text-sm">
          {instalados.data.deOtros > 0
            ? t('loraPlaca.hayDeOtros', {
                n: instalados.data.deOtros,
                carpeta: instalados.data.esperaCarpeta,
              })
            : t('loraPlaca.ningunoInstalado', { carpeta: instalados.data.esperaCarpeta })}
        </p>
      )}

      <label className="mt-4 flex flex-wrap items-center gap-2 text-sm">
        <span className="text-xs text-[var(--color-ink-muted)]">{t('loraPlaca.archivo')}</span>
        <select
          value={local.archivo ?? ''}
          disabled={guardando || !instalados.data}
          onChange={(event) => guardar({ archivo: event.target.value || null })}
          className="min-w-[22rem] rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-3 py-1.5 font-mono text-xs"
        >
          <option value="">{t('loraPlaca.ninguno')}</option>
          {/*
            Se muestra también el que está guardado aunque no aparezca en la lista.

            Pasa cuando alguien renombra o mueve el archivo en la placa: sin esta entrada el
            desplegable saltaría solo a «ninguno» y la pantalla diría que no hay LoRA cuando en la
            base sí lo hay. Se marca como faltante en vez de mentir.
          */}
          {local.archivo && !instalados.data?.loras.some((l) => l.archivo === local.archivo) && (
            <option value={local.archivo}>
              {local.archivo} {t('loraPlaca.noEstaEnLaPlaca')}
            </option>
          )}
          {instalados.data?.loras.map((l) => (
            <option key={l.archivo} value={l.archivo}>
              {l.archivo} · {Math.round(l.bytes / 1024 / 1024)} MB
            </option>
          ))}
        </select>
      </label>

      <label className="mt-3 flex flex-wrap items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={local.activo}
          disabled={guardando || !local.archivo}
          onChange={(event) => guardar({ activo: event.target.checked })}
        />
        <strong>{t('loraPlaca.encendido')}</strong>
        <span className="text-xs text-[var(--color-ink-muted)]">
          {local.archivo ? t('loraPlaca.encendidoQue') : t('loraPlaca.elegiPrimero')}
        </span>
      </label>

      {local.activo && (
        <label className="mt-3 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-xs text-[var(--color-ink-muted)]">{t('lora.cuantoTira')}</span>
          <select
            value={estado.data?.peso ?? 1}
            disabled={guardando}
            onChange={(event) => guardar({ peso: Number(event.target.value) })}
            className="rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-3 py-1.5"
          >
            {[0.6, 0.8, 1, 1.2, 1.4].map((n) => (
              <option key={n} value={n}>
                {n.toFixed(1)}
                {n === 1 ? t('lora.porOmision') : ''}
              </option>
            ))}
          </select>
        </label>
      )}

      {instalados.data && (
        <p className="mt-4 font-mono text-xs text-[var(--color-ink-muted)]">
          {t('loraPlaca.carpeta', { carpeta: instalados.data.carpeta })}
        </p>
      )}
      {fallo && (
        <p className="mt-3 rounded-xl border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/5 p-3 text-xs break-words">
          {fallo}
        </p>
      )}
    </Card>
  );
}

/**
 * El set de entrenamiento generado EN LA PLACA, que reemplaza al que se armaba con lo publicado.
 *
 * ── Por qué se cambió de fuente ───────────────────────────────────────────────
 * El flujo anterior juntaba las fotos ya publicadas de la influencer y las etiquetaba. Sirve para
 * empezar y tiene un techo: lo publicado es uniforme —la misma luz, los mismos encuadres, la
 * misma ropa— y un LoRA aprende lo que se repite. Con material uniforme aprende el uniforme.
 *
 * La grilla **garantiza** la variación en vez de encargarla, cruzando encuadre, ángulo, luz, ropa
 * y fondo. Eso sólo se puede generar, y en placa propia no gasta cuota de nadie.
 *
 * ── Por qué progreso y miniaturas, y no imágenes ──────────────────────────────
 * Un set son ~110 tomas, ~55 minutos y ~200 MB. Ninguna petición HTTP sobrevive eso, y menos a
 * través de un túnel. Las imágenes se quedan en la placa —que es la misma máquina desde la que
 * se sube a RunPod— y por el cable van sólo el progreso y miniaturas de 256 px, que es todo lo
 * que hace falta para curar: mirar y elegir.
 *
 * ── Por qué el ZIP no se descarga ─────────────────────────────────────────────
 * Porque son 64 MB, que en base64 son 85, y un Worker tiene 128 MB de memoria. Pero sobre todo
 * porque no hace falta: el archivo ya está en la máquina desde la que se sube. Se muestra la
 * ruta.
 */
function SetEnLaPlaca({ creatorId, disparador }: { creatorId: string; disparador: string }) {
  const { t } = useT();
  const aviso = useAviso();
  const [jobId, setJobId] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);
  const [elegidas, setElegidas] = useState<string[]>([]);
  const [zip, setZip] = useState<{ ruta: string; imagenes: number; bytes: number } | null>(null);

  const trabajo = useApi<{
    id: string;
    estado: string;
    hechas: number;
    total: number;
    actual: string | null;
    error: string | null;
  }>(jobId ? `/api/creators/${creatorId}/lora/set/trabajo/${jobId}` : null, 5_000);

  const corriendo = trabajo.data?.estado === 'corriendo';

  /*
    Las miniaturas se recargan solas mientras el set se genera, y se quedan quietas cuando no.
    Sondear un directorio que no cambia es gasto sin nada a cambio.
  */
  const miniaturas = useApi<{
    total: number;
    imagenes: { nombre: string; bytes: number; caption: string; miniatura: string }[];
  }>(`/api/creators/${creatorId}/lora/set/miniaturas`, corriendo ? 20_000 : undefined);

  const alternar = (nombre: string) =>
    setElegidas((previas) =>
      previas.includes(nombre) ? previas.filter((n) => n !== nombre) : [...previas, nombre],
    );

  const enCurso = (etiqueta: string): boolean => ocupado === etiqueta;

  return (
    <Card title={t('setPlaca.tarjeta')} accent={corriendo}>
      <p className="text-sm text-[var(--color-ink-muted)]">{t('setPlaca.que')}</p>

      {corriendo && trabajo.data ? (
        <div className="mt-4">
          <div className="flex items-center justify-between text-sm">
            <span className="tabular-nums">
              <strong>{trabajo.data.hechas}</strong> / {trabajo.data.total}
            </span>
            <span className="font-mono text-xs text-[var(--color-ink-muted)]">
              {trabajo.data.actual ?? ''}
            </span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-[var(--color-border-subtle)]">
            <div
              className="h-full bg-[var(--color-accent)] transition-all"
              style={{
                width: `${Math.round((trabajo.data.hechas / Math.max(1, trabajo.data.total)) * 100)}%`,
              }}
            />
          </div>
          <p className="mt-2 text-xs text-[var(--color-ink-muted)]">
            {t('setPlaca.tarda', {
              minutos: Math.max(
                1,
                Math.round(((trabajo.data.total - trabajo.data.hechas) * 32) / 60),
              ),
            })}
          </p>
          <button
            type="button"
            disabled={enCurso('cancelar')}
            onClick={() => {
              setOcupado('cancelar');
              void api
                .post(`/api/creators/${creatorId}/lora/set/trabajo/${jobId}/cancelar`, {})
                .then(() => void trabajo.reload())
                .catch((caught: unknown) =>
                  setFallo(caught instanceof Error ? caught.message : String(caught)),
                )
                .finally(() => setOcupado(null));
            }}
            className="mt-3 rounded-lg border border-[var(--color-border-subtle)] px-3 py-1.5 text-xs transition hover:border-[var(--color-danger)] disabled:opacity-40"
          >
            {t('setPlaca.cancelar')}
          </button>
        </div>
      ) : (
        <div className="mt-4">
          <Button
            disabled={enCurso('generar')}
            onClick={() => {
              setOcupado('generar');
              setFallo(null);
              void api
                .post<{ id: string; total: number }>(`/api/creators/${creatorId}/lora/set`, {})
                .then((r) => {
                  setJobId(r.id);
                  aviso.ok(t('setPlaca.arrancado', { n: r.total }));
                })
                .catch((caught: unknown) =>
                  setFallo(caught instanceof Error ? caught.message : String(caught)),
                )
                .finally(() => setOcupado(null));
            }}
          >
            {enCurso('generar') ? t('setPlaca.arrancando') : t('setPlaca.generar')}
          </Button>
        </div>
      )}

      {trabajo.data?.error && (
        <p className="mt-3 rounded-xl border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/5 p-3 text-xs break-words">
          {trabajo.data.error}
        </p>
      )}

      {(miniaturas.data?.total ?? 0) > 0 && (
        <div className="mt-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm">
              {t('setPlaca.enLaPlaca', {
                n: miniaturas.data?.total ?? 0,
                elegidas: elegidas.length,
              })}
            </span>
            <Button
              disabled={enCurso('zip') || elegidas.length === 0}
              onClick={() => {
                setOcupado('zip');
                setFallo(null);
                void api
                  .post<{ ruta: string; imagenes: number; bytes: number }>(
                    `/api/creators/${creatorId}/lora/set/zip`,
                    { elegidas },
                  )
                  .then((r) => {
                    setZip(r);
                    aviso.ok(t('setPlaca.zipListo', { n: r.imagenes }));
                  })
                  .catch((caught: unknown) =>
                    setFallo(caught instanceof Error ? caught.message : String(caught)),
                  )
                  .finally(() => setOcupado(null));
              }}
            >
              {enCurso('zip')
                ? t('setPlaca.armando')
                : t('setPlaca.armarZip', { n: elegidas.length })}
            </Button>
          </div>

          {/*
            La grilla de curaduría. Se elige mirando, que es para lo que existen las miniaturas:
            el nombre lleva el encuadre que se PIDIÓ, y ése no siempre es el que salió.
          */}
          <div className="mt-3 grid grid-cols-[repeat(auto-fill,minmax(96px,1fr))] gap-2">
            {miniaturas.data?.imagenes.map((imagen) => {
              const marcada = elegidas.includes(imagen.nombre);
              return (
                <button
                  key={imagen.nombre}
                  type="button"
                  title={imagen.caption}
                  onClick={() => alternar(imagen.nombre)}
                  className={
                    'overflow-hidden rounded-lg border-2 transition ' +
                    (marcada
                      ? 'border-[var(--color-accent)] opacity-100'
                      : 'border-transparent opacity-60 hover:opacity-100')
                  }
                >
                  <img
                    src={`data:image/jpeg;base64,${imagen.miniatura}`}
                    alt={imagen.nombre}
                    className="block h-full w-full object-cover"
                  />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {zip && (
        <div className="mt-4 rounded-xl border border-[var(--color-ok)]/40 bg-[var(--color-ok)]/5 p-3">
          <p className="text-sm">
            {t('setPlaca.zipEn', { n: zip.imagenes, mb: Math.round(zip.bytes / 1048576) })}
          </p>
          <code className="mt-1 block font-mono text-xs break-all text-[var(--color-ink-muted)]">
            {zip.ruta}
          </code>
          <p className="mt-2 text-xs text-[var(--color-ink-muted)]">{t('setPlaca.zipQueHacer')}</p>
        </div>
      )}

      <p className="mt-4 text-xs text-[var(--color-ink-muted)]">
        {t('lora.disparadorAntes')}
        <code className="font-mono">{disparador}</code>
        {t('lora.disparadorDespues')}
      </p>

      {fallo && (
        <p className="mt-3 rounded-xl border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/5 p-3 text-xs break-words">
          {fallo}
        </p>
      )}
    </Card>
  );
}

function LoraTab({ data }: { data: CreatorDetailData }) {
  const { t } = useT();
  const aviso = useAviso();
  /**
   * Los pasos del entrenamiento, con 1500 por omisión.
   *
   * ── Por qué no 300, que es el valor del spec ──────────────────────────────────
   * Porque el 300 de Runware es el valor por omisión de un entrenador de ESTILO, y un estilo se
   * aprende rápido: es un patrón global. Una cara son rasgos finos y necesita ver cada imagen
   * muchas más veces. Para un set de 15 a 20 imágenes, el mínimo para que la identidad quede
   * fijada está entre 1500 y 2500.
   *
   * Con 18 imágenes, 300 pasos son unas 16 pasadas sobre el set y 1500 son unas 83.
   *
   * Se conservan los valores bajos porque sirven para otra cosa: comprobar que la tubería entera
   * anda —dataset, entrenamiento, publicación, generación— por medio dólar en vez de tres.
   */
  const [pasos, setPasos] = useState(1500);
  /**
   * Sobre qué arquitectura entrenar.
   *
   * No es una preferencia de calidad: un LoRA queda atado a la familia con la que se entrenó, así
   * que esto decide en qué modelos se va a poder usar después. FLUX abre toda su familia —incluido
   * Juggernaut Pro Flux— y Z-Image sólo abre Z-Image.
   */
  const [entrenador, setEntrenador] = useState('flux-1-dev');

  const catalogo = useApi<{
    entrenadoresDeLora?: {
      id: string;
      etiqueta: string;
      usdPorPaso: number;
      familia: string;
      nota?: string;
    }[];
  }>('/api/settings');
  const entrenadores = catalogo.data?.entrenadoresDeLora ?? [];
  const elegido = entrenadores.find((e) => e.id === entrenador);
  const [ocupado, setOcupado] = useState<string | null>(null);
  /** Lo que falló, quieto: el aviso flotante dura cinco segundos y estos mensajes son largos. */
  const [fallo, setFallo] = useState<string | null>(null);
  const [encima, setEncima] = useState(false);

  /** Los ajustes globales, para el peso del LoRA de contenido. Su configuración vive en Modelos. */
  const ajustes = useApi<{
    settings: { nsfwLoraAir: string | null; nsfwLoraPeso: number | null };
  }>('/api/settings');

  /**
   * Sube un ZIP armado afuera, o uno que se perdió.
   *
   * Se manda el archivo crudo con su `content-type`: no hace falta un formulario multiparte para
   * un solo archivo, y evita el borde de tener que parsearlo del otro lado.
   */
  const subirZip = (archivo: File) => {
    setOcupado('subir');
    void fetch(`/api/creators/${data.creator.id}/dataset/subir`, {
      method: 'POST',
      headers: { 'content-type': 'application/zip' },
      body: archivo,
      credentials: 'same-origin',
    })
      .then(async (respuesta) => {
        const cuerpo = (await respuesta.json()) as {
          url?: string;
          imagenes?: number;
          bytes?: number;
          venceEn?: string;
          error?: { message?: string };
        };
        if (!respuesta.ok) throw new Error(cuerpo.error?.message ?? `Error ${respuesta.status}`);
        setZipNuevo({
          url: cuerpo.url ?? '',
          imagenes: cuerpo.imagenes ?? 0,
          bytes: cuerpo.bytes ?? 0,
          venceEn: cuerpo.venceEn ?? '12 horas',
        });
        setFallo(null);
        aviso.ok(t('lora.zipSubido', { n: cuerpo.imagenes ?? 0 }));
      })
      .catch((caught: unknown) =>
        setFallo(caught instanceof Error ? caught.message : String(caught)),
      )
      .finally(() => setOcupado(null));
  };

  /**
   * El ZIP del paso 1, que el paso 2 consume.
   *
   * ── Por qué vive en el estado y no se rearma al entrenar ──────────────────────
   * Porque entre preparar y entrenar pueden cambiar el piso, el tope o las imágenes publicadas.
   * Rearmarlo al entrenar significaría entrenar sobre un set que nadie miró — y mirar el set es
   * justamente lo que hace este paso.
   *
   * Se borra al mover cualquiera de los dos controles: un ZIP que ya no corresponde a lo que se
   * está viendo es peor que ninguno, porque el botón de entrenar seguiría ahí prometiendo entrenar
   * con lo de la pantalla.
   */
  const [zipNuevo, setZipNuevo] = useState<{
    url: string;
    imagenes: number;
    bytes: number;
    venceEn: string;
  } | null>(null);

  /**
   * El último entrenamiento, para que la pantalla no ofrezca lanzar otro encima.
   *
   * Se refresca cada quince segundos mientras corre. Sin esto, el botón quedaba igual después de
   * mandar: nada decía que ya estaba entrenando y volver a pulsarlo costaba otros cincuenta y
   * cinco centavos — que es exactamente lo que pasó la primera vez.
   */
  const entrenamiento = useApi<{
    entrenamiento: Entrenamiento | null;
    historial: Entrenamiento[];
    activo: string | null;
    peso: number;
    minutosPorPaso: number | null;
    zip: { url: string; imagenes: number; bytes: number; preparadoEn: string } | null;
  }>(`/api/creators/${data.creator.id}/lora`, 15_000);

  /*
    Ya no se consulta el resumen de lo publicado, y esa es la decision de fondo de esta pantalla.

    El set salia de las fotos ya publicadas: se elegian las de mejor parecido, se etiquetaban y se
    entrenaba con eso. Sirve para empezar y tiene un techo que es del metodo: lo publicado es
    uniforme —la misma luz, los mismos encuadres, la misma ropa— y un LoRA aprende lo que se
    repite. Con material uniforme aprende el uniforme, no la persona.

    El set ahora se genera en la placa desde la grilla, que cruza encuadre, angulo, luz, ropa y
    fondo a proposito. Ver `SetEnLaPlaca`.
  */
  const disparador = data.creator.publicName.toLowerCase().replace(/[^a-z0-9]+/g, '');
  /**
   * El ZIP con el que se va a entrenar: el recién armado, o el último que quedó guardado.
   *
   * ── Por qué el servidor puede devolver uno ────────────────────────────────────
   * Porque el archivo no caduca — lo que vence a las doce horas es la firma del enlace, y
   * refirmarlo es todo lo que hace falta. Antes no se guardaba dónde había quedado, así que
   * recargar la pantalla obligaba a rearmarlo: gratis en dinero pero no en tiempo, porque las
   * imágenes sin caption cacheado se vuelven a describir y eso sí se cobra.
   */
  const guardado = entrenamiento.data?.zip;
  const zip =
    zipNuevo ??
    (guardado
      ? {
          url: guardado.url,
          imagenes: guardado.imagenes,
          bytes: guardado.bytes,
          venceEn: '12 horas',
        }
      : null);

  const corriendo = entrenamiento.data?.entrenamiento;

  /** Cuántos minutos lleva algo lanzado en ese momento. */
  const minutosDesde = (iso: string) =>
    Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));

  /** Cuánto tardaría éste, según lo que tardaron los anteriores. */
  const estimado =
    entrenamiento.data?.minutosPorPaso != null && corriendo?.pasos
      ? Math.round(entrenamiento.data.minutosPorPaso * corriendo.pasos)
      : null;
  /*
    «Terminó» es un estado final, y los demás no.

    Se enumeran los finales en vez de los intermedios: un estado intermedio que no conocemos hace
    esperar, que es inofensivo; uno final desconocido dejaría el botón bloqueado para siempre.
  */
  const yaTermino =
    corriendo !== null &&
    corriendo !== undefined &&
    ['success', 'completed', 'failed', 'error', 'cancelled'].includes(corriendo.estado);

  return (
    <div className="space-y-4">
      {fallo && (
        <Card title={t('lora.noSePudo')}>
          <p className="rounded-xl border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/5 p-3 text-xs break-words">
            {fallo}
          </p>
          <button
            type="button"
            onClick={() => setFallo(null)}
            className="mt-3 text-xs underline underline-offset-2 hover:text-[var(--color-accent)]"
          >
            {t('lora.entendidoOcultar')}
          </button>
        </Card>
      )}

      <EstadoDelLora creatorId={data.creator.id} />

      <LoraDeLaPlaca creatorId={data.creator.id} />

      <SetEnLaPlaca creatorId={data.creator.id} disparador={disparador} />

      {/*
        El paso 2 aparece cuando existe el paso 1, y no antes.

        Un botón de entrenar siempre visible invita a pulsarlo sin haber mirado el set — y
        entrenar cuesta y deja un LoRA que se usa durante meses. Que dependa del ZIP no es una
        traba: es el orden.
      */}
      {corriendo && !yaTermino && (
        <Card title="Entrenando" accent>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-[var(--color-accent)]" />
            <span>
              <strong className="tabular-nums">{corriendo.pasos}</strong> pasos · lleva{' '}
              <strong className="tabular-nums">{minutosDesde(corriendo.lanzadoEn)}</strong> min
              {corriendo.avance != null ? ` · ${corriendo.avance}%` : ''}
            </span>
          </div>

          {/*
            La estimación sale de lo que tardaron los anteriores, no de un número inventado.

            Runware no informa cuánto va a tardar, así que la única fuente honesta es la propia
            historia: minutos por paso, promediados de los entrenamientos que ya terminaron. Sin
            ninguno, no se estima — inventar un número es peor que no darlo, porque alguien lo
            espera y se va.
          */}
          <p className="mt-2 text-xs text-[var(--color-ink-muted)]">
            {estimado != null ? t('lora.estimado', { minutos: estimado }) : t('lora.sinEstimacion')}
          </p>
          <p className="mt-1 text-xs text-[var(--color-ink-muted)]">{t('lora.seActualizaSola')}</p>
        </Card>
      )}

      {/*
        Todos los entrenamientos, no sólo el último.

        Entrenar varias veces es lo normal: 300 pasos para probar la tubería, 1500 para la
        identidad, quizá otro en otra arquitectura. Mostrar sólo el último esconde los anteriores
        aunque sigan existiendo en Runware — y el último no siempre es el mejor.
      */}
      {(entrenamiento.data?.historial?.length ?? 0) > 0 && (
        <Card title={`Entrenamientos · ${entrenamiento.data?.historial.length}`}>
          <ul className="space-y-2">
            {entrenamiento.data?.historial.map((corrida) => (
              <li
                key={corrida.taskUUID ?? corrida.lanzadoEn}
                className={
                  'flex flex-wrap items-center gap-3 rounded-xl border p-3 text-sm ' +
                  (corrida.activo
                    ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft)]'
                    : 'border-[var(--color-border-subtle)]')
                }
              >
                <span className="tabular-nums">
                  <strong>{corrida.pasos ?? '—'}</strong> pasos
                </span>
                {/*
                  El estado se dice sólo cuando NO salió bien.

                  «success» es el caso esperado y ocupa lugar sin agregar nada: si la fila está y
                  tiene sus pesos para descargar, salió bien. Lo que hay que poder ver de un
                  vistazo es lo otro — que uno está corriendo, que uno falló, o que no se pudo
                  consultar.
                */}
                <span className="text-xs text-[var(--color-ink-muted)]">
                  {corrida.entrenador ?? 'z-image'}
                  {['success', 'completed'].includes(corrida.estado)
                    ? ''
                    : ` · ${corrida.estado}`}{' '}
                  · {haceCuanto(corrida.lanzadoEn)}
                  {corrida.costoUsd != null && ` · USD ${corrida.costoUsd.toFixed(3)}`}
                </span>

                <span className="ml-auto flex flex-wrap items-center gap-2">
                  {corrida.activo && (
                    <span className="text-xs font-medium text-[var(--color-accent)]">
                      {t('lora.enUso')}
                    </span>
                  )}

                  {/*
                    Elegir cuál se usa, que no siempre es el último.

                    El sistema adopta el más reciente sólo cuando no hay ninguno elegido. Después
                    de eso manda esta elección, porque entrenar de nuevo no debería pisar una
                    decisión que alguien tomó mirando resultados.
                  */}
                  {!corrida.activo && corrida.estado === 'success' && corrida.air && (
                    <button
                      type="button"
                      disabled={ocupado !== null}
                      onClick={() => {
                        setOcupado('activo');
                        void api
                          .post(`/api/creators/${data.creator.id}/lora/activo`, {
                            air: corrida.air ?? '',
                            familia: corrida.familia ?? null,
                          })
                          .then(() => {
                            aviso.ok(t('lora.ahoraSeGeneraConEste'));
                            void entrenamiento.reload();
                          })
                          .catch((caught: unknown) =>
                            setFallo(caught instanceof Error ? caught.message : String(caught)),
                          )
                          .finally(() => setOcupado(null));
                      }}
                      className="rounded-lg border border-[var(--color-border-subtle)] px-2 py-1 text-xs transition hover:border-[var(--color-accent)] disabled:opacity-40"
                    >
                      {t('lora.usarEste')}
                    </button>
                  )}

                  {corrida.safetensors && (
                    <a
                      href={corrida.safetensors}
                      className="rounded-lg border border-[var(--color-border-subtle)] px-2 py-1 text-xs transition hover:border-[var(--color-accent)]"
                    >
                      .safetensors
                    </a>
                  )}
                </span>
              </li>
            ))}
          </ul>

          {/*
            Los dos pesos juntos, porque se ajustan juntos.

            Compiten: subir el de identidad puede tapar el desbloqueo y que vuelva a salir vestida;
            subir el de contenido puede deformar la cara. Tenerlos en pestañas distintas obligaba a
            ir y volver en cada prueba, y a acordarse de qué se había cambiado del otro lado.

            El identificador del LoRA de contenido, su disparador y su arquitectura siguen en
            Modelos: eso es configuración y se toca una vez. Esto es lo que se mueve en cada vuelta.
          */}
          {/*
            El peso, editable sin reentrenar.

            Es el único ajuste que no cuesta: el LoRA ya está entrenado y esto sólo cambia cuánto
            tira. Por encima de 1 marca más lo que aprendió —y también sus defectos: piel rígida,
            poses acartonadas—. Con un LoRA de contenido apilado además compiten, así que subir
            uno puede tapar al otro.
          */}
          {entrenamiento.data?.activo && (
            <label className="mt-3 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-xs text-[var(--color-ink-muted)]">{t('lora.cuantoTira')}</span>
              <select
                value={entrenamiento.data.peso}
                disabled={ocupado !== null}
                onChange={(event) => {
                  setOcupado('activo');
                  void api
                    .post(`/api/creators/${data.creator.id}/lora/activo`, {
                      air: entrenamiento.data?.activo,
                      peso: Number(event.target.value),
                    })
                    .then(() => void entrenamiento.reload())
                    .catch((caught: unknown) =>
                      setFallo(caught instanceof Error ? caught.message : String(caught)),
                    )
                    .finally(() => setOcupado(null));
                }}
                className="rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-3 py-1.5"
              >
                {[0.6, 0.8, 1, 1.2, 1.4].map((n) => (
                  <option key={n} value={n}>
                    {n.toFixed(1)}
                    {n === 1 ? t('lora.porOmision') : ''}
                  </option>
                ))}
              </select>
              <span className="text-xs text-[var(--color-ink-muted)]">{t('lora.masMarca')}</span>
            </label>
          )}

          {ajustes.data?.settings.nsfwLoraAir && (
            <label className="mt-2 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-xs text-[var(--color-ink-muted)]">
                {t('lora.cuantoTiraContenido')}
              </span>
              <select
                defaultValue={String(ajustes.data.settings.nsfwLoraPeso ?? 0.8)}
                onChange={(event) => {
                  void api
                    .patch('/api/settings', { nsfwLoraPeso: event.target.value })
                    .then(() => void ajustes.reload())
                    .catch((caught: unknown) =>
                      setFallo(caught instanceof Error ? caught.message : String(caught)),
                    );
                }}
                className="rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-3 py-1.5"
              >
                {[0.4, 0.6, 0.8, 1, 1.2].map((n) => (
                  <option key={n} value={n}>
                    {n.toFixed(1)}
                    {n === 0.8 ? t('lora.porOmision') : ''}
                  </option>
                ))}
              </select>
              <span className="text-xs text-[var(--color-ink-muted)]">{t('lora.conPoco')}</span>
            </label>
          )}

          {/*
            Generar sin LoRA es una opción, no un borrado.

            Comparar contra el modelo pelado es la única forma de saber cuánto está aportando el
            LoRA. Los pesos siguen en Runware: esto sólo deja de mandarlos.
          */}
          {entrenamiento.data?.activo && (
            <button
              type="button"
              disabled={ocupado !== null}
              onClick={() => {
                setOcupado('activo');
                void api
                  .post(`/api/creators/${data.creator.id}/lora/activo`, { air: '' })
                  .then(() => {
                    aviso.ok(t('lora.sinLoraHasta'));
                    void entrenamiento.reload();
                  })
                  .catch(() => undefined)
                  .finally(() => setOcupado(null));
              }}
              className="mt-3 text-xs underline underline-offset-2 hover:text-[var(--color-accent)]"
            >
              {t('lora.generarSinLora')}
            </button>
          )}
        </Card>
      )}

      {/*
        El paso 2 se muestra SIEMPRE, y se explica cuando no se puede.

        Antes aparecía sólo si había un ZIP en pantalla, y el ZIP vive en el estado del componente:
        al recargar desaparecía y con él el paso entero. Quien volvía al día siguiente veía el
        historial y ninguna forma de entrenar de nuevo — el paso existía y era invisible.

        Sigue exigiendo el ZIP: entrenar tiene que ser sobre el set que se miró. Lo que cambia es
        que ahora se ve por qué.
      */}
      {!(corriendo && !yaTermino) && (
        <Card title={t('lora.entrenarTitulo')} accent={Boolean(zip)}>
          {zip ? (
            <p className="text-sm text-[var(--color-ink-muted)]">
              {t('lora.seEntrenaConAntes')}
              <strong>{t('lora.ese')}</strong>
              {t('lora.seEntrenaConDespues', {
                imagenes: zip.imagenes,
                mb: (zip.bytes / 1024 / 1024).toFixed(1),
              })}
            </p>
          ) : (
            <p className="rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-3 text-sm text-[var(--color-ink-muted)]">
              {t('lora.primeroZipAntes')}
              <strong>{t('lora.ese')}</strong>
              {t('lora.primeroZipDespues')}
              <br />
              {t('lora.primeroZipP2')}
            </p>
          )}
          {/*
            Lo que decide la elección, dicho donde se elige.

            No es calidad: es en qué modelos se va a poder usar el LoRA después. Elegir mal no se
            arregla con un ajuste, hay que volver a entrenar y volver a pagar.
          */}
          {elegido?.nota && (
            <p className="mt-2 text-xs text-[var(--color-ink-muted)]">{elegido.nota}</p>
          )}

          <div className="mt-3 flex flex-wrap items-end gap-3 text-sm">
            <label>
              <span className="mb-1 block text-xs text-[var(--color-ink-muted)]">Entrenar en</span>
              <select
                value={entrenador}
                onChange={(event) => setEntrenador(event.target.value)}
                className="rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-3 py-2"
              >
                {entrenadores.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.etiqueta}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span className="mb-1 block text-xs text-[var(--color-ink-muted)]">Pasos</span>
              <select
                value={pasos}
                onChange={(event) => setPasos(Number(event.target.value))}
                className="rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-3 py-2"
              >
                {[300, 500, 800, 1200, 1500, 2000, 2500, 3000].map((n) => (
                  <option key={n} value={n}>
                    {n}
                    {n <= 500 ? t('lora.soloProbar') : ''}
                    {n === 1500 ? t('lora.minimoIdentidad') : ''}
                    {n === 3000 ? t('lora.maximoUtil') : ''}
                  </option>
                ))}
              </select>
            </label>

            <p className="text-[var(--color-ink-muted)]">
              {t('lora.cuesta')}
              <strong className="text-[var(--color-ink)] tabular-nums">
                USD {(pasos * (elegido?.usdPorPaso ?? 0.00182)).toFixed(2)}
              </strong>{' '}
            </p>

            <Button
              disabled={ocupado !== null || !zip}
              onClick={() => {
                if (!zip) return;
                setOcupado('entrenar');
                void api
                  .post<{ taskUUID: string; air: string }>(
                    `/api/creators/${data.creator.id}/lora`,
                    { pasos, dataset: zip.url, entrenador },
                  )
                  .then((r) => {
                    setFallo(null);
                    aviso.ok(t('lora.entrenandoQueda', { air: r.air }));
                    void entrenamiento.reload();
                  })
                  .catch((caught: unknown) =>
                    setFallo(caught instanceof Error ? caught.message : String(caught)),
                  )
                  .finally(() => setOcupado(null));
              }}
            >
              {t(ocupado === 'entrenar' ? 'lora.enviando' : 'lora.entrenar')}
            </Button>
          </div>

          {zip && (
            <p className="mt-2 text-xs text-[var(--color-ink-muted)]">
              {t('lora.zipVenceAntes', { cuando: zip.venceEn })}
              <a href={zip.url} className="underline underline-offset-2">
                {t('lora.descargarlo')}
              </a>
              {t('lora.zipVenceDespues')}
            </p>
          )}
        </Card>
      )}

      {/*
        Subir un ZIP, para los dos casos en que armarlo acá no sirve.

        Uno: el archivo se perdió y rearmarlo cuesta —las imágenes sin caption cacheado se vuelven
        a describir—. Dos, y más importante: alguien podó el set a mano, sacó las peores y quiere
        entrenar con ÉSE. Es exactamente lo que conviene hacer cuando el parecido no alcanza, y sin
        esto había que conformarse con lo que el filtro automático eligiera.
      */}
      <Card title={t('lora.oSubiZip')}>
        <label
          onDragOver={(event) => {
            event.preventDefault();
            setEncima(true);
          }}
          onDragLeave={() => setEncima(false)}
          onDrop={(event) => {
            event.preventDefault();
            setEncima(false);
            const archivo = event.dataTransfer.files[0];
            if (archivo) subirZip(archivo);
          }}
          className={
            'flex cursor-pointer flex-col items-center gap-1 rounded-xl border-2 border-dashed p-6 text-center text-sm transition ' +
            (encima
              ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft)]'
              : 'border-[var(--color-border-subtle)] hover:border-[var(--color-border-strong)]')
          }
        >
          <input
            type="file"
            accept=".zip,application/zip"
            className="sr-only"
            onChange={(event) => {
              const archivo = event.target.files?.[0];
              if (archivo) subirZip(archivo);
            }}
          />
          <span className="font-medium">
            {t(ocupado === 'subir' ? 'lora.subiendo' : 'lora.arrastraZip')}
          </span>
          <span className="text-xs text-[var(--color-ink-muted)]">{t('lora.sirveElQue')}</span>
        </label>
      </Card>
    </div>
  );
}

function ContentTab({ data, onChange }: { data: CreatorDetailData; onChange: () => void }) {
  const { t } = useT();
  const aviso = useAviso();
  const { animar, videoEstado, videoError, animando } = useAnimar(onChange, data.videoJobs);
  const { movimientoDe, setMovimiento } = useMovimientos();
  const fanvue = useApi<FanvueProfilesResponse>(
    `/api/integrations/fanvue/creators?creatorId=${data.creator.id}`,
  );

  /**
   * Anima la toma de una publicación y le asigna el clip.
   *
   * Es la misma generación, con el paso que la vuelve útil: sin asignarlo, el video queda en
   * la galería y la publicación sigue saliendo con la foto — que es exactamente lo que uno no
   * quería al pulsar «animar».
   */
  const animarPara = async (contentItemId: string, assetId: string): Promise<void> => {
    const videoId = await animar(assetId, 'sutil', { prompt: movimientoDe(assetId) });
    if (!videoId) return;
    await api.post(`/api/content/${contentItemId}/asset`, { assetId: videoId });
    onChange();
  };

  const ajustes = useApi<{ settings: { telegramStars: { imagenNsfw: number } } }>('/api/settings');
  const precioEnStars = ajustes.data?.settings.telegramStars.imagenNsfw ?? 0;
  const [exported, setExported] = useState<Record<string, unknown> | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // Publicar es irreversible y público. El primer click arma, el segundo dispara: en una
  // fila de botones donde todos los demás son inocuos, uno que postea de verdad no puede
  // costar lo mismo que «Exportar».
  /* Fanvue tiene su propio botón en su pestaña: su material y su flujo son otros. */
  const PLATAFORMAS_DEL_LOTE = ['instagram', 'facebook', 'x'];

  const [arming, setArming] = useState<string | null>(null);
  /*
    El lote tiene su propio armado, y guarda EN QUÉ plataforma.

    `arming` guarda qué par publicación+plataforma está a un clic de salir. El lote no es uno de
    esos pares: es una plataforma entera, y confundirlos haría que armar el lote de Instagram
    dejara armada también una publicación suelta.
  */
  const [loteArmado, setLoteArmado] = useState<string | null>(null);
  const [avanceLote, setAvanceLote] = useState<{ hechas: number; total: number } | null>(null);
  /* El aviso flotante se va a los cinco segundos y se lleva el motivo de cada fallo. */
  const [fallosDelLote, setFallosDelLote] = useState<string[]>([]);
  const [published, setPublished] = useState<PublishOutcome | null>(null);

  /**
   * Si esa plataforma puede publicar por API ahora mismo.
   *
   * Se mira el permiso, no sólo el estado: una conexión activa sin el permiso de publicar
   * responde igual de bien y devuelve un paquete manual. Ofrecer «Publicar ahora» ahí
   * prometería algo que no va a pasar.
   *
   * ── Y el permiso es el de CADA plataforma ─────────────────────────────────────
   * Exigía `instagram_business_content_publish` para todas, que es un permiso que sólo existe
   * en Instagram. Con eso, una Página de Facebook conectada y con permiso para publicar no
   * mostraba el botón nunca: el sistema podía publicar y la pantalla decía que no.
   *
   * Fanvue usa una conexión de manager, así que su capacidad sale del perfil asignado y no de
   * `data.connections`, que contiene autorizaciones individuales de Meta.
   */
  const PERMISO_DE_PUBLICAR: Record<string, string> = {
    instagram: 'instagram_business_content_publish',
    facebook: 'pages_manage_posts',
  };

  const canPublishOn = (platform: string): boolean => {
    if (platform === 'fanvue') return Boolean(fanvue.data?.connected && fanvue.data.current);

    /*
      Telegram no tiene OAuth, y sin esto el boton de publicar no aparecia nunca.

      ── El defecto ──────────────────────────────────────────────────────────────
      Esta funcion busca un PERMISO de OAuth en `PERMISO_DE_PUBLICAR` y devuelve `false` cuando
      no lo encuentra. Se escribio cuando las cuatro plataformas se conectaban por OAuth, y para
      Telegram —que publica con un bot administrador del canal— no hay permiso que buscar. La
      publicacion quedaba con «Exportar» y nada mas, como si no hubiera adonde mandarla.

      ── Por que alcanza con que haya canal ──────────────────────────────────────
      Porque el panel no puede saber si el bot es administrador de ese canal: eso es por canal y
      se comprueba con una llamada a Telegram. El servidor SI lo comprueba antes de cada
      publicacion, y si falta el permiso cae a paquete manual en vez de fallar. Asi que el boton
      es optimista a proposito: ofrece intentarlo, y quien decide es el servidor.

      Sin canal cargado no se ofrece: ahi si se sabe, y seria un boton que no puede funcionar.
    */
    if (platform === 'telegram') {
      return data.accounts.some(
        (cuenta) => cuenta.platform === 'telegram' && (cuenta.username ?? '').trim().length > 0,
      );
    }

    const permiso = PERMISO_DE_PUBLICAR[platform];
    if (!permiso) return false;

    return data.connections.some(
      (connection) =>
        connection.platform === platform &&
        connection.status === 'ACTIVE' &&
        connection.scopes.includes(permiso),
    );
  };

  /**
   * El post real de esta publicación en esa plataforma, si ya salió.
   *
   * Antes el botón se decidía por `item.status`, que es **un solo campo para las tres
   * plataformas**: algo publicado en Instagram y exportado a Fanvue volvía a decir «Publicar
   * ahora», invitando a postearlo dos veces. Lo que responde de verdad esa pregunta es si
   * hay un `external_post` publicado para ese par publicación + plataforma.
   */
  const publishedOn = (itemId: string, platform: string) =>
    data.posts.find(
      (post) =>
        post.contentItemId === itemId && post.platform === platform && post.status === 'PUBLISHED',
    );

  /**
   * Lo que falta publicar en cada plataforma.
   *
   * ── Por qué por plataforma y no una lista sola ────────────────────────────────
   * Porque publicar es por par publicación + plataforma. La misma toma puede estar arriba en
   * Instagram y sin salir en Facebook, y un botón que dijera «publicar todo» tendría que decidir
   * solo en cuáles — que es justo la decisión que uno quiere tomar.
   *
   * ── Qué se excluye ────────────────────────────────────────────────────────────
   * Lo ya publicado en ESA plataforma, mirando `external_posts` y no `item.status`: ese campo es
   * uno solo para las cuatro, y por él una toma publicada en Instagram decía «ya publicado» en
   * Facebook sin haber salido.
   */
  const pendientesEn = (platform: string) =>
    data.content.filter(
      (item) =>
        item.variants.some((variant) => variant.platform === platform) &&
        !publishedOn(item.id, platform) &&
        !(platform === 'instagram' && item.status === 'PUBLISHED'),
    );

  /**
   * Los destinos de UNA publicación que todavía se pueden publicar.
   *
   * Es la misma condición que decide si sale el botón suelto de cada variante, escrita una vez:
   * que la variante exista, que la plataforma pueda publicar, que no haya salido ya ahí, y la
   * excepción vieja de Instagram —`item.status` es un campo solo para las cuatro, así que una
   * toma publicada en Instagram decía «ya publicado» en las demás sin haber salido—.
   *
   * Si divergiera de la del botón suelto, el selector ofrecería un destino que el botón no, o al
   * revés, y las dos formas de publicar la misma toma dirían cosas distintas.
   */
  const destinosPendientesDe = (item: CreatorDetailData['content'][number]): DestinoPendiente[] =>
    item.variants
      .filter(
        (variant) =>
          canPublishOn(variant.platform) &&
          !publishedOn(item.id, variant.platform) &&
          !(variant.platform === 'instagram' && item.status === 'PUBLISHED'),
      )
      .map((variant) => ({
        platform: variant.platform,
        dePago: variant.audience === 'subscribers',
        // Regalar es una elección que hoy sólo existe en Telegram: es la única con muro de pago.
        puedeRegalar: variant.platform === 'telegram',
      }));

  const publicarLoteEn = async (platform: string): Promise<void> => {
    const pendientes = pendientesEn(platform);
    setAvanceLote({ hechas: 0, total: pendientes.length });
    const fallos: string[] = [];
    let hechas = 0;

    /*
      En fila, igual que el lote de Fanvue y por lo mismo: cada publicación sube media y tarda,
      mandarlas juntas multiplica la chance de que una se caiga por tiempo, y si una falla las
      anteriores ya salieron de verdad.
    */
    for (const item of pendientes) {
      try {
        const respuesta = await api.post<{ result: { status: string } }>(
          `/api/content/${item.id}/publish?platform=${platform}`,
        );
        if (respuesta.result.status === 'PUBLISHED') hechas += 1;
        else fallos.push(`${item.id.slice(-6)}: ${respuesta.result.status}`);
      } catch (caught) {
        fallos.push(caught instanceof Error ? caught.message : String(caught));
      }
      setAvanceLote({ hechas: hechas + fallos.length, total: pendientes.length });
      onChange();
    }

    setAvanceLote(null);
    setFallosDelLote(fallos);
    aviso.ok(
      fallos.length === 0
        ? `${hechas} publicadas en ${platform}.`
        : `${hechas} publicadas y ${fallos.length} que no salieron.`,
    );
  };

  /**
   * Sólo las tomas publicables, y sólo las que siguen en pie.
   *
   * Por tipo: las frontales y los ángulos de la hoja miden 448×448 y existen para anclar la
   * cara, no para subirse. El servidor las rechaza, y ofrecerlas sería invitar a un error
   * que después hay que explicar.
   *
   * Por estado: faltaba, y no era cosmético. El selector mostraba las descartadas junto a
   * las buenas, así que se podía asignar a un post una imagen que ya se había decidido no
   * usar; el error habría aparecido en Instagram, no acá.
   */
  const shots = data.assets.filter(
    (asset) =>
      asset.kind !== 'reference_candidate' &&
      asset.kind !== 'content_extended' &&
      // La portada no se publica desde acá: se sube a mano a Facebook.
      asset.kind !== 'generic' &&
      !IDENTITY_KINDS.includes(asset.kind) &&
      !isRetired(asset) &&
      /*
        Y por clasificación, por el mismo motivo que las otras dos exclusiones.

        Una toma explícita sólo puede ir al feed de suscriptores de Fanvue, y el servidor lo
        impide al publicar. Ofrecerla acá es invitar al error que el comentario de arriba dice
        que hay que evitar — con el agravante de que aparece al final, cuando ya se eligió.
      */
      asset.clasificacion !== 'nsfw',
  );

  const act = async (key: string, run: () => Promise<unknown>): Promise<void> => {
    setBusy(key);
    try {
      await run();
      onChange();
    } catch (caught) {
      // Lo de la API ya salió como aviso flotante. Acá sólo lo que ni llegó a la API.
      if (!(caught instanceof ApiRequestError)) aviso.error(t('gal.accionFallo'));
    } finally {
      setBusy(null);
    }
  };

  /**
   * Lo rechazado no se muestra.
   *
   * Una publicación queda en `REJECTED` cuando se descarta su imagen desde la galería: sin
   * imagen no hay nada que publicar, y las dos son la misma decisión cuando la publicación
   * nació junto a la foto. Dejarla acá sería ofrecer algo que ya se rechazó.
   *
   * Se filtra en la pantalla y no en la consulta a propósito: la fila sigue existiendo, con
   * el rastro de qué se generó y por qué no salió.
   */
  const yaPublicada = (item: (typeof data.content)[number]): boolean =>
    item.status === 'PUBLISHED' ||
    data.posts.some((post) => post.contentItemId === item.id && post.status === 'PUBLISHED');

  /*
    Lo de Fanvue no entra acá, y hasta hace poco no hacía falta decirlo.

    Esta pestaña es el contenido que va a las redes. Las tomas de Fanvue no llegaban a tener
    publicación, así que la lista era apta por construcción; desde que se pueden publicar, esa
    garantía se perdió y aparecieron mezcladas entre las publicadas.

    Se filtra por la clase del archivo y no por la plataforma de la variante: una publicación de
    Fanvue con una foto apta —un adelanto— sí pertenece acá, y lo que no pertenece es el material
    explícito, sea cual sea su destino.
  */
  const visible = data.content.filter(
    (item) => item.status !== 'REJECTED' && item.clasificacion !== 'nsfw',
  );

  /**
   * Dos grupos con su título, no una lista ordenada.
   *
   * Ordenar alcanzaba para que lo publicado quedara abajo, pero no para que se note: una
   * lista larga de tarjetas parecidas sigue leyéndose como una sola cosa, y hay que recorrerla
   * para descubrir dónde termina lo que falta decidir.
   *
   * Separadas y con encabezado, la pregunta «¿qué me queda por publicar?» se responde
   * mirando, sin contar. Dentro de cada grupo se conserva el orden del servidor.
   */
  const pendientes = visible.filter((item) => !yaPublicada(item));
  const publicadas = visible.filter((item) => yaPublicada(item));
  /*
    El servidor las devuelve de la más nueva a la más vieja, así que quedarse con las primeras
    es quedarse con las últimas que salieron.
  */
  const ultimasPublicadas = publicadas.slice(0, ULTIMAS_PUBLICADAS);

  if (visible.length === 0) {
    return (
      <div className="space-y-4">
        <ContentStudio data={data} onChange={onChange} />
        <EmptyState
          title={t('cont.sinContenido')}
          description={t(data.content.length === 0 ? 'cont.sinCalendario' : 'cont.todoDescartado')}
        />
      </div>
    );
  }

  const withoutImage = visible.filter((item) => item.assetId === null).length;

  return (
    <div className="space-y-4">
      <ContentStudio data={data} onChange={onChange} />

      {/*
        Publicar la tanda entera, una plataforma por vez.

        Publicar es por par publicación + plataforma: la misma toma puede estar arriba en
        Instagram y sin salir en Facebook. Un botón «publicar todo» tendría que decidir solo en
        cuáles, que es justo la decisión que uno quiere tomar — así que hay uno por plataforma
        conectada, y cada uno dice cuántas le faltan.

        Conserva el armado en dos pasos del botón individual. Publicar es la única acción del
        sistema que no se puede deshacer, y hacerla masiva es donde MENOS conviene ahorrarse el
        segundo clic.
      */}
      {avanceLote ? (
        <Card title={t('cont.publicando')}>
          <p className="text-sm text-[var(--color-ink-muted)]">
            {t('cont.avanceLote', { hechas: avanceLote.hechas, total: avanceLote.total })}
          </p>
        </Card>
      ) : (
        PLATAFORMAS_DEL_LOTE.filter(
          (platform) => canPublishOn(platform) && pendientesEn(platform).length > 1,
        ).length > 0 && (
          <Card title={t('cont.publicarEnTanda')}>
            <div className="flex flex-wrap gap-2">
              {PLATAFORMAS_DEL_LOTE.filter(
                (platform) => canPublishOn(platform) && pendientesEn(platform).length > 1,
              ).map((platform) =>
                loteArmado === platform ? (
                  <span key={platform} className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setLoteArmado(null);
                        setFallosDelLote([]);
                        void publicarLoteEn(platform);
                      }}
                      className="rounded-lg border border-[var(--color-danger)] px-3 py-1.5 text-sm text-[var(--color-danger)]"
                    >
                      {t('cont.siPublicarEn', {
                        n: pendientesEn(platform).length,
                        plataforma: platform,
                      })}
                    </button>
                    <button
                      type="button"
                      onClick={() => setLoteArmado(null)}
                      className="rounded-lg border border-[var(--color-border-subtle)] px-3 py-1.5 text-sm"
                    >
                      {t('comun.cancelar')}
                    </button>
                  </span>
                ) : (
                  <button
                    key={platform}
                    type="button"
                    disabled={busy !== null}
                    onClick={() => setLoteArmado(platform)}
                    className="rounded-lg border border-[var(--color-border-subtle)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)] disabled:opacity-40"
                  >
                    {t('cont.plataformaCuantas', {
                      plataforma: platform,
                      n: pendientesEn(platform).length,
                    })}
                  </button>
                ),
              )}
            </div>
            <p className="mt-2 text-xs text-[var(--color-ink-muted)]">{t('cont.salenDeAUna')}</p>

            {/* Lo que no salió, con su motivo y sin desaparecer solo. */}
            {fallosDelLote.length > 0 && (
              <div className="mt-3 rounded-xl border border-[var(--color-warn)]/40 bg-[var(--color-warn)]/10 p-2.5">
                <p className="text-xs font-medium">
                  {t('fanvue.noSalieron', { n: fallosDelLote.length })}
                </p>
                <ul className="mt-1 space-y-0.5 text-xs text-[var(--color-ink-muted)]">
                  {fallosDelLote.map((fallo) => (
                    <li key={fallo}>· {fallo}</li>
                  ))}
                </ul>
              </div>
            )}
          </Card>
        )
      )}

      {exported && (
        <Card title={t('cont.paqueteListo')}>
          <pre className="overflow-x-auto rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-3 text-xs whitespace-pre-wrap">
            {String((exported as { copyableText?: string }).copyableText ?? '')}
          </pre>
          <div className="mt-3 flex flex-wrap gap-2">
            <CopyButton
              text={String((exported as { copyableText?: string }).copyableText ?? '')}
              label={t('cont.copiarTexto')}
            />
            {typeof (exported as { mediaUrl?: string }).mediaUrl === 'string' && (
              <a
                href={(exported as { mediaUrl: string }).mediaUrl}
                download
                className="inline-flex items-center rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] px-4 py-2 text-sm font-medium transition hover:border-[var(--color-border-strong)] hover:bg-[var(--color-accent-soft)]"
              >
                {t('cont.descargarImagen')}
              </a>
            )}
            {typeof (exported as { deepLink?: string }).deepLink === 'string' && (
              <a
                href={(exported as { deepLink: string }).deepLink}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] px-4 py-2 text-sm font-medium transition hover:border-[var(--color-border-strong)] hover:bg-[var(--color-accent-soft)]"
              >
                {t('cont.abrirPlataforma')}
              </a>
            )}
            <Button variant="secondary" onClick={() => setExported(null)}>
              {t('comun.cerrar')}
            </Button>
          </div>
        </Card>
      )}

      {/*
        Publicar puede terminar de dos formas y la diferencia importa. Si la conexión no
        estaba sana, el sistema cae a paquete manual y devuelve 200: sin decirlo, «Publicar
        ahora» parecería haber publicado algo que nadie va a ver.
      */}
      {published && (
        <div
          className={`rounded-2xl border p-4 text-sm ${
            published.status === 'PUBLISHED'
              ? 'border-[var(--color-accent)]/40 bg-[var(--color-accent-soft)]'
              : 'border-[var(--color-warn)]/35 bg-[var(--color-warn)]/10'
          }`}
        >
          {published.status === 'PUBLISHED' ? (
            <>
              <p className="font-medium">{t('cont.publicadoEnInstagram')}</p>
              {published.permalink && (
                <a
                  href={published.permalink}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="mt-1 inline-block text-[var(--color-accent)] underline underline-offset-2"
                >
                  {t('cont.verLaPublicacion')}
                </a>
              )}
            </>
          ) : (
            <>
              <p className="font-medium">{t('cont.salioComoPaquete')}</p>
              <p className="mt-1 text-[var(--color-ink-muted)]">{t('cont.salioComoPaqueteQue')}</p>
            </>
          )}
          <button
            type="button"
            onClick={() => setPublished(null)}
            className="mt-2 text-xs text-[var(--color-ink-muted)] underline-offset-2 hover:underline"
          >
            {t('comun.cerrar')}
          </button>
        </div>
      )}

      {/*
        Recuperar lo publicado sin registrar.

        Sale acá y para todas las influencers de una: el problema no fue de ninguna en
        particular sino de la ruta de publicar, así que ofrecerlo de a una obligaría a
        acordarse de cuáles pasaron por ahí — y justamente lo que se perdió es el registro
        que permitiría saberlo.
      */}
      {visible.some((item) => item.status === 'PUBLISHED') && (
        <ReconcileBanner onChange={onChange} />
      )}

      {withoutImage > 0 && (
        <div className="rounded-2xl border border-[var(--color-warn)]/35 bg-[var(--color-warn)]/10 p-4 text-sm">
          <p className="font-medium">
            {t(withoutImage === 1 ? 'cont.sinImagenUna' : 'cont.sinImagenVarias', {
              n: withoutImage,
            })}
          </p>
          <p className="mt-1 text-[var(--color-ink-muted)]">{t('cont.sinImagenQue')}</p>
        </div>
      )}

      {pendientes.length > 0 && (
        <h3 className="pt-2 text-xs font-semibold tracking-wide text-[var(--color-ink-muted)] uppercase">
          {t('cont.paraPublicar', { n: pendientes.length })}
        </h3>
      )}

      {[...pendientes, ...ultimasPublicadas].map((item, orden) => {
        const image = data.assets.find((asset) => asset.id === item.assetId);
        // El encabezado del segundo grupo va en la primera publicada, para no repetir el
        // recorrido ni partir el `map` en dos con la misma tarjeta escrita dos veces.
        const abreArchivo = orden === pendientes.length && ultimasPublicadas.length > 0;

        /**
         * Ya salió en alguna plataforma.
         *
         * Se mira `external_posts` y no sólo `item.status`, que es un único campo para las
         * tres plataformas. El estado también cuenta, porque hay publicaciones de la época en
         * que la ruta del panel no registraba nada y de ésas es lo único que quedó.
         */
        const publicado = yaPublicada(item);

        const card = (
          <Card key={item.id}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium">{item.topic}</p>
                <p className="text-xs text-[var(--color-ink-muted)]">
                  {item.pillar} · {item.aspectRatio} · {formatDateTime(item.scheduledFor)}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {/*
                Aprobar es lo que la pone en cola, y sin esto no había cómo.

                El cron sólo publica lo aprobado —la puerta se agregó porque antes publicaba
                borradores que nadie había leído— pero nada en el sistema pasaba una publicación a
                aprobada. O sea que la puerta quedó sin manija y el cron dejó de publicar todo,
                que desde afuera se ve igual que si estuviera roto.
              */}
                {item.status !== 'PUBLISHED' && (
                  <button
                    type="button"
                    disabled={busy !== null || item.assetId === null}
                    title={
                      item.assetId === null
                        ? t('post.elegiImagenPrimero')
                        : item.status === 'APPROVED' || item.status === 'SCHEDULED'
                          ? t('post.sacarDeLaCola')
                          : t('post.ponerEnCola')
                    }
                    onClick={() => {
                      const aprobada = item.status === 'APPROVED' || item.status === 'SCHEDULED';
                      void act(`ap-${item.id}`, () =>
                        api.post(`/api/content/${item.id}/${aprobada ? 'unapprove' : 'approve'}`),
                      );
                    }}
                    className={
                      'rounded-lg border px-2 py-1 text-xs transition disabled:opacity-40 ' +
                      (item.status === 'APPROVED' || item.status === 'SCHEDULED'
                        ? 'border-[var(--color-ok)] text-[var(--color-ok)]'
                        : 'border-[var(--color-accent)] font-medium text-[var(--color-accent)]')
                    }
                  >
                    {busy === `ap-${item.id}`
                      ? '…'
                      : item.status === 'APPROVED' || item.status === 'SCHEDULED'
                        ? 'En cola ✓'
                        : 'Aprobar'}
                  </button>
                )}
                <StatusBadge status={item.status} />
                {/*
                Borrar la publicación, con su imagen o su clip de vuelta a la lista.

                No existía, y su ausencia se notó de la peor forma: una publicación que salió con
                el archivo equivocado sólo se podía sacar consultando la base a mano, y mientras
                tanto el asset quedaba atrapado en ella —fuera de «Contenido sin publicación», sin
                nada que explicara por qué no aparecía—.

                La confirmación nombra lo que ya salió afuera, porque es lo único que este botón
                no puede deshacer: borrar acá no borra el post de Instagram.
              */}
                {/*
                Volver a sacar una foto que ya salió, con texto nuevo.

                Sólo aparece en las publicadas: en una que todavía no salió, «republicar» no
                significa nada — para eso está publicarla.

                No toca la vieja. La vieja ya salió, tiene su fecha y su enlace, y pisarle el
                texto reescribiría la historia de algo que la gente ya vio. Se crea una
                publicación nueva con la misma imagen y su propio caption.
              */}
                {item.status === 'PUBLISHED' && (
                  <button
                    type="button"
                    disabled={busy !== null}
                    title={t('post.republicarTitulo')}
                    onClick={() => {
                      if (!window.confirm(t('post.confirmarRepublicar'))) {
                        return;
                      }
                      void act(`re-post-${item.id}`, () =>
                        api.post(`/api/content/${item.id}/republish`),
                      );
                    }}
                    className="rounded-lg border border-[var(--color-border-subtle)] px-2 py-1 text-xs text-[var(--color-ink-muted)] transition hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] disabled:opacity-50"
                  >
                    {busy === `re-post-${item.id}` ? 'Escribiendo…' : 'Republicar'}
                  </button>
                )}

                <button
                  type="button"
                  disabled={busy !== null}
                  title={t('post.borrarTitulo')}
                  onClick={() => {
                    const yaSalio = item.status === 'PUBLISHED';
                    if (
                      !window.confirm(
                        t(yaSalio ? 'post.confirmarBorrarPublicada' : 'post.confirmarBorrar'),
                      )
                    ) {
                      return;
                    }
                    void act(`del-post-${item.id}`, () => api.del(`/api/content/${item.id}`));
                  }}
                  className="rounded-lg border border-[var(--color-border-subtle)] px-2 py-1 text-xs text-[var(--color-ink-muted)] transition hover:border-[var(--color-danger)] hover:text-[var(--color-danger)] disabled:opacity-50"
                >
                  {t(busy === `del-post-${item.id}` ? 'post.borrando' : 'post.borrar')}
                </button>
              </div>
            </div>

            {/*
            Las tomas disponibles, acá mismo, junto al caption.
            La elegida lleva anillo; pulsar otra la reemplaza. Sin modos ni «cambiar»: la
            tira **es** el selector, y con tres a seis miniaturas caben todas a la vista.
          */}
            {/*
            Publicado: la imagen se muestra, no se elige.

            Cambiarla acá no cambia el post que ya está en Instagram — sólo haría que el panel
            afirme algo distinto de lo que la audiencia ve. Y no hay forma de deshacerlo desde
            este lado: la única corrección posible es en la plataforma.
          */}
            {/*
            Por qué no sale sola, dicho donde se espera que salga.

            Una publicación en borrador con la hora vencida se ve exactamente igual que una que
            falló: pendiente, la hora pasó, y nada ocurrió. La diferencia es que a ésta le falta
            una decisión, y el único lugar donde eso se puede decir es al lado de ella.
          */}
            {item.status === 'DRAFT' && (
              <p className="mt-3 text-xs text-[var(--color-ink-muted)]">
                {t('post.enBorradorAntes')}
                <strong>{t('post.noSePublicaSola')}</strong>
                {t('post.enBorradorDespues')}
              </p>
            )}

            <div className="mt-4 rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-3">
              {publicado ? (
                <div className="flex items-center gap-3">
                  {image &&
                    (image.kind === 'content_video' ? (
                      <video
                        src={image.url}
                        controls
                        className="h-16 w-16 rounded-lg object-cover"
                      />
                    ) : (
                      <img
                        src={miniatura(image.url, 64)}
                        alt={item.topic}
                        className="h-16 w-16 rounded-lg object-cover"
                      />
                    ))}
                  <p className="text-xs text-[var(--color-ink-muted)]">
                    {t('post.yaPublicadaEtiqueta')}
                  </p>
                </div>
              ) : (
                <>
                  <ImagePicker
                    item={item}
                    shots={shots}
                    busy={busy}
                    onAssign={(assetId) =>
                      void act(`img-${item.id}`, () =>
                        api.post(`/api/content/${item.id}/asset`, { assetId }),
                      )
                    }
                    onChange={onChange}
                  />

                  {/*
                  Animar, desde la publicación y no sólo desde la galería.
                  
                  El botón vivía en la grilla de tomas sin publicación, así que desaparecía en
                  cuanto se generaba el texto — justo cuando uno decide qué va a publicar. Acá
                  está en el momento correcto, y el clip queda asignado a esta publicación sin
                  un paso más: es para lo que se generó.
                */}
                  {image && image.kind === 'content_scene' && (
                    <div className="max-w-[20rem]">
                      <CampoDeMovimiento
                        valor={movimientoDe(image.id)}
                        disabled={animando[image.id]}
                        onCambio={(texto) => setMovimiento(image.id, texto)}
                      />
                    </div>
                  )}

                  {image && image.kind === 'content_scene' && (
                    <button
                      type="button"
                      disabled={
                        busy !== null ||
                        (image ? animando[image.id] : false) ||
                        image.status !== 'APPROVED'
                      }
                      title={
                        image.status === 'APPROVED'
                          ? t('post.animarTitulo')
                          : t('post.aprobarEnGaleria')
                      }
                      onClick={() => void animarPara(item.id, image.id)}
                      className="mt-2 rounded-xl border border-[var(--color-border-subtle)] px-3 py-1.5 text-xs font-medium transition hover:border-[var(--color-border-strong)] disabled:opacity-50"
                    >
                      {animando[image.id]
                        ? videoEstado[image.id] || t('estudio.animando')
                        : image.status === 'APPROVED'
                          ? t('post.animarEstaToma', { precio: precioDelClip(data.videoPlan) })
                          : t('estudio.aprobarParaAnimar')}
                    </button>
                  )}

                  {image && recorteAvisado(image, data.videoPlan) && (
                    <p className="mt-1 text-xs text-[var(--color-ink-muted)]">
                      {recorteAvisado(image, data.videoPlan)}
                    </p>
                  )}
                  {image && videoError[image.id] && (
                    <p className="mt-1 text-xs text-[var(--color-danger)]">
                      {videoError[image.id]}
                    </p>
                  )}
                  {image && (
                    <button
                      type="button"
                      disabled={busy !== null}
                      onClick={() =>
                        void act(`img-${item.id}`, () => api.del(`/api/content/${item.id}/asset`))
                      }
                      className="mt-2 text-xs text-[var(--color-ink-muted)] underline-offset-2 hover:text-[var(--color-danger)] hover:underline"
                    >
                      {t('post.quitarImagen')}
                    </button>
                  )}
                </>
              )}
            </div>

            <PublicarEnVarias
              contentItemId={item.id}
              destinos={destinosPendientesDe(item)}
              disabled={busy !== null || item.assetId === null}
              precioEnStars={precioEnStars}
              onHecho={onChange}
            />

            <div className="mt-4 space-y-3">
              {item.variants.map((variant) => (
                <div
                  key={variant.id}
                  className="rounded-xl border border-[var(--color-border-subtle)] p-3"
                >
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs font-semibold uppercase">{variant.platform}</span>
                    <div className="flex flex-wrap items-center gap-2">
                      {publishedOn(item.id, variant.platform)?.permalink ? (
                        <a
                          href={publishedOn(item.id, variant.platform)?.permalink ?? '#'}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="text-xs text-[var(--color-accent)] underline underline-offset-2"
                        >
                          {t('post.yaPublicadoVer')}
                        </a>
                      ) : publishedOn(item.id, variant.platform) ? (
                        <span className="text-xs text-[var(--color-ink-muted)]">
                          {t('post.yaPublicadoSinEnlace')}
                        </span>
                      ) : /*
                        `item.status` sólo vale para Instagram, y sólo por lo viejo.

                        Es UN campo para las cuatro plataformas: en cuanto una publicación sale
                        en Instagram, las otras tres decían «ya publicado» sin haber salido, y no
                        había forma de publicarlas. El estado compartido bloqueaba justo lo que
                        falta hacer.

                        Se conserva para Instagram porque hubo publicaciones reales que salieron
                        cuando esta ruta todavía no registraba nada en `external_posts`: de ésas
                        lo único que quedó es este estado, e ignorarlo haría reaparecer «Publicar
                        ahora» sobre posts que ya están arriba. Instagram era la única con API en
                        esa época, así que la excepción no alcanza a ninguna otra.
                      */
                      variant.platform === 'instagram' && item.status === 'PUBLISHED' ? (
                        <span className="text-xs text-[var(--color-ink-muted)]">
                          {t('post.yaPublicadoSinEnlace')}
                        </span>
                      ) : (
                        canPublishOn(variant.platform) && (
                          <PublishButton
                            armed={arming === `${item.id}:${variant.platform}`}
                            busy={busy === `pub-${item.id}:${variant.platform}`}
                            disabled={busy !== null || item.assetId === null}
                            reason={item.assetId === null ? t('post.igSinImagen') : undefined}
                            onArm={() => setArming(`${item.id}:${variant.platform}`)}
                            onCancel={() => setArming(null)}
                            onPublish={() => {
                              setArming(null);
                              setPublished(null);
                              void act(`pub-${item.id}:${variant.platform}`, async () => {
                                const response = await api.post<{ result: PublishOutcome }>(
                                  `/api/content/${item.id}/publish?platform=${variant.platform}`,
                                );
                                setPublished(response.result);
                              });
                            }}
                          />
                        )
                      )}
                      <Button
                        variant="secondary"
                        onClick={() =>
                          void api
                            .post<{ package: Record<string, unknown> }>(
                              `/api/content/${item.id}/export?platform=${variant.platform}`,
                            )
                            .then((result) => setExported(result.package))
                        }
                      >
                        Exportar
                      </Button>
                    </div>
                  </div>
                  {/*
                    El texto se corrige donde se lee, salvo en la plataforma donde ya salió: ver
                    `TextoDeLaVariante`. La condición es la misma que aplica el servidor —el
                    registro de ESE destino, con la excepción vieja de Instagram— y no
                    `item.status`, que es uno para las cuatro.
                  */}
                  <TextoDeLaVariante
                    contentItemId={item.id}
                    variant={variant}
                    editable={
                      !publishedOn(item.id, variant.platform) &&
                      !(variant.platform === 'instagram' && item.status === 'PUBLISHED')
                    }
                    onChange={onChange}
                  />
                </div>
              ))}
            </div>
          </Card>
        );

        if (!publicado) return card;

        /*
          Plegada, no oculta.

          Lo publicado ya no requiere ninguna decisión: no se le cambia la imagen, no se
          vuelve a publicar y no hay nada que elegir. Abierto ocupa el mismo lugar que lo que
          sí falta hacer. Sigue estando a un click para quien quiera revisarlo.
        */
        const plegada = (
          <details
            key={item.id}
            className="group rounded-2xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)]"
          >
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-2xl p-4 text-sm">
              <span className="min-w-0 truncate">
                <span className="font-medium">{item.topic}</span>
                <span className="ml-2 text-xs text-[var(--color-ink-muted)]">
                  {t('post.yaPublicada')}
                </span>
              </span>
              <span
                aria-hidden="true"
                className="shrink-0 text-[var(--color-ink-muted)] transition-transform group-open:rotate-90"
              >
                ›
              </span>
            </summary>
            <div className="p-4 pt-0">{card}</div>
          </details>
        );

        if (!abreArchivo) return plegada;

        return (
          <div key={item.id} className="space-y-4">
            <h3 className="pt-2 text-xs font-semibold tracking-wide text-[var(--color-ink-muted)] uppercase">
              {publicadas.length > ultimasPublicadas.length
                ? t('post.yaPublicadasUltimas', {
                    n: ultimasPublicadas.length,
                    total: publicadas.length,
                  })
                : t('post.yaPublicadas', { n: publicadas.length })}
            </h3>
            {plegada}
          </div>
        );
      })}
    </div>
  );
}

/**
 * El texto de una variante, leído y corregible en el mismo lugar.
 *
 * ── Por qué vive acá y no en un diálogo ───────────────────────────────────────
 * Porque lo que hay que corregir se decide leyéndolo, y se lee acá: el pie de Instagram al lado
 * del de Telegram, con la foto arriba. Un diálogo aparte se abre sin la comparación que hizo
 * ver el problema, y obliga a cerrarlo para volver a mirar.
 *
 * ── Por qué los hashtags son un campo de texto y no una lista ─────────────────
 * Porque así se escriben en cualquier otro lado: se tipean separados por espacios, con o sin
 * `#`. El servidor los normaliza —saca el `#`, los acentos y los espacios de adentro— y
 * devuelve los que guardó, que es lo que se vuelve a dibujar: si algo se limpió, se ve.
 *
 * ── Y por qué no aparece en lo ya publicado ───────────────────────────────────
 * Igual que con la imagen: el post ya está arriba con su texto, y cambiarlo de este lado sólo
 * haría que el panel diga otra cosa. El servidor lo rechaza también; acá no se ofrece.
 */
function TextoDeLaVariante({
  contentItemId,
  variant,
  editable,
  onChange,
}: {
  contentItemId: string;
  variant: ContentView['variants'][number];
  editable: boolean;
  onChange: () => void;
}) {
  const { t } = useT();
  const aviso = useAviso();
  const [editando, setEditando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [caption, setCaption] = useState(variant.caption);
  const [hashtags, setHashtags] = useState(variant.hashtags.join(' '));
  const [altText, setAltText] = useState(variant.altText);

  const abrir = (): void => {
    setCaption(variant.caption);
    setHashtags(variant.hashtags.join(' '));
    setAltText(variant.altText);
    setEditando(true);
  };

  const guardar = async (): Promise<void> => {
    setGuardando(true);
    try {
      await api.patch(`/api/content/${contentItemId}/variant`, {
        platform: variant.platform,
        caption,
        /* Se parte por espacios y comas: las dos formas en que alguien separa una lista. */
        hashtags: hashtags
          .split(/[s,]+/)
          .map((etiqueta) => etiqueta.trim())
          .filter((etiqueta) => etiqueta.length > 0),
        altText,
      });
      setEditando(false);
      onChange();
    } catch (caught) {
      if (!(caught instanceof ApiRequestError)) aviso.error(t('post.noSePudoGuardarTexto'));
    } finally {
      setGuardando(false);
    }
  };

  if (!editando) {
    return (
      <>
        <p className="text-sm whitespace-pre-wrap">{variant.caption}</p>
        {variant.hashtags.length > 0 && (
          <p className="mt-2 text-xs text-[var(--color-accent)]">
            {variant.hashtags.map((tag) => `#${tag}`).join(' ')}
          </p>
        )}
        {variant.altText && (
          <p className="mt-2 text-xs text-[var(--color-ink-muted)]">Alt: {variant.altText}</p>
        )}
        {editable && (
          <button
            type="button"
            onClick={abrir}
            title={t('post.editarTextoTitulo')}
            className="mt-2 text-xs text-[var(--color-ink-muted)] underline underline-offset-2 hover:text-[var(--color-accent)]"
          >
            {t('post.editarTexto')}
          </button>
        )}
      </>
    );
  }

  return (
    <div className="space-y-2">
      <label className="block">
        <span className="text-xs font-medium text-[var(--color-ink-muted)]">{t('post.elPie')}</span>
        <textarea
          autoFocus
          rows={3}
          maxLength={300}
          value={caption}
          onChange={(event) => setCaption(event.target.value)}
          /* Escape cierra sin guardar. Enter NO guarda: un pie de varias líneas es normal. */
          onKeyDown={(event) => {
            if (event.key === 'Escape') setEditando(false);
          }}
          className="mt-1 w-full rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-surface)] p-2 text-sm"
        />
        <span className="text-xs text-[var(--color-ink-muted)]">
          {t('post.pieCuantoQueda', { n: 300 - caption.length })}
        </span>
      </label>

      <label className="block">
        <span className="text-xs font-medium text-[var(--color-ink-muted)]">
          {t('post.losHashtags')}
        </span>
        <input
          value={hashtags}
          onChange={(event) => setHashtags(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setEditando(false);
          }}
          className="mt-1 w-full rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-surface)] px-2 py-1.5 text-sm"
        />
        <span className="text-xs text-[var(--color-ink-muted)]">{t('post.hashtagsComo')}</span>
      </label>

      <label className="block">
        <span className="text-xs font-medium text-[var(--color-ink-muted)]">{t('post.elAlt')}</span>
        <textarea
          rows={2}
          maxLength={1000}
          value={altText}
          onChange={(event) => setAltText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setEditando(false);
          }}
          className="mt-1 w-full rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-surface)] p-2 text-sm"
        />
        <span className="text-xs text-[var(--color-ink-muted)]">{t('post.altComo')}</span>
      </label>

      <div className="flex gap-2">
        <button
          type="button"
          disabled={guardando || caption.trim().length === 0}
          onClick={() => void guardar()}
          className="rounded-lg bg-[var(--color-accent)] px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
        >
          {t(guardando ? 'comun.guardando' : 'comun.guardar')}
        </button>
        <button
          type="button"
          disabled={guardando}
          onClick={() => setEditando(false)}
          className="rounded-lg border border-[var(--color-border-subtle)] px-3 py-1 text-xs disabled:opacity-50"
        >
          {t('comun.cancelar')}
        </button>
      </div>
    </div>
  );
}

type ReconcileReport = {
  publicName: string;
  mediaFound: number;
  linked: { permalink: string | null; topic: string }[];
  relinked: { permalink: string | null; topic: string }[];
  missing: { topic: string }[];
  unmatched: { topic: string }[];
  skipped: string | null;
};

/**
 * Recupera de Instagram las publicaciones que salieron sin quedar registradas.
 *
 * Informa qué vinculó y qué no. Lo segundo importa tanto como lo primero: el emparejamiento
 * es por texto y prefiere no vincular antes que vincular mal, así que lo que queda afuera no
 * es un fallo silencioso sino la parte que hay que mirar a ojo.
 */
function ReconcileBanner({ onChange }: { onChange: () => void }) {
  const { t } = useT();
  const aviso = useAviso();
  const [reports, setReports] = useState<ReconcileReport[] | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const response = await api.post<{ linked: number; reports: ReconcileReport[] }>(
        '/api/reconcile/instagram',
      );
      setReports(response.reports);
      onChange();
    } catch (cause) {
      if (!(cause instanceof ApiRequestError)) aviso.error(t('gal.accionFallo'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-[var(--color-border-subtle)] p-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[var(--color-ink-muted)]">{t('recon.pregunta')}</p>
        <Button variant="secondary" disabled={busy} onClick={() => void run()}>
          {t(busy ? 'recon.consultando' : 'recon.recuperar')}
        </Button>
      </div>

      {reports && (
        <div className="mt-3 space-y-2 text-xs">
          {reports.map((report) => (
            <div key={report.publicName}>
              <p className="font-medium">{report.publicName}</p>
              {report.skipped ? (
                <p className="text-[var(--color-ink-muted)]">{report.skipped}</p>
              ) : (
                <>
                  <p className="text-[var(--color-ink-muted)]">
                    {t('recon.resumen', {
                      encontradas: report.mediaFound,
                      vinculadas: report.linked.length,
                      corregidas: report.relinked.length,
                    })}
                  </p>
                  {report.linked.map((link) => (
                    <p key={link.topic} className="truncate text-[var(--color-ink-muted)]">
                      ✓ {link.topic}
                    </p>
                  ))}
                  {report.relinked.map((link) => (
                    <p key={link.topic} className="truncate text-[var(--color-ink-muted)]">
                      {t('recon.corregido', { tema: link.topic })}
                    </p>
                  ))}
                  {report.missing.map((gone) => (
                    <p key={gone.topic} className="truncate text-[var(--color-warn)]">
                      {t('recon.yaNoEsta', { tema: gone.topic })}
                    </p>
                  ))}
                  {report.unmatched.map((miss) => (
                    <p key={miss.topic} className="truncate text-[var(--color-warn)]">
                      {t('recon.sinEmparejar', { tema: miss.topic })}
                    </p>
                  ))}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

type AccountMetrics = {
  username: string | null;
  accountType: string | null;
  followers: number | null;
  follows: number | null;
  mediaCount: number | null;
  views: number | null;
  reach: number | null;
  interactions: number | null;
  accountsEngaged: number | null;
  windowDays: number;
  fetchedAt: string;
};

/**
 * Las métricas de la cuenta, pedidas a Instagram en el momento.
 *
 * Se cargan solas al abrir Plataformas y sólo si la conexión está activa: sin token no hay
 * nada que leer, y mostrar un botón que siempre falla es peor que no mostrarlo.
 *
 * `null` se dibuja como «—», nunca como cero. Una cuenta nueva sin actividad devuelve nulos
 * en las estadísticas, y escribir «0 visualizaciones» afirmaría algo que nadie midió.
 */
function InstagramMetrics({ creatorId, enabled }: { creatorId: string; enabled: boolean }) {
  const { t } = useT();
  const [metrics, setMetrics] = useState<AccountMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<{ metrics: AccountMetrics }>(
        `/api/creators/${creatorId}/metrics/instagram`,
      );
      setMetrics(response.metrics);
    } catch (cause) {
      // Una lectura que falla no sale como aviso flotante: se muestra donde iba el dato, que
      // es el único lugar donde significa algo. Ver el comentario de `alSalirMal` en `api.ts`.
      setError(cause instanceof ApiRequestError ? cause.payload.message : t('recon.noSePudoLeer'));
    } finally {
      setLoading(false);
    }
  }, [creatorId]);

  useEffect(() => {
    if (enabled) void load();
  }, [enabled, load]);

  if (!enabled) return null;

  const n = (value: number | null): string =>
    value === null ? '—' : value.toLocaleString(idiomaActual());

  return (
    <div className="mt-3 border-t border-[var(--color-border-subtle)] pt-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-[var(--color-ink-muted)]">{t('met.titulo')}</p>
        <button
          type="button"
          disabled={loading}
          onClick={() => void load()}
          className="text-xs text-[var(--color-ink-muted)] underline-offset-2 hover:text-[var(--color-accent)] hover:underline disabled:opacity-50"
        >
          {loading ? 'Leyendo…' : 'Actualizar'}
        </button>
      </div>

      {/*
        Una lectura que falla se muestra acá y no como aviso flotante.

        La diferencia con las acciones es dónde significa algo: un botón que no hizo lo que
        prometió hay que verlo aunque el ojo esté en otra parte; un dato que no llegó se
        entiende en el hueco donde iba a estar. Ver `alSalirMal` en `api.ts`.
      */}
      {error && <p className="mt-2 text-xs text-[var(--color-danger)]">{error}</p>}

      {metrics && (
        <>
          <dl className="mt-2 grid grid-cols-3 gap-2 text-center">
            {[
              { label: t('met.seguidores'), value: metrics.followers },
              { label: t('met.siguiendo'), value: metrics.follows },
              { label: t('met.publicaciones'), value: metrics.mediaCount },
            ].map((stat) => (
              <div
                key={stat.label}
                className="rounded-xl border border-[var(--color-border-subtle)] p-2"
              >
                <dd className="text-sm font-semibold">{n(stat.value)}</dd>
                <dt className="text-[10px] text-[var(--color-ink-muted)]">{stat.label}</dt>
              </div>
            ))}
          </dl>

          <p className="mt-3 text-[10px] text-[var(--color-ink-muted)]">
            {t('met.ultimosDias', { n: metrics.windowDays })}
          </p>
          <dl className="mt-1 space-y-1 text-xs">
            {[
              { label: t('met.visualizaciones'), value: metrics.views },
              { label: t('met.alcance'), value: metrics.reach },
              { label: t('met.interacciones'), value: metrics.interactions },
              { label: t('met.cuentasAlcanzadas'), value: metrics.accountsEngaged },
            ].map((stat) => (
              <div key={stat.label} className="flex justify-between gap-2">
                <dt className="text-[var(--color-ink-muted)]">{stat.label}</dt>
                <dd className="font-medium">{n(stat.value)}</dd>
              </div>
            ))}
          </dl>

          {metrics.views === null && metrics.reach === null && (
            <p className="mt-2 text-[10px] text-[var(--color-ink-muted)]">
              {t('met.sinEstadisticas')}
            </p>
          )}

          <p className="mt-2 text-[10px] text-[var(--color-ink-muted)]">
            {t('met.leido', { cuando: formatDateTime(metrics.fetchedAt) })}
          </p>
        </>
      )}
    </div>
  );
}

/**
 * Publicar de verdad, en dos pasos.
 *
 * Una publicación en Instagram es pública, la ve la audiencia y no se deshace limpiamente.
 * En una fila donde «Exportar» sólo abre un panel, un botón que postea no puede costar el
 * mismo click — el primero arma y muestra qué va a pasar, el segundo dispara.
 *
 * Desarmar está a la vista: cancelar es un click, no la tecla de escape ni pulsar afuera.
 */
/**
 * Escribe la descripción del canal de Telegram y la aplica.
 *
 * ── Por qué acá y no en Tareas ────────────────────────────────────────────────
 * Porque las tareas son pasos de alta: se hacen una vez y se cierran. Esto se va a rehacer —
 * cuando cambie el tono, cuando el texto envejezca— y su sitio natural es al lado del canal, que
 * es lo que describe.
 *
 * ── Por qué se aplica sola ────────────────────────────────────────────────────
 * Porque es texto y se puede volver a generar pulsando otra vez. No es publicar, que sí es
 * irreversible. Lo que quedó escrito se muestra acá mismo: aplicar algo sin decir qué quedó
 * obliga a ir a Telegram para enterarse.
 */
/*
  Los dos textos del canal, que no son el mismo texto dicho dos veces.

  La DESCRIPCION la lee quien todavia no entro —esta en la ficha del canal, antes de suscribirse—
  y por eso no puede pedir que se unan. El MENSAJE ANCLADO lo lee quien ya entro, y por eso si
  puede: lo que sigue despues de suscribirse es abrir lo que hay.

  Un canal de Telegram no puede saludar a quien se suscribe —eso son los grupos, donde entrar deja
  una entrada que un bot contesta—, asi que anclar es lo mas parecido a una bienvenida que existe.
*/
function TextoDelCanal({
  creatorId,
  ruta,
  campo,
  claves,
}: {
  creatorId: string;
  ruta: string;
  campo: 'descripcion' | 'mensaje';
  claves: { escribir: Clave; otraVez: Clave; explica: Clave; error: Clave };
}) {
  const { t } = useT();
  const aviso = useAviso();
  const [busy, setBusy] = useState(false);
  const [escrito, setEscrito] = useState<string | null>(null);
  const [sinAnclar, setSinAnclar] = useState(false);

  return (
    <div className="mt-3">
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          void api
            .post<{ descripcion?: string; mensaje?: string; anclado?: boolean }>(
              `/api/creators/${creatorId}/telegram/${ruta}`,
            )
            .then((respuesta) => {
              setEscrito(respuesta[campo] ?? null);
              setSinAnclar(respuesta.anclado === false);
            })
            .catch((causa: unknown) => {
              if (!(causa instanceof ApiRequestError)) aviso.error(t(claves.error));
            })
            .finally(() => setBusy(false));
        }}
        className="rounded-lg border border-[var(--color-border-subtle)] px-3 py-2 text-sm hover:border-[var(--color-accent)] disabled:opacity-40"
      >
        {t(busy ? 'tg.escribiendo' : escrito ? claves.otraVez : claves.escribir)}
      </button>
      <p className="mt-1.5 text-xs text-[var(--color-ink-muted)]">{t(claves.explica)}</p>

      {escrito && (
        <p className="mt-2 whitespace-pre-line rounded-lg bg-[var(--color-surface-sunken)] p-2.5 text-xs italic">
          {escrito}
        </p>
      )}

      {/*
        Publicado pero sin anclar no es un fallo: el mensaje ya esta en el canal.

        Son dos permisos distintos y el bot puede tener uno solo. Mostrarlo como error haria
        pensar que no se publico nada y llevaria a pulsar de nuevo, que publicaria un segundo
        mensaje igual de suelto.
      */}
      {sinAnclar && <p className="mt-2 text-xs text-[var(--color-warning)]">{t('tg.sinAnclar')}</p>}
    </div>
  );
}

function TextosDelCanal({ creatorId }: { creatorId: string }) {
  return (
    <div className="mt-3 border-t border-[var(--color-border-subtle)] pt-3">
      <TextoDelCanal
        creatorId={creatorId}
        ruta="bienvenida"
        campo="descripcion"
        claves={{
          escribir: 'tg.escribir',
          otraVez: 'tg.otraVez',
          explica: 'tg.escribirQue',
          error: 'tg.noSePudo',
        }}
      />
      <TextoDelCanal
        creatorId={creatorId}
        ruta="mensaje-fijado"
        campo="mensaje"
        claves={{
          escribir: 'tg.fijar',
          otraVez: 'tg.fijarOtra',
          explica: 'tg.fijarQue',
          error: 'tg.noSeFijo',
        }}
      />
    </div>
  );
}

/** Un destino que todavia se puede publicar, para el selector de varias. */
type DestinoPendiente = {
  platform: string;
  /** Lo que la variante guardada declara: en Telegram, si sale detras del muro. */
  dePago: boolean;
  /** Si en esta plataforma se puede elegir regalarla. Hoy solo Telegram. */
  puedeRegalar: boolean;
};

/**
 * Publicar la misma toma en varios destinos, con una sola decision.
 *
 * ── El problema ───────────────────────────────────────────────────────────────
 * Cada destino tenia su boton y cada boton pedia confirmacion, asi que una publicacion con tres
 * destinos costaba seis pulsaciones repartidas por la tarjeta. Con Telegram enchufado eso dejo
 * de ser una molestia y paso a ser una fuente de olvidos: se publicaba en Fanvue, se seguia
 * trabajando, y Telegram quedaba sin salir sin que nada lo dijera.
 *
 * ── Por que marcadas todas por omision ────────────────────────────────────────
 * Porque el caso frecuente es ese, y porque el error de olvidarse un destino es silencioso —no
 * pasa nada, la toma simplemente no aparece nunca— mientras que el de publicar en uno de mas se
 * ve enseguida. Desmarcar es un clic; acordarse de un destino que no se ofrecio no es nada.
 *
 * ── Por que igual hay confirmacion ────────────────────────────────────────────
 * Publicar es lo unico irreversible de esta pantalla, y aca lo es por varias a la vez. La
 * confirmacion NOMBRA los destinos y dice cuales cobran: es el ultimo momento en que se puede
 * ver que Telegram iba a salir gratis antes de que lo vea todo el canal.
 *
 * ── Por que en fila y no todas juntas ─────────────────────────────────────────
 * Igual que los lotes que ya existian, y por lo mismo: cada publicacion sube media y tarda,
 * mandarlas juntas multiplica la chance de que una caiga por tiempo, y lo que falla despues de
 * que otra salio no se puede deshacer.
 */
function PublicarEnVarias({
  contentItemId,
  destinos,
  disabled,
  precioEnStars,
  onHecho,
}: {
  contentItemId: string;
  destinos: DestinoPendiente[];
  disabled: boolean;
  precioEnStars: number;
  onHecho: () => void;
}) {
  const { t } = useT();
  const aviso = useAviso();
  const [desmarcadas, setDesmarcadas] = useState<string[]>([]);
  const [regala, setRegala] = useState<string[]>([]);
  const [armado, setArmado] = useState(false);
  const [avance, setAvance] = useState<{ hechas: number; total: number } | null>(null);
  const [fallos, setFallos] = useState<string[]>([]);

  /*
    Se guarda lo DESMARCADO, no lo marcado.

    La lista de destinos cambia sola: en cuanto uno sale deja de estar pendiente y desaparece.
    Con una lista de marcados habria que sincronizarla con cada cambio —y un destino nuevo
    aparecería sin marcar, que es justo el olvido que este selector viene a evitar—. Guardando
    lo desmarcado, lo que aparece entra marcado y lo que se fue no deja nada atras.
  */
  const elegidos = destinos.filter((destino) => !desmarcadas.includes(destino.platform));

  const comoSale = (destino: DestinoPendiente): string =>
    !destino.puedeRegalar
      ? ''
      : regala.includes(destino.platform) || !destino.dePago
        ? t('multi.gratis')
        : t('multi.conStars', { stars: precioEnStars });

  const publicar = async (): Promise<void> => {
    setArmado(false);
    setFallos([]);
    setAvance({ hechas: 0, total: elegidos.length });

    const malos: string[] = [];
    let salieron = 0;
    let manuales = 0;

    for (const destino of elegidos) {
      const gratis = destino.puedeRegalar && regala.includes(destino.platform);
      try {
        const respuesta = await api.post<{ result: PublishOutcome }>(
          `/api/content/${contentItemId}/publish?platform=${destino.platform}` +
            (gratis ? '&gratis=1' : ''),
        );
        /*
          «Paquete manual» no es un fallo: es que ese destino no tenia como publicar solo.

          Contarlo entre los fallos —como hace el lote viejo— haria buscar un error que no
          existe, y peor, taparia que hay un paquete esperando que alguien lo suba a mano.
        */
        if (respuesta.result.status === 'PUBLISHED') salieron += 1;
        else manuales += 1;
      } catch (causa) {
        malos.push(
          `${destino.platform}: ${causa instanceof Error ? causa.message : String(causa)}`,
        );
      }
      setAvance({ hechas: salieron + manuales + malos.length, total: elegidos.length });
    }

    setAvance(null);
    setFallos(malos);
    onHecho();

    if (malos.length > 0) {
      aviso.error(t('multi.noSalieron', { n: salieron, fallos: malos.length }));
    } else if (manuales > 0) {
      aviso.ok(t('multi.salieronConManual', { n: salieron, manual: manuales }));
    } else {
      aviso.ok(t('multi.salieron', { n: salieron }));
    }
  };

  // Con un solo destino el boton de siempre ya alcanza, y una lista de uno es ruido.
  if (destinos.length < 2) return null;

  return (
    <div className="mt-3 rounded-xl border border-[var(--color-border-subtle)] p-3">
      <p className="text-xs font-semibold uppercase">{t('multi.titulo')}</p>
      <p className="mt-1 text-xs text-[var(--color-ink-muted)]">{t('multi.explica')}</p>

      <div className="mt-2 space-y-1.5">
        {destinos.map((destino) => {
          const marcado = !desmarcadas.includes(destino.platform);
          return (
            <div key={destino.platform} className="text-xs">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={marcado}
                  disabled={avance !== null}
                  onChange={() =>
                    setDesmarcadas((antes) =>
                      marcado
                        ? [...antes, destino.platform]
                        : antes.filter((cual) => cual !== destino.platform),
                    )
                  }
                />
                <span className="font-medium uppercase">{destino.platform}</span>
                {comoSale(destino) !== '' && (
                  <span className="text-[var(--color-ink-muted)]">{comoSale(destino)}</span>
                )}
              </label>

              {/*
                La eleccion de cobrar o regalar vive DENTRO de la fila de su destino.

                En los botones sueltos son dos botones distintos porque ahi cada uno se pulsa y
                pasa; aca no hay pulsacion por destino, asi que la eleccion tiene que estar donde
                se ve a que destino pertenece. Y sale escrita arriba, al lado del nombre, para
                que la confirmacion no sea el primer lugar donde se lee.
              */}
              {marcado && destino.puedeRegalar && destino.dePago && (
                <label className="mt-1 ml-6 flex items-center gap-2 text-[var(--color-ink-muted)]">
                  <input
                    type="checkbox"
                    checked={regala.includes(destino.platform)}
                    disabled={avance !== null}
                    onChange={() =>
                      setRegala((antes) =>
                        antes.includes(destino.platform)
                          ? antes.filter((cual) => cual !== destino.platform)
                          : [...antes, destino.platform],
                      )
                    }
                  />
                  {t('multi.regalar')}
                </label>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-3">
        {avance !== null ? (
          <span className="text-xs text-[var(--color-ink-muted)]">
            {t('multi.publicando', { hechas: avance.hechas, total: avance.total })}
          </span>
        ) : armado ? (
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-[var(--color-ink-muted)]">
              {t('multi.seVanA', {
                lista: elegidos
                  .map((destino) =>
                    comoSale(destino) === ''
                      ? destino.platform
                      : `${destino.platform} (${comoSale(destino)})`,
                  )
                  .join(', '),
              })}
            </span>
            <Button onClick={() => void publicar()}>{t('post.confirmar')}</Button>
            <Button variant="secondary" onClick={() => setArmado(false)}>
              {t('comun.cancelar')}
            </Button>
          </span>
        ) : (
          <Button
            variant="secondary"
            disabled={disabled || elegidos.length === 0}
            title={elegidos.length === 0 ? t('multi.elegiUna') : undefined}
            onClick={() => setArmado(true)}
          >
            {t('multi.publicarEn', { n: elegidos.length })}
          </Button>
        )}
      </div>

      {fallos.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-xs text-[var(--color-danger)]">
          {fallos.map((fallo) => (
            <li key={fallo}>{fallo}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PublishButton({
  armed,
  busy,
  disabled,
  reason,
  label,
  onArm,
  onCancel,
  onPublish,
}: {
  armed: boolean;
  busy: boolean;
  disabled: boolean;
  reason?: string;
  /**
   * Qué dice el botón, cuando «Publicar ahora» no alcanza.
   *
   * En Telegram hay dos botones para el mismo destino —con Stars y gratis— y dos botones que
   * dicen lo mismo son dos formas de pulsar el equivocado. En el resto no hay elección, así que
   * el rótulo por omisión sigue sirviendo.
   */
  label?: string;
  onArm: () => void;
  onCancel: () => void;
  onPublish: () => void;
}) {
  const { t } = useT();
  if (busy) {
    return <Button disabled>{t('post.publicando')}</Button>;
  }

  if (!armed) {
    return (
      <Button variant="secondary" disabled={disabled} onClick={onArm} title={reason}>
        {label ?? t('post.publicarAhora')}
      </Button>
    );
  }

  return (
    <span className="flex items-center gap-2">
      <span className="text-xs text-[var(--color-ink-muted)]">{t('post.sePublicaDeVerdad')}</span>
      <Button onClick={onPublish}>{t('post.confirmar')}</Button>
      <Button variant="secondary" onClick={onCancel}>
        {t('comun.cancelar')}
      </Button>
    </span>
  );
}

function PlatformsTab({ data, onChange }: { data: CreatorDetailData; onChange: () => void }) {
  const { t } = useT();
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {data.accounts.map((account) => {
        const connection = data.connections.find(
          (candidate) => candidate.platform === account.platform,
        );

        return (
          <Card key={account.platform} title={account.platform}>
            <dl className="space-y-2 text-sm">
              {/*
                El usuario se corrige acá, no reabriendo la tarea.

                Se carga completando «Crear la cuenta», y esa tarjeta desaparece al terminarla:
                una errata quedaba grabada sin forma de arreglarla desde el panel. El dato no
                vive en la tarea sino en la cuenta — la tarea fue el momento en que se capturó.
              */}
              {/*
                En Fanvue el usuario no se escribe: lo confirma Fanvue.

                El handle de una creadora viene con el perfil al vincularlo, y se muestra más
                abajo con el `@`. Un campo editable acá crearía una segunda fuente para el mismo
                dato, y en cuanto las dos dejen de coincidir el panel diría una cosa y Fanvue
                otra — sin que nada falle y sin forma de saber cuál es la buena.

                En Instagram y Facebook sí se escribe: ahí el usuario se elige al crear la cuenta
                a mano y nadie nos lo confirma después.
              */}
              {account.platform === 'fanvue' ? (
                <Field
                  label={t('plat2.usuario')}
                  value={account.username ?? t('plat2.loConfirmaFanvue')}
                />
              ) : (
                <EditableField
                  label={t('plat2.usuario')}
                  value={account.username}
                  placeholder={t('plat2.sinCrear')}
                  onSave={(nuevo) =>
                    api
                      .patch(`/api/creators/${data.creator.id}/platforms/${account.platform}`, {
                        username: nuevo,
                      })
                      .then(onChange)
                  }
                />
              )}
              <Field label={t('plat2.estado')} value={account.status} />
              <EditableField
                label={t('plat2.correo')}
                value={account.emailAddress}
                placeholder="—"
                onSave={(nuevo) =>
                  api
                    .patch(`/api/creators/${data.creator.id}/platforms/${account.platform}`, {
                      emailAddress: nuevo,
                    })
                    .then(onChange)
                }
              />
              {account.emailDeliversTo && (
                <Field label={t('plat2.llegaA')} value={account.emailDeliversTo} />
              )}

              {/*
                Asignar la casilla propia, si todavía no tiene.

                Fanvue exige una dirección real y distinta por cuenta, y rechaza los alias con
                «+» —medido—, así que la estrategia de sufijos que sirve en Instagram no sirve
                ahí. Ésta vive en el dominio del panel y su correo se lee acá mismo.

                Es un botón y no algo que pase solo al abrir la ficha porque la dirección es
                PERMANENTE: Fanvue no deja cambiarla nunca. Que haya que pulsarlo la convierte
                en una decisión en vez de un efecto de mirar una pantalla.
              */}
              {account.platform === 'fanvue' && faltaCasillaDeFanvue(account) && (
                <AsignarCorreo creatorId={data.creator.id} onChange={onChange} />
              )}
              {/*
                Telegram no tiene OAuth, y «sin conectar» seria mentir.

                Publica un bot que es administrador del canal, con un token que vive en
                Credenciales y sirve para todas las influencers. Con la etiqueta de OAuth, esta
                fila decia «Not connected» para siempre sobre algo que no se conecta nunca — y
                mandaba a buscar un boton que no existe.
              */}
              <Field
                label={t(
                  account.platform === 'fanvue'
                    ? 'plat2.administracion'
                    : account.platform === 'telegram'
                      ? 'plat2.publicaCon'
                      : 'plat2.conexionOauth',
                )}
                value={
                  account.platform === 'fanvue'
                    ? t('plat2.conexionGeneral')
                    : account.platform === 'telegram'
                      ? t('plat2.botCompartido')
                      : connection
                        ? t('plat2.conScopes', {
                            estado: connection.status,
                            n: connection.scopes.length,
                          })
                        : t('plat2.sinConectar')
                }
              />
            </dl>

            {account.suggestedUsernames.length > 0 && !account.username && (
              <div className="mt-3">
                <p className="text-xs font-medium text-[var(--color-ink-muted)]">
                  {t('plat2.usuariosSugeridos')}
                </p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {account.suggestedUsernames.map((username) => (
                    <span
                      key={username}
                      className="rounded bg-[var(--color-accent-soft)] px-2 py-0.5 text-xs"
                    >
                      {username}
                    </span>
                  ))}
                </div>
                <p className="mt-2 text-xs text-[var(--color-ink-muted)]">
                  {t('plat2.disponibilidad')}
                </p>
              </div>
            )}

            {account.platform === 'instagram' && (
              <InstagramMetrics
                creatorId={data.creator.id}
                enabled={connection?.status === 'ACTIVE'}
              />
            )}

            {/*
              El bloque de conexión, para las plataformas que tienen OAuth.

              Estaba escrito para Instagram y así se quedó cuando llegó Facebook: la tarea de
              alta decía «pulsá Conectar Facebook» y ese botón no existía en ninguna pantalla.
              Es la misma clase de defecto que la lista de plataformas escrita a mano — algo se
              agrega en un lado y no llega al otro.
            */}
            {(account.platform === 'instagram' || account.platform === 'facebook') && (
              <ConexionOAuth
                creatorId={data.creator.id}
                platform={account.platform}
                connected={connection?.status === 'ACTIVE'}
                canPublish={
                  connection?.scopes.includes(PERMISO_DE_CONECTAR[account.platform] ?? '') ?? false
                }
                onChange={onChange}
              />
            )}

            {account.platform === 'fanvue' && (
              <FanvueProfileLink creatorId={data.creator.id} onChange={onChange} />
            )}

            {/*
              El banner del perfil, acá y no dentro de la tarea de alta.

              La portada de Facebook vive en su tarjeta de tarea porque se necesita una vez, al
              crear la Página. El banner de Fanvue no: es la primera imagen que ve alguien que
              llega al perfil, se cambia cuando se quiere, y la tarea de alta de una influencer
              que ya está en Fanvue hace meses está cerrada. Puesto ahí habría sido un botón que
              nadie vuelve a ver.
            */}
            {account.platform === 'fanvue' && (
              <GenerarPortada
                creatorId={data.creator.id}
                onChange={onChange}
                formato="fanvue"
                hecho={bannerDeFanvueDe(data)}
              />
            )}

            {/* Sin canal no hay nada que describir, y el botón fallaría al pulsarlo. */}
            {account.platform === 'telegram' && (account.username ?? '').trim().length > 0 && (
              <TextosDelCanal creatorId={data.creator.id} />
            )}

            {/*
              Ni una Página ni un canal tienen contraseña: los administra un perfil que sí la tiene.

              Mostrar el campo invitaría a pegar la contraseña de una persona real en un lugar
              pensado para cuentas de influencer. En Facebook la API además lo rechaza; en Telegram
              no hay ni siquiera a qué rechazárselo — el canal se crea desde una cuenta que ya
              existe, y lo único que el sistema necesita de él es su nombre.
            */}
            {account.platform !== 'facebook' && account.platform !== 'telegram' && (
              <AccountPassword
                creatorId={data.creator.id}
                platform={account.platform}
                hasPassword={account.hasPassword}
                updatedAt={account.passwordUpdatedAt}
                onChange={onChange}
              />
            )}
          </Card>
        );
      })}
    </div>
  );
}

/**
 * Conectar y desconectar Instagram.
 *
 * Conectar **navega**, no hace `fetch`: el destino es la pantalla de Instagram, y el
 * navegador tiene que llegar ahí con su propia sesión. Pedirlo por `fetch` devolvería el
 * HTML del login de Meta dentro de una respuesta que nadie puede mostrar.
 *
 * La distinción entre «conectada» y «puede publicar» no es un detalle: la app de Meta puede
 * conceder el inicio de sesión y no el permiso de publicar. En ese estado todo se ve bien y
 * las publicaciones salen como paquete manual sin que nadie sepa por qué.
 */
/**
 * Conectar y desconectar una plataforma por OAuth.
 *
 * Sirve para Instagram y para Facebook: el flujo es el mismo —ir a Meta, volver con el
 * resultado en la barra de direcciones— y lo único que cambia es el nombre y qué permiso hace
 * falta para publicar. Tenerlo una sola vez es lo que evita que la próxima plataforma llegue a
 * la mitad de las pantallas.
 */
/**
 * Elegir a qué Página de Facebook conectar esta influencer.
 *
 * Aparece sólo cuando el perfil administra varias y la autorización quedó a medias: con una
 * sola no hay nada que preguntar y la conexión termina sola.
 */
function ElegirPagina({ creatorId, onChange }: { creatorId: string; onChange: () => void }) {
  const { t } = useT();
  const aviso = useAviso();
  const [pages, setPages] = useState<{ id: string; nombre: string }[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ pages: { id: string; nombre: string }[] }>(
        `/api/integrations/facebook/pages?creatorId=${creatorId}`,
      )
      .then((r) => setPages(r.pages))
      .catch(() => setPages([]));
  }, [creatorId]);

  if (!pages)
    return <p className="text-xs text-[var(--color-ink-muted)]">{t('plat2.buscandoPaginas')}</p>;

  return (
    <div className="mb-3 rounded-xl border border-[var(--color-border-subtle)] p-3">
      <p className="text-xs font-medium">{t('plat2.cualPagina')}</p>
      <p className="mt-0.5 mb-2 text-xs text-[var(--color-ink-muted)]">
        {t('plat2.cualPaginaQue')}
      </p>
      <div className="flex flex-wrap gap-2">
        {pages.map((page) => (
          <Button
            key={page.id}
            variant="secondary"
            disabled={busy !== null}
            onClick={() => {
              setBusy(page.id);
              api
                .post(`/api/integrations/facebook/pages?creatorId=${creatorId}`, {
                  pageId: page.id,
                })
                .then(() => {
                  aviso.ok(t('plat2.conectadaA', { pagina: page.nombre }));
                  onChange();
                })
                .catch((cause: unknown) => {
                  if (!(cause instanceof ApiRequestError)) aviso.error(t('plat2.noSePudoConectar'));
                })
                .finally(() => setBusy(null));
            }}
          >
            {busy === page.id ? t('plat2.conectando') : page.nombre}
          </Button>
        ))}
      </div>
    </div>
  );
}

type FanvueProfile = {
  uuid: string;
  handle: string;
  displayName: string;
  nickname: string | null;
  avatarUrl: string | null;
};

type FanvueProfilesResponse = {
  connected: boolean;
  creators: FanvueProfile[];
  links: Array<{
    creatorId: string;
    fanvueCreatorUuid: string;
    handle: string;
    displayName: string;
    avatarUrl: string | null;
  }>;
  current: {
    creatorId: string;
    fanvueCreatorUuid: string;
    handle: string;
    displayName: string;
    avatarUrl: string | null;
  } | null;
};

/** Asigna un perfil remoto a la influencer; el OAuth sigue siendo uno solo, el del manager. */
function FanvueProfileLink({ creatorId, onChange }: { creatorId: string; onChange: () => void }) {
  const { t } = useT();
  const state = useApi<FanvueProfilesResponse>(
    `/api/integrations/fanvue/creators?creatorId=${creatorId}`,
  );
  const aviso = useAviso();
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState(false);

  if (state.loading && !state.data) return <Loading label={t('plat2.consultandoFanvue')} />;
  if (state.error) return <ErrorBanner error={state.error} onRetry={state.reload} />;
  if (!state.data?.connected) {
    return (
      <div className="mt-3 border-t border-[var(--color-border-subtle)] pt-3 text-sm">
        {t('plat2.primeroManagerAntes')}
        <Link to="/credenciales" className="underline underline-offset-2">
          {t('fanvue.credenciales')}
        </Link>
        .
      </div>
    );
  }

  const usedElsewhere = new Set(
    state.data.links
      .filter((link) => link.creatorId !== creatorId)
      .map((link) => link.fanvueCreatorUuid),
  );
  const options = state.data.creators.filter((profile) => !usedElsewhere.has(profile.uuid));

  const link = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post('/api/integrations/fanvue/creators/link', {
        creatorId,
        fanvueCreatorUuid: selected,
      });
      aviso.ok(t('plat2.perfilAsignado'));
      state.reload();
      onChange();
    } catch (cause) {
      if (!(cause instanceof ApiRequestError)) aviso.error(t('plat2.noSePudoAsignar'));
    } finally {
      setBusy(false);
    }
  };

  const unlink = async () => {
    setBusy(true);
    try {
      await api.post('/api/integrations/fanvue/creators/unlink', { creatorId });
      aviso.ok(t('plat2.perfilDesasignado'));
      setSelected('');
      state.reload();
      onChange();
    } catch (cause) {
      if (!(cause instanceof ApiRequestError)) aviso.error(t('plat2.noSePudoDesasignar'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 border-t border-[var(--color-border-subtle)] pt-3">
      {state.data.current ? (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <div>
            <p className="font-medium">✓ {state.data.current.displayName}</p>
            <p className="text-xs text-[var(--color-ink-muted)]">
              {t('plat2.administradaPor', { handle: state.data.current.handle })}
            </p>
          </div>
          <Button variant="secondary" disabled={busy} onClick={() => void unlink()}>
            {t(busy ? 'plat2.quitando' : 'plat2.desasignar')}
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-[var(--color-ink-muted)]">{t('plat2.elegiCual')}</p>
          <div className="flex flex-wrap gap-2">
            <select
              value={selected}
              onChange={(event) => setSelected(event.target.value)}
              className="min-w-48 flex-1 rounded-lg border border-[var(--color-border-subtle)] bg-transparent px-3 py-1.5 text-sm"
            >
              <option value="">{t('plat2.elegirPerfil')}</option>
              {options.map((profile) => (
                <option key={profile.uuid} value={profile.uuid}>
                  {profile.displayName} (@{profile.handle})
                </option>
              ))}
            </select>
            <Button disabled={!selected || busy} onClick={() => void link()}>
              {t(busy ? 'plat2.asignando' : 'plat2.asignar')}
            </Button>
          </div>
          {options.length === 0 && (
            /*
              Dos situaciones distintas que decían lo mismo.

              «No quedan perfiles libres» sólo es cierto cuando el manager administra creadoras y
              todas están ya asignadas. Cuando no administra ninguna —el caso normal al empezar,
              porque la cuenta de la influencer todavía no existe o no invitó al manager— ese
              mensaje manda a buscar un perfil libre que nunca hubo, en vez de decir qué falta.
            */
            <p className="text-xs text-[var(--color-danger)]">
              {state.data.creators.length === 0
                ? t('plat2.sinCreadoras')
                : t('plat2.todasAsignadas')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * El permiso que hay que tener otorgado para que la conexión sirva de algo.
 *
 * Es OTRO mapa que `PERMISO_DE_PUBLICAR`, y la diferencia importa: aquél gobierna el botón
 * «Publicar ahora» y Fanvue no está porque su publicación todavía no está implementada. Éste
 * gobierna el cartel de la conexión, y ahí Fanvue sí va — se puede conectar y conviene saber si
 * la autorización quedó completa.
 *
 * Unirlos haría aparecer un botón de publicar que caería en el paquete manual sin avisar.
 */
const PERMISO_DE_CONECTAR: Record<string, string> = {
  instagram: 'instagram_business_content_publish',
  facebook: 'pages_manage_posts',
};

const NOMBRE_DE_PLATAFORMA: Record<string, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  fanvue: 'Fanvue',
};

/**
 * Asigna la casilla propia de una influencer, y muestra lo que le llegue.
 *
 * La dirección vive en el dominio del panel y su correo entra por el manejador del Worker, así
 * que la confirmación de alta de Fanvue aparece acá al lado de la tarea que la pide — en vez de
 * en una bandeja que hay que ir a buscar.
 */
function AsignarCorreo({ creatorId, onChange }: { creatorId: string; onChange: () => void }) {
  const { t } = useT();
  const aviso = useAviso();
  const [busy, setBusy] = useState(false);

  return (
    <div className="sm:col-span-2">
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          if (!window.confirm(t('plat2.confirmarCasilla'))) {
            return;
          }

          setBusy(true);
          void api
            .post<{ direccion: string }>(`/api/creators/${creatorId}/correo`, {})
            .then((r) => aviso.ok(t('plat2.casillaAsignada', { direccion: r.direccion })))
            .then(onChange)
            .catch(() => undefined)
            .finally(() => setBusy(false));
        }}
        className="rounded-lg border border-[var(--color-border-subtle)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)] disabled:opacity-40"
      >
        {t(busy ? 'plat2.asignando' : 'plat2.asignarCasilla')}
      </button>
      <p className="mt-1.5 text-xs text-[var(--color-ink-muted)]">
        {t('plat2.casillaQueAntes')}
        <strong>{t('plat2.rechazaAlias')}</strong>
        {t('plat2.casillaQueMedio')}
        <strong>{t('plat2.noTocaOtras')}</strong>.
      </p>
    </div>
  );
}

/**
 * Todo lo del correo de una influencer: su dirección y lo que le llegó.
 *
 * ── Por qué es una pestaña y ya no vivía con Tareas ───────────────────────────
 * Vivía ahí con un argumento bueno: el único motivo para mirar este correo es completar un alta,
 * y la confirmación llega mientras se está en esa pantalla. Lo que ese argumento no cubría es
 * cambiar la dirección — que no tiene nada que ver con una tarea y no tenía dónde vivir.
 *
 * Para no perder aquello, Tareas conserva un aviso cuando hay correo sin mirar.
 */
function CorreoTab({ data, onChange }: { data: CreatorDetailData; onChange: () => void }) {
  const { t } = useT();
  const state = useApi<{ correos: CorreoView[] }>(`/api/creators/${data.creator.id}/correo`);
  const aviso = useAviso();
  const [local, setLocal] = useState('');
  const [armado, setArmado] = useState(false);
  const [busy, setBusy] = useState(false);

  const actual = data.direccionDeCorreo ?? '';
  const dominio = data.dominioDeCorreo ?? '';

  const cambiar = async (): Promise<void> => {
    setBusy(true);
    try {
      const { direccion } = await api.post<{ direccion: string }>(
        `/api/creators/${data.creator.id}/correo/cambiar`,
        { local: local.trim().toLowerCase() },
      );
      setLocal('');
      setArmado(false);
      aviso.ok(t('correo.ahoraSuDireccion', { direccion }));
      onChange();
    } catch (caught) {
      if (!(caught instanceof ApiRequestError)) aviso.error(t('correo.noSePudoCambiar'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card title={t('correo.suDireccion')}>
        <div className="flex flex-wrap items-center gap-2">
          <code className="min-w-0 flex-1 rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-surface-raised)] px-3 py-2 font-mono text-sm break-all">
            {actual || t('correo.todaviaNoTiene')}
          </code>
          {actual && <CopyButton text={actual} label={t('comun.copiar')} />}
        </div>

        {/*
          Cambiarla pide confirmación, y no por ceremonia.

          El manejador de correo resuelve por dirección contra las filas guardadas: en cuanto
          cambia, lo que llegue a la vieja se RECHAZA con un rebote. Si hay un alta a medias
          —un enlace de confirmación en camino— se pierde, y no hay forma de recuperarlo.
        */}
        <div className="mt-4 flex flex-wrap items-end gap-2">
          <label className="flex-1">
            <span className="text-xs text-[var(--color-ink-muted)]">
              {t('correo.cambiarlaPor')}
            </span>
            <div className="mt-1 flex items-center gap-1">
              <input
                value={local}
                onChange={(event) => setLocal(event.target.value)}
                placeholder={t('correo.nombre')}
                autoComplete="off"
                className="min-w-0 flex-1 rounded-lg border border-[var(--color-border-subtle)] bg-transparent px-3 py-1.5 text-sm"
              />
              <span className="text-sm text-[var(--color-ink-muted)]">@{dominio}</span>
            </div>
          </label>

          {armado ? (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() => void cambiar()}
                className="rounded-lg border border-[var(--color-danger)] px-3 py-1.5 text-sm text-[var(--color-danger)] disabled:opacity-40"
              >
                {t(busy ? 'correo.cambiando' : 'correo.siCambiarla')}
              </button>
              <button
                type="button"
                onClick={() => setArmado(false)}
                className="rounded-lg border border-[var(--color-border-subtle)] px-3 py-1.5 text-sm"
              >
                {t('comun.cancelar')}
              </button>
            </>
          ) : (
            <button
              type="button"
              disabled={local.trim().length < 3}
              onClick={() => setArmado(true)}
              className="rounded-lg border border-[var(--color-border-subtle)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)] disabled:opacity-40"
            >
              {t('estudio.cambiar')}
            </button>
          )}
        </div>

        <p className="mt-2 text-xs text-[var(--color-ink-muted)]">
          {armado ? t('correo.armadoQue') : t('correo.enReposoQue')}
        </p>
      </Card>

      {state.data && state.data.correos.length > 0 ? (
        <Card title={t('correo.recibido', { n: state.data.correos.length })}>
          <ListaDeCorreos correos={state.data.correos} onChange={() => state.reload()} />
          <p className="mt-3 text-xs text-[var(--color-ink-muted)]">{t('correo.seConserva')}</p>
        </Card>
      ) : (
        <EmptyState title={t('correo.nadaAun')} description={t('correo.nadaAunQue')} />
      )}
    </div>
  );
}

/**
 * Que llegó correo, sin traerse la bandeja entera a Tareas.
 *
 * La bandeja vivía acá porque la confirmación de un alta llega mientras se está en esta pantalla,
 * y en otra pestaña hay que acordarse de ir a buscarla. Esto conserva ese aviso —lo único que
 * importaba— y deja el correo donde se puede además cambiar la dirección.
 */
function AvisoDeCorreo({ creatorId }: { creatorId: string }) {
  const { t } = useT();
  const state = useApi<{ correos: CorreoView[] }>(`/api/creators/${creatorId}/correo`);

  if (!state.data || state.data.correos.length === 0) return null;

  return (
    <p className="rounded-xl border border-[var(--color-accent)]/40 bg-[var(--color-accent-soft)] p-2.5 text-xs">
      {t('correo.llegaronAntes', { n: state.data.correos.length })}
      <strong>{t('tab.correo')}</strong>.
    </p>
  );
}

function ConexionOAuth({
  creatorId,
  platform,
  connected,
  canPublish,
  onChange,
}: {
  creatorId: string;
  platform: string;
  connected: boolean;
  canPublish: boolean;
  onChange: () => void;
}) {
  const { t } = useT();
  const nombre = NOMBRE_DE_PLATAFORMA[platform] ?? platform;
  const permiso = PERMISO_DE_CONECTAR[platform] ?? '';
  const aviso = useAviso();
  const [busy, setBusy] = useState(false);

  // El resultado del OAuth vuelve por la barra de direcciones, porque el callback es un
  // redirect del navegador. Se lee una vez y se limpia, para que recargar no repita el aviso.
  const [outcome, setOutcome] = useState<string | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('oauth');
    if (!result) return;

    /*
      El resultado es de UNA plataforma, y hay que mirar cuál.

      Con Instagram sola no hacía falta. Con dos, los dos bloques leían el mismo parámetro y el
      error de Facebook aparecía bajo «Instagram: …» — mandando a revisar la integración
      equivocada, que es peor que no mostrar nada.

      El callback ya manda `platform` en la vuelta; sólo faltaba mirarlo.
    */
    if (params.get('platform') !== platform) return;

    setOutcome(params.get('detalle') ?? result);
    window.history.replaceState({}, '', window.location.pathname);
  }, [platform]);

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.post(`/api/integrations/${platform}/disconnect?creatorId=${creatorId}`);
      onChange();
    } catch (cause) {
      if (!(cause instanceof ApiRequestError)) aviso.error(t('oauth.noSePudoDesconectar'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 border-t border-[var(--color-border-subtle)] pt-3">
      {outcome && (
        <p className="mb-2 text-xs text-[var(--color-ink-muted)]">
          {outcome === 'ok' && t('oauth.conectada')}
          {outcome === 'cancelled' && t('oauth.cancelada')}
          {outcome === 'sin-permiso-publicar' && t('oauth.sinPermiso', { permiso })}
          {!['ok', 'cancelled', 'sin-permiso-publicar'].includes(outcome) &&
            t('oauth.otro', { plataforma: nombre, resultado: outcome })}
        </p>
      )}

      {connected && !canPublish && (
        <p className="mb-2 text-xs text-[var(--color-danger)]">{t('oauth.sinPermisoAviso')}</p>
      )}

      {/*
        La elección de Página, cuando la autorización quedó a medias.

        Antes esto era un error con la lista de Páginas y un número para copiar a mano. Funcionaba
        y era un rodeo: el sistema ya tenía la lista y la hacía transcribir.
      */}
      {platform === 'facebook' && outcome === 'elegir-pagina' && (
        <ElegirPagina creatorId={creatorId} onChange={onChange} />
      )}

      {connected ? (
        <Button variant="secondary" onClick={disconnect} disabled={busy}>
          {t('oauth.desconectar', { plataforma: nombre })}
        </Button>
      ) : (
        <Button
          onClick={() => {
            window.location.href = `/api/integrations/${platform}/connect?creatorId=${creatorId}`;
          }}
        >
          {t('oauth.conectar', { plataforma: nombre })}
        </Button>
      )}
    </div>
  );
}

/**
 * Contraseña de una cuenta de plataforma.
 *
 * ── Por qué el panel guarda contraseñas ─────────────────────────────────────────
 * Las cuentas de las influencers las crea una persona a mano, y sus contraseñas terminan en
 * una nota o en la memoria. Perder la de una cuenta ya verificada y con contenido publicado
 * es perder la cuenta.
 *
 * ── Qué garantiza y qué no ──────────────────────────────────────────────────────
 * Se cifra con AES-256-GCM antes de guardarse, el listado sólo dice **si** existe, y verla
 * es una petición aparte que queda en la bitácora. Eso protege contra una filtración de la
 * base. No protege contra quien tenga acceso al panel: para eso está la contraseña de
 * administrador, y conviene que sea buena.
 */
function AccountPassword({
  creatorId,
  platform,
  hasPassword,
  updatedAt,
  onChange,
}: {
  creatorId: string;
  platform: string;
  hasPassword: boolean;
  updatedAt: string | null;
  onChange: () => void;
}) {
  const { t } = useT();
  const aviso = useAviso();
  const [value, setValue] = useState('');
  const [revealed, setRevealed] = useState<string | null>(null);
  const [editing, setEditing] = useState(!hasPassword);
  const [busy, setBusy] = useState(false);

  const base = `/api/creators/${creatorId}/platforms/${platform}/password`;

  const run = async (action: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    try {
      await action();
    } catch (caught) {
      if (!(caught instanceof ApiRequestError)) aviso.error(t('gal.accionFallo'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 border-t border-[var(--color-border-subtle)] pt-3">
      <p className="text-xs font-medium text-[var(--color-ink-muted)]">{t('clave.titulo')}</p>

      {hasPassword && !editing ? (
        <div className="mt-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] px-2 py-1.5 font-mono text-xs break-all">
              {revealed ?? '••••••••••••'}
            </code>
            <button
              type="button"
              disabled={busy}
              aria-label={t(revealed ? 'clave.ocultar' : 'clave.mostrar')}
              title={t(revealed ? 'clave.ocultar' : 'clave.mostrar')}
              onClick={() => {
                if (revealed) {
                  setRevealed(null);
                  return;
                }
                // Se pide al servidor recién al mostrarla: así el valor no viaja en cada
                // carga de la pantalla, y la lectura queda registrada.
                void run(async () => {
                  const result = await api.post<{ password: string }>(`${base}/reveal`);
                  setRevealed(result.password);
                });
              }}
              className="rounded-lg p-1.5 text-[var(--color-ink-faint)] transition hover:text-[var(--color-ink)]"
            >
              <Icon name={revealed ? 'eye-off' : 'eye'} size={16} />
            </button>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-3">
            {revealed && <CopyButton text={revealed} label={t('comun.copiar')} />}
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-xs text-[var(--color-ink-muted)] underline-offset-2 hover:underline"
            >
              {t('estudio.cambiar')}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await api.del(base);
                  setRevealed(null);
                  onChange();
                })
              }
              className="text-xs text-[var(--color-ink-muted)] underline-offset-2 hover:text-[var(--color-danger)] hover:underline"
            >
              {t('clave.borrar')}
            </button>
            {updatedAt && (
              <span className="text-xs text-[var(--color-ink-faint)]">
                {t('clave.guardada', { cuando: formatDateTime(updatedAt) })}
              </span>
            )}
          </div>
        </div>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => {
              await api.post(base, { password: value });
              setValue('');
              setEditing(false);
              onChange();
            });
          }}
          className="mt-1.5"
        >
          <div className="flex flex-wrap gap-2">
            <input
              type="password"
              value={value}
              onChange={(event) => setValue(event.target.value)}
              placeholder={t('clave.laQueUsaste')}
              autoComplete="new-password"
              className="min-w-0 flex-1 px-3 py-1.5 text-xs"
            />
            <Button type="submit" variant="secondary" disabled={busy || value.length === 0}>
              {t('comun.guardar')}
            </Button>
          </div>
          {hasPassword && (
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setValue('');
              }}
              className="mt-1.5 text-xs text-[var(--color-ink-muted)] underline-offset-2 hover:underline"
            >
              {t('comun.cancelar')}
            </button>
          )}
        </form>
      )}
    </div>
  );
}

/**
 * Tareas que se cierran capturando el dato que produjeron, no con un botón suelto.
 *
 * Son las que crean algo afuera: la casilla y cada cuenta de plataforma. Marcarlas sin el
 * dato pierde justo lo que el paso generó.
 */
/**
 * Corrige lo que una tarea ya completada dejó guardado.
 *
 * ── Por qué no reabre la tarea ────────────────────────────────────────────────
 * Volverla a «pendiente» diría que el alta está sin hacer cuando la cuenta ya existe, y la
 * influencer volvería a contar como incompleta. Lo que hay que arreglar es el dato, no el
 * estado: se guarda contra la cuenta de plataforma, que es donde vive.
 *
 * ── Por qué la contraseña va vacía y no rellena ───────────────────────────────
 * Se guarda cifrada y consultarla es una acción aparte, con su registro de auditoría. Traerla
 * acá sólo para poder editarla la mostraría cada vez que alguien abre la tarea. Vacía
 * significa «no la toques»; escrita, la reemplaza.
 */
function CorrectTaskValue({
  task,
  data,
  onChange,
}: {
  task: TaskView;
  data: CreatorDetailData;
  onChange: () => void;
}) {
  const aviso = useAviso();
  const { t } = useT();
  const account = data.accounts.find((candidate) => candidate.platform === task.platform);
  const anyEmail = data.accounts.find((candidate) => (candidate.emailAddress ?? '').length > 0);
  /*
    Corregir tiene que ofrecer LO QUE ESA TAREA GUARDÓ, no siempre lo mismo.

    Pedía usuario y contraseña en todas. En Facebook las dos están mal: lo que se guarda es el
    identificador de la Página, y contraseña no hay ninguna —la Página la administra un perfil
    personal que ya tiene la suya—. Corregir ofrecía cambiar dos datos que no existen y no el
    único que sí.
  */
  const esCorreo = task.type === 'CREATE_EMAIL_ACCOUNT';
  const esPagina = task.platform === 'facebook';
  const actual =
    (esCorreo ? anyEmail?.emailAddress : esPagina ? account?.externalUserId : account?.username) ??
    '';

  const [abierto, setAbierto] = useState(false);
  const [valor, setValor] = useState(actual);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  if (!abierto) {
    return (
      <button
        type="button"
        onClick={() => {
          setValor(actual);
          setPassword('');
          setAbierto(true);
        }}
        className="text-xs text-[var(--color-ink-muted)] underline underline-offset-2 hover:text-[var(--color-accent)]"
      >
        {t('clave.corregir')}
      </button>
    );
  }

  const guardar = async (): Promise<void> => {
    setBusy(true);
    try {
      const destino = `/api/creators/${data.creator.id}/platforms/${task.platform}`;

      if (valor.trim() !== actual) {
        await api.patch(
          destino,
          esCorreo
            ? { emailAddress: valor.trim() }
            : esPagina
              ? { externalUserId: valor.trim() }
              : { username: valor.trim() },
        );
      }
      if (password.length > 0) {
        await api.post(`${destino}/password`, { password });
      }

      setAbierto(false);
      onChange();
    } catch (caught) {
      if (!(caught instanceof ApiRequestError)) aviso.error(t('ident.noSePudoGuardar'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 rounded-xl border border-[var(--color-border-subtle)] p-3">
      <label className="block text-xs font-medium" htmlFor={`corregir-${task.id}`}>
        {t(
          esCorreo
            ? 'clave.direccionDeCorreo'
            : esPagina
              ? 'clave.identificadorPagina'
              : 'clave.nombreDeUsuario',
        )}
      </label>
      <input
        id={`corregir-${task.id}`}
        value={valor}
        onChange={(event) => setValor(event.target.value)}
        className="w-full rounded-lg border border-[var(--color-border-subtle)] px-2 py-1 text-sm"
      />

      {/* Una Página no tiene contraseña propia: la administra un perfil que ya tiene la suya. */}
      {!esCorreo && !esPagina && task.platform !== 'manual_export' && (
        <>
          <label className="block text-xs font-medium" htmlFor={`corregir-pass-${task.id}`}>
            {t('clave.passwordNueva')}
          </label>
          <input
            id={`corregir-pass-${task.id}`}
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder={t('clave.dejaloVacio')}
            className="w-full rounded-lg border border-[var(--color-border-subtle)] px-2 py-1 text-sm"
          />
        </>
      )}

      <div className="flex gap-2">
        <Button disabled={busy} onClick={() => void guardar()}>
          {t(busy ? 'comun.guardando' : 'comun.guardar')}
        </Button>
        <Button variant="secondary" onClick={() => setAbierto(false)}>
          {t('comun.cancelar')}
        </Button>
      </div>
    </div>
  );
}

/**
 * Si a esta cuenta de Fanvue todavía le falta su casilla propia.
 *
 * ── Por qué es una función y no la condición suelta ───────────────────────────
 * La misma pregunta se hace en tres lugares —la pestaña Plataformas, los datos de la tarea y el
 * botón para asignarla— y las tres tienen que contestar lo mismo. Si una se corrige y las otras
 * no, la tarea muestra una dirección que el botón sigue considerando pendiente, o al revés.
 *
 * Un alias con «+» cuenta como pendiente: es el que heredó de Instagram, y Fanvue lo colapsa a
 * la dirección base y responde que la cuenta ya existe. Sirve para todo menos para esto.
 */
function faltaCasillaDeFanvue(cuenta?: { emailAddress: string | null }): boolean {
  return !cuenta?.emailAddress || cuenta.emailAddress.includes('+');
}

function capturesValue(task: TaskView): boolean {
  return task.type === 'CREATE_EMAIL_ACCOUNT' || task.type === 'CREATE_ACCOUNT';
}

/** Nombres de usuario sugeridos para la plataforma de esta tarea. */
function usernamesFor(task: TaskView, data?: CreatorDetailData): string[] {
  return (
    data?.accounts.find((account) => account.platform === task.platform)?.suggestedUsernames ?? []
  );
}

/** Un dato concreto que la tarea manda usar, listo para copiar. */
type TaskField = {
  label: string;
  value: string;
  /** Varias opciones entre las que elegir, como los nombres de usuario. */
  options?: string[];
  /** Por qué falta, cuando falta. Más útil que no mostrar la fila. */
  missing?: string;
  /**
   * De dónde sale el valor, cuando no es obvio.
   *
   * Distinto de `missing`: eso explica una ausencia, esto explica una presencia rara. El
   * alias sobre la casilla principal no está guardado en ninguna fila —se calcula— así que
   * quien lo vea en la configuración de la cuenta va a querer saber por qué aparece.
   */
  hint?: string;
  /**
   * El valor es una URL que se abre, no un texto que se pega.
   *
   * Una imagen no se copia y se pega en un formulario: se abre, se descarga y se sube. Sin
   * esto había que copiar una URL firmada de trescientos caracteres y pegarla en la barra de
   * direcciones, que es pedirle a alguien que haga de navegador.
   */
  link?: boolean;
};

/**
 * Los valores que hace falta pegar en la plataforma externa.
 *
 * ── Por qué se resuelven acá y no al crear la tarea ─────────────────────────────
 * La tarea guarda *qué clase* de dato usar («la dirección de correo asignada»), no el dato.
 * Está bien que sea así: las instrucciones se escriben una vez y los datos cambian —se
 * regenera la ficha, se configura un dominio de correo, se recalculan los usuarios—. Si el
 * valor se hubiera copiado dentro de la tarea al crearla, hoy mostraría algo viejo.
 *
 * Lo que faltaba era el otro extremo: resolverlos al mostrarlos. Sin eso, la tarjeta decía
 * «usá el nombre de usuario sugerido» y había que ir a buscarlo a otra pestaña.
 */
/**
 * El último banner de Fanvue generado, si hay alguno.
 *
 * ── Por qué por la procedencia y no por la forma ──────────────────────────────
 * Porque `stage` es lo que la ruta escribió al generarlo, y la forma es una consecuencia. Buscar
 * «la apaisada más ancha» —que es lo que hace la tarjeta del alta— acierta hoy y deja de acertar
 * en cuanto exista cualquier otra imagen ancha.
 */
function bannerDeFanvueDe(data: CreatorDetailData): AssetView | undefined {
  return data.assets
    .filter((asset) => asset.provenance?.parameters?.stage === 'fanvue-banner' && !isRetired(asset))
    .sort((a, b) =>
      (b.provenance?.generatedAt ?? '').localeCompare(a.provenance?.generatedAt ?? ''),
    )[0];
}

/**
 * Genera una imagen apaisada: la portada de una Página, o el banner de Fanvue.
 *
 * Aparte del resto de las acciones porque no completa la tarea ni guarda un dato: produce un
 * archivo para subir a mano.
 *
 * ── Por qué el banner se muestra acá mismo ────────────────────────────────────
 * Porque si no, no se ve en ninguna parte. Las imágenes de tipo `generic` están excluidas a
 * propósito de la Galería, de la lista de contenido y del selector de publicación —no son tomas
 * que se publiquen desde el panel— así que su único escaparate era el campo «Banner» de la
 * tarjeta del alta de Fanvue. Y esa tarjeta desaparece al completar la tarea: en una influencer
 * que ya está en Fanvue, el banner se generaba bien y no lo veía nadie. Pasó, tal cual, la
 * primera vez que se pulsó el botón.
 *
 * Un solo componente con dos textos porque el gesto es el mismo: un botón que pide una imagen que
 * el sistema no produce por ningún otro camino, y que después alguien sube a mano.
 */
function GenerarPortada({
  creatorId,
  onChange,
  formato = 'pagina',
  hecho,
}: {
  creatorId: string;
  onChange: () => void;
  formato?: 'pagina' | 'fanvue';
  /** Lo ya generado, para verlo sin salir de acá. Sólo lo pasa el banner. */
  hecho?: AssetView;
}) {
  const { t } = useT();
  const aviso = useAviso();
  const [busy, setBusy] = useState(false);
  const banner = formato === 'fanvue';

  return (
    <div className="mt-3">
      <Button
        variant="secondary"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          api
            .post(`/api/creators/${creatorId}/cover`, { formato })
            .then(() => {
              aviso.ok(t(banner ? 'datos.bannerGenerado' : 'datos.portadaGenerada'));
              onChange();
            })
            .catch((cause: unknown) => {
              if (!(cause instanceof ApiRequestError)) aviso.error(t('datos.noSePudoGenerar'));
            })
            .finally(() => setBusy(false));
        }}
      >
        {t(
          busy
            ? banner
              ? 'datos.generandoBanner'
              : 'datos.generandoPortada'
            : banner
              ? 'datos.generarBanner'
              : 'datos.generarPortada',
        )}
      </Button>
      {/*
        La imagen, a la vista y en su forma real.

        Se muestra a lo ancho de la tarjeta y no como miniatura cuadrada: lo que hay que juzgar de
        un banner es el reparto —dónde cae ella, qué esquina queda libre— y eso una miniatura
        recortada no lo enseña. El enlace abre el archivo entero, que es lo que se sube.
      */}
      {hecho && (
        <a
          href={hecho.url}
          target="_blank"
          rel="noreferrer"
          title={t('datos.abrirImagen')}
          className="mt-2 block"
        >
          <img
            src={hecho.url}
            alt=""
            className="w-full rounded border border-[var(--color-border-subtle)]"
          />
          <span className="mt-1 block text-xs text-[var(--color-ink-faint)]">
            {hecho.width}×{hecho.height}
          </span>
        </a>
      )}
      <p className="mt-1 text-xs text-[var(--color-ink-muted)]">
        {t(banner ? 'datos.bannerPorQue' : 'datos.portadaPorQue')}
      </p>
    </div>
  );
}

/** Las imágenes que se pueden ofrecer: ni descartadas ni rechazadas. */
function usablesDe(data: CreatorDetailData) {
  return data.assets.filter((asset) => asset.status !== 'DISCARDED' && asset.status !== 'REJECTED');
}

/**
 * La foto de perfil, elegida y no buscada.
 *
 * La CANÓNICA cuando existe: es la cara que el resto del sistema usa como referencia, así que el
 * perfil y el contenido muestran a la misma persona. Sin canónica se cae a cualquiera aprobada,
 * que es mejor que mandar a recorrer la pestaña Imágenes a ojo.
 */
function fotoDePerfilDe(data: CreatorDetailData) {
  const usables = usablesDe(data);
  return (
    usables.find((asset) => asset.id === data.creator.canonicalReferenceAssetId) ??
    usables.find((asset) => asset.kind === 'profile_photo') ??
    usables[0]
  );
}

/**
 * Una biografía corta y lista para pegar, armada de los datos y no de la historia.
 *
 * La historia de la Character Bible son varios párrafos: sirve para escribir, no para pegar.
 * Facebook corta la descripción de una Página en 255 caracteres y la de Instagram en 150, así que
 * ofrecerla entera invitaba a pegar algo que se trunca a mitad de frase.
 *
 * Se arma de los campos estructurados —nombre, nicho, ubicación narrativa— porque son cortos por
 * construcción y no hay que recortar nada.
 *
 * No lleva el disclosure de IA: va aparte, como su propio dato para copiar, porque no todas las
 * plataformas lo piden en el mismo lugar.
 *
 * `conInstagram` sólo en Facebook: una Página nueva no tiene con qué probar que la persona existe
 * en otro lado, y la cuenta de Instagram sí tiene historia.
 */
function biografiaDe(data: CreatorDetailData, conInstagram: boolean): string {
  const nichos = data.creator.niche.slice(0, 2).join(' y ');
  const lugar = data.creator.countryNarrative ?? '';
  const usuarioIg = data.accounts.find((cuenta) => cuenta.platform === 'instagram')?.username;

  return [
    texto('bio.creadoraDe', { quien: data.creator.publicName }),
    nichos ? texto('bio.sobre', { nichos }) : '',
    lugar ? texto('bio.desde', { lugar }) : '',
    '.',
    conInstagram && usuarioIg ? ` IG: instagram.com/${usuarioIg}` : '',
  ].join('');
}

/**
 * Biografías para Fanvue, que no son las de Instagram.
 *
 * ── Por qué hace falta un generador aparte ────────────────────────────────────
 * La biografía general está escrita para una red abierta: tercera persona, descriptiva,
 * «creadora de contenido digital sobre moda». En Instagram eso funciona porque quien llega ya
 * está viendo las fotos gratis y la biografía sólo tiene que ubicarlo.
 *
 * En Fanvue el visitante está decidiendo si paga. Una descripción neutra no le da ningún motivo,
 * y encima suena a cuenta corporativa en un lugar donde todas las demás hablan en primera persona
 * y de manera directa. La misma frase que en Instagram es correcta, acá es un cartel de «no hay
 * nada que ver».
 *
 * Van tres para elegir porque el tono correcto depende del personaje —no todas funcionan con la
 * misma voz— y elegir de una lista es más rápido que reescribir.
 */
function biografiasDeFanvue(data: CreatorDetailData): string[] {
  const nombre = data.creator.publicName.split(' ')[0] ?? data.creator.publicName;
  const edad = data.creator.fictionalAge;
  const nicho = data.creator.niche[0] ?? '';
  const presentacion = edad ? `${nombre}, ${edad}` : nombre;

  /*
    La biografía habla como ELLA, no como el panel.

    El resto de la interfaz está en rioplatense porque se la lee el operador. Esto no: lo lee un
    suscriptor, y lo firma la influencer. Una madrileña que escribe «escribime» se delata sola.

    El voseo se deduce de la ubicación narrativa, que es el único dato de dónde es. No es
    exhaustivo —hay voseo en media Centroamérica— pero cubre los casos que aparecen, y el defecto
    es el tuteo, que no chirría en ningún lado.
  */
  /*
    En inglés no hay variantes: es la lengua franca de la plataforma.

    El español se parte en dos porque el voseo delata una madrileña que escribe «escribime». El
    inglés no tiene esa fractura para lo que acá se escribe —tres párrafos cortos en primera
    persona— y una versión británica frente a una estadounidense no cambia nada que un
    suscriptor pueda notar.
  */
  if (idiomaActual() === 'en') {
    return [
      `Everything that does not fit on my socials lives here: uncensored photos and videos, new ` +
        `every week.\n\nAnd you actually get me — DMs, your requests, and content made to order.` +
        `\n\nCome in and we start wherever you want. 👇`,

      `${presentacion}. Out there you see me dressed up and fully covered; in here, rather less.` +
        `\n\nNew content every week, no filters, and I answer every message.\n\nSubscribe and ` +
        `tell me what you want to see. 👇`,

      `Exclusive photos and videos${nicho ? `, mostly ${nicho},` : ''} that go nowhere else.` +
        `\n\nSubscribing gets you straight through to me: private chat, requests, and extras for ` +
        `whoever gets there first.\n\nPlaces are limited and this price will not stay. 👇`,
    ];
  }

  const lugar = (data.creator.countryNarrative ?? '').toLowerCase();
  const vosea = /argentin|uruguay|paraguay|rosario|córdoba|cordoba|montevideo|buenos aires/.test(
    lugar,
  );

  if (vosea) {
    return [
      `Todo lo que no entra en mis redes está acá: fotos y videos sin censura, nuevos cada semana.\n\n` +
        `Y me tenés de verdad — privados, pedidos tuyos y contenido hecho a medida.\n\n` +
        `Entrá y empezamos por donde vos quieras. 👇`,

      `${presentacion}. Afuera me ves arreglada y con todo puesto; acá, bastante menos.\n\n` +
        `Contenido nuevo cada semana, sin filtros, y contesto todos los mensajes.\n\n` +
        `Suscribite y contame qué querés ver. 👇`,

      `Fotos y videos exclusivos${nicho ? `, sobre todo ${nicho},` : ''} que no salen de acá.\n\n` +
        `Suscribiéndote tenés acceso directo a mí: chat privado, pedidos y extras para los que llegan primero.\n\n` +
        `Los lugares son limitados y este precio no se queda así. 👇`,
    ];
  }

  return [
    `Todo lo que no cabe en mis redes está aquí: fotos y vídeos sin censura, nuevos cada semana.\n\n` +
      `Y me tienes de verdad — privados, peticiones tuyas y contenido hecho a medida.\n\n` +
      `Entra y empezamos por donde tú quieras. 👇`,

    `${presentacion}. Fuera me ves arreglada y con todo puesto; aquí, bastante menos.\n\n` +
      `Contenido nuevo cada semana, sin filtros, y respondo todos los mensajes.\n\n` +
      `Suscríbete y dime qué quieres ver. 👇`,

    `Fotos y vídeos exclusivos${nicho ? `, sobre todo ${nicho},` : ''} que no salen de aquí.\n\n` +
      `Al suscribirte tienes acceso directo a mí: chat privado, peticiones y extras para quien llega primero.\n\n` +
      `Las plazas son limitadas y este precio no se va a quedar así. 👇`,
  ];
}

function TaskData({ task, data }: { task: TaskView; data: CreatorDetailData }) {
  const { t } = useT();
  const account = data.accounts.find((candidate) => candidate.platform === task.platform);
  const fields: TaskField[] = [];

  if (task.type === 'CREATE_ACCOUNT' && task.platform !== 'fanvue') {
    fields.push({
      label: texto('datos.nombrePublico'),
      value: data.creator.publicName,
    });

    fields.push({
      label: texto('datos.usuarioSugerido'),
      value: account?.suggestedUsernames[0] ?? '',
      ...(account?.suggestedUsernames.length
        ? { options: account.suggestedUsernames }
        : { missing: texto('datos.sinSugerencias') }),
    });

    /*
      Categoría y biografía, para no tener que ir a buscarlas.

      Las tres plataformas piden una categoría al crear la cuenta o la Página, y una biografía.
      Estaban en la ficha, a dos pestañas de distancia, mientras la guía decía «poné la
      categoría que corresponda al nicho» sin decir cuál era.
    */
    if (data.creator.niche.length > 0) {
      fields.push({
        label: texto('datos.categoria'),
        value: data.creator.niche.join(', '),
      });
    }

    /*
      Una biografía corta y lista para pegar, armada de los datos y no de la historia.

      La historia de la Character Bible son varios párrafos: sirve para escribir, no para
      pegar. Facebook corta la descripción de una Página en 255 caracteres y la de Instagram en
      150, así que ofrecerla entera invitaba a pegar algo que se trunca a mitad de frase.

      Se arma de los campos estructurados —nombre, nicho, ubicación narrativa— porque son
      cortos por construcción y no hay que recortar nada.

      No lleva el disclosure de IA a propósito: acá se pidió que no figure. La declaración en
      los captions es otra cosa y sigue igual, con su propio ajuste en Consumo.
    */

    /*
      En Facebook, el enlace a su Instagram al final.

      Una Página nueva no tiene con qué probar que la persona existe en otro lado, y la cuenta de
      Instagram sí tiene historia. Además es la que manda tráfico: quien la encuentre en Facebook
      va a querer verla donde publica todos los días.

      Sólo si la cuenta existe. Poner «instagram.com/» seguido de nada sería peor que no ponerlo.
    */
    const bio = biografiaDe(data, task.platform === 'facebook');

    fields.push({
      label: texto('datos.bioSugerida', { n: bio.length }),
      value: bio,
    });

    /*
      El enlace de verdad va en «Sitio web», no en la descripción.

      Facebook no convierte las URLs de la descripción en enlaces: quedan como texto que hay que
      copiar a mano. La Página tiene un campo propio para esto —Sitio web, en Contacto y datos
      básicos— y ahí sí es clicable.

      Se deja también en la biografía porque se lee sin abrir nada, pero el que sirve para ir es
      éste.
    */
    const usuarioIg = data.accounts.find((cuenta) => cuenta.platform === 'instagram')?.username;
    if (task.platform === 'facebook' && usuarioIg) {
      fields.push({
        label: texto('datos.sitioWeb'),
        value: `https://www.instagram.com/${usuarioIg}/`,
      });
    }

    /*
      Las dos fotos que pide cualquier alta, elegidas y no buscadas.

      Estaban en la pestaña Imágenes, que tiene decenas: la guía decía «la foto aprobada» y
      encontrarla era recorrerlas a ojo. Acá se eligen solas y se abren en una pestaña.

      La de perfil es la CANÓNICA cuando existe: es la cara que el resto del sistema usa como
      referencia, así que el perfil y el contenido muestran a la misma persona. Sin canónica se
      cae a cualquiera aprobada, que es mejor que nada.
    */
    const usables = usablesDe(data);
    const perfil = fotoDePerfilDe(data);

    if (perfil) {
      fields.push({ label: texto('datos.fotoDePerfil'), value: perfil.url, link: true });
    }

    /*
      La portada se genera, no se elige.

      Todo el material del sistema es vertical —4:5 y 9:16, que es lo que rinde en un feed— y
      una portada es una franja de 851×315. Recortar una vertical a eso deja una banda de la
      cara: no es que falte la toma correcta, es que no existe ninguna con esa forma.

      Si ya se generó una, se ofrece como enlace. Si no, el botón la genera.
    */
    const portada = [...usables]
      .filter((asset) => asset.width > asset.height)
      .sort((a, b) => b.width / b.height - a.width / a.height)[0];

    if (portada) {
      fields.push({
        label: texto('datos.fotoDePortada', { ancho: portada.width, alto: portada.height }),
        value: portada.url,
        link: true,
      });
    }

    /*
      El correo no aplica a una Página de Facebook.

      Las otras plataformas registran una CUENTA y para eso hace falta una casilla. Una Página
      la crea un perfil personal que ya existe y ya tiene su correo: pedir uno acá mostraba
      «Todavía no hay casilla» como si algo faltara, y mandaba a completar una tarea que para
      esta plataforma no hace falta.
    */
    if (task.platform !== 'facebook') {
      /*
        La dirección puede estar sin guardar y existir igual.

        Con una casilla principal configurada, el alias de cada influencer se CALCULA —no se
        escribe en la fila de cada plataforma— así que `account.emailAddress` está vacío
        aunque la dirección exista y funcione. La pantalla leía sólo la fila y avisaba
        «Todavía no hay casilla», mandando a completar una tarea que el sistema decide NO
        crear justamente porque el alias ya la resuelve. Un callejón: el cartel pedía ir a un
        sitio que no existe.

        Fanvue queda fuera y no es un olvido: rechaza los alias con «+», así que su cuenta
        necesita una dirección propia. Ofrecerle el alias sería mandarla a un registro que va
        a fallar, que es peor que decirle que falta.
      */
      /*
        Fanvue ya no es la excepción, si la dirección es de dominio propio.

        Se la excluía porque rechaza los alias con «+», y ofrecerle uno sería mandarla a un
        registro que falla. Una dirección de dominio no tiene «+»: le sirve igual que a las
        demás. La exclusión se mantiene sólo cuando lo que hay es un alias.
      */
      const sugerida = data.direccionDeCorreo ?? null;
      const esAlias = sugerida?.includes('+') === true;
      const alias = task.platform === 'fanvue' && esAlias ? null : sugerida;
      const direccion = account?.emailAddress || alias;

      fields.push({
        label: 'Correo',
        value: direccion ?? '',
        ...(direccion
          ? account?.emailAddress
            ? {}
            : {
                // Se dice de dónde sale, porque no está guardada en ningún lado y quien la
                // mire en la configuración de la cuenta va a querer saber por qué.
                hint: texto('datos.aliasSobreCasilla'),
              }
          : {
              missing:
                task.platform === 'fanvue'
                  ? texto('datos.fanvueNecesitaPropia')
                  : texto('datos.sinCasilla'),
            }),
      });
    }
  }

  if (
    task.type === 'CREATE_ACCOUNT' ||
    task.type === 'ACCEPT_TERMS' ||
    task.type === 'APPROVE_PROFILE'
  ) {
    if (data.creator.disclosureText) {
      fields.push({
        label: texto('datos.disclosure'),
        value: data.creator.disclosureText,
      });
    }
  }

  if (task.type === 'CREATE_EMAIL_ACCOUNT') {
    fields.push({ label: texto('datos.nombreParaLaCuenta'), value: data.creator.publicName });

    /**
     * Las direcciones las calcula el servidor, igual que los nombres de usuario.
     *
     * La web no depende de `@vcos/domain` a propósito: sólo habla con la API. Duplicar acá
     * las reglas de Gmail sería tener dos versiones de la misma decisión, y la del cliente
     * se quedaría vieja sin que nadie lo note.
     */
    const emails = data.suggestedEmails;

    fields.push({
      label: texto('datos.direccionesSugeridas'),
      value: emails[0] ?? '',
      ...(emails.length ? { options: emails } : { missing: texto('datos.sinDirecciones') }),
    });
  }

  if (task.type === 'CONVERT_TO_PROFESSIONAL' && data.creator.niche.length > 0) {
    fields.push({
      label: texto('datos.categoriaSugerida'),
      value: data.creator.niche[0] ?? '',
    });
  }

  /*
    La casilla de Fanvue, en la tarjeta de la tarea que la manda usar.

    El paso 1 decía «andá a la pestaña Plataformas, fila de Fanvue, y pulsá el botón»: mandaba a
    otra pantalla a buscar un dato que esta misma tarjeta puede dar, y en esa pestaña la fila
    muestra además el alias viejo con «+», que se parece bastante a una casilla asignada como
    para darla por buena.

    Va con su botón de copiar por lo de siempre, pero acá pesa el doble: la dirección es
    PERMANENTE y transcribirla mal no se arregla después.
  */
  if (task.type === 'APPROVE_PROFILE' && task.platform === 'fanvue') {
    fields.push({ label: texto('datos.nombrePublico'), value: data.creator.publicName });
    fields.push({
      label: texto('datos.correoDeLaCuenta'),
      value: account?.emailAddress ?? '',
      ...(faltaCasillaDeFanvue(account)
        ? {
            missing: texto('datos.sinCasillaPropia'),
          }
        : {}),
    });

    /*
      La biografía y las imágenes, que el paso 4 manda usar «las que da el panel».

      Las daba para el alta de Instagram y Facebook, y no acá — así que la instrucción nombraba
      datos que la tarjeta no mostraba, y había que ir a buscarlos a la pestaña Imágenes entre
      decenas. Sin el enlace a Instagram: eso es de una Página nueva de Facebook, que necesita
      probar que la persona existe en otro lado.
    */
    const biosFanvue = biografiasDeFanvue(data);
    fields.push({
      label: texto('datos.bioSugerida', { n: biosFanvue[0]?.length ?? 0 }),
      value: biosFanvue[0] ?? '',
      options: biosFanvue,
    });

    const perfilFanvue = fotoDePerfilDe(data);
    if (perfilFanvue) {
      fields.push({ label: texto('datos.fotoDePerfil'), value: perfilFanvue.url, link: true });
    }

    const bannerFanvue = [...usablesDe(data)]
      .filter((asset) => asset.width > asset.height)
      .sort((a, b) => b.width / b.height - a.width / a.height)[0];
    if (bannerFanvue) {
      fields.push({
        label: `Banner (${bannerFanvue.width}×${bannerFanvue.height})`,
        value: bannerFanvue.url,
        link: true,
      });
    }
  }

  if (fields.length === 0) return null;

  return (
    <div className="rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-3">
      <p className="text-xs font-medium text-[var(--color-ink-muted)]">
        {t('datos.deQuien', { quien: data.creator.publicName })}
      </p>
      <dl className="mt-2 space-y-2.5">
        {fields.map((field) => (
          <div key={field.label}>
            <dt className="text-xs text-[var(--color-ink-faint)]">{field.label}</dt>
            {field.missing ? (
              <dd className="mt-0.5 text-xs text-[var(--color-warn)]">{field.missing}</dd>
            ) : (
              <dd className="mt-0.5 flex flex-wrap items-center gap-2">
                {field.link ? (
                  <a
                    className="min-w-0 flex-1 rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-surface-raised)] px-2 py-1 text-xs underline"
                    href={field.value}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('datos.abrirImagen')}
                  </a>
                ) : (
                  <code className="min-w-0 flex-1 rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-surface-raised)] px-2 py-1 font-mono text-xs break-words whitespace-pre-wrap">
                    {field.value}
                  </code>
                )}
                <CopyButton text={field.value} label={t('comun.copiar')} />
              </dd>
            )}
            {field.hint && !field.missing && (
              <dd className="mt-1 text-xs text-[var(--color-ink-muted)]">{field.hint}</dd>
            )}
            {/*
                Las alternativas se copian de a una: la disponibilidad no está verificada y casi
                siempre hay que probar más de un usuario.

                En fila y monoespaciadas cuando son cortas —nombres de usuario, que es para lo que
                se hizo esto—, y apiladas a lo ancho cuando son frases. Una biografía de ciento
                cuarenta caracteres metida en una ficha de las otras no se lee: se corta, se
                mezcla con la de al lado, y elegir entre tres deja de ser posible.
              */}
            {field.options && field.options.length > 1 && (
              <dd
                className={
                  field.options.some((option) => option.length > 40)
                    ? 'mt-1.5 flex flex-col gap-1.5'
                    : 'mt-1 flex flex-wrap gap-1'
                }
              >
                {field.options.slice(1, 7).map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => void navigator.clipboard.writeText(option)}
                    title={t('comun.copiar')}
                    className={
                      option.length > 40
                        ? 'rounded-lg border border-[var(--color-border-subtle)] px-2 py-1.5 text-left text-xs text-[var(--color-ink-muted)] transition hover:border-[var(--color-border-strong)] hover:text-[var(--color-ink)]'
                        : 'rounded-md border border-[var(--color-border-subtle)] px-1.5 py-0.5 font-mono text-[0.7rem] text-[var(--color-ink-muted)] transition hover:border-[var(--color-border-strong)] hover:text-[var(--color-ink)]'
                    }
                  >
                    {option}
                  </button>
                ))}
              </dd>
            )}
          </div>
        ))}
      </dl>
    </div>
  );
}

/**
 * Cierra la tarea del correo guardando la dirección que quedó.
 *
 * No alcanza con marcarla completada: lo que importa es **cuál** casilla quedó, y sin ese
 * dato el alta de cada plataforma vuelve a pedirlo. La dirección se guarda en todas las
 * cuentas de la influencer, porque la casilla es una sola y se comparte.
 */
/**
 * Cierra una tarea guardando el dato que produjo.
 *
 * ── Por qué no alcanza con «ya lo completé» ─────────────────────────────────────
 * Los dos pasos que crean algo afuera —la casilla y cada cuenta de plataforma— producen un
 * dato que el sistema necesita: la dirección y el nombre de usuario. Marcar la tarea sin
 * capturarlo pierde justo lo que el paso generó, y después no hay forma de saber con qué
 * usuario se registró esa influencer sin abrir la app.
 *
 * El campo viene precargado con la sugerencia, pero es **editable**: la disponibilidad no se
 * verifica, así que lo más probable es que el nombre libre no sea el primero de la lista.
 * Asumir que se usó el sugerido guardaría un dato falso, que es peor que no guardar ninguno.
 */
function CompleteWithValue({
  task,
  onChange,
  field,
  label,
  hint,
  placeholder,
  initial,
  options = [],
  submitLabel,
  withPassword = false,
  withFreeTrial = false,
}: {
  task: TaskView;
  onChange: () => void;
  /** Qué campo espera el endpoint. */
  /** Qué dato guarda esta tarea. `externalUserId` es el identificador de la Página. */
  field: 'emailAddress' | 'username' | 'externalUserId';
  label: string;
  hint: ReactNode;
  placeholder: string;
  initial: string;
  /** Alternativas para rellenar con un clic. */
  options?: string[];
  submitLabel: string;
  /**
   * Pide también la contraseña.
   *
   * Se carga acá y no en Plataformas porque **este** es el momento en que la tenés en la
   * mano: la acabás de elegir en el formulario de la red. Mandar a otra pestaña a guardarla
   * es garantizar que no se guarde. Consultarla después sí es cosa de Plataformas, que es
   * donde vive la cuenta.
   */
  withPassword?: boolean;
  /**
   * Pide también el enlace de prueba gratis.
   *
   * Sólo Fanvue: es la única de las cuatro que tiene promociones, y el enlace lleva un
   * identificador que genera la plataforma y que no se puede derivar del nombre de usuario.
   * O se pega acá, o la página pública no puede ofrecerlo.
   *
   * Va en la tarea por el mismo motivo que la contraseña: es el momento en que quien opera lo
   * tiene delante, recién copiado del panel de Fanvue.
   */
  withFreeTrial?: boolean;
}) {
  const { t } = useT();
  const aviso = useAviso();
  const [value, setValue] = useState(initial);
  const [password, setPassword] = useState('');
  const [freeTrial, setFreeTrial] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post(`/api/tasks/${task.id}/complete`, {
        [field]: value.trim(),
        ...(withPassword && password.length > 0 ? { password } : {}),
        ...(withFreeTrial && freeTrial.trim().length > 0 ? { freeTrialUrl: freeTrial.trim() } : {}),
      });
      onChange();
    } catch (caught) {
      if (!(caught instanceof ApiRequestError)) aviso.error(t('datos.noSePudoGuardarTarea'));
      setBusy(false);
    }
  };

  const alternatives = options.filter((option) => option !== value).slice(0, 6);

  return (
    <form
      onSubmit={(event) => void submit(event)}
      className="rounded-xl border border-[var(--color-accent)]/35 bg-[var(--color-accent-soft)] p-3"
    >
      <label htmlFor={`value-${task.id}`} className="block text-xs font-medium">
        {label}
      </label>

      <div className="mt-1.5 flex flex-wrap gap-2">
        <input
          id={`value-${task.id}`}
          type={field === 'emailAddress' ? 'email' : 'text'}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder={placeholder}
          required
          autoComplete="off"
          spellCheck={false}
          className="min-w-0 flex-1 px-3 py-2 font-mono text-sm"
        />
        <Button type="submit" disabled={busy || value.trim().length === 0}>
          {busy ? 'Guardando…' : submitLabel}
        </Button>
      </div>

      {withPassword && (
        <div className="mt-2">
          <label htmlFor={`pass-${task.id}`} className="block text-xs font-medium">
            {t('datos.passwordOpcional')}{' '}
            <span className="text-[var(--color-ink-faint)]">{t('datos.opcional')}</span>
          </label>
          <div className="relative mt-1.5">
            <input
              id={`pass-${task.id}`}
              type={revealed ? 'text' : 'password'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder={t('datos.laQuePusiste')}
              autoComplete="new-password"
              className="w-full py-2 pr-10 pl-3 text-sm"
            />
            <button
              type="button"
              onClick={() => setRevealed((current) => !current)}
              aria-label={t(revealed ? 'clave.ocultar' : 'clave.mostrar')}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-[var(--color-ink-faint)] transition hover:text-[var(--color-ink)]"
            >
              <Icon name={revealed ? 'eye-off' : 'eye'} size={16} />
            </button>
          </div>
          <p className="mt-1 text-xs text-[var(--color-ink-muted)]">{t('datos.seGuardaCifrada')}</p>
        </div>
      )}

      {withFreeTrial && (
        <div className="mt-2">
          <label htmlFor={`trial-${task.id}`} className="block text-xs font-medium">
            {t('tarea.enlaceDePrueba')}{' '}
            <span className="text-[var(--color-ink-faint)]">{t('datos.opcional')}</span>
          </label>
          <input
            id={`trial-${task.id}`}
            type="url"
            value={freeTrial}
            onChange={(event) => setFreeTrial(event.target.value)}
            placeholder="https://www.fanvue.com/usuario?free_trial=…"
            autoComplete="off"
            spellCheck={false}
            className="mt-1.5 w-full px-3 py-2 font-mono text-sm"
          />
          <p className="mt-1 text-xs text-[var(--color-ink-muted)]">
            {t('tarea.enlaceDePruebaQue')}
          </p>
        </div>
      )}

      {alternatives.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {alternatives.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setValue(option)}
              className="rounded-md border border-[var(--color-border-subtle)] px-1.5 py-0.5 font-mono text-[0.7rem] text-[var(--color-ink-muted)] transition hover:border-[var(--color-border-strong)] hover:text-[var(--color-ink)]"
            >
              {option}
            </button>
          ))}
        </div>
      )}

      {<p className="mt-1.5 text-xs text-[var(--color-ink-muted)]">{hint}</p>}
    </form>
  );
}

/**
 * Los pasos de Fanvue que son de la PERSONA y no del personaje.
 *
 * Crear la cuenta de creador y verificar la identidad se hacen una vez y valen para todas. En
 * la ficha de cada influencer aparecían repetidos y se leían como «un KYC por cada una», que no
 * es lo que pide Fanvue ni sería viable.
 *
 * Se muestran en Credenciales, que es la pantalla de lo que tiene que tener listo cada usuario.
 * Acá se ocultan: siguen existiendo como tarea —es donde vive el estado y su auditoría— pero no
 * tienen nada que hacer en la ficha de un personaje.
 */
const PASOS_DE_LA_PERSONA = new Set(['CREATE_ACCOUNT', 'COMPLETE_KYC']);

const esPasoDeLaPersona = (task: TaskView): boolean =>
  task.platform === 'fanvue' && PASOS_DE_LA_PERSONA.has(task.type);

/** Las pendientes que de verdad son de esta influencer. Es lo que cuenta el globito. */
const pendientesDeLaFicha = (tasks: TaskView[]): number =>
  tasks.filter((task) => task.status !== 'COMPLETED' && !esPasoDeLaPersona(task)).length;

/**
 * La página pública de la influencer: encenderla y copiar su dirección.
 *
 * ── Por qué vive en Tareas y no en Plataformas ────────────────────────────────
 * Porque es un paso del alta, no una configuración: se hace una vez, produce un dato que hay que
 * llevar a otro lado —la dirección, que va en la biografía de cada red— y después no se toca. Eso
 * es exactamente lo que son las otras tarjetas de esta pestaña.
 *
 * ── Por qué la dirección se muestra completa y con botón de copiar ────────────
 * Porque el trabajo no termina al generarla: termina cuando está pegada en Instagram. Una URL que
 * hay que seleccionar a mano desde un párrafo se copia mal —se lleva un espacio, o media— y el
 * enlace roto no se descubre hasta que alguien lo pulsa.
 */
function PaginaPublica({
  creatorId,
  slug,
  publicadaEn,
  tieneCanonica,
  onChange,
}: {
  creatorId: string;
  slug: string;
  publicadaEn: string | null;
  tieneCanonica: boolean;
  onChange: () => void;
}) {
  const { t } = useT();
  const aviso = useAviso();
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState<string | null>(null);

  const cambiar = async (publicar: boolean): Promise<void> => {
    setBusy(true);
    try {
      const respuesta = await api.post<{ url: string; publicada: boolean }>(
        `/api/creators/${creatorId}/pagina-proxy`,
        { publicar },
      );
      setUrl(respuesta.publicada ? respuesta.url : null);
      aviso.ok(t(publicar ? 'proxy.publicada' : 'proxy.despublicada'));
      onChange();
    } catch (cause) {
      if (!(cause instanceof ApiRequestError)) aviso.error(t('proxy.noSePudo'));
    } finally {
      setBusy(false);
    }
  };

  /*
    La dirección se arma acá sólo mientras no haya respuesta del servidor.

    La buena es la que devuelve la API, porque el servidor sabe en qué dominio corre. Ésta es para
    que la tarjeta no aparezca vacía al abrir la ficha de una influencer ya publicada — y usa el
    mismo origen del navegador, que en producción es el mismo.
  */
  const direccion = url ?? (publicadaEn ? `${window.location.origin}/p/${slug}` : null);

  return (
    <Card title={t('proxy.titulo')}>
      <p className="text-sm text-[var(--color-ink-muted)]">{t('proxy.que')}</p>

      {!tieneCanonica && publicadaEn === null && (
        <p className="mt-3 rounded-xl border border-[var(--color-warn)]/35 bg-[var(--color-warn)]/10 p-3 text-xs">
          {t('proxy.faltaCanonica')}
        </p>
      )}

      {direccion && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-3">
          <code className="min-w-0 flex-1 truncate text-xs">{direccion}</code>
          <CopyButton text={direccion} />
          <a
            href={direccion}
            target="_blank"
            rel="noreferrer noopener"
            className="rounded-lg border border-[var(--color-border-subtle)] px-2.5 py-1 text-xs hover:border-[var(--color-accent)]"
          >
            {t('proxy.abrir')}
          </a>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-3">
        {publicadaEn === null ? (
          <Button disabled={busy || !tieneCanonica} onClick={() => void cambiar(true)}>
            {t(busy ? 'proxy.generando' : 'proxy.generar')}
          </Button>
        ) : (
          <>
            <span className="text-xs text-[var(--color-ok)]">
              Publicada {haceCuanto(publicadaEn)}
            </span>
            {/*
              Despublicar deja 404, y eso se dice sin adornos.

              No borra nada de lo que ya circuló: si la dirección está pegada en una biografía,
              sigue estando y va a dejar de funcionar. Prometer que «se saca de internet» sería
              mentir sobre lo único que importa saber antes de pulsarlo.
            */}
            <button
              type="button"
              disabled={busy}
              onClick={() => void cambiar(false)}
              className="text-xs text-[var(--color-ink-muted)] underline underline-offset-2 hover:text-[var(--color-danger)] disabled:opacity-40"
            >
              Despublicar
            </button>
          </>
        )}
      </div>
    </Card>
  );
}

export function TasksTab({
  tasks: todas,
  onChange,
  data,
}: {
  tasks: TaskView[];
  onChange: () => void;
  /** Ficha completa, para poder mostrar los valores que la tarea manda usar. */
  data?: CreatorDetailData;
}) {
  const { t } = useT();
  const tieneFanvue = todas.some((task) => task.platform === 'fanvue');

  /*
    Las dos cosas que ordenan los pasos de Fanvue, preguntadas una vez por ficha.

    Cuál es la principal, y si ya cobró. Con eso se decide qué se muestra y qué espera: la
    principal hace un camino y las otras catorce hacen otro, y ninguna de las otras puede empezar
    hasta que ella haya completado un retiro.

    `useApi` con `null` no consulta, así que una influencer sin Fanvue no paga estas dos llamadas.
  */
  const ajustes = useApi<{ settings: { fanvuePrincipalCreatorId: string | null } }>(
    tieneFanvue ? '/api/settings' : null,
  );
  const fanvue = useApi<{ cuenta: boolean; kyc: boolean; retiro: boolean }>(
    tieneFanvue ? '/api/fanvue/cuenta' : null,
  );

  /*
    Sin ficha no se ordena nada, y por eso el orden depende de `data`.

    El componente se exporta y la ficha es opcional. Sin ella no se puede saber si ESTA es la
    principal, y `esLaPrincipal` daría `false` — que no significa «no lo es» sino «no se sabe».
    Con ese falso se escondería el paso del retiro y se bloquearía el grupo en una vista donde
    nadie decidió nada.
  */
  const seSabeElOrden = Boolean(data && ajustes.data && fanvue.data);
  const principal = seSabeElOrden
    ? (ajustes.data?.settings.fanvuePrincipalCreatorId ?? null)
    : null;
  const esLaPrincipal = principal !== null && principal === data?.creator.id;

  /*
    El paso del retiro sólo se muestra en la principal.

    Es un paso real de las quince fichas —se crea en todas, porque las tareas se crean por
    influencer— pero ocurre UNA vez y en una cuenta concreta. Mostrarlo en las catorce restantes
    diría que cada una tiene que cobrar antes de existir, que es al revés: cobran DESPUÉS.

    Mientras no se sepa cuál es la principal no se esconde en ninguna: esconder un paso por una
    respuesta que todavía no llegó lo hace desaparecer sin que nadie haya decidido nada.
  */
  const tasks = todas.filter(
    (task) =>
      !esPasoDeLaPersona(task) &&
      !(task.type === 'COMPLETE_WITHDRAWAL' && principal !== null && !esLaPrincipal),
  );

  if (tasks.length === 0) {
    return <EmptyState title={t('tareas.sinTareas')} description={t('tareas.sinTareasQue')} />;
  }

  return (
    <>
      <RequisitoDeFanvue visible={tieneFanvue} />
      {/*
        Arriba de las tareas, porque produce un dato que las demás piden.

        La dirección de esta página es lo que va en la biografía de cada red, y esas altas son
        justamente las tareas de abajo. Tenerla al final obligaría a hacer las altas primero y
        volver a editarlas después para pegar el enlace.
      */}
      {data && (
        <div className="mb-4">
          <PaginaPublica
            creatorId={data.creator.id}
            slug={data.creator.slug}
            publicadaEn={data.creator.proxyPublishedAt}
            tieneCanonica={data.creator.canonicalReferenceAssetId !== null}
            onChange={onChange}
          />
        </div>
      )}
      <ListaDeTareas
        tasks={tasks}
        onChange={onChange}
        data={data}
        fanvue={
          tieneFanvue && seSabeElOrden
            ? { principal, esLaPrincipal, retiro: fanvue.data?.retiro ?? false }
            : undefined
        }
      />
    </>
  );
}

/**
 * Avisa que faltan los pasos de Fanvue que se hacen en Credenciales.
 *
 * Sacarlos de la ficha resolvió que aparecieran repetidos en las trece influencers, pero se
 * llevó también la SECUENCIA: quien llega a «creá el perfil vinculado» sin cuenta verificada no
 * tiene forma de saber qué le falta ni dónde está. Un paso invisible es peor que uno repetido.
 *
 * Sólo aparece cuando algo falta y cuando la influencer tiene Fanvue. Si está todo hecho, no
 * dice nada: un cartel permanente que informa que todo está bien deja de leerse en una semana.
 */
function RequisitoDeFanvue({ visible }: { visible: boolean }) {
  const { t } = useT();
  const estado = useApi<{ cuenta: boolean; kyc: boolean }>(visible ? '/api/fanvue/cuenta' : null);

  if (!visible || !estado.data) return null;
  if (estado.data.cuenta && estado.data.kyc) return null;

  const falta = [
    !estado.data.cuenta ? t('tareas.faltaCuenta') : null,
    !estado.data.kyc ? t('tareas.faltaKyc') : null,
  ].filter(Boolean);

  return (
    <div className="mb-4 rounded-2xl border border-[var(--color-warn)]/40 bg-[var(--color-warn)]/10 p-4 text-sm">
      <strong>{t('tareas.antesDeFanvue')}</strong>
      {t('tareas.antesDeFanvueMedio', { que: falta.join(t('tareas.faltaY')) })}
      <strong>{t('tareas.unaSolaVez')}</strong>
      {t('tareas.antesDeFanvueDespues')}
      <Link to="/credenciales" className="underline underline-offset-2">
        {t('tareas.estaEnCredenciales')}
      </Link>
    </div>
  );
}

function ListaDeTareas({
  tasks,
  onChange,
  data,
  fanvue,
}: {
  tasks: TaskView[];
  onChange: () => void;
  data?: CreatorDetailData;
  /** Cómo está el orden de Fanvue. `undefined` mientras no se sepa, o si no tiene Fanvue. */
  fanvue?: { principal: string | null; esLaPrincipal: boolean; retiro: boolean };
}) {
  const { t, idioma } = useT();
  return (
    <div className="space-y-4">
      {/*
        Queda sólo para AGREGAR pasos nuevos. El texto de los que ya existen se pone al día
        solo al abrir la ficha: pedirle a alguien que pulse un botón para que la pantalla
        deje de mentirle exige saber de antemano que estaba mintiendo.
      */}
      {data && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[var(--color-border-subtle)] p-3 text-sm">
          <p className="text-[var(--color-ink-muted)]">{t('tareas.faltaAlgunPaso')}</p>
          <Button
            variant="secondary"
            onClick={() =>
              void api
                .post(`/api/creators/${data.creator.id}/onboarding-tasks?idioma=${idioma}`)
                .then(onChange)
            }
          >
            Actualizar pasos
          </Button>
        </div>
      )}

      {groupTasks(tasks).map((group) => (
        <TaskGroup
          key={group.key}
          group={group}
          onChange={onChange}
          data={data}
          /**
           * Las plataformas dependen del correo, y la dependencia es dura: toda red pide una
           * dirección al registrarse. Marcarla evita que alguien abra Instagram, llegue al
           * formulario y descubra ahí que le falta un dato.
           *
           * `undefined` cuando no hay ficha —la vista de todas las tareas la carga aparte—:
           * en ese caso no se bloquea nada, porque no se puede saber.
           */
          blockedBy={
            data && PLATFORM_GROUPS.includes(group.key) && !hasEmail(data)
              ? t('tareas.faltaCorreo')
              : group.key === 'fanvue'
                ? bloqueoDeFanvue(fanvue)
                : undefined
          }
        />
      ))}
    </div>
  );
}

/**
 * Qué falta antes de poder empezar los pasos de Fanvue de ESTA influencer.
 *
 * ── Los dos frenos, y por qué son distintos ───────────────────────────────────
 * El primero es nuestro: mientras no se sepa cuál es la cuenta principal, no se sabe qué
 * procedimiento corresponde. Una se da de alta con documento de identidad y las otras catorce
 * cuelgan de ella sin documentos; enseñar uno de los dos al azar manda a alguien a hacer un
 * trámite que no era.
 *
 * El segundo lo puso Fanvue: no habilita cuentas vinculadas hasta que la primera haya completado
 * un retiro. No es una recomendación — sin eso, la única forma de crear otra es el alta normal,
 * que pide documento otra vez y choca con su regla de una cuenta de creador por persona.
 *
 * ── Por qué avisa en vez de esconder ──────────────────────────────────────────
 * Porque un grupo que desaparece no explica nada. Esto lo deja a la vista, cerrado y con el
 * motivo escrito, que es la diferencia entre «todavía no» y «acá no hay nada».
 */
function bloqueoDeFanvue(estado?: {
  principal: string | null;
  esLaPrincipal: boolean;
  retiro: boolean;
}): string | undefined {
  // Sin respuesta todavía no se bloquea: no se puede afirmar que falte algo que no se preguntó.
  if (!estado) return undefined;

  if (estado.principal === null) {
    return texto('tareas.faltaPrincipal');
  }

  if (!estado.esLaPrincipal && !estado.retiro) {
    return texto('tareas.faltaRetiro');
  }

  return undefined;
}

/** Un grupo de tareas relacionadas, con su nombre y su avance. */
type TaskGrouping = { key: string; title: string; tasks: TaskView[] };

/** Grupos que no pueden empezarse sin una casilla de correo. */
const PLATFORM_GROUPS = ['instagram', 'facebook', 'fanvue'];

/**
 * Si la influencer ya tiene casilla.
 *
 * Se mira sobre las cuentas y no sobre el estado de la tarea: lo que habilita registrarse en
 * una red es que exista una dirección, no que alguien haya marcado un paso como hecho.
 */
function hasEmail(data: CreatorDetailData): boolean {
  return data.accounts.some((account) => (account.emailAddress ?? '').length > 0);
}

/**
 * Agrupa las tareas por lo que uno va a hacer de una sentada.
 *
 * Doce tarjetas seguidas son una lista imposible de leer; separadas en cinco bloques —el
 * correo, la revisión interna y una por red— cada bloque es una sesión de trabajo.
 *
 * La agrupación es por plataforma **salvo** en `manual_export`, que es el cajón del sistema
 * para lo que no pertenece a ninguna red y mezcla dos cosas sin relación: elegir la cara y
 * crear la casilla de correo. Agruparlas juntas por compartir un valor interno sería
 * ordenar la pantalla por cómo está guardado el dato en vez de por lo que hay que hacer.
 */
function groupTasks(tasks: TaskView[]): TaskGrouping[] {
  const groups = new Map<string, TaskGrouping>();

  /**
   * El orden es la secuencia real de trabajo.
   *
   * Primero la revisión interna —elegir la cara define quién es la influencer—, después el
   * correo, y al final las plataformas, que dependen de él: ninguna red deja registrarse
   * sin una dirección.
   */
  const order = ['interno', 'email', 'instagram', 'facebook', 'fanvue'];

  for (const task of tasks) {
    const { key, title } = groupOf(task);
    const existing = groups.get(key);
    if (existing) existing.tasks.push(task);
    else groups.set(key, { key, title, tasks: [task] });
  }

  return [...groups.values()].sort((a, b) => {
    const ia = order.indexOf(a.key);
    const ib = order.indexOf(b.key);
    // Una plataforma futura que no esté en la lista va al final, no al principio.
    return (ia === -1 ? order.length : ia) - (ib === -1 ? order.length : ib);
  });
}

const PLATFORM_TITLE: Record<string, string> = {
  instagram: 'Instagram',
  fanvue: 'Fanvue',
  x: 'X',
};

function groupOf(task: TaskView): { key: string; title: string } {
  if (task.type === 'CREATE_EMAIL_ACCOUNT')
    return { key: 'email', title: texto('tareas.grupoCorreo') };
  if (task.platform === 'manual_export')
    return { key: 'interno', title: texto('tareas.grupoInterno') };
  return { key: task.platform, title: PLATFORM_TITLE[task.platform] ?? task.platform };
}

function TaskGroup({
  group,
  onChange,
  data,
  blockedBy,
}: {
  group: TaskGrouping;
  onChange: () => void;
  data?: CreatorDetailData;
  /** Qué falta antes de poder empezar este grupo. `undefined` si no depende de nada. */
  blockedBy?: string;
}) {
  const pending = group.tasks.filter((task) => task.status !== 'COMPLETED').length;

  /**
   * Un grupo terminado arranca cerrado.
   *
   * Es la mitad del valor de agrupar: lo hecho deja de ocupar pantalla, pero sigue estando
   * a un clic —conviene poder releer qué usuario se registró en Instagram sin tener que
   * buscarlo en otro lado.
   */
  // Un grupo bloqueado también arranca cerrado: todavía no hay nada que hacer ahí.
  const [open, setOpen] = useState(pending > 0 && blockedBy === undefined);

  return (
    <section className="panel overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-5 py-3.5 text-left transition hover:bg-[var(--color-surface-sunken)]"
      >
        <span className="flex items-center gap-2.5">
          <span
            aria-hidden="true"
            className={
              'text-xs text-[var(--color-ink-faint)] transition-transform ' +
              (open ? 'rotate-90' : '')
            }
          >
            ▶
          </span>
          <span className="text-sm font-semibold">{group.title}</span>
          {blockedBy !== undefined ? (
            <span className="rounded-full border border-[var(--color-ink-faint)]/40 px-2 py-0.5 text-xs font-medium text-[var(--color-ink-faint)]">
              en espera
            </span>
          ) : pending > 0 ? (
            <span className="rounded-full bg-[var(--color-warn)]/15 px-2 py-0.5 text-xs font-medium text-[var(--color-warn)]">
              {pending} pendiente{pending === 1 ? '' : 's'}
            </span>
          ) : (
            <span className="text-xs text-[var(--color-ok)]">✓ completo</span>
          )}
        </span>
        <span className="text-xs tabular-nums text-[var(--color-ink-faint)]">
          {group.tasks.length - pending}/{group.tasks.length}
        </span>
      </button>

      {blockedBy !== undefined && (
        <p className="border-t border-[var(--color-border-subtle)] bg-[var(--color-warn)]/8 px-5 py-2.5 text-xs text-[var(--color-warn)]">
          {blockedBy}
        </p>
      )}

      {open && (
        <div className="space-y-3 border-t border-[var(--color-border-subtle)] p-5">
          {group.tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onChange={onChange}
              data={data}
              blocked={blockedBy !== undefined}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * Las instrucciones de una tarea, con los pasos numerados como pasos.
 *
 * ── Por qué ───────────────────────────────────────────────────────────────────
 * Se mostraban con `whitespace-pre-wrap` dentro de un solo párrafo: los saltos de línea se
 * respetaban, pero seis pasos seguidos quedaban como un muro de texto donde no se distinguía
 * dónde terminaba uno y empezaba el siguiente. Y son instrucciones que alguien sigue **mientras
 * opera en otra pestaña**, mirando de reojo — perder el renglón ahí cuesta caro.
 *
 * Un párrafo que arranca con «1. » se muestra con el número afuera y el texto alineado en su
 * propia columna, así que al volver la vista se cae en el paso, no en el medio de una oración.
 * El resto del texto no cambia de aspecto: lo que no está numerado sigue siendo un párrafo.
 */
function Instrucciones({ texto, className = '' }: { texto: string; className?: string }) {
  const parrafos = texto.split(/\n\s*\n/).filter((p) => p.trim() !== '');

  return (
    <div className={`space-y-2 ${className}`}>
      {parrafos.map((parrafo, i) => {
        const paso = /^(\d+)\.\s+([\s\S]*)$/.exec(parrafo.trim());

        if (!paso) {
          return (
            <p key={i} className="whitespace-pre-wrap">
              {parrafo.trim()}
            </p>
          );
        }

        /*
          El lugar donde ocurre el paso, destacado.

          Un procedimiento que va y vuelve entre dos sitios —el panel y la plataforma— se lee mal
          si hay que deducir de la oración dónde está uno parado. Los pasos que empiezan con una
          etiqueta corta y dos puntos («Acá:», «En Fanvue:») la muestran aparte, así que la
          alternancia se ve de un vistazo en vez de leerse.
        */
        const etiquetado = /^([^:\n]{1,16}):\s+([\s\S]*)$/.exec(paso[2] ?? '');

        return (
          <div key={i} className="flex gap-2">
            <span className="shrink-0 tabular-nums font-medium text-[var(--color-ink-muted)]">
              {paso[1]}.
            </span>
            <p className="whitespace-pre-wrap">
              {etiquetado && (
                <span className="mr-1.5 font-medium text-[var(--color-ink)]">{etiquetado[1]}:</span>
              )}
              {etiquetado ? etiquetado[2] : paso[2]}
            </p>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Cuál influencer es la cuenta principal de Fanvue, y qué implica para ésta.
 *
 * ── Por qué ───────────────────────────────────────────────────────────────────
 * Las influencers no se crean todas igual: la primera va por el alta normal y lleva verificación
 * de identidad, y las demás cuelgan de ella como cuentas vinculadas, sin documentos. La tarea
 * puede explicar los dos caminos, pero no puede saber cuál le toca a ésta — la principal es la
 * que tiene el KYC, y eso vive en Fanvue, no acá.
 *
 * Así que se pregunta una vez y se guarda. Después cada ficha muestra sólo su camino, en vez de
 * dos ramas entre las que hay que elegir cada vez. Elegir mal no es gratis: se descubre a mitad
 * del alta, cuando Fanvue pide un documento que no correspondía.
 */
/** El correo con el que quedó registrada la cuenta principal, que el panel no puede saber. */
function CorreoDeLaPrincipal({
  creatorId,
  correoActual,
  onChange,
}: {
  creatorId: string;
  correoActual: string | null;
  onChange: () => void;
}) {
  const { t } = useT();
  const aviso = useAviso();
  const [valor, setValor] = useState(correoActual ?? '');
  const [busy, setBusy] = useState(false);

  const guardar = () => {
    setBusy(true);
    void api
      .patch(`/api/creators/${creatorId}/platforms/fanvue`, { emailAddress: valor.trim() })
      .then(() => aviso.ok('Correo guardado.'))
      .then(onChange)
      .catch(() => undefined)
      .finally(() => setBusy(false));
  };

  return (
    <div className="mt-2.5">
      <label className="text-xs text-[var(--color-ink-faint)]">
        {t('principal.correoRegistrada')}
      </label>
      <div className="mt-1 flex flex-wrap gap-2">
        <input
          type="email"
          value={valor}
          onChange={(e) => setValor(e.target.value)}
          placeholder={t('principal.elQueUsaste')}
          className="min-w-0 flex-1 rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-surface-raised)] px-2 py-1 font-mono text-xs"
        />
        <button
          type="button"
          disabled={busy || valor.trim() === (correoActual ?? '')}
          onClick={guardar}
          className="rounded-lg border border-[var(--color-border-subtle)] px-3 py-1 text-sm hover:border-[var(--color-accent)] disabled:opacity-40"
        >
          {t(busy ? 'comun.guardando' : 'comun.guardar')}
        </button>
      </div>
      <p className="mt-1 text-xs text-[var(--color-ink-muted)]">{t('principal.correoQue')}</p>
    </div>
  );
}

function CuentaPrincipalDeFanvue({
  creatorId,
  publicName,
  principal,
  correoActual,
  onSaved,
  onChange,
}: {
  creatorId: string;
  publicName: string;
  principal: string | null;
  /** El que hoy figura en su fila de Fanvue. */
  correoActual: string | null;
  onSaved: () => void;
  onChange: () => void;
}) {
  const { t } = useT();
  const otras = useApi<{ creators: CreatorSummary[] }>('/api/creators');
  const aviso = useAviso();
  const [busy, setBusy] = useState(false);

  const esEsta = principal === creatorId;
  const nombreDeLaPrincipal =
    otras.data?.creators.find((c) => c.id === principal)?.publicName ?? t('principal.laPrincipal');

  const marcar = (valor: string | null) => {
    setBusy(true);
    void api
      .patch('/api/settings', { fanvuePrincipalCreatorId: valor })
      .then(onSaved)
      .then(() =>
        aviso.ok(
          valor ? t('principal.ahoraEs', { quien: publicName }) : t('principal.sinPrincipal'),
        ),
      )
      .catch(() => undefined)
      .finally(() => setBusy(false));
  };

  /*
    Mientras nadie la eligió, la ficha muestra esto y NADA MÁS.

    Cualquier otra cosa que dijera estaría mal en al menos una influencer: los pasos de la
    principal y los de una vinculada no se parecen —una sube un documento, la otra ni siquiera
    entra a esa cuenta—. Mostrar el procedimiento equivocado y corregirlo después es peor que no
    mostrar ninguno, porque quien lo leyó ya empezó a hacerlo.

    Ocultar el resto lo decide la tarjeta; acá sólo se declara que falta la respuesta.
  */
  if (principal === null) {
    return (
      <div className="rounded-xl border border-[var(--color-warn)]/40 bg-[var(--color-warn)]/5 p-3">
        <p className="text-sm">
          {t('principal.antesDeEmpezar')}
          <strong>{t('principal.cualEs')}</strong>
          {t('principal.cualEsQue')}
        </p>
        <button
          type="button"
          disabled={busy}
          onClick={() => marcar(creatorId)}
          className="mt-2.5 rounded-lg border border-[var(--color-border-subtle)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)] disabled:opacity-40"
        >
          {busy ? t('comun.guardando') : t('principal.es', { quien: publicName })}
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-3">
      {/*
        El recuadro ES el paso de crear la cuenta, no un aviso sobre él.

        Antes el paso 2 de las instrucciones decía «creá su cuenta» y este recuadro decía cómo, así
        que la misma acción aparecía dos veces y en dos lugares distintos de la tarjeta. Ahora el
        paso salió de la lista y vive acá, donde está la única información que decide cuál de los
        dos procedimientos corre.
      */}
      <p className="text-xs font-medium text-[var(--color-ink-muted)]">
        {t('principal.comoSeCrea')}
      </p>
      {esEsta ? (
        <>
          <p className="mt-0.5 text-sm font-medium">{t('principal.altaNormal')}</p>
          <p className="mt-1 text-xs text-[var(--color-ink-muted)]">
            {t('principal.altaNormalAntes')}
            <strong>{t('principal.llevaKyc')}</strong>
            {t('principal.altaNormalDespues')}
          </p>

          {/*
            El correo REAL de su cuenta, que no tiene por qué ser la casilla del panel.

            A las vinculadas el panel les impone la casilla, porque es donde llega el enlace de
            alta. La principal no: se registra con el correo que el dueño elija, y muy a menudo ya
            existía antes que este panel. Mostrar la casilla como si fuera su correo guarda un dato
            falso — y encima es el que sirve para recuperar la cuenta.
          */}
          <CorreoDeLaPrincipal
            creatorId={creatorId}
            correoActual={correoActual}
            onChange={onChange}
          />
        </>
      ) : (
        <>
          <p className="mt-0.5 text-sm font-medium">{t('principal.vinculada')}</p>
          <p className="mt-1 text-xs text-[var(--color-ink-muted)]">
            {t('principal.vinculadaAntes')}
            <strong>{nombreDeLaPrincipal}</strong>
            {t('principal.vinculadaMedio1')}
            <strong>{t('principal.tipoIa')}</strong>
            {t('principal.vinculadaMedio2')}
            <strong>{t('principal.noUsesAltaNormal')}</strong>
            {t('principal.vinculadaMedio3')}
            <strong>{t('principal.unRetiro')}</strong>.
          </p>
        </>
      )}
      {/*
        Corregir cuál es la principal es un botón, no un enlace al pie.

        Es la decisión que ordena las trece fichas, y elegirla mal manda a un alta con documento
        que no correspondía. Escondida en letra chica, se descubre después de haber seguido el
        camino equivocado.
      */}
      <button
        type="button"
        disabled={busy}
        onClick={() => marcar(esEsta ? null : creatorId)}
        className="mt-2.5 rounded-lg border border-[var(--color-border-subtle)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)] disabled:opacity-40"
      >
        {busy
          ? t('comun.guardando')
          : esEsta
            ? t('principal.esOtra')
            : t('principal.enRealidadEs', { quien: publicName })}
      </button>
    </div>
  );
}

function TaskCard({
  task,
  onChange,
  data,
  blocked = false,
}: {
  task: TaskView;
  onChange: () => void;
  data?: CreatorDetailData;
  /** `true` si falta un paso previo. Se muestra igual, pero no se puede completar. */
  blocked?: boolean;
}) {
  const { t } = useT();
  const taskTypeLabel = useTaskTypeLabel();

  /*
    Cuál es la cuenta principal de Fanvue, y qué hacer mientras no se sepa.

    Se pide sólo en la ficha que lo usa —`useApi` con `null` no consulta nada— para no repetir la
    misma llamada en las doce tarjetas del grupo.

    Mientras la respuesta falte, la tarjeta muestra ÚNICAMENTE la pregunta. Los pasos de una
    cuenta principal y los de una vinculada no se parecen: una sube un documento de identidad, la
    otra ni siquiera entra a esa cuenta. Enseñar uno de los dos al azar y corregirlo después es
    peor que no enseñar ninguno, porque quien lo leyó ya empezó a hacerlo.
  */
  const esAltaDeFanvue =
    task.type === 'APPROVE_PROFILE' && task.platform === 'fanvue' && task.status !== 'COMPLETED';
  const ajustes = useApi<{ settings: { fanvuePrincipalCreatorId: string | null } }>(
    esAltaDeFanvue ? '/api/settings' : null,
  );
  /*
    Los ajustes vienen ANIDADOS bajo `settings`, junto a `flags` y al catálogo de modelos.

    Leerlos de la raíz devolvía `undefined`, que no es `null` — y esa diferencia rompía las dos
    mitades a la vez: el recuadro veía «sin elegir» y preguntaba, y el resto de la tarjeta veía
    «todavía no llegó» y se mostraba igual. Aparecían la pregunta y el procedimiento juntos, que
    es justo lo que esto venía a evitar.
  */
  const principalDeFanvue = ajustes.data?.settings.fanvuePrincipalCreatorId ?? null;
  const faltaElegirPrincipal =
    esAltaDeFanvue && ajustes.data !== null && principalDeFanvue === null;

  return (
    <div className="rounded-xl border border-[var(--color-border-subtle)] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold">
            {taskTypeLabel(task.type, task.platform)} · {task.platform}
          </p>
          <StatusBadge status={task.status} />
        </div>
        {task.status !== 'COMPLETED' && task.status !== 'BLOCKED' && (
          <div className="flex gap-2">
            {task.externalUrl && (
              <a
                href={task.externalUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] px-4 py-2 text-sm font-medium transition hover:border-[var(--color-border-strong)] hover:bg-[var(--color-accent-soft)]"
              >
                {t('cont.abrirPlataforma')}
              </a>
            )}
            {/*
                  Los pasos que crean algo afuera no se cierran con un botón suelto: lo que
                  importa es el dato que produjeron. Ver `CompleteWithValue`.
                */}
            {!capturesValue(task) && (
              <Button
                disabled={blocked}
                title={blocked ? t('tarea.faltaPasoPrevio') : undefined}
                onClick={() => void api.post(`/api/tasks/${task.id}/complete`).then(onChange)}
              >
                {t('tarea.yaLoCompleté')}
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => void api.post(`/api/tasks/${task.id}/revalidate`).then(onChange)}
            >
              {t('tarea.revalidar')}
            </Button>
          </div>
        )}
      </div>

      {task.status === 'COMPLETED' ? (
        /*
              Hecha ya no se lee: se consulta.
              Lo que importa entonces es el resultado —qué dirección, qué usuario quedó— y
              cuándo. Las instrucciones siguen ahí por si hay que rehacer el paso, pero
              plegadas: dejar un instructivo de cinco puntos abierto sobre algo terminado hace
              que la lista de tareas parezca llena de trabajo pendiente que no existe.
            */
        <div className="mt-4 space-y-3 text-sm">
          {data && <TaskOutcome task={task} data={data} />}

          {/*
                Lo cargado se puede corregir después de terminar la tarea.

                Antes no: se completaba, la tarjeta pasaba a «hecho» y una errata en el usuario
                o en la contraseña quedaba grabada sin ninguna forma de arreglarla desde el
                panel. Y son justo los datos que se escriben apurado, copiando del formulario
                de la red mientras se está dando de alta.
              */}
          <div className="flex flex-wrap items-center gap-4">
            {data && capturesValue(task) && (
              <CorrectTaskValue task={task} data={data} onChange={onChange} />
            )}

            {/*
                  Deshacer es para el otro error: haber marcado hecho algo que no se hizo.
                  
                  Sólo devuelve el estado. Lo que la tarea guardó —usuario, correo,
                  contraseña— sigue en la cuenta: borrarlo sería peor que el error que se
                  está deshaciendo, porque la cuenta existe en la red igual.
                */}
            <button
              type="button"
              onClick={() => void api.post(`/api/tasks/${task.id}/reopen`).then(onChange)}
              className="text-xs text-[var(--color-ink-muted)] underline underline-offset-2 hover:text-[var(--color-warn)]"
            >
              Deshacer
            </button>
          </div>

          <details>
            <summary className="cursor-pointer text-xs text-[var(--color-ink-muted)]">
              {t('tarea.verInstrucciones')}
            </summary>
            <Instrucciones
              texto={task.instructions}
              className="mt-2 text-sm text-[var(--color-ink-muted)]"
            />
          </details>
        </div>
      ) : (
        <div className="mt-4 space-y-3 text-sm">
          {/*
              Antes de los pasos, no después.

              Este recuadro decide QUÉ SIGNIFICAN los cinco pasos: si esta influencer es la cuenta
              principal, el paso 2 es un alta con documento; si es una vinculada, es un formulario
              en la configuración de otra cuenta. Puesto abajo, se leía el procedimiento entero y
              recién al final se sabía cuál de los dos era.
            */}
          {esAltaDeFanvue && ajustes.data !== null && data && (
            <CuentaPrincipalDeFanvue
              creatorId={task.creatorId}
              publicName={data.creator.publicName}
              principal={principalDeFanvue}
              correoActual={
                data.accounts.find((cuenta) => cuenta.platform === 'fanvue')?.emailAddress ?? null
              }
              onSaved={ajustes.reload}
              onChange={onChange}
            />
          )}

          {!faltaElegirPrincipal && (
            <>
              <div>
                <p className="text-xs font-medium text-[var(--color-ink-muted)]">
                  {t('tarea.queHacer')}
                </p>
                <Instrucciones texto={task.instructions} className="mt-0.5" />
              </div>

              {task.status !== 'COMPLETED' && capturesValue(task) && !blocked && (
                <>
                  {task.type === 'CREATE_EMAIL_ACCOUNT' && (
                    <CompleteWithValue
                      task={task}
                      onChange={onChange}
                      field="emailAddress"
                      label={
                        data?.direccionDeCorreo
                          ? t('tarea.aliasSobreCasilla')
                          : t('tarea.direccionCreada')
                      }
                      placeholder="nombre@gmail.com"
                      initial={data?.direccionDeCorreo ?? ''}
                      options={data?.suggestedEmails ?? []}
                      submitLabel={
                        data?.direccionDeCorreo
                          ? t('tarea.usarEstaDireccion')
                          : t('tarea.guardarYCompletar')
                      }
                      hint={
                        data?.direccionDeCorreo
                          ? t('tarea.nadaQueCrear')
                          : t('tarea.seGuardaParaTodas')
                      }
                    />
                  )}

                  {/*
                Generar la portada, sólo en Facebook.

                Es la única plataforma de las cuatro que pide una imagen apaisada, y el sistema
                no produce ninguna: todo el material es vertical porque es lo que rinde en un
                feed. El botón la genera anclada a la hoja de identidad, así que es la misma
                persona que en el resto.
              */}
                  {task.type === 'APPROVE_PROFILE' && task.platform === 'fanvue' && (
                    <CompleteWithValue
                      task={task}
                      onChange={onChange}
                      field="username"
                      label={t('tarea.usuarioEnFanvue')}
                      placeholder="camilarivas"
                      initial=""
                      submitLabel={t('tarea.guardarYCompletar')}
                      withPassword
                      withFreeTrial
                      hint={t('tarea.usuarioEnFanvueQue')}
                    />
                  )}

                  {task.type === 'CREATE_ACCOUNT' && task.platform === 'facebook' && (
                    <GenerarPortada creatorId={task.creatorId} onChange={onChange} />
                  )}

                  {task.type === 'CREATE_ACCOUNT' &&
                    (task.platform === 'facebook' ? (
                      /*
                    En una Página el nombre de usuario es opcional, y la contraseña no existe.

                    Una Página no tiene credenciales propias: la administra un perfil que ya
                    tiene las suyas. Y el nombre de usuario —el que queda en
                    facebook.com/<usuario>— se puede dejar vacío, y Facebook a veces ni siquiera
                    lo permite en una Página recién creada.

                    Con el formulario común, completar la tarea exigía inventar un dato que
                    puede no existir todavía.
                  */
                      <CompleteWithValue
                        task={task}
                        onChange={onChange}
                        field="externalUserId"
                        /*
                      Lo que se pide es el IDENTIFICADOR, no el nombre de usuario.

                      El nombre de usuario es opcional, Facebook suele no permitirlo en Páginas
                      nuevas, y no sirve para publicar. El identificador sí: es lo que la API
                      usa, y es lo único que distingue una Página sin ambigüedad.

                      Y resuelve algo que hoy queda al azar: quien administra varias Páginas
                      —cualquiera con más de una influencer— tenía todas conectadas a la primera
                      que devolvía Meta, que es un orden que nadie eligió. Publicaba bien, en la
                      Página equivocada.
                    */
                        label={t('tarea.identificadorPagina')}
                        placeholder="1255352300996216"
                        initial=""
                        submitLabel={t('tarea.guardarYCompletar')}
                        hint={t('tarea.identificadorPaginaQue')}
                      />
                    ) : (
                      <CompleteWithValue
                        task={task}
                        onChange={onChange}
                        field="username"
                        label={t('tarea.usuarioRegistradoEn', { plataforma: task.platform })}
                        placeholder="usuario"
                        initial={usernamesFor(task, data)[0] ?? ''}
                        options={usernamesFor(task, data)}
                        submitLabel={t('tarea.guardarYCompletar')}
                        // Un canal no tiene contraseña propia: ver la tarjeta de plataformas.
                        withPassword={task.platform !== 'telegram'}
                        withFreeTrial={task.platform === 'fanvue'}
                        hint={t('tarea.usuarioRegistradoQue')}
                      />
                    ))}
                </>
              )}

              {/*
              Los valores concretos, no su descripción.
              «Usá el nombre de usuario sugerido» obliga a ir a buscarlo a otra pestaña y
              transcribirlo. Con el dato acá y un botón de copiar, la tarea se hace sin salir
              de la tarjeta — que es el único motivo por el que una tarjeta de tarea existe.
            */}
              {data && <TaskData task={task} data={data} />}

              {/*
              Y el botón que la asigna, también acá.

              Mostrar el dato pero mandar a otra pestaña a generarlo dejaba la tarea a medias: se
              veía qué faltaba y no se podía resolver. Sigue siendo un botón, y no algo que pase
              solo al abrir la ficha, porque la dirección es permanente — que haya que pulsarlo
              la mantiene como una decisión.
            */}
              {task.type === 'APPROVE_PROFILE' &&
                task.platform === 'fanvue' &&
                task.status !== 'COMPLETED' &&
                !blocked &&
                data &&
                faltaCasillaDeFanvue(
                  data.accounts.find((cuenta) => cuenta.platform === 'fanvue'),
                ) && <AsignarCorreo creatorId={task.creatorId} onChange={onChange} />}

              {/*
              El porqué y las advertencias, abajo y plegados.

              §16 pide explicar por qué la tarea es necesaria. Se sigue explicando — plegar no es
              esconder—, pero se leen una vez y después estorban: son texto fijo que se repite
              igual en las doce tareas, y estaban ANTES de los datos que hay que copiar. Para
              llegar a lo que se usa había que pasar por encima de lo que ya se sabía.

              El orden ahora sigue el momento en que cada cosa hace falta: qué hacer, con qué
              hacerlo, y recién después por qué.
            */}
              <details className="group">
                <summary className="cursor-pointer list-none text-xs text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]">
                  <span className="group-open:hidden">{t('tarea.porQueCerrado')}</span>
                  <span className="hidden group-open:inline">{t('tarea.porQueAbierto')}</span>
                </summary>

                <div className="mt-2 space-y-3">
                  <Instrucciones texto={task.rationale} className="text-[var(--color-ink-muted)]" />

                  {task.dataToUse.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-[var(--color-ink-muted)]">
                        {t('tarea.queDatosUsar')}
                      </p>
                      <ul className="mt-0.5 list-disc pl-5 text-[var(--color-ink-muted)]">
                        {task.dataToUse.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {task.dataToNeverShare.length > 0 && (
                    <div className="rounded-lg border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/5 p-3">
                      <p className="text-xs font-semibold text-[var(--color-danger)]">
                        {t('tarea.queNoCompartir')}
                      </p>
                      <ul className="mt-1 list-disc pl-5">
                        {task.dataToNeverShare.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </details>

              {task.blockedReason && (
                <p className="text-sm text-[var(--color-warn)]">Bloqueada: {task.blockedReason}</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Con qué quedó resuelta una tarea.
 *
 * Se lee del estado actual del sistema, no de la tarea: la tarea registra que el paso se
 * hizo, y el dato que produjo vive donde corresponde —la dirección en la cuenta, el usuario
 * en la plataforma—. Copiarlo dentro de la tarea al completarla haría que mostrara algo
 * viejo en cuanto cambiara.
 */
function TaskOutcome({ task, data }: { task: TaskView; data: CreatorDetailData }) {
  const { t } = useT();
  const account = data.accounts.find((candidate) => candidate.platform === task.platform);
  const anyEmail = data.accounts.find((candidate) => (candidate.emailAddress ?? '').length > 0);

  const result =
    task.type === 'CREATE_EMAIL_ACCOUNT'
      ? anyEmail?.emailAddress
      : task.type === 'CREATE_ACCOUNT'
        ? account?.username
        : null;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-[var(--color-ok)]/30 bg-[var(--color-ok)]/8 p-3">
      <span className="text-sm font-medium text-[var(--color-ok)]">{t('tarea.hecho')}</span>
      {result ? (
        <>
          <code className="rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-surface-raised)] px-2 py-1 font-mono text-xs break-all">
            {result}
          </code>
          <CopyButton text={result} label={t('comun.copiar')} />
        </>
      ) : (
        <span className="text-xs text-[var(--color-ink-muted)]">
          {task.completedAt ? formatDateTime(task.completedAt) : t('tarea.marcadaCompletada')}
        </span>
      )}
    </div>
  );
}

function WorkflowTab({ data }: { data: CreatorDetailData }) {
  const { t } = useT();
  if (data.workflowRuns.length === 0) {
    return <EmptyState title={t('wf.sinCorridas')} description={t('wf.sinCorridasQue')} />;
  }

  return (
    <div className="space-y-4">
      {data.workflowRuns.map((run) => (
        <Card key={run.id} title={t('wf.corrida', { id: run.id.slice(-8) })}>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <StatusBadge status={run.status} />
            <span className="text-[var(--color-ink-muted)]">{formatDateTime(run.startedAt)}</span>
            <span className="text-[var(--color-ink-muted)]">
              {t('wf.creditos', { n: run.estimatedNeurons.toFixed(0) })}
            </span>
          </div>

          <ol className="mt-4 space-y-1">
            {run.completedSteps.map((step) => (
              <li key={step} className="flex items-center gap-2 text-xs">
                <span className="text-[var(--color-ok)]" aria-hidden="true">
                  ✓
                </span>
                <span>{step}</span>
              </li>
            ))}
            {run.currentStep && !run.completedSteps.includes(run.currentStep) && (
              <li className="flex items-center gap-2 text-xs font-medium">
                <span className="text-[var(--color-accent)]" aria-hidden="true">
                  ▸
                </span>
                <span>{run.currentStep}</span>
              </li>
            )}
          </ol>

          {run.error && (
            <p className="mt-3 text-sm text-[var(--color-danger)]">
              {run.error.code}: {run.error.message}
            </p>
          )}
        </Card>
      ))}
    </div>
  );
}

/**
 * Las etapas que consumieron, no un total suelto.
 *
 * «40.920 créditos en 15 imágenes» al lado de una pestaña Contenido con dos tomas se lee como
 * si se hubieran cobrado trece que no están. Están: son las candidatas frontales —de las que
 * se elige una y el resto se descarta, que es el punto— y los tres ángulos de la hoja.
 *
 * El desglose es la respuesta a «¿por qué pagué quince?», y sin él la única lectura posible
 * era que algo se cobró de más.
 */
const USAGE_STAGES: { label: Clave; hint: Clave; kinds: (kind: string) => boolean }[] = [
  {
    label: 'uso.candidatas',
    hint: 'uso.candidatasQue',
    kinds: (kind) => kind === 'reference_candidate' || kind === 'canonical_reference',
  },
  {
    label: 'uso.hoja',
    hint: 'uso.hojaQue',
    kinds: (kind) => kind.startsWith('identity_'),
  },
  {
    label: 'uso.tomas',
    hint: 'uso.tomasQue',
    kinds: (kind) => kind === 'content_scene',
  },
];

function UsageTab({ data }: { data: CreatorDetailData }) {
  const { t } = useT();
  const total = data.assets.reduce((sum, asset) => sum + asset.provenance.estimatedNeurons, 0);

  const porEtapa = USAGE_STAGES.map((stage) => {
    const assets = data.assets.filter((asset) => stage.kinds(asset.kind));
    return {
      ...stage,
      count: assets.length,
      credits: assets.reduce((sum, asset) => sum + asset.provenance.estimatedNeurons, 0),
    };
  }).filter((stage) => stage.count > 0);

  // Un tipo de asset que no encaje en ninguna etapa dejaría el desglose sin cuadrar con el
  // total, y un desglose que no suma es peor que no tener desglose.
  const clasificadas = porEtapa.reduce((sum, stage) => sum + stage.count, 0);
  const otras = data.assets.length - clasificadas;

  return (
    <div className="space-y-6">
      <Card title={t('uso.titulo')}>
        <p className="text-3xl font-semibold tabular-nums">{total.toFixed(0)}</p>
        <p className="mt-1 text-sm text-[var(--color-ink-muted)]">
          {t('uso.creditosEn', { n: data.assets.length })}
        </p>

        <ul className="mt-4 divide-y divide-[var(--color-border-subtle)]">
          {porEtapa.map((stage) => (
            <li key={stage.label} className="flex items-baseline justify-between gap-4 py-2">
              <div>
                <p className="text-sm font-medium">{t(stage.label)}</p>
                <p className="text-xs text-[var(--color-ink-muted)]">{t(stage.hint)}</p>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-sm font-semibold tabular-nums">
                  {t('uso.imagenes', { n: stage.count })}
                </p>
                <p className="text-xs tabular-nums text-[var(--color-ink-muted)]">
                  {t('wf.creditos', { n: stage.credits.toFixed(0) })}
                </p>
              </div>
            </li>
          ))}
          {otras > 0 && (
            <li className="flex items-baseline justify-between gap-4 py-2 text-sm">
              <span className="text-[var(--color-ink-muted)]">{t('uso.otras')}</span>
              <span className="tabular-nums">{t('uso.imagenes', { n: otras })}</span>
            </li>
          )}
        </ul>
      </Card>

      <Card title={t('uso.publicacionesExternas')}>
        {data.posts.length === 0 ? (
          <p className="text-sm text-[var(--color-ink-muted)]">{t('uso.nadaPublicado')}</p>
        ) : (
          <ul className="divide-y divide-[var(--color-border-subtle)]">
            {data.posts.map((post) => (
              <li key={post.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <span>{post.platform}</span>
                <div className="flex items-center gap-3">
                  {/* Sin métricas de la API no se muestra un número: se dice que no hay (§12.11). */}
                  <span className="text-xs text-[var(--color-ink-muted)]">
                    {t(post.metrics ? 'uso.conMetricas' : 'uso.sinMetricas')}
                  </span>
                  <StatusBadge status={post.status} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function AuditTab({ data }: { data: CreatorDetailData }) {
  const { t } = useT();
  return (
    <Card title={t('audit.titulo')}>
      {data.audit.length === 0 ? (
        <p className="text-sm text-[var(--color-ink-muted)]">{t('audit.sinEventos')}</p>
      ) : (
        <ul className="divide-y divide-[var(--color-border-subtle)]">
          {data.audit.map((entry, index) => (
            <li
              key={`${entry.action}-${index}`}
              className="flex justify-between gap-3 py-2 text-sm"
            >
              <span className="font-mono text-xs">{entry.action}</span>
              <span className="text-xs text-[var(--color-ink-muted)]">
                {entry.actorType} · {formatDateTime(entry.createdAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * El error que dejó a una influencer a mitad de camino, con la salida que corresponda.
 *
 * ── Por qué no un «Reintentar» a secas ──────────────────────────────────────────
 * La mitad de las corridas que fallan mueren en la validación de identidad, por una política
 * que se arregla cambiando una palabra de la ficha. Reintentar con el mismo texto falla
 * idéntico — y un botón que no hace nada es peor que no tener botón: la primera vez se prueba,
 * la segunda se desconfía del resto de la pantalla.
 *
 * El sistema ya sabe cuál es cuál. Cada error lleva `retryable`, y esa distinción existe desde
 * que se decidió no reintentar violaciones de política; hasta ahora no había llegado a la
 * interfaz.
 *
 * ── Lo que NO se pierde al retomar ──────────────────────────────────────────────
 * La ficha se guarda **antes** de validarse, así que existe aunque la validación haya fallado.
 * Retomar reutiliza la influencer, su nombre y su ficha; las candidatas ya generadas tampoco se
 * vuelven a pagar. Crear una nueva tiraría todo eso.
 */
function ErrorDeCreacion({
  error,
  enError,
  creatorId,
  onRetomar,
}: {
  error: { code: string; message: string; retryable: boolean };
  enError: boolean;
  creatorId: string;
  onRetomar: () => void;
}) {
  const { t } = useT();
  const aviso = useAviso();
  const navigate = useNavigate();
  const [yendo, setYendo] = useState(false);

  const retomar = async (): Promise<void> => {
    setYendo(true);
    try {
      const { workflowRunId } = await api.post<{ workflowRunId: string }>(
        `/api/creators/${creatorId}/retry`,
      );
      navigate(`/workflow/${workflowRunId}`);
    } catch (caught) {
      if (!(caught instanceof ApiRequestError)) aviso.error(t('errcre.noSePudoRetomar'));
      setYendo(false);
      onRetomar();
    }
  };

  return (
    <div
      role="alert"
      className="rounded-xl border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/10 p-4"
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-semibold text-[var(--color-danger)]">{error.code}</p>
        {/*
          Cerrarlo, porque si no se queda hasta que otra corrida lo pise.

          Un aviso ya resuelto —o que se decidió no resolver, como un bloqueo de gasto en una
          influencer que se deja quieta— ocupa semanas el lugar donde va el próximo. A la tercera
          vez que aparece el mismo, nadie los lee.

          No se borra solo al retomar porque a veces no se retoma: cerrar es del que mira, no del
          sistema. Y si el problema sigue, la próxima corrida lo vuelve a escribir.
        */}
        <button
          type="button"
          title={t('errcre.descartar')}
          onClick={() => {
            void api
              .post(`/api/creators/${creatorId}/descartar-error`, {})
              .then(onRetomar)
              .catch(() => undefined);
          }}
          className="shrink-0 rounded-lg px-2 text-lg leading-none text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]"
        >
          ×
        </button>
      </div>
      <p className="mt-1 text-sm">{error.message}</p>

      {enError && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {/*
            El remedio lo decide el **código**, no el booleano `retryable`.

            Con dos ramas —reintentable o no— todo lo no reintentable caía en «corregí la ficha»,
            y eso sólo es cierto para una violación de política. Se vio en producción: alguien
            eligió el nivel «atrevido», que genera con fal, y como era una cuenta nueva tenía el
            modo USD 0 activo por defecto. El sistema bloqueó el gasto —correctamente— y la
            pantalla le pidió que corrigiera un texto que no tenía nada de malo.

            Un mensaje exacto con un remedio equivocado es peor que no decir nada: manda a
            alguien a arreglar lo que funciona mientras lo que falla queda sin tocar.
          */}
          {error.code === 'SPEND_BLOCKED' ? (
            <>
              <Button variant="secondary" onClick={() => navigate('/consumo')}>
                {t('errcre.irAConsumo')}
              </Button>
              <Button disabled={yendo} onClick={() => void retomar()}>
                {t(yendo ? 'errcre.retomando' : 'errcre.yaLoHabilite')}
              </Button>
              <span className="w-full text-xs text-[var(--color-ink-muted)]">
                {t('errcre.gastoAntes')}
                <strong>{t('errcre.modoCero')}</strong>
                {t('errcre.gastoDespues')}
              </span>
            </>
          ) : error.code === 'QUOTA_EXHAUSTED' ? (
            <>
              <Button disabled={yendo} onClick={() => void retomar()}>
                {t(yendo ? 'errcre.retomando' : 'errcre.reintentarAhora')}
              </Button>
              <span className="w-full text-xs text-[var(--color-ink-muted)]">
                {t('errcre.cuotaQue')}
              </span>
            </>
          ) : error.code === 'POLICY_VIOLATION' ? (
            <>
              {/*
                Sin botón de reintentar: con el mismo texto volvería a fallar igual. Primero se
                corrige la ficha —guardarla ya la valida— y recién entonces tiene sentido seguir.
              */}
              <Button
                variant="secondary"
                onClick={() => navigate(`/influencer/${creatorId}?tab=identidad`)}
              >
                {t('errcre.corregirFicha')}
              </Button>
              <Button disabled={yendo} onClick={() => void retomar()}>
                {t(yendo ? 'errcre.retomando' : 'errcre.yaLaCorregi')}
              </Button>
              <span className="w-full text-xs text-[var(--color-ink-muted)]">
                {t('errcre.politicaQue')}
              </span>
            </>
          ) : (
            <>
              <Button disabled={yendo} onClick={() => void retomar()}>
                {t(yendo ? 'errcre.retomando' : 'errcre.reintentar')}
              </Button>
              <span className="text-xs text-[var(--color-ink-muted)]">
                {t(error.retryable ? 'errcre.reintentable' : 'errcre.noReintentable')}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
